// ═══════════════════════════════════════════════════════════════
// ARCHIVO DESTINO: frontend/src/Agencia.jsx
// ═══════════════════════════════════════════════════════════════
import { useState, useEffect, useMemo, useRef, Component } from "react";
import { getFrontendConfig } from "./config";
import { fechaHora, horaLocal } from "./fechas";
import BrandMark from "./BrandMark";
import Icon from "./Icon";
import PageHeader from "./PageHeader";
// La mascota de los estados vacíos. Este archivo no la importaba: los
// estados vacíos del sportsbook de la agencia eran una raya de texto
// gris —o un ícono suelto— donde el jugador pone el personaje, y
// `emptyStatesShowMascot.test.js` no podía verlo porque leía solo
// `App.jsx` y `Web.jsx`.
import Mascot from "./Mascot";
import LineaTiempo from "./LineaTiempo";
import { Handshake, Video, Zap, Gift, Monitor, Banknote, Wrench, Link, PartyPopper, Printer, Building2, Star, Pencil, Send, Lock, Minus, VolumeX, Volume2, Headphones, Bot, PenLine, Smartphone, RefreshCw, Key, Save, Palette, Trash2, Globe, FileText, Image as ImageIcon, Bell, Moon, Scale } from "lucide-react";
import { useDesktopShellWidth } from "./desktopShellLayout";
import MobileTabMenu from "./MobileTabMenu";
import { picksDelEvento } from "./agenciaPicks";
// La combinada del mismo partido: el tope por partido y la cuota con el
// margen de correlación. La regla vive una sola vez en su módulo, y eso es lo
// que el T7 de sportsbook-homologado vino a arreglar: hasta entonces existía
// sólo dentro de `FlujoManual`, y la pantalla de venta en vivo del mostrador
// reemplazaba el primer mercado del partido sin avisar. Esa pantalla ya no
// está —se fue con `/api/live/combined`— y el módulo se queda igual: lo
// comparten `FlujoManual`, el armador de combos y /sitio.
import { topeMismoPartido, puedeSumarAlPartido,
  useCuotaCombinada } from "./combosMismoPartido";
import { conRef, envioCumplido, mensajeDeConflicto } from "./refDeApuesta";
// Las piezas del sportsbook que el jugador ya tiene. Estaban copiadas a
// mano en este archivo y pasó lo previsible: se rediseñó una pantalla
// (PR #231 y #232) y esta se quedó en la versión anterior durante
// semanas. Se importan, no se vuelven a declarar: la guardia
// `unSoloCarrusel.test.js` exige que el carrusel viva en un solo .jsx.
import { CuotasCarrusel, CSS_CUOTAS_CARRUSEL, tituloMercado, ordenMercado,
  etiquetaResultado } from "./cuotasDeEvento";
import { SelectorDesplegable } from "./filtrosDeEvento";
import { filtrarCatalogo, deportesDisponibles,
  ligasDisponibles } from "./catalogoDeportivo";

// La hora del partido, en la zona horaria del dispositivo.
// Se prefiere commence_time (ISO con zona) sobre el texto ya
// formateado que manda el servidor: el navegador sabe dónde está
// el usuario y no hay que adivinarlo por IP.
// `horaLocal` vive en `fechas.js`: era idéntica en cuatro pantallas y el
// panel del admin no la tenía, así que ahí la hora salía en la zona de la
// casa y no en la de quien miraba.

// Captura errores de render y muestra el mensaje en pantalla (evita pantalla negra)
class CazaError extends Component {
  constructor(p){ super(p); this.state={err:null}; }
  static getDerivedStateFromError(err){ return {err}; }
  componentDidCatch(err,info){ console.error("CazaError:",err,info); }
  render(){
    if(this.state.err){
      return (
        <div style={{padding:SPACING[20],background:"#1a0000",minHeight:"100vh",color:"#fff",
          fontFamily:"monospace",fontSize:13,whiteSpace:"pre-wrap",lineHeight:1.5}}>
          <div style={{color:"#FF1744",fontWeight:700,fontSize:16,marginBottom:10}}>
            <Icon name="triangle-alert" size={16}/> Error en esta pantalla</div>
          <div style={{color:"#FFB800",marginBottom:8}}>{String(this.state.err&&this.state.err.message||this.state.err)}</div>
          <div style={{color:"#888",fontSize:12,marginBottom:16}}>
            {String(this.state.err&&this.state.err.stack||"").slice(0,600)}</div>
          <button onClick={()=>this.setState({err:null})}
            style={{background:"#7C3AED",border:"none",borderRadius:RADII.md,padding:"12px 20px",
              color:"#fff",fontSize:14,cursor:"pointer"}}>Reintentar</button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { oscuro as Q, F_BODY, F_MONO, F_NUM, RADII, SPACING, TEXT, ELEVATION, inkOn, ov } from "./theme";
import { money } from "./moneda";

// Atajo para los montos que todavia no saben de que moneda son. Delega en
// el formateador unico; M4-M6 cambian cada llamada por money(n, moneda).
// Ya no hay `ars` acá: M5 dejó esta pantalla sin un solo monto que no sepa
// su moneda. Los documentos que se IMPRIMEN —el ticket y el cierre— además
// llevan el código al lado del símbolo: en un papel nadie puede preguntar
// después cuál de los cuatro "$" era.

// Montos sugeridos según la moneda. 20.000 es una apuesta corriente en
// pesos y una barbaridad en dólares, así que la escala se ajusta.
// Devuelve siempre 4 valores para las fichas rápidas.
const MONTOS_POR_MONEDA = {
  ARS: [2000, 5000, 10000, 20000],
  CLP: [2000, 5000, 10000, 20000],
  COP: [5000, 10000, 20000, 50000],
  PYG: [10000, 20000, 50000, 100000],
  USD: [5, 10, 20, 50],
  EUR: [5, 10, 20, 50],
  BRL: [10, 25, 50, 100],
  UYU: [100, 250, 500, 1000],
  PEN: [10, 25, 50, 100],
  MXN: [50, 100, 250, 500],
};
function montosDe(moneda){
  return MONTOS_POR_MONEDA[(moneda||"ARS").toUpperCase()]
      || MONTOS_POR_MONEDA.ARS;
}
// El monto inicial: el segundo de la lista, ni el mínimo ni el máximo.
function montoInicial(moneda){ return montosDe(moneda)[1]; }

// ── EL NOMBRE DEL EMISOR DE LA FILA FIJA DE 1X2 ───────────────
//
// Las cuatro pantallas que dibujan el 1X2 con el atajo `ev.odds.L/E/V` —acá,
// `Web.jsx`, `Box.jsx` y el armador de combos del admin— necesitan el NOMBRE
// con el que GR8 emite ese resultado, que es lo único que `resolver_una`
// encuentra en el índice de claves de selección. `ev.odds` no lo trae: lo arma
// `gr8_catalogo._odds_compactas` colapsando `h2h_3_way` y `h2h` en tres
// posiciones, así que mirándolo no se puede saber de qué mercado salió.
//
// Lo que sí trae el catálogo es `ev.markets`, el diccionario del emisor. Acá se
// lee UNA clave literal —`h2h`— y el nombre sale de adentro de ese mercado. No
// se deriva nada: no se prueba `h2h_3_way` "por si acaso" ni se traduce
// "Empate" a "Draw" con una tabla nuestra, porque la etiqueta de pantalla no es
// inyectiva sobre los nombres del emisor y la mejor tabla posible acierta 1 de
// 4 (medido en L3).
//
// Cuando el nombre NO está en ese diccionario, el nombre y el mercado van los
// dos VACÍOS. Es el caso del 1X2 del fútbol, que GR8 emite en `h2h_3_way`: el
// pick sigue sin liquidar —como antes— pero no miente diciendo que GR8 publicó
// ese resultado en `h2h`. Un pick sin clave es honesto; uno con la clave
// inventada se liquida contra la pregunta que el jugador no hizo. Es la misma
// regla y el mismo repliegue que `OddsButtons` (`cuotasDeEvento.jsx:152`).
//
// Van juntos a propósito: los dos salen del MISMO diccionario, así que mandar
// uno sin el otro sería afirmar la mitad de algo que no se leyó.
//
// Está copiada textual en las otras tres pantallas. Su casa sería un módulo
// compartido —`cuotasDeEvento.jsx`, que esta pantalla ya importa—, pero ese
// archivo no estaba en las superficies de esta tanda; queda anotado. Mientras
// tanto `losCombosSeLiquidan.test.js` ejecuta las cuatro copias y exige que
// den lo mismo, letra por letra.
export function nombresDe1x2(ev){
  const mkt = (ev?.markets || {}).h2h || {};
  const home = ev?.h || ev?.home || "";
  const away = ev?.a || ev?.away || "";
  const de = (nombre) => (nombre in mkt
    ? { nombre, market: "h2h" }
    : { nombre: "", market: "" });
  return { L: de(home), E: de("Draw"), V: de(away) };
}

const fmt = n => Number(n||0).toFixed(2);
const nowStr = () => new Date().toLocaleString("es-AR",{hour12:false});
const expires24 = () => { const d=new Date(); d.setHours(d.getHours()+24); return d.toLocaleString("es-AR",{hour12:false}); };

// ── API ───────────────────────────────────────────────────────
const { apiUrl: API_URL, appOrigin: APP_ORIGIN, botUsername: BOT_USERNAME } = getFrontendConfig();

// ── Compartir combos en redes: texto + placa PNG ──────────────
function textoCombo(combo, ref, link){
  const picks=(combo.picks||[]).map((p,i)=>{
    const local=p.home||p.h||"";
    const sel=p.sel||p.label||"";
    const odd=fmt(p.odd||p.cuota||0);
    return `${i+1}. ${local?local+" — ":""}${sel} @${odd}`;
  }).join("\n");
  return `🔥 ${combo.nombre||"COMBINADA"} 🔥\n\n`+
    `${picks}\n\n`+
    `💰 Cuota total: ${fmt(combo.odd||combo.odd_total||0)}x\n`+
    (combo.codigo?`🎟️ Código: ${combo.codigo}\n`:"")+
    `🎟️ Mi código: ${ref}\n`+
    `👉 Jugá acá: ${link}`;
}

function descargarPlacaCombo(combo, ref, link){
  try{
    const W=1080, H=1350;
    const cv=document.createElement("canvas"); cv.width=W; cv.height=H;
    const x=cv.getContext("2d");
    // Fondo degradé
    const g=x.createLinearGradient(0,0,W,H);
    g.addColorStop(0,"#0a0118"); g.addColorStop(0.5,"#1a0a3a"); g.addColorStop(1,"#04040f");
    x.fillStyle=g; x.fillRect(0,0,W,H);
    // Marco
    x.strokeStyle="#7C3AED"; x.lineWidth=6;
    x.strokeRect(30,30,W-60,H-60);
    // Título marca
    x.fillStyle="#00F0FF"; x.font="bold 64px system-ui"; x.textAlign="center";
    x.fillText("IAQP", W/2, 130);
    // Nombre del combo
    x.fillStyle="#ffffff"; x.font="bold 52px system-ui";
    const nombre=(combo.nombre||"COMBINADA").toUpperCase();
    x.fillText(nombre.length>22?nombre.slice(0,22)+"…":nombre, W/2, 240);
    // Picks
    x.textAlign="left"; x.font="34px system-ui";
    let y=360;
    const picks=(combo.picks||[]).slice(0,8);
    picks.forEach((p,i)=>{
      const local=p.home||p.h||"";
      const sel=p.sel||p.label||"";
      const odd=fmt(p.odd||p.cuota||0);
      x.fillStyle="#a78bfa"; x.fillText(`${i+1}.`, 90, y);
      x.fillStyle="#ffffff";
      let linea=`${local?local+" — ":""}${sel}`;
      if(linea.length>34) linea=linea.slice(0,34)+"…";
      x.fillText(linea, 150, y);
      x.fillStyle="#00F0FF"; x.textAlign="right";
      x.fillText(`@${odd}`, W-90, y);
      x.textAlign="left";
      y+=70;
    });
    // Cuota total
    y+=20;
    x.fillStyle="#22c55e"; x.font="bold 60px system-ui"; x.textAlign="center";
    x.fillText(`CUOTA ${fmt(combo.odd||combo.odd_total||0)}x`, W/2, y);
    // Código y link
    y=H-300;
    if(combo.codigo){
      x.fillStyle="#E8C547"; x.font="bold 44px system-ui";
      x.fillText(`Código: ${combo.codigo}`, W/2, y); y+=70;
    }
    x.fillStyle="#E8C547"; x.font="bold 44px system-ui";
    x.fillText(`Mi código: ${ref}`, W/2, y); y+=90;
    x.fillStyle="#7C3AED"; x.fillRect(W/2-360,y-50,720,90);
    x.fillStyle="#ffffff"; x.font="bold 34px system-ui";
    x.fillText("👉 JUGÁ CONMIGO", W/2, y+10);
    // Bot de Telegram bien visible
    x.fillStyle="#00F0FF"; x.font="bold 40px system-ui";
    x.fillText(`📲 Telegram: @${BOT_USERNAME}`, W/2, y+90);
    // Descargar
    cv.toBlob(b=>{
      const a=document.createElement("a");
      a.href=URL.createObjectURL(b);
      a.download=`combo_${ref}_${combo.id||"qp"}.png`;
      a.click();
    },"image/png");
    return true;
  }catch(e){ return false; }
}

const API_BOT = API_URL;

class SinConexion extends Error {
  constructor(){ super("Sin conexión con el servidor"); this.name="SinConexion"; }
}
class SesionExpirada extends Error {
  constructor(){ super("La sesión expiró"); this.name="SesionExpirada"; }
}

// Header de autenticación con el token que devolvió el login
const authHeaders = (token) => token ? {"Authorization":`Bearer ${token}`} : {};

async function fetchBetslip(code, token){
  let r;
  try {
    r = await fetch(`${API_URL}/api/betslip/${code}`, {
      headers: authHeaders(token),
    });
  } catch(e) {
    throw new SinConexion();
  }
  if(r.status===401) throw new SesionExpirada();
  if(!r.ok){
    const e = await r.json().catch(()=>({}));
    throw new Error(e.detail || `Error ${r.status}`);
  }
  return r.json();
}

async function payBetslip(code, stake, token){
  let r;
  try {
    r = await fetch(`${API_URL}/api/betslip/${code}/pay`, {
      method:"POST",
      headers:{"Content-Type":"application/json", ...authHeaders(token)},
      body:JSON.stringify({stake}),
    });
  } catch(e) {
    throw new SinConexion();
  }
  if(r.status===401) throw new SesionExpirada();
  if(!r.ok){
    const e = await r.json().catch(()=>({}));
    throw new Error(e.detail || `No se pudo registrar el pago (${r.status})`);
  }
  return r.json();
}

// ── DESIGN ────────────────────────────────────────────────────
function GCard({ children, style={}, glow, onClick, level=1 }){
  // `level` is what a card is sitting on, not how important it is.
  //
  // Level 1 sits on the page and keeps the prototype's panel treatment: an
  // opaque surface, one hairline, and elevation. Level 2 sits inside another
  // card or a sheet, where a second identical hairline separates without
  // ranking — nest three of them and every box looks equally important. It
  // steps the surface up to `raised` instead and drops the border, so depth
  // is read from the surface rather than from yet another line.
  //
  // `glow` outranks both: a coloured border means this box needs attention.
  // That is the one job a border does here that a surface cannot.
  const raised = level >= 2;
  return(
    <div onClick={onClick} style={{
      background: raised ? Q.raised : Q.card,
      border: glow ? `1px solid ${glow}44`
        : raised ? "none" : `1px solid ${Q.border}`,
      borderRadius: raised ? RADII.md : RADII.lg,
      boxShadow: glow ? `${ELEVATION}, 0 0 24px ${glow}22`
        : raised ? "none" : ELEVATION,
      // overflow visible: con "hidden" la tarjeta recorta lo que exceda
      // su alto y un campo entero puede quedar invisible sin ningún
      // error. Ya pasó en Agencia y costó horas de diagnóstico.
      position:"relative", overflow:"visible", maxWidth:"100%", minWidth:0,
      cursor:onClick?"pointer":"default", ...style,
    }}>
      {!raised&&<div style={{position:"absolute",top:0,left:0,right:0,height:1,
        background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.1),transparent)",
        pointerEvents:"none"}}/>}
      {children}
    </div>
  );
}

function AlertaError({ mensaje, critico=false }){
  if(!mensaje) return null;
  return(
    <div style={{
      background:critico?`${Q.red}22`:"rgba(255,23,68,0.10)",
      border:`2px solid ${Q.red}`, borderRadius:RADII.lg,
      padding:critico?"16px":"12px 14px", marginBottom:12,
      display:"flex", alignItems:"flex-start", gap:SPACING[12],
    }}>
      <span style={{fontSize:critico?24:18,lineHeight:1}}><Icon name="triangle-alert" size={critico?22:16}/></span>
      <div>
        {critico&&<div style={{color:Q.red,fontWeight:900,fontSize:16,
          fontFamily:F_BODY,marginBottom:4,letterSpacing:0.5}}>
          NO COBRAR
        </div>}
        <div style={{color:critico?Q.text:Q.red,fontSize:critico?13:12,
          fontFamily:F_BODY,lineHeight:1.4}}>{mensaje}</div>
      </div>
    </div>
  );
}

// Antes dibujaba su propio hexágono en SVG junto a "IAQP" en degradé,
// como placeholder de un logo que todavía no existía. Ahora que el logo
// real está disponible (BrandMark), el hexágono queda de más: la imagen
// misma es la marca, tal como la usa el prototipo (sin hexágono al lado).
function QPLogo({ size=20 }){
  return <BrandMark size={size}/>;
}

function Btn({ label, onClick, color=Q.violet, outline=false, size="md", full=false, disabled=false }){
  const h=size==="lg"?"50px":size==="sm"?"34px":"44px";
  const fs=size==="lg"?15:size==="sm"?12:13;
  return(
    <button onClick={onClick} disabled={disabled} style={{
      height:h, width:full?"100%":"auto",
      padding:`0 ${size==="sm"?"10px":"18px"}`,
      // An outline button used to be a coloured 1px box. Stack four of
      // them and the screen is all edges and no ranking, which is the
      // whole complaint. A secondary action reads perfectly well as a
      // raised surface with a coloured label: the colour still says what
      // kind of action it is, without drawing a frame around it.
      background:disabled?"rgba(255,255,255,0.04)":outline?"rgba(255,255,255,0.06)":`linear-gradient(135deg,${color},${color}CC)`,
      border:outline&&!disabled?"none":`1px solid ${disabled?Q.dim:color}`,borderRadius:RADII.lg,
      // inkOn measures contrast against the fill and picks dark or light
      // ink — a hardcoded "#fff" is unreadable on a light accent like
      // Q.green. Do not special-case any one colour here; let the helper
      // decide for all of them.
      color:disabled?Q.muted:outline?color:inkOn(color),
      fontSize:fs,fontWeight:700,cursor:disabled?"not-allowed":"pointer",
      display:"flex",alignItems:"center",justifyContent:"center",gap:SPACING[8],
      fontFamily:F_BODY,textTransform:"uppercase",
      // A neutral shadow, not a coloured halo: ELEVATION is the
      // prototype's panel-level shadow (18px offset, 32px blur) and
      // reads as an oversized floating box under a button this small.
      boxShadow:(!outline&&!disabled)?"0 4px 12px rgba(0,0,0,0.35)":"none",
    }}>{label}</button>
  );
}

// ── TICKET TÉRMICO ────────────────────────────────────────────
// En el celular no sirve abrir una ventana y auto-cerrarla: se cierra
// antes de que el usuario elija impresora o "Guardar como PDF".
// En su lugar mostramos el ticket en pantalla con botones para
// imprimir/guardar PDF y descargar, y el usuario decide cuándo.
async function registrarImpresion(tipo, referencia){
  try{
    let token=null;
    try{ const g=JSON.parse(localStorage.getItem("qp_agencia")||"null"); token=g&&g.token; }catch(e){}
    await fetch(`${API_URL}/api/imprimir`,{
      method:"POST",
      headers:{"Content-Type":"application/json",...(token?{"Authorization":`Bearer ${token}`}:{})},
      body:JSON.stringify({tipo:tipo||"ticket",referencia:referencia||""}),
    });
  }catch(e){}
}

function abrirVentanaImpresion(html, w=420, h=650){
  try{
    const cont = document.createElement("div");
    cont.id = "qp-ticket-overlay";
    cont.style.cssText =
      "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.85);"+
      "display:flex;flex-direction:column;align-items:center;"+
      "overflow-y:auto;padding:16px;-webkit-overflow-scrolling:touch;";

    const barra = document.createElement("div");
    barra.style.cssText =
      "display:flex;gap:8px;width:100%;max-width:420px;margin-bottom:12px;"+
      "position:sticky;top:0;";
    barra.innerHTML =
      '<button id="qp-print" style="flex:1;background:linear-gradient(135deg,#7C3AED,#00F0FF);'+
      'border:none;border-radius:10px;padding:13px;color:#fff;font-weight:700;'+
      'font-size:14px;font-family:system-ui;cursor:pointer">🖨️ Imprimir / Guardar PDF</button>'+
      '<button id="qp-close" style="background:rgba(255,255,255,0.15);'+
      'border:none;border-radius:10px;padding:13px 18px;color:#fff;font-weight:700;'+
      'font-size:14px;font-family:system-ui;cursor:pointer">✕</button>';

    const marco = document.createElement("iframe");
    marco.style.cssText =
      "width:100%;max-width:420px;height:70vh;border:none;border-radius:12px;"+
      "background:white;box-shadow:0 8px 40px rgba(0,0,0,0.5);";

    cont.appendChild(barra);
    cont.appendChild(marco);
    document.body.appendChild(cont);

    const doc = marco.contentWindow.document;
    doc.open(); doc.write(html); doc.close();

    const cerrar = ()=>{ try{ document.body.removeChild(cont); }catch(e){} };
    document.getElementById("qp-close").onclick = cerrar;
    document.getElementById("qp-print").onclick = ()=>{
      try{
        marco.contentWindow.focus();
        marco.contentWindow.print();   // el usuario elige impresora o PDF
        registrarImpresion(window.__qpTicketTipo||"ticket", window.__qpTicketRef||"");
      }catch(e){
        alert("No se pudo abrir el diálogo de impresión.");
      }
    };
    return true;
  }catch(e){
    // Respaldo: abrir en pestaña nueva sin auto-cerrar
    const win = window.open("","_blank");
    if(!win){
      alert("El navegador bloqueó la ventana.\nPermití las ventanas emergentes para este sitio.");
      return false;
    }
    win.document.write(html);
    win.document.close();
    return true;
  }
}

function printTicket(slip, tipo="apuesta"){
  try{
    window.__qpTicketTipo = tipo;
    window.__qpTicketRef = (slip && (slip.code||slip.id)) ? String(slip.code||slip.id) : "";
  }catch(e){}
  const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Courier New',monospace;font-size:12px;width:80mm;padding:4mm;background:white;color:black}
.c{text-align:center}.b{font-weight:bold}.lg{font-size:15px}.xl{font-size:20px}
.div{border-top:1px dashed #000;margin:5px 0}
.row{display:flex;justify-content:space-between;margin:2px 0}
.pick{margin:4px 0;padding:3px 0;border-bottom:1px dotted #aaa}
.ft{font-size:10px;text-align:center;color:#555;margin-top:4px}
</style></head><body>
<div class="c b xl">IAQP</div>
<div class="c" style="font-size:10px">Agencia Oficial · ${slip.agencia||"AGE001"}</div>
<div class="div"></div>
<div class="c b lg">${tipo==="cobro"?"COMPROBANTE DE COBRO":tipo==="cierre"?"CIERRE DE CAJA":"TICKET DE APUESTA"}</div>
<div class="c b" style="font-size:18px;margin:4px 0">#${slip.code}</div>
<div class="row"><span>Usuario:</span><span>${slip.user||"—"}</span></div>
<div class="row"><span>Emitido:</span><span>${slip.created_at||nowStr()}</span></div>
<div class="row"><span>Válido hasta:</span><span>${slip.expires_at||expires24()}</span></div>
<div class="div"></div>
<div class="b c">COMBINADA (${slip.picks?.length||0} picks)</div>
<div class="div"></div>
${(slip.picks||[]).map((p,i)=>`
<div class="pick">
  <div class="b">${i+1}. ${p.home} vs ${p.away}</div>
  <div>${p.sport}</div>
  <div class="row"><span>→ ${p.sel}</span><span class="b">@${p.odd}</span></div>
</div>`).join("")}
<div class="div"></div>
<div class="row b"><span>CUOTA TOTAL:</span><span>${fmt(slip.odd_total)}x</span></div>
<div class="row b lg"><span>APOSTADO:</span><span>${money(slip.stake, slip.moneda, {conCodigo:true})}</span></div>
<div class="row b lg"><span>RETORNO POT.:</span><span>${money(slip.potential_win, slip.moneda, {conCodigo:true})}</span></div>
${tipo==="cobro"?`<div class="div"></div><div class="c b" style="font-size:16px">PAGADO: ${money(slip.potential_win, slip.moneda, {conCodigo:true})}</div>`:""}
<div class="div"></div>
<div class="c" style="font-size:40px;margin:6px 0">▓▓▓▓▓▓</div>
<div class="c b">${slip.code}</div>
<div class="div"></div>
<div class="ft">${APP_ORIGIN.replace(/^https?:\/\//,"")} · @${BOT_USERNAME}</div>
<div class="ft">Jugá con responsabilidad · Solo mayores de 18</div>
</body></html>`;
  return abrirVentanaImpresion(html, 420, 650);
}

function printCierre(data, tipo, agencia){
  const mon = agencia?.moneda || "ARS";
  const html=`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Courier New',monospace;font-size:12px;width:80mm;padding:4mm;background:white;color:black}
.c{text-align:center}.b{font-weight:bold}.lg{font-size:15px}.xl{font-size:18px}
.div{border-top:1px dashed #000;margin:5px 0}
.row{display:flex;justify-content:space-between;margin:3px 0}
.ft{font-size:10px;text-align:center;color:#555;margin-top:4px}
</style></head><body>
<div class="c b xl">IAQP</div>
<div class="c b">CIERRE ${tipo.toUpperCase()}</div>
<div class="c">${agencia.name} · ${agencia.code}</div>
<div class="div"></div>
<div class="row"><span>Período:</span><span>${data.periodo}</span></div>
<div class="row"><span>Generado:</span><span>${nowStr()}</span></div>
<div class="div"></div>
<div class="b c">RESUMEN OPERACIONES</div>
<div class="div"></div>
<div class="row"><span>Total tickets:</span><span>${data.tickets}</span></div>
<div class="row"><span>Tickets bot:</span><span>${data.tickets_bot}</span></div>
<div class="row"><span>Tickets manual:</span><span>${data.tickets_manual}</span></div>
<div class="div"></div>
<div class="row b lg"><span>COBRADO:</span><span>${money(data.cobrado, mon, {conCodigo:true})}</span></div>
<div class="row b"><span>PAGADO (premios):</span><span>${money(data.pagado, mon, {conCodigo:true})}</span></div>
<div class="div"></div>
<div class="row b lg"><span>NETO AGENCIA:</span><span>${money(data.neto, mon, {conCodigo:true})}</span></div>
<div class="div"></div>
<div class="ft">Este comprobante es válido como cierre oficial.</div>
<div class="ft">iaqp.com · ${agencia.code}</div>
</body></html>`;
  return abrirVentanaImpresion(html, 420, 600);
}



// ── ESCUDO DE EQUIPO ──────────────────────────────────────────
function hashColor(str){
  let h=0;
  for(let i=0;i<(str||"").length;i++) h=((h<<5)-h)+str.charCodeAt(i);
  h=Math.abs(h);
  const colors=["#7C3AED","#2979FF","#00BCD4","#FF6B35","#E91E63",
    "#009688","#FF5722","#3F51B5","#8BC34A","#FF9800",
    "#9C27B0","#00ACC1","#43A047","#F4511E","#1E88E5"];
  return colors[h % colors.length];
}

function TeamLogo({ name, size=28 }){
  const [falloImg,setFalloImg]=useState(false);
  const color = hashColor(name);
  const iniciales = (name||"?").split(" ").filter(Boolean)
    .slice(0,2).map(w=>w[0].toUpperCase()).join("");

  if(name && !falloImg) return(
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:"rgba(255,255,255,0.06)",border:`1.5px solid ${color}66`,
      display:"flex",alignItems:"center",justifyContent:"center",
      overflow:"hidden"}}>
      <img src={`${API_URL}/api/team-logo/nombre/${encodeURIComponent(name)}`}
        alt={name} loading="lazy" onError={()=>setFalloImg(true)}
        style={{width:"78%",height:"78%",objectFit:"contain"}}/>
    </div>
  );

  return(
    <div style={{width:size,height:size,borderRadius:"50%",flexShrink:0,
      background:`linear-gradient(135deg,${color}CC,${color}88)`,
      border:`1.5px solid ${color}`,display:"flex",
      alignItems:"center",justifyContent:"center"}}>
      <span style={{color:"#fff",fontWeight:900,fontSize:size*0.36,
        fontFamily:F_BODY,lineHeight:1}}>
        {iniciales||"?"}
      </span>
    </div>
  );
}

// El diccionario de mercados en español, `tituloMercado`,
// `ordenMercado` y `etiquetaResultado` estaban copiados acá byte por
// byte desde `App.jsx`. Ahora se importan de `cuotasDeEvento.jsx`: son
// los mismos nombres que ve el jugador, y una tabla de 34 mercados
// escrita dos veces es una tabla que mañana se traduce en una sola
// pantalla.

// ── PANEL DE MERCADOS DE UN EVENTO ────────────────────────────
// Muestra lo que ya vino en el listado y, además, pide a la API los
// mercados adicionales (córners, tarjetas, goleadores) que solo se
// pueden consultar evento por evento.
function MercadosEvento({ ev, bets, onToggle, color=Q.violet }){
  const [extra,setExtra]=useState(null);
  const [cargando,setCargando]=useState(false);
  const [aviso,setAviso]=useState("");

  const home = ev.h || ev.home || "";
  const away = ev.a || ev.away || "";
  const sportKey = ev.sport_key;

  useEffect(()=>{
    if(!sportKey || !ev.id) return;
    let vivo = true;
    setCargando(true);
    fetch(`${API_BOT}/api/event/${sportKey}/${ev.id}/markets`)
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!vivo) return;
        if(d?.markets && Object.keys(d.markets).length) setExtra(d.markets);
        else setAviso("No hay mercados adicionales para este partido");
      })
      .catch(()=>{ if(vivo) setAviso("No se pudieron cargar los mercados"); })
      .finally(()=>{ if(vivo) setCargando(false); });
    return ()=>{ vivo=false; };
  },[sportKey, ev.id]);

  const todos = { ...(ev.markets||{}), ...(extra||{}) };
  const claves = Object.keys(todos)
    .filter(k=>todos[k] && Object.keys(todos[k]).length)
    .sort((a,b)=>ordenMercado(a)-ordenMercado(b));

  if(!claves.length && !cargando)
    return (
      <div style={{color:Q.dim,fontSize:12,padding:"12px 0",textAlign:"center",
        fontFamily:F_BODY}}>
        {aviso||"Sin mercados disponibles"}
      </div>
    );

  return(
    <div style={{marginTop:8,paddingTop:SPACING[8],borderTop:`1px solid ${Q.dim}`}}>
      {claves.map(k=>{
        const resultados = Object.entries(todos[k]);
        // Los mercados de jugador traen decenas de nombres: scroll propio
        const muchos = resultados.length > 6;
        return(
          <div key={k} style={{marginBottom:10}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,fontFamily:F_BODY,
              marginBottom:5,display:"flex",justifyContent:"space-between"}}>
              <span>{tituloMercado(k)}</span>
              {muchos&&<span style={{color:Q.dim}}>{resultados.length} opciones</span>}
            </div>
            <div style={{
              display:"grid",
              gridTemplateColumns: resultados.length<=3?`repeat(${resultados.length},1fr)`:"1fr 1fr",
              gap:SPACING[4],
              maxHeight: muchos?180:"none",
              overflowY: muchos?"auto":"visible",
            }}>
              {resultados.map(([nombre,cuota])=>{
                const etiqueta = etiquetaResultado(nombre, home, away);
                const sel = bets.some(b=>b.id===ev.id && b.label===etiqueta);
                return(
                  // El mismo bucle que `MercadosEvento` de `App.jsx`, y la
                  // misma razón para el quinto argumento: `nombre` —la clave
                  // cruda del diccionario del emisor, que es lo que
                  // `Object.entries(todos[k])` desarma— viaja AL LADO de la
                  // etiqueta, nunca en su lugar. La etiqueta es la identidad
                  // del pick en el carrito (`b.label===etiqueta`, arriba) y
                  // es lo que el cajero lee; el nombre es lo único que el
                  // índice de claves de GR8 conoce. Sin él el boleto se cobra
                  // y lo liquida una persona a mano.
                  //
                  // `k` va cuarto por lo mismo: con el mercado vacío el
                  // nombre hay que buscarlo en todos los del evento, y el
                  // nombre pelado de un equipo lo producen dos.
                  <button key={nombre}
                    onClick={()=>onToggle(ev, etiqueta, cuota, k, nombre)}
                    style={{
                      background:sel?`linear-gradient(135deg,${color}44,${Q.violet}22)`
                                    :"rgba(255,255,255,0.04)",
                      border:`1.5px solid ${sel?color:Q.border}`,
                      borderRadius:RADII.md,padding:"8px 8px",cursor:"pointer",
                      display:"flex",alignItems:"center",
                      justifyContent:"space-between",gap:SPACING[8],minWidth:0,
                    }}>
                    <span style={{color:Q.muted,fontSize:12,
                      fontFamily:F_BODY,
                      overflow:"hidden",textOverflow:"ellipsis",
                      whiteSpace:"nowrap",flex:1,textAlign:"left"}}>{etiqueta}</span>
                    <span style={{color:sel?color:Q.text,fontWeight:700,fontSize:12,
                      fontFamily:F_BODY,flexShrink:0}}>
                      {fmt(cuota)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {cargando&&(
        <div style={{color:Q.dim,fontSize:12,textAlign:"center",padding:"8px 0",
          fontFamily:F_BODY}}>
          Buscando córners, tarjetas y goleadores...
        </div>
      )}
    </div>
  );
}

// ── FILTRO DE EVENTOS ─────────────────────────────────────────
// El cajero necesita encontrar un partido puntual rápido, con el cliente
// esperando en el mostrador. Filtra por liga y por nombre de equipo.
function FiltroEventos({ ligas, liga, setLiga, busqueda, setBusqueda, total }){
  return(
    <div style={{marginBottom:12}}>
      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
        background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
        borderRadius:RADII.md,padding:"8px 12px",marginBottom:8}}>
        <span style={{color:Q.muted,fontSize:15}}><Icon name="search" size={14} color={Q.muted}/></span>
        <input value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          placeholder="Buscar equipo..."
          style={{background:"transparent",border:"none",color:Q.text,
            fontSize:14,flex:1,minWidth:0,
            fontFamily:F_BODY}}/>
        {busqueda&&(
          <button onClick={()=>setBusqueda("")} style={{background:"transparent",
            border:"none",color:Q.muted,fontSize:16,cursor:"pointer",
            padding:0,lineHeight:1}}>✕</button>
        )}
      </div>

      <div style={{display:"flex",gap:SPACING[4],overflowX:"auto",paddingBottom:SPACING[4],
        WebkitOverflowScrolling:"touch"}}>
        <button onClick={()=>setLiga(null)} style={{
          background:!liga?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`
                          :"rgba(255,255,255,0.04)",
          border:`1px solid ${!liga?Q.cyan:Q.border}`,borderRadius:RADII.xl,
          padding:"8px 16px",cursor:"pointer",color:!liga?Q.cyan:Q.muted,
          fontSize:12,fontWeight:!liga?700:400,whiteSpace:"nowrap",
          flexShrink:0,fontFamily:F_BODY,
        }}>Todas ({total})</button>
        {ligas.map(l=>(
          <button key={l.name} onClick={()=>setLiga(l.name)} style={{
            background:liga===l.name?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`
                                    :"rgba(255,255,255,0.04)",
            border:`1px solid ${liga===l.name?Q.cyan:Q.border}`,borderRadius:RADII.xl,
            padding:"8px 16px",cursor:"pointer",
            color:liga===l.name?Q.cyan:Q.muted,
            fontSize:12,fontWeight:liga===l.name?700:400,whiteSpace:"nowrap",
            flexShrink:0,fontFamily:F_BODY,
          }}>{l.icon} {l.name} ({(l.events||[]).length})</button>
        ))}
      </div>
    </div>
  );
}

// Aplica liga + texto sobre la lista de deportes
function filtrarDeportes(deportes, liga, busqueda){
  const q = (busqueda||"").trim().toLowerCase();
  return (deportes||[])
    .filter(d=>!liga || d.name===liga)
    .map(d=>({...d, events:(d.events||[]).filter(ev=>{
      if(!q) return true;
      return `${ev.h||ev.home||""} ${ev.a||ev.away||""}`.toLowerCase().includes(q);
    })}))
    .filter(d=>d.events.length>0);
}

// ── LOGIN ──────────────────────────────────────────────────────
function LoginScreen({ onLogin }){
  const [user,setUser]=useState("");
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  const login=async()=>{
    if(loading) return;
    setErr(""); setLoading(true);
    let r;
    try {
      r = await fetch(`${API_URL}/api/agencias/login`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({username:user,password:pass}),
      });
    } catch(e){
      setErr("Sin conexión con el servidor. No se puede operar hasta que vuelva.");
      setLoading(false);
      return;
    }
    if(!r.ok){
      const e=await r.json().catch(()=>({}));
      setErr(e.detail||"Usuario o contraseña incorrectos");
      setLoading(false);
      return;
    }
    const data=await r.json();
    setLoading(false);
    if(!data.token){
      setErr("El servidor no devolvió una sesión válida. Avisá al administrador.");
      return;
    }
    onLogin(data);
  };

  return(
    <div style={{background:Q.void,minHeight:"100vh",display:"flex",
      alignItems:"center",justifyContent:"center",padding:SPACING[24],position:"relative"}}>
      <div style={{position:"fixed",inset:0,pointerEvents:"none",
        backgroundImage:`linear-gradient(${Q.violet}04 1px,transparent 1px),linear-gradient(90deg,${Q.violet}04 1px,transparent 1px)`,
        backgroundSize:"28px 28px"}}/>
      <div style={{width:"100%",maxWidth:380,position:"relative",zIndex:1}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <QPLogo size={44}/>
          <div style={{color:Q.muted,fontSize:12,marginTop:8,
            fontFamily:F_BODY}}>Panel de Agencia Oficial</div>
        </div>
        <GCard style={{padding:SPACING[32]}}>
          {[["Usuario","text",user,setUser,"usuario"],
            ["Contraseña","password",pass,setPass,"••••••••"]].map(([l,t,v,sv,ph])=>(
            <div key={l} style={{marginBottom:16}}>
              <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
                letterSpacing:1,fontFamily:F_BODY,marginBottom:6}}>{l}</div>
              <input type={t} value={v} onChange={e=>sv(e.target.value)} placeholder={ph}
                onKeyDown={e=>e.key==="Enter"&&login()}
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
                  color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
            </div>
          ))}
          <AlertaError mensaje={err}/>
          <Btn label={loading?"INGRESANDO...":"INGRESAR"} onClick={login}
            color={Q.violet} size="lg" full disabled={loading||!user||!pass}/>
        </GCard>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FLUJO A+C — CÓDIGO DEL BOT
// ═══════════════════════════════════════════════════════════════
function FlujoCodigo({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const isDesktop=useDesktopShellWidth();
  const [code,setCode]=useState("");
  const [slip,setSlip]=useState(null);
  const [err,setErr]=useState("");
  const [payErr,setPayErr]=useState("");
  const [loading,setLoading]=useState(false);
  const [paying,setPaying]=useState(false);
  const [step,setStep]=useState("buscar");
  const [coValor,setCoValor]=useState(null);
  const [coMsg,setCoMsg]=useState("");
  const [coProc,setCoProc]=useState(false);
  // Reservas esperando pago: el cajero las ve llegar sin que el cliente
  // tenga que dictarle el código. Se refresca sola.
  const [reservas,setReservas]=useState([]);

  const cargarReservas=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/reservas`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok){ const d=await r.json(); setReservas(d.reservas||[]); }
    }catch(e){}
  };

  useEffect(()=>{
    cargarReservas();
    const t=setInterval(cargarReservas,20000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[]);

  // La ventanilla tiene su propia puerta: `/cashout/agencia`.
  //
  // Antes las dos —la del jugador y la del mostrador— eran la misma ruta sin
  // autenticación, y quién era cada uno salía de un campo del cuerpo. Ahora
  // esta ruta pide la sesión de agencia (`requiere_agencia`) y exige que el
  // boleto sea de la rama, igual que liquidar y que pagar-caja. Por eso van
  // los `authHeaders`: sin ellos es 401, y es correcto que lo sea.
  const consultarCashout=async()=>{
    setCoMsg(""); setCoProc(true); setCoValor(null);
    try{
      const r=await fetch(`${API_URL}/api/betslip/${slip.code}/cashout/agencia`,{
        headers:{...authHeaders(agencia.token)},
      });
      const d=await r.json();
      if(d.disponible){ setCoValor(d.valor); setStep("cashout"); }
      else { setCoMsg(d.motivo||d.detail||"Cash out no disponible"); }
    }catch(e){ setCoMsg("Error al consultar"); }
    setCoProc(false);
  };

  const ejecutarCashout=async()=>{
    if(coProc) return;
    setCoProc(true); setCoMsg("");
    try{
      const r=await fetch(`${API_URL}/api/betslip/${slip.code}/cashout/agencia`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({valor_esperado:coValor}),
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setSlip(sl=>({...sl,cashed:true,cash_valor:d.valor})); setStep("done_cashout"); }
      else { setCoMsg(d.detail||"No se pudo cashear"); }
    }catch(e){ setCoMsg("Error al cashear"); }
    setCoProc(false);
  };

  const pagarCashoutCaja=async()=>{
    if(coProc) return;
    setCoProc(true); setCoMsg("");
    try{
      const r=await fetch(`${API_URL}/api/betslip/${slip.code}/cashout/pagar-caja`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setSlip(sl=>({...sl,cashed:true,cash_valor:d.valor})); setStep("done_cashout"); }
      else setCoMsg(d.detail||"No se pudo pagar");
    }catch(e){ setCoMsg("Error al pagar"); }
    setCoProc(false);
  };

  const [retiro,setRetiro]=useState(null);
  const [retProc,setRetProc]=useState(false);
  const buscar=async()=>{
    setErr(""); setPayErr(""); setSlip(null); setRetiro(null); setStep("buscar"); setLoading(true);
    try {
      // Código de retiro (RT-XXXXX): flujo distinto
      if(code.trim().toUpperCase().startsWith("RT-")){
        const r=await fetch(`${API_URL}/api/retiro/${code.trim().toUpperCase()}`,
          { headers: authHeaders(agencia.token) });
        if(r.status===401){ onSesionExpirada(); return; }
        const d=await r.json();
        if(!r.ok) throw new Error(d.detail||"Retiro no encontrado");
        setRetiro(d); setStep("retiro");
      } else {
        const found = await fetchBetslip(code, agencia.token);
        setSlip(found); setStep("confirmar");
      }
    } catch(e){
      if(e.name==="SesionExpirada"){ onSesionExpirada(); return; }
      setErr(e.name==="SinConexion"
        ? "Sin conexión con el servidor. No se puede validar el código — no cobrar."
        : (e.message||"Código no encontrado."));
    }
    setLoading(false);
  };

  const pagarRetiro=async()=>{
    if(retProc) return;
    setRetProc(true); setErr("");
    try{
      const r=await fetch(`${API_URL}/api/retiro/${retiro.code}/pagar`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setRetiro(rt=>({...rt,estado:"pagado"})); setStep("retiro_ok"); }
      else setErr(d.detail||"No se pudo pagar");
    }catch(e){ setErr("Error al pagar"); }
    setRetProc(false);
  };

  const confirmarApuesta=()=>{ setStep("done_confirmado"); };
  const pagarLocal=()=>{ setPayErr(""); setStep("pagar"); };

  const confirmarPago=async(monto)=>{
    if(paying) return;
    setPayErr(""); setPaying(true);
    try {
      const result = await payBetslip(slip.code, monto, agencia.token);
      setSlip(s=>({...s,
        stake: result.stake,
        potential_win: result.potential_win,
        aviso_tope: result.aviso_tope,
        topeado: result.topeado,
        paid_local: true}));
      setStep("done_pagado");
    } catch(e){
      if(e.name==="SesionExpirada"){ onSesionExpirada(); return; }
      setPayErr(e.name==="SinConexion"
        ? "No hay conexión con el servidor, la apuesta NO quedó registrada. No aceptes el efectivo."
        : `La apuesta NO quedó registrada: ${e.message}`);
    }
    setPaying(false);
  };

  const reset=()=>{ setCode(""); setSlip(null); setRetiro(null); setErr(""); setPayErr(""); setStep("buscar"); };

  return(
    <div>
      <PageHeader icon={<Bot/>} title="Código / Bot"
        description="Buscá una apuesta por su código para pagarla o gestionar su cash out."/>
      <GCard style={{padding:SPACING[20],marginBottom:16}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:4,
          fontFamily:F_BODY}}>Buscar código de apuesta</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:14}}>
          El cliente muestra el código QP-XXXXX desde su celular o ticket
        </div>
        {/* Stacked on a phone: side by side the field needs the whole width
            to show a code, so the button was pushed past the right edge. */}
        <div style={{display:"flex",gap:SPACING[8],
          flexDirection:isDesktop?"row":"column"}}>
          <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())}
            onKeyDown={e=>e.key==="Enter"&&buscar()}
            placeholder="QP-47829"
            style={{flex:1,background:"rgba(255,255,255,0.06)",
              border:`1.5px solid ${Q.border}`,borderRadius:RADII.md,
              padding:"12px 16px",color:Q.text,fontSize:20,
              fontFamily:F_BODY,fontWeight:700,letterSpacing:2}}/>
          <Btn label={loading?"...":"BUSCAR"} onClick={buscar}
            disabled={!code||loading} color={Q.violet} full={!isDesktop}/>
        </div>
        {err&&<div style={{marginTop:12}}><AlertaError mensaje={err}/></div>}
      </GCard>

      {/* Reservas esperando pago. Tocar una la carga en el buscador,
          así el cajero no tiene que tipear el código. Desaparecen
          solas al cobrarse o al vencer. */}
      {!slip&&reservas.length>0&&(
        <GCard style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",marginBottom:10}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>
              <Icon name="ticket" size={13}/> Esperando pago <span style={{color:Q.amber}}>
                ({reservas.length})</span></div>
            <button onClick={cargarReservas} style={{background:"transparent",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"4px 12px",
              color:Q.muted,fontSize:12,cursor:"pointer"}}>↻ Actualizar</button>
          </div>

          {reservas.map(r=>(
            <div key={r.code} onClick={()=>{ setCode(r.code); setErr(""); }}
              style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",gap:SPACING[8],padding:"8px 4px",cursor:"pointer",
                borderBottom:`1px solid ${Q.border}`}}>
              <div style={{minWidth:0,flex:1}}>
                <span style={{color:Q.gold,fontWeight:800,fontSize:14,
                  fontFamily:F_BODY,
                  letterSpacing:0.5}}>{r.code}</span>
                <div style={{color:Q.muted,fontSize:12,marginTop:2,
                  overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap"}}>
                  {r.terminal&&(
                    <span style={{color:Q.cyan,marginRight:4}}><Monitor size={12}/></span>
                  )}
                  {r.cliente} · {r.resumen}</div>
                <div style={{color:Q.dim,fontSize:12,marginTop:1}}>
                  {r.hace} · {r.picks} {r.picks===1?"pick":"picks"} ·{" "}
                  cuota {r.odd_total.toFixed(2)}
                  {r.terminal&&` · ${r.terminal}`}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:Q.cyan,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>Cobrar ›</div>
              </div>
            </div>
          ))}

          <div style={{color:Q.dim,fontSize:12,marginTop:8,lineHeight:1.5,
            fontFamily:F_BODY}}>
            Tocá una para cargarla en el buscador. Solo se ven las
            reservas de clientes de tu red.</div>
        </GCard>
      )}

      {slip&&step==="confirmar"&&slip.status==="cashout_pending"&&(
        <GCard style={{padding:SPACING[20],marginBottom:12}}>
          <div style={{color:Q.gold,fontWeight:800,fontSize:16,marginBottom:6,
            fontFamily:F_BODY}}><Icon name="receipt-text" size={16}/> Cash out para cobrar</div>
          <div style={{color:Q.muted,fontSize:13,marginBottom:16}}>
            El cliente hizo cash out y viene a cobrar en efectivo.
          </div>
          <div style={{textAlign:"center",marginBottom:16}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              fontFamily:F_BODY}}>A pagar</div>
            <div style={{color:Q.gold,fontWeight:900,fontSize:34,
              fontFamily:F_BODY}}>{money(slip.potential_win, mon)}</div>
          </div>
          <AlertaError mensaje={coMsg} critico/>
          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label="Cancelar" onClick={reset} outline color={Q.muted} full disabled={coProc}/>
            <Btn label={coProc?"PAGANDO...":`PAGAR ${money(slip.potential_win, mon)}`}
              onClick={pagarCashoutCaja} color={Q.gold} full disabled={coProc}/>
          </div>
        </GCard>
      )}
      {slip&&step==="confirmar"&&slip.status!=="cashout_pending"&&(
        <div>
          <GCard style={{padding:SPACING[20],marginBottom:12}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{color:Q.green,fontWeight:900,fontSize:18,
                  fontFamily:F_BODY}}>{slip.code}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {slip.user} · {slip.created_at}
                </div>
              </div>
              <div style={{background:`${Q.green}22`,border:`1px solid ${Q.green}`,
                borderRadius:RADII.xl,padding:"4px 12px",color:Q.green,
                fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                VÁLIDO
              </div>
            </div>
            {(slip.picks||[]).map((p,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:"8px 0",
                borderBottom:i<slip.picks.length-1?`1px solid ${Q.dim}`:"none"}}>
                <div>
                  <div style={{color:Q.text,fontWeight:600,fontSize:13,
                    fontFamily:F_BODY}}>{p.home} vs {p.away}</div>
                  <div style={{color:Q.muted,fontSize:12}}>{p.sport} · {p.sel}</div>
                </div>
                <div style={{color:Q.cyan,fontWeight:700,fontSize:16,
                  fontFamily:F_BODY}}>@{p.odd}</div>
              </div>
            ))}
            <div style={{marginTop:14,paddingTop:SPACING[12],borderTop:`1px solid ${Q.violet}44`}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{color:Q.muted,fontSize:13}}>Cuota total</span>
                <span style={{color:Q.gold,fontWeight:700,fontSize:16,
                  fontFamily:F_BODY}}>{fmt(slip.odd_total)}x</span>
              </div>
              {slip.stake>0&&<div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{color:Q.muted,fontSize:13}}>Apostado (online)</span>
                <span style={{color:Q.text,fontWeight:700,fontSize:15,
                  fontFamily:F_BODY}}>{money(slip.stake, mon)}</span>
              </div>}
              {slip.potential_win>0&&<div style={{display:"flex",justifyContent:"space-between"}}>
                <span style={{color:Q.muted,fontSize:14,fontWeight:700}}>RETORNO POT.</span>
                <span style={{color:Q.green,fontWeight:900,fontSize:22,
                  fontFamily:F_BODY}}>{money(slip.potential_win, mon)}</span>
              </div>}
            </div>
          </GCard>

          <AlertaError mensaje={payErr} critico/>

          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:12,
              fontFamily:F_BODY}}>¿Qué quiere hacer el cliente?</div>
            <div style={{display:"flex",flexDirection:"column",gap:SPACING[8]}}>
              <button onClick={confirmarApuesta} style={{
                background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.green}`,
                borderRadius:RADII.lg,padding:SPACING[16],cursor:"pointer",textAlign:"left"}}>
                <div style={{color:Q.green,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY,marginBottom:3}}>
                  Confirmar apuesta y entregar ticket
                </div>
                <div style={{color:Q.muted,fontSize:12}}>
                  Ya apostó online — quiere el comprobante impreso
                </div>
              </button>
              <button onClick={pagarLocal} style={{
                background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.amber}`,
                borderRadius:RADII.lg,padding:SPACING[16],cursor:"pointer",textAlign:"left"}}>
                <div style={{color:Q.amber,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY,marginBottom:3}}>
                  Pagar en efectivo en el local
                </div>
                <div style={{color:Q.muted,fontSize:12}}>
                  Trajo el código pero paga en efectivo acá
                </div>
              </button>
              {slip.status==="active"&&(
                <button onClick={consultarCashout} disabled={coProc} style={{
                  background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.gold}`,
                  borderRadius:RADII.lg,padding:SPACING[16],cursor:"pointer",textAlign:"left"}}>
                  <div style={{color:Q.gold,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY,marginBottom:3}}>
                    {coProc?"Calculando...":<><Icon name="wallet-cards" size={13}/> Cash out (retirar ahora)</>}
                  </div>
                  <div style={{color:Q.muted,fontSize:12}}>
                    Cerrar la apuesta ya, al valor actual en vivo
                  </div>
                </button>
              )}
            </div>
            {coMsg&&<div style={{color:Q.red,fontSize:12,marginTop:10,textAlign:"center",
              fontFamily:F_BODY}}>{coMsg}</div>}
          </GCard>
          <Btn label="Cancelar" onClick={reset} outline color={Q.muted} full/>
        </div>
      )}

      {step==="retiro"&&retiro&&(
        <GCard style={{padding:SPACING[20],marginBottom:12}}>
          <div style={{color:Q.gold,fontWeight:800,fontSize:16,marginBottom:6,
            fontFamily:F_BODY}}><Banknote size={16}/> Retiro para pagar</div>
          <div style={{color:Q.muted,fontSize:13,marginBottom:16}}>
            El cliente solicitó un retiro y viene a cobrar en efectivo.
          </div>
          <div style={{textAlign:"center",marginBottom:16}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              fontFamily:F_BODY}}>A pagar a {retiro.cliente}</div>
            <div style={{color:Q.gold,fontWeight:900,fontSize:34,
              fontFamily:F_BODY}}>{money(retiro.monto, mon)} {retiro.moneda}</div>
          </div>
          <AlertaError mensaje={err} critico/>
          {retiro.estado!=="pendiente"?(
            <div style={{color:Q.amber,fontSize:13,textAlign:"center",
              fontFamily:F_BODY}}>Este retiro ya está {retiro.estado}</div>
          ):(
            <div style={{display:"flex",gap:SPACING[8]}}>
              <Btn label="Cancelar" onClick={reset} outline color={Q.muted} full disabled={retProc}/>
              <Btn label={retProc?"PAGANDO...":`PAGAR ${money(retiro.monto, mon)}`}
                onClick={pagarRetiro} color={Q.gold} full disabled={retProc}/>
            </div>
          )}
        </GCard>
      )}
      {step==="retiro_ok"&&retiro&&(
        <GCard style={{padding:SPACING[24],textAlign:"center",marginBottom:12}}>
          <div style={{marginBottom:8}}><Icon name="wallet-cards" size={44}/></div>
          <div style={{color:Q.green,fontWeight:800,fontSize:18,marginBottom:6,
            fontFamily:F_BODY}}>Retiro pagado</div>
          <div style={{color:Q.text,fontWeight:900,fontSize:26,
            fontFamily:F_BODY}}>{money(retiro.monto, mon)} {retiro.moneda}</div>
          <Btn label="Listo" onClick={reset} color={Q.violet} full/>
        </GCard>
      )}
      {step==="pagar"&&slip&&(
        <PagoLocalForm slip={slip} error={payErr} paying={paying}
          onConfirm={confirmarPago} onCancel={reset}/>
      )}
      {step==="cashout"&&slip&&(
        <GCard style={{padding:SPACING[20],marginBottom:12}}>
          <div style={{color:Q.gold,fontWeight:700,fontSize:15,marginBottom:4,
            fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Cash out</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:16}}>
            Valor actual calculado con las cuotas en vivo. Si el cliente acepta,
            se cierra la apuesta y se le paga este monto.
          </div>
          <div style={{textAlign:"center",marginBottom:16}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              fontFamily:F_BODY}}>Se paga ahora</div>
            <div style={{color:Q.gold,fontWeight:900,fontSize:34,
              fontFamily:F_BODY}}>{money(coValor, mon)}</div>
            <div style={{color:Q.muted,fontSize:12}}>vs retorno potencial {money(slip.potential_win, mon)}</div>
          </div>
          <AlertaError mensaje={coMsg} critico/>
          <div style={{display:"flex",gap:SPACING[8]}}>
            <Btn label="Cancelar" onClick={()=>setStep("confirmar")} outline color={Q.muted} full disabled={coProc}/>
            <Btn label={coProc?"PROCESANDO...":`PAGAR ${money(coValor, mon)}`}
              onClick={ejecutarCashout} color={Q.gold} full disabled={coProc}/>
          </div>
        </GCard>
      )}
      {step==="done_cashout"&&slip&&(
        <DoneScreen slip={{...slip,agencia:agencia.code,
          potential_win:slip.cash_valor,stake:slip.stake}}
          titulo={`Cash out pagado · ${money(slip.cash_valor, mon)}`}
          color={Q.gold} tipo="apuesta" onReset={reset}/>
      )}
      {step==="done_confirmado"&&slip&&(
        <DoneScreen slip={{...slip,agencia:agencia.code}}
          titulo="Apuesta confirmada" color={Q.green} tipo="apuesta" onReset={reset}/>
      )}
      {step==="done_pagado"&&slip&&(
        <>
          {slip.aviso_tope&&(
            <GCard glow={Q.amber} style={{padding:SPACING[16],marginBottom:10,
              background:`${Q.amber}12`}}>
              <div style={{color:Q.amber,fontWeight:700,fontSize:13,textAlign:"center",
                fontFamily:F_BODY}}><Icon name="triangle-alert" size={13}/> {slip.aviso_tope}</div>
              <div style={{color:Q.muted,fontSize:12,textAlign:"center",marginTop:4,
                fontFamily:F_BODY}}>
                El premio se topeó al máximo permitido.</div>
            </GCard>
          )}
          <DoneScreen slip={{...slip,agencia:agencia.code}}
            titulo="Pago registrado" color={Q.amber} tipo="apuesta" onReset={reset}/>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FLUJO B — APUESTA MANUAL
// ═══════════════════════════════════════════════════════════════
function FlujoManual({ agencia }){
  const mon = agencia?.moneda || "ARS";
  const [picks,setPicks]=useState([]);
  const [monto,setMonto]=useState(montoInicial(agencia?.moneda));
  const [cliente,setCliente]=useState("");
  const [codigoInf,setCodigoInf]=useState("");
  const [step,setStep]=useState("armar");
  const [slip,setSlip]=useState(null);
  const [prematchDeportes,setPrematchDeportes]=useState(null);
  const [abiertos,setAbiertos]=useState({});
  const toggleMercados=(id)=>setAbiertos(a=>({...a,[id]:!a[id]}));
  // Hasta el T3 de sportsbook-homologado esta pantalla no tenía filtro de
  // deporte: el que decía "liga" filtraba en realidad por `d.name`, que en
  // el catálogo de `/api/live/prematch` es el DEPORTE. O sea que el cajero
  // nunca pudo filtrar por liga, con 68 torneos en la lista.
  const [deporteSel,setDeporteSel]=useState(null);
  const [liga,setLiga]=useState(null);
  const [busqueda,setBusqueda]=useState("");
  const [bbFeature,setBbFeature]=useState({bet_builder:false,bb_max_picks:4});

  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/features`,{headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():null).then(d=>{ if(d) setBbFeature(d); }).catch(()=>{});
  },[]);

  useEffect(()=>{
    fetch(`${API_BOT}/api/live/prematch`)
      .then(r=>r.ok?r.json():null)
      .then(data=>setPrematchDeportes(data?.sports || []))
      .catch(()=>setPrematchDeportes([]));
  },[]);

  // `market` y `nombre` llegan CUARTO y QUINTO desde los botones de cuota —el
  // carrusel y el panel de "Todos los mercados"— y este carrito los tiraba.
  //
  // `nombre` es la clave literal que emite GR8 ("Draw", "Over 2.5", el nombre
  // pelado del equipo) y es lo único que el índice de claves de selección
  // conoce. `label` es la etiqueta que el cajero lee, y sigue siendo la
  // IDENTIDAD del pick acá abajo (`${ev.home}-${ev.away}-${label}`): por eso
  // el nombre viaja al lado y no en su lugar.
  //
  // Sin `market` el reconocedor tiene que buscar el nombre en TODOS los
  // mercados del evento, y el nombre pelado de un equipo lo producen dos
  // (`h2h` y `h2h_3_way`, que son dos preguntas distintas): queda ambiguo,
  // que es sin clave igual que sin nombre. Guardar sólo el nombre dejaba la
  // mitad del camino roto.
  const togglePick=(ev,sport,label,odd,market,nombre)=>{
    const id=`${ev.home}-${ev.away}-${label}`;
    setPicks(p=>{
      const w=p.filter(x=>x.id!==id);
      if(p.find(x=>x.id===id)) return w;
      // Límite de selecciones del mismo partido. La cuenta y el tope
      // viven en `combosMismoPartido.js` porque los aplican también el
      // armador de combos y /sitio: una sola regla para que el cajero
      // aprenda una sola cosa.
      const tope=topeMismoPartido(bbFeature);
      if(!puedeSumarAlPartido(p,ev,tope)){
        alert(`Máximo ${tope} selecciones del mismo partido`);
        return p;
      }
      return[...w,{id,home:ev.home,away:ev.away,sel:label,odd,sport,
        market:market||"", nombre:nombre||"",
        event_id:ev.event_id||ev.id||null, sport_key:ev.sport_key||null,
        // Hora del partido: la anulación la usa para saber si el
        // evento está por empezar. Sin esto el boleto no se puede anular.
        commence_time:ev.commence_time||ev.time||""}];
    });
  };
  // La cuota la calcula `useCuotaCombinada` más abajo: el producto bruto y
  // la cuota con margen salen del mismo lugar porque son la misma cuenta.

  // ── Los filtros de Prematch, en cascada ─────────────────────────
  // Mismo cableado que ScreenPrematch del jugador: cada desplegable se
  // arma sobre el catálogo filtrado por TODO lo anterior menos por sí
  // mismo. Si la liga elegida entrara en el cálculo de su propia lista,
  // al elegirla desaparecerían las demás y el cajero quedaría sin
  // salida; y si la lista se armara sobre el catálogo completo,
  // ofrecería ligas que llevan a una pantalla vacía.
  //
  // Sin filtro de etapa, a diferencia del jugador: ahí los partidos en
  // curso tienen pantalla propia, y acá la pestaña Prematch muestra todo
  // lo que devuelve el endpoint. Cambiar eso sería esconderle oferta al
  // cajero, que no es lo que se pidió.
  const catPrematch = useMemo(
    ()=>filtrarCatalogo(prematchDeportes||[],{busqueda}),
    [prematchDeportes,busqueda]);
  const deportesPre = useMemo(()=>deportesDisponibles(catPrematch),[catPrematch]);
  // Una selección que ya no existe —cambió la oferta, terminó el
  // partido— vuelve sola a "todos" en vez de dejar la lista vacía con un
  // filtro puesto que no se ve. Se resuelve derivando y no con un
  // efecto: un efecto dejaría un render intermedio en blanco.
  const deporteActivo = deportesPre.some(d=>d.name===deporteSel)?deporteSel:null;
  const porDeporte = useMemo(
    ()=>filtrarCatalogo(catPrematch,{deporte:deporteActivo}),
    [catPrematch,deporteActivo]);
  // La salida del filtro, la misma regla que el jugador tiene escrita al
  // lado de sus dos botones: un filtro sin forma de deshacerlo deja al
  // cajero frente a una pantalla vacía creyendo que no hay oferta.
  const hayFiltrosPre = !!(deporteSel||liga||busqueda.trim());
  const limpiarFiltrosPre=()=>{ setDeporteSel(null); setLiga(null); setBusqueda(""); };
  const ligasPre = useMemo(()=>ligasDisponibles(porDeporte),[porDeporte]);
  const ligaActiva = ligasPre.some(l=>l.liga===liga)?liga:null;
  const prematchVisible = useMemo(
    ()=>filtrarCatalogo(porDeporte,{liga:ligaActiva}),
    [porDeporte,ligaActiva]);
  const totalPrematch = catPrematch.reduce((n,s)=>n+s.events.length,0);
  const enEstaVista = porDeporte.reduce((n,s)=>n+s.events.length,0);
  // Las dos listas en la forma que entiende el desplegable. El orden lo
  // siguen decidiendo deportesDisponibles() y ligasDisponibles(): acá
  // solo se renombran los campos.
  const opcionesDeporte = useMemo(()=>deportesPre.map(d=>({
    clave:d.name, rotulo:d.name, eventos:d.eventos })),[deportesPre]);
  const opcionesLiga = useMemo(()=>ligasPre.map(l=>({
    clave:l.liga, rotulo:l.liga, eventos:l.eventos })),[ligasPre]);

  // Mismo partido y cuota con el margen de correlación. La regla está en
  // `combosMismoPartido.js`, y la cotiza el servidor porque
  // `/api/bet-builder/cotizar` es matemática pura sobre
  // `{picks, mismo_partido}` y no tiene noción de vivo ni de prematch
  // (casino_api.py:22910).
  const {mismoPartido,totOdd}=useCuotaCombinada(picks);
  // Lista de selecciones desplegable, para poder sacar de a una
  const [verPicks,setVerPicks]=useState(false);

  const [guardando,setGuardando]=useState(false);
  const [errGuardar,setErrGuardar]=useState("");

  // Antes esto armaba el código con Math.random() y no tocaba el servidor:
  // el ticket impreso no existía en la base, no entraba en los cierres, y
  // si el cliente volvía a cobrar no había registro de nada.
  // Ahora se crea el boleto y se cobra, las dos cosas contra la API.
  const confirmar=async()=>{
    if(guardando) return;
    setErrGuardar(""); setGuardando(true);
    try {
      // 1. crear el boleto
      let r = await fetch(`${API_URL}/api/betslip`,{
        method:"POST",
        headers:{"Content-Type":"application/json", ...authHeaders(agencia.token)},
        body:JSON.stringify(conRef("agencia-manual", {
          // `sel` es lo que se liquida, así que gana el nombre que EMITE GR8
          // sobre la etiqueta que el cajero leyó al tocar la cuota. Y
          // `market` se agrega: `/api/betslip` ya lo guarda
          // (casino_api.py:17356) y sin él el nombre se busca en todos los
          // mercados del evento, donde el nombre pelado de un equipo queda
          // ambiguo entre `h2h` y `h2h_3_way`.
          //
          // El carrito sigue guardando la etiqueta en `sel`: lo que cambia es
          // qué se manda, no qué se guarda ni qué se imprime. `setSlip` más
          // abajo pasa `picks` tal cual, así que el boleto impreso sigue
          // diciendo lo que el cajero leyó.
          picks: picks.map(p=>({
            home:p.home, away:p.away, sel:p.nombre||p.sel, odd:p.odd,
            sport:p.sport, market:p.market||"",
            event_id:p.event_id||null, sport_key:p.sport_key||null,
            commence_time:p.commence_time||"",
          })),
          cliente: cliente || null,
          agencia_code: agencia.code,
          codigo_influencer: codigoInf || null,
          odd_total: totOdd,
          mismo_partido: mismoPartido,
        })),
      });
      if(r.status===401) throw new SesionExpirada();
      if(!r.ok){
        const e = await r.json().catch(()=>({}));
        throw new Error(mensajeDeConflicto("agencia-manual", r.status, e.detail) || e.detail || `No se pudo registrar (${r.status})`);
      }
      const creado = await r.json();

      // 2. cobrarlo con el monto que entregó el cliente
      const pago = await payBetslip(creado.code, monto, agencia.token);
      // La `ref` del boleto se suelta recién con el cobro hecho: si el cobro
      // falla, el reintento recupera el MISMO boleto (refDeApuesta.js) y lo
      // cobra, en vez de crear otro que quede huérfano.
      envioCumplido("agencia-manual");

      setSlip({
        code: creado.code,
        user: cliente || "Cliente mostrador",
        created_at: nowStr(),
        expires_at: expires24(),
        status: "active",
        picks,
        stake: pago.stake,
        odd_total: pago.odd_total,
        potential_win: pago.potential_win,
        agencia: agencia.code,
      });
      setStep("done");
    } catch(e){
      setErrGuardar(e.name==="SinConexion"
        ? "Sin conexión con el servidor. La apuesta NO quedó registrada, no aceptes el efectivo."
        : `La apuesta NO quedó registrada: ${e.message}`);
    }
    setGuardando(false);
  };

  const reset=()=>{ setPicks([]); setMonto(montoInicial(agencia?.moneda)); setCliente(""); setCodigoInf(""); setStep("armar"); setSlip(null); };

  if(step==="done"&&slip) return(
    <DoneScreen slip={slip} titulo="Apuesta registrada"
      color={Q.violet} tipo="apuesta" onReset={reset}/>
  );

  return(
    <div>
      <PageHeader icon={<Pencil/>} title="Apuesta manual"
        description="Armá una apuesta a mano eligiendo los partidos y el monto vos mismo."/>
      <GCard style={{padding:SPACING[16],marginBottom:14}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
          letterSpacing:1,fontFamily:F_BODY,marginBottom:6}}>
          Nombre del cliente (opcional)
        </div>
        <input value={cliente} onChange={e=>setCliente(e.target.value)}
          placeholder="Ej: Juan Pérez"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
            color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
        <div style={{color:Q.muted,fontSize:12,margin:"10px 0 6px",
          fontFamily:F_BODY}}>
          Código de influencer (opcional)</div>
        <input value={codigoInf} onChange={e=>setCodigoInf(e.target.value.toUpperCase())}
          placeholder="Ej: JUANTIPS o REF1A2B"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${codigoInf?Q.cyan:Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
            color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
      </GCard>

      {bbFeature.bet_builder&&(
        <div style={{background:`${Q.violet}12`,border:`1px solid ${Q.violet}55`,borderRadius:RADII.md,
          padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:SPACING[8]}}>
          <Wrench size={16}/>
          <div>
            <div style={{color:Q.violet2||Q.violet,fontWeight:700,fontSize:12,
              fontFamily:F_BODY}}>Bet Builder activo</div>
            <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
              Podés combinar hasta {bbFeature.bb_max_picks} mercados del mismo partido</div>
          </div>
        </div>
      )}

      {/* ACÁ VIVÍAN LAS DOS PESTAÑAS, Prematch y En Vivo.
          Quedó una sola oferta —el prematch de `/api/live/prematch`, que es el
          catálogo de GR8— porque la de En Vivo leía `/api/live/combined`, la
          pantalla en vivo de la cadena vieja. Medido contra staging el
          2026-10-06, esa ruta devolvía `{"matches": [], "count": 0}`: el cajero
          abría la pestaña y veía el cartel de "sin partidos en curso" siempre,
          con 122 partidos en curso en el catálogo de GR8 de al lado.
          La ruta ya no existe, así que la pestaña se fue con su fuente en vez
          de quedar pidiendo un 404. Con ella se fueron `liveDeportes`,
          `ligaLive`, `busqLive` y los cuatro `useMemo` del filtro de liga.
          LO QUE LA AGENCIA PIERDE: vender en vivo desde el mostrador. Hoy no
          lo podía hacer igual —la lista llegaba vacía— pero recuperarlo es
          trabajo, no un `fetch` cambiado de URL: el catálogo de GR8 trae
          `etapa: "Live"` y las cuotas, y no trae marcador ni minuto. Es la
          misma migración que ya se le hizo a la pantalla del jugador
          (`App.jsx`, `ScreenLive`) y a /sitio (`Web.jsx`). */}
      <>
          {!prematchDeportes&&(
            <GCard style={{padding:SPACING[24],textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:12,
                fontFamily:F_BODY}}>
                Cargando cuotas...
              </div>
              <div style={{color:Q.dim,fontSize:12,marginTop:6}}>
                La primera carga del día puede demorar unos segundos
              </div>
            </GCard>
          )}

          {prematchDeportes&&prematchDeportes.length>0&&(<>
            {/* Buscador de equipo o liga: los dos nombres ya vienen en la
                respuesta. Antes solo miraba los equipos, así que escribir
                el nombre de un torneo no encontraba nada. */}
            <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
              background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",marginBottom:8}}>
              <Icon name="scan-search" size={14} color={Q.muted}/>
              <input value={busqueda} onChange={e=>setBusqueda(e.target.value)}
                placeholder="Buscar equipo o liga..."
                style={{background:"transparent",border:"none",color:Q.text,
                  fontSize:14,flex:1,minWidth:0,fontFamily:F_BODY}}/>
              {busqueda&&(
                <button onClick={()=>setBusqueda("")} style={{background:"transparent",
                  border:"none",color:Q.muted,fontSize:16,cursor:"pointer",
                  padding:0,lineHeight:1}}>✕</button>
              )}
            </div>

            {/* Deporte y liga, uno al lado del otro: los mismos dos
                desplegables que ve el jugador. Eran una tira de chips que
                se deslizaba de costado —y que además filtraba por deporte
                diciendo "liga"—, así que el cajero no tenía filtro de liga
                y de los deportes veía los cuatro que entraban en el ancho
                de la pantalla. Son las listas de ESTA vista, no las del
                catálogo entero, así que un filtro nunca lleva a una lista
                vacía. */}
            {(deportesPre.length>1||ligasPre.length>1)&&(
              <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
                marginBottom:12}}>
                {deportesPre.length>1&&(
                  <SelectorDesplegable rotulo="Deporte" todas="Todos los deportes"
                    opciones={opcionesDeporte} activa={deporteActivo}
                    total={totalPrematch} color={Q.violet}
                    onElegir={clave=>setDeporteSel(clave)}/>
                )}
                {ligasPre.length>1&&(
                  <SelectorDesplegable rotulo="Liga" todas="Todas las ligas"
                    opciones={opcionesLiga} activa={ligaActiva}
                    total={enEstaVista} color={Q.violet}
                    onElegir={clave=>setLiga(clave)}/>
                )}
              </div>
            )}
          </>)}

          {/* La mascota le toma el lugar al ícono de bandeja de entrada que
              estaba acá —y por eso `Inbox` sale del import de lucide: este
              era su único uso en el archivo—. El estado vacío del jugador
              lleva una ilustración, no dos, y la que lleva es el personaje.
              Sin filtro que deshacer tampoco acá: lo que está vacío es la
              respuesta. */}
          {prematchDeportes&&prematchDeportes.length===0&&(
            <GCard style={{padding:SPACING[24],textAlign:"center"}}>
              <Mascot size={64} style={{margin:"0 auto 8px"}}/>
              <div style={{color:Q.muted,fontSize:12,
                fontFamily:F_BODY}}>
                No hay eventos con cuotas en este momento
              </div>
            </GCard>
          )}

          {/* Acá SÍ hay filtro, así que va la salida: el mismo botón y el
              mismo texto que el jugador. El mensaje se queda como estaba
              —nombra el filtro, que es la causa— porque el jugador no tiene
              un literal equivalente para este caso. */}
          {prematchVisible.length===0
            && prematchDeportes && prematchDeportes.length>0 && (
            <GCard style={{padding:SPACING[24],textAlign:"center"}}>
              <Mascot size={64} style={{margin:"0 auto 8px"}}/>
              <div style={{color:Q.muted,fontSize:12,
                fontFamily:F_BODY}}>
                Ningún partido coincide con el filtro
              </div>
              {hayFiltrosPre&&(
                <button onClick={limpiarFiltrosPre} style={{marginTop:12,
                  background:ov(0.05),border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                  padding:"8px 16px",cursor:"pointer",color:Q.text,fontSize:13,
                  fontFamily:F_BODY}}>Quitar los filtros</button>
              )}
            </GCard>
          )}

          {/* Agrupado por deporte igual que el jugador, y no al revés.
              Acá el deporte era la `GCard` y los eventos eran `div`
              pelados adentro, separados con una raya abajo; en
              `ScreenPrematch` es lo contrario: el deporte es un
              encabezado y cada evento es una tarjeta. La inversión no era
              solo de aspecto. Al sacar la raya —en una rejilla, una línea
              debajo de cada celda es una línea perdida— los eventos se
              quedaron sin nada que los separe, porque un `div` pelado no
              tiene fondo propio. En monitor las columnas los separan, pero
              en teléfono hay una sola columna y dos partidos seguidos
              quedaban pegados en un bloque de texto, que es justo la
              pantalla donde más trabaja el cajero.
              La vuelta no es devolver la raya: es que el evento sea una
              tarjeta, como la del jugador. Una tarjeta trae su propio
              fondo, así que el separador deja de ser un problema de CSS y
              pasa a ser el fondo de la tarjeta. */}
          {prematchVisible.map(d=>(
            <div key={d.name} style={{marginBottom:14}}>
              {/* El encabezado del deporte: misma forma de fila que el
                  jugador —flex, centrado, `gap` de 8— y el mismo contador
                  de eventos a la derecha. El título conserva su color, su
                  peso y su tamaño: lo que cambia es que ya no está adentro
                  de una tarjeta. */}
              <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
                marginBottom:8}}>
                <span style={{color:Q.violet2,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{d.icon} {d.name}</span>
                <span style={{color:Q.dim,fontSize:12,
                  fontFamily:F_NUM}}>{(d.events||[]).length}</span>
              </div>
              <div style={{display:"grid",
                gridTemplateColumns:"repeat(auto-fit,minmax(min(360px,100%),1fr))",
                gap:SPACING[12]}}>
                {(d.events||[]).map(ev=>(
                  // `level` por defecto, que es 1: la tarjeta ya no está
                  // adentro de otra —el deporte pasó a ser un encabezado—
                  // así que apoya en la página y le corresponde el
                  // tratamiento de panel, con su superficie opaca y su
                  // hairline. El `level=2` es para lo que va anidado.
                  // Mismo relleno que las tarjetas de la pestaña en vivo
                  // de la agencia, para que las dos pestañas se vean
                  // iguales entre sí y no solo parecidas al jugador.
                  <GCard key={ev.id||(ev.h+ev.a)} style={{padding:SPACING[16]}}>
                    {/* La misma fila disparadora que la pestaña en vivo y que
                        el jugador: abre el acordeón y muestra su estado con la
                        flecha, leyendo el mismo `abiertos[ev.id]` que el botón.
                        Los escudos suben de 22 a 26, que es la medida del
                        jugador: la misma lista a dos tamaños distintos es
                        justo lo que el T6 vino a juntar. */}
                    <div onClick={()=>toggleMercados(ev.id)} style={{display:"flex",
                      justifyContent:"space-between",alignItems:"center",
                      marginBottom:6,gap:SPACING[8],cursor:"pointer"}}>
                      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],minWidth:0}}>
                        <TeamLogo name={ev.h} size={26}/>
                        <span style={{color:Q.text,fontWeight:600,fontSize:13,
                          fontFamily:F_BODY,overflow:"hidden",
                          textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {ev.h} <span style={{color:Q.dim}}>vs</span> {ev.a}
                        </span>
                        <TeamLogo name={ev.a} size={26}/>
                      </div>
                      <span style={{display:"flex",alignItems:"center",gap:SPACING[8],
                        flexShrink:0}}>
                        <span style={{color:Q.muted,fontSize:12}}>{horaLocal(ev)}</span>
                        <span style={{color:Q.muted,fontSize:18}}>{abiertos[ev.id]?"▲":"▼"}</span>
                      </span>
                    </div>
                    {/* El mismo carrusel que la pestaña En Vivo y que el
                        jugador: dos pestañas de una misma pantalla que se ven
                        distinto es peor que cualquiera de las dos.
                        `/api/live/prematch` es un alias de `all_markets()`
                        (casino_api.py:18863), así que `ev.markets` viene
                        completo. El deporte se saca de `d.name`, que es como
                        el catálogo agrupa. */}
                    <CuotasCarrusel ev={ev} bets={picksDelEvento(picks,ev)}
                      onToggle={(e,etiqueta,cuota,mercado,nombre)=>togglePick(
                        {home:ev.h,away:ev.a,event_id:ev.id,sport_key:ev.sport_key,
                         commence_time:ev.commence_time||ev.time||""},
                        d.name,etiqueta,cuota,mercado,nombre)}/>

                    {/* Sin recuadro, el mismo motivo que la pestaña en vivo. */}
                    <button onClick={()=>toggleMercados(ev.id)} style={{
                      width:"100%",background:"transparent",border:"none",
                      borderRadius:RADII.md,padding:"4px",cursor:"pointer",color:Q.dim,
                      fontSize:12,marginTop:6,fontFamily:F_BODY,
                    }}>
                      {abiertos[ev.id]?"▲ Menos mercados":"▼ Todos los mercados"}
                    </button>
                    {abiertos[ev.id]&&(
                      <MercadosEvento ev={ev}
                        bets={picksDelEvento(picks,ev)}
                        onToggle={(e,label,odd,mercado,nombre)=>
                          togglePick({home:ev.h,away:ev.a,event_id:ev.id,sport_key:ev.sport_key,commence_time:ev.commence_time||ev.time||""}, d.name, label, odd, mercado, nombre)}
                        color={Q.cyan}/>
                    )}
                  </GCard>
                ))}
              </div>
            </div>
          ))}
        </>

      {picks.length>0&&(
        <div style={{position:"sticky",bottom:0,zIndex:20,
          background:`linear-gradient(0deg,${Q.void} 80%,transparent)`,
          paddingTop:SPACING[20],
          paddingBottom:"calc(16px + env(safe-area-inset-bottom))"}}>
          <GCard style={{padding:SPACING[16]}}>
            {/* Las selecciones cargadas, con botón para sacar cada una.
                Antes había que buscar la cuota en la lista y tocarla de
                nuevo para quitarla, que no era evidente. */}
            {verPicks&&(
              <div style={{marginBottom:10,paddingBottom:SPACING[12],
                borderBottom:`1px solid ${Q.border}`,maxHeight:180,
                overflowY:"auto"}}>
                {picks.map(p=>(
                  <div key={p.id} style={{display:"flex",
                    justifyContent:"space-between",alignItems:"center",
                    gap:SPACING[8],padding:"4px 0"}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.dim,fontSize:12,
                        overflow:"hidden",textOverflow:"ellipsis",
                        whiteSpace:"nowrap"}}>
                        {p.home} — {p.away}</div>
                      <div style={{color:Q.text,fontSize:12}}>
                        {p.sel} · <span style={{color:Q.gold}}>
                          {fmt(p.odd)}</span></div>
                    </div>
                    <button onClick={()=>setPicks(x=>x.filter(y=>y.id!==p.id))}
                      aria-label="Quitar"
                      style={{background:"transparent",border:"none",
                        color:Q.red,fontSize:17,cursor:"pointer",
                        padding:"0 4px",flexShrink:0,lineHeight:1}}>×</button>
                  </div>
                ))}
                <button onClick={()=>{
                    if(window.confirm("¿Borrar todas las selecciones?"))
                      setPicks([]);
                  }} style={{width:"100%",marginTop:7,
                  background:"transparent",border:`1px solid ${Q.border}`,
                  borderRadius:RADII.sm,padding:"8px",cursor:"pointer",
                  color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}>
                  Borrar todo</button>
              </div>
            )}

            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:10}}>
              <div onClick={()=>setVerPicks(v=>!v)} style={{cursor:"pointer"}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  {picks.length} picks · <span style={{color:Q.cyan}}>{fmt(totOdd)}x</span>
                  <span style={{color:Q.dim,fontSize:12,marginLeft:5}}>
                    {verPicks?"▲":"▼"}</span>
                  {mismoPartido&&<span style={{color:Q.gold,fontSize:12,marginLeft:6}}><Link size={11}/> mismo partido</span>}
                </div>
                <div style={{color:Q.green,fontSize:12}}>Ret: {money(Math.round(monto*totOdd), mon)}</div>
              </div>
              <div style={{display:"flex",gap:SPACING[4]}}>
                {montosDe(agencia?.moneda).map(v=>(
                  <button key={v} onClick={()=>setMonto(v)} style={{
                    background:monto===v?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                    border:`1px solid ${monto===v?Q.violet:Q.border}`,
                    borderRadius:RADII.md,padding:"4px 8px",cursor:"pointer",
                    color:monto===v?Q.cyan:Q.muted,fontSize:12,fontWeight:monto===v?700:400,
                    fontFamily:F_BODY,
                  }}>{v>=1000?`$${v/1000}K`:v}</button>
                ))}
              </div>
        {/* Campo libre: las fichas son atajos, no un límite */}
        <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:8}}>
          <span style={{color:Q.muted,fontSize:12,flexShrink:0,
            fontFamily:F_BODY}}>Otro:</span>
          <input type="number" inputMode="numeric" min="0" value={monto}
            onChange={e=>setMonto(Math.max(0,Number(e.target.value)||0))}
            aria-label="Monto a apostar"
            style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 8px",
              color:Q.text,fontSize:13,fontWeight:700,
              fontFamily:F_BODY}}/>
        </div>
            </div>
            <AlertaError mensaje={errGuardar} critico/>
            <Btn label={guardando?"REGISTRANDO..."
                                 :`CONFIRMAR Y COBRAR ${money(monto, mon)}`}
              onClick={confirmar} color={Q.violet} size="lg" full
              disabled={guardando}/>
          </GCard>
        </div>
      )}
    </div>
  );
}

// ── PAGO EN LOCAL ──────────────────────────────────────────────
function PagoLocalForm({ slip, onConfirm, onCancel, error, paying }){
  const [monto,setMonto]=useState(slip.stake||10000);
  return(
    <GCard glow={error?Q.red:Q.amber} style={{padding:SPACING[20],marginBottom:12}}>
      <div style={{color:Q.amber,fontWeight:700,fontSize:15,marginBottom:4,
        fontFamily:F_BODY}}>Cobrar apuesta en efectivo</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:16}}>
        Cuota: {fmt(slip.odd_total)}x · Ingresá el monto que cobra el cliente
      </div>

      <AlertaError mensaje={error} critico/>

      <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
        letterSpacing:1,fontFamily:F_BODY,marginBottom:6}}>Monto en efectivo</div>
      <input type="number" value={monto} onChange={e=>setMonto(Number(e.target.value))}
        style={{width:"100%",background:"rgba(255,255,255,0.06)",
          border:`1.5px solid ${Q.amber}`,borderRadius:RADII.md,padding:"12px 16px",
          color:Q.amber,fontSize:22,fontFamily:F_BODY,
          fontWeight:700,marginBottom:10}}/>
      <div style={{color:Q.muted,fontSize:12,marginBottom:16}}>
        Retorno potencial: <strong style={{color:Q.green,fontSize:16}}>{money(Math.round(monto*slip.odd_total), slip?.moneda)}</strong>
      </div>
      <div style={{display:"flex",gap:SPACING[8]}}>
        <Btn label="Cancelar" onClick={onCancel} outline color={Q.muted} full disabled={paying}/>
        <Btn label={paying?"REGISTRANDO...":`COBRAR ${money(monto, slip?.moneda)}`}
          onClick={()=>onConfirm(monto)} color={Q.amber} full
          disabled={paying||!monto||monto<=0}/>
      </div>
    </GCard>
  );
}

// ── DONE SCREEN ───────────────────────────────────────────────
function DoneScreen({ slip, titulo, color, tipo, onReset }){
  const [printed,setPrinted]=useState(false);
  const imprimir=()=>{ if(printTicket(slip, tipo)) setPrinted(true); };
  return(
    <GCard glow={color} style={{padding:SPACING[24],textAlign:"center"}}>
      <div style={{marginBottom:10}}>
        {color===Q.green?<Icon name="circle-check" size={52} color={color}/>:color===Q.amber?<Icon name="wallet-cards" size={52} color={color}/>:<PartyPopper size={52} color={color}/>}
      </div>
      <div style={{color,fontWeight:900,fontSize:20,
        fontFamily:F_BODY,marginBottom:6}}>{titulo}</div>
      <div style={{background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.border}`,
        borderRadius:RADII.lg,padding:SPACING[16],marginBottom:16,textAlign:"left"}}>
        {[
          ["Código",slip.code,Q.cyan],
          ["Cliente",slip.user,Q.text],
          ["Picks",(slip.picks?.length||0)+" selecciones",Q.text],
          ["Apostado",money(slip.stake, slip?.moneda),Q.text],
          ["Retorno pot.",money(slip.potential_win, slip?.moneda),Q.green],
        ].map(([l,v,c])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",
            padding:"4px 0",borderBottom:`1px solid ${Q.dim}`}}>
            <span style={{color:Q.muted,fontSize:12}}>{l}</span>
            <span style={{color:c,fontWeight:700,fontSize:12,
              fontFamily:F_BODY}}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
        <button onClick={imprimir} style={{
          flex:1,background:printed?"rgba(255,255,255,0.04)":`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
          border:`1px solid ${printed?Q.border:Q.violet}`,borderRadius:RADII.lg,padding:SPACING[16],
          color:printed?Q.muted:"#fff",fontWeight:700,fontSize:14,cursor:"pointer",
          fontFamily:F_BODY,textTransform:"uppercase",
        }}><Printer size={14}/> {printed?"Reimprimir":"Imprimir ticket"}</button>
      </div>
      <Btn label="NUEVA OPERACIÓN" onClick={onReset} color={color} full size="lg"/>
    </GCard>
  );
}

// ── CORREGIR PICK (compartido) ────────────────────────────────
// Cuando la IA leyó mal un pick, el cliente lo corrige acá: elige otra
// selección del mismo partido, ajusta la cuota, cambia el partido entero
// o quita la selección. Se despliega bajo el pick (móvil-friendly).
function CorregirPick({ pick, token, onAplicar, onQuitar, onSesionExpirada }){
  const [modo,setModo]=useState("seleccion");   // seleccion | partido
  const [buscar,setBuscar]=useState("");
  const [encontrados,setEncontrados]=useState([]);
  const [buscando,setBuscando]=useState(false);
  const [ajuste,setAjuste]=useState("");         // cuota manual opcional
  const opciones=pick.opciones||[];

  // Buscar otro partido por nombre
  const buscarEventos=async()=>{
    if(buscar.trim().length<2||buscando) return;
    setBuscando(true);
    try{
      const r=await fetch(`${API_URL}/api/buscar-eventos?q=${encodeURIComponent(buscar.trim())}`,
        {headers:authHeaders(token)});
      if(r.status===401){ onSesionExpirada&&onSesionExpirada(); return; }
      const d=await r.json();
      setEncontrados(d.eventos||[]);
    }catch(e){ setEncontrados([]); }
    setBuscando(false);
  };

  // El mismo par que `CorregirPickApp` (`App.jsx`) y `CorregirPickBox`
  // (`Box.jsx`), y por lo mismo: `op.sel` y `op.mkt` son las ETIQUETAS que el
  // cajero lee, `op.nombre` y `op.market` son los nombres que EMITE GR8 y los
  // únicos que el índice de claves de selección conoce. Mandando sólo las
  // etiquetas, el boleto se COBRABA —el prefijo `gr8_` del `sport_key` le
  // alcanza a la validación de venta— y después no se podía liquidar por
  // clave: quedaba `otro_proveedor` y lo terminaba pagando a mano la misma
  // persona que lo acababa de cargar.
  //
  // `market: op.mkt` era el peor de los dos: la etiqueta "1X2" corresponde a
  // DOS mercados del emisor según el deporte (`h2h_3_way` si se puede
  // empatar, `h2h` si no), así que ninguna traducción nuestra puede acertar
  // para los dos.
  //
  // La etiqueta no se toca: la fila del pick corregido dibuja
  // `pick.selection` y el boleto impreso también. Ver E5a-1b.

  // Elegir una opción del partido actual
  const elegirOpcion=(op)=>{
    const cuota = ajuste.trim()? parseFloat(ajuste) : op.odd;
    onAplicar({
      selection: op.sel,
      nombre: op.nombre,
      market: op.market,
      odd_nuestra: op.odd,
      odd_final: cuota,
      odd_ajustada: cuota,
      ajustada: !!ajuste.trim(),
      estado: "ok",
    });
  };

  // Elegir un partido nuevo y una de sus opciones
  const elegirNuevoPartido=(ev, op)=>{
    const cuota = ajuste.trim()? parseFloat(ajuste) : op.odd;
    onAplicar({
      home: ev.home, away: ev.away,
      home_real: ev.home, away_real: ev.away,
      event_id: ev.event_id, sport_key: ev.sport_key,
      selection: op.sel,
      nombre: op.nombre, market: op.market,
      odd_nuestra: op.odd, odd_final: cuota, odd_ajustada: cuota,
      ajustada: !!ajuste.trim(), estado:"ok",
      opciones: ev.opciones,
    });
  };

  return(
    <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
      {/* Nombres reales, para comparar con lo leído */}
      {(pick.home_real||pick.away_real)&&(
        <div style={{color:Q.muted,fontSize:12,marginBottom:8,
          fontFamily:F_BODY}}>
          En nuestro sistema: <span style={{color:Q.cyan}}>{pick.home_real} vs {pick.away_real}</span>
        </div>
      )}

      {/* Candidatos parecidos (cuando no encontró el partido) */}
      {(pick.candidatos&&pick.candidatos.length>0)&&(
        <div style={{marginBottom:10}}>
          <div style={{color:Q.amber,fontSize:12,marginBottom:6,
            fontFamily:F_BODY}}>
            ¿Quisiste decir alguno de estos?
          </div>
          {pick.candidatos.map((ev,ci)=>(
            <div key={ci} style={{background:`${Q.amber}0E`,
              border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
              padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:5,
                fontFamily:F_BODY}}>{ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevoPartido(ev,op)} style={{
                    background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Alternar entre corregir selección o cambiar partido */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <button onClick={()=>setModo("seleccion")} style={{flex:1,
          background:modo==="seleccion"?`${Q.violet}33`:"rgba(255,255,255,0.04)",
          border:`1px solid ${modo==="seleccion"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="seleccion"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
          Cambiar selección</button>
        <button onClick={()=>setModo("partido")} style={{flex:1,
          background:modo==="partido"?`${Q.violet}33`:"rgba(255,255,255,0.04)",
          border:`1px solid ${modo==="partido"?Q.violet:Q.border}`,borderRadius:RADII.md,
          padding:"8px",cursor:"pointer",color:modo==="partido"?Q.cyan:Q.muted,
          fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
          Otro partido</button>
      </div>

      {modo==="seleccion"&&(
        <div>
          {opciones.length===0&&(
            <div style={{color:Q.muted,fontSize:12,marginBottom:8,
              fontFamily:F_BODY}}>
              No tenemos opciones de este partido. Probá "Otro partido".
            </div>
          )}
          {opciones.map((op,k)=>(
            <button key={k} onClick={()=>elegirOpcion(op)} style={{width:"100%",
              display:"flex",justifyContent:"space-between",alignItems:"center",
              background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.border}`,
              borderRadius:RADII.md,padding:"8px 12px",marginBottom:5,cursor:"pointer"}}>
              <span style={{color:Q.text,fontSize:12,
                fontFamily:F_BODY}}>{op.sel}</span>
              <span style={{color:Q.cyan,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{fmt(op.odd)}</span>
            </button>
          ))}
        </div>
      )}

      {modo==="partido"&&(
        <div>
          <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
            <input value={buscar} onChange={e=>setBuscar(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&buscarEventos()}
              placeholder="Nombre del equipo..."
              style={{flex:1,background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                color:Q.text,fontSize:14,fontFamily:F_BODY}}/>
            <button onClick={buscarEventos} style={{background:`${Q.violet}33`,
              border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"0 16px",
              cursor:"pointer",color:Q.cyan,fontSize:12,fontWeight:700,
              fontFamily:F_BODY}}>{buscando?"...":"Buscar"}</button>
          </div>
          {encontrados.map((ev,k)=>(
            <div key={k} style={{background:"rgba(255,255,255,0.03)",
              border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px 12px",marginBottom:6}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,marginBottom:6,
                fontFamily:F_BODY}}>{ev.home} vs {ev.away}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[4]}}>
                {(ev.opciones||[]).map((op,j)=>(
                  <button key={j} onClick={()=>elegirNuevoPartido(ev,op)} style={{
                    background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                    borderRadius:RADII.sm,padding:"4px 8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:600,
                    fontFamily:F_BODY}}>
                    {op.sel} · {fmt(op.odd)}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ajuste manual de cuota + quitar */}
      <div style={{display:"flex",gap:SPACING[8],marginTop:8,alignItems:"center"}}>
        <input value={ajuste} onChange={e=>setAjuste(e.target.value)}
          placeholder="Cuota a mano (opcional)" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
        <button onClick={onQuitar} style={{background:`${Q.red}18`,
          border:`1px solid ${Q.red}`,borderRadius:RADII.md,padding:"8px 12px",
          cursor:"pointer",color:Q.red,fontSize:12,fontWeight:700,
          fontFamily:F_BODY}}>Quitar</button>
      </div>
      {ajuste.trim()&&(
        <div style={{color:Q.amber,fontSize:12,marginTop:5,
          fontFamily:F_BODY}}>
          La cuota a mano se aplica al elegir una opción de arriba.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MEJORAR COMBINADA — sube captura, la IA la lee y replica
// ═══════════════════════════════════════════════════════════════
function MejorarCombinada({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [imagenes,setImagenes]=useState([]);    // [{b64, tipo, preview}]
  const [analizando,setAnalizando]=useState(false);
  // El código del cupón que el cliente muestra en el celular. En el mostrador
  // es el camino rápido: el cajero teclea cinco caracteres en vez de pedirle
  // el teléfono al cliente para sacarle una foto a la pantalla.
  const [codigo,setCodigo]=useState("");
  const [res,setRes]=useState(null);
  const [err,setErr]=useState("");
  const [monto,setMonto]=useState(montoInicial(agencia?.moneda));
  const [cliente,setCliente]=useState("");
  const [cobrando,setCobrando]=useState(false);
  const [okMsg,setOkMsg]=useState("");
  const [corrigiendo,setCorrigiendo]=useState(null);  // índice del pick en corrección

  // Reemplaza un pick por la versión corregida
  const aplicarCorreccion=(idx, nuevo)=>{
    setRes(r=>{
      const picks=r.picks.map((p,i)=>i===idx?{...p,...nuevo}:p);
      const validos=picks.filter(p=>p.odd_final);
      let tot=1; validos.forEach(p=>tot*=p.odd_final);
      return {...r, picks,
        picks_ok: validos.length,
        cuota_total: validos.length?Math.round(tot*100)/100:null};
    });
    setCorrigiendo(null);
  };
  const quitarPick=(idx)=>{
    setRes(r=>{
      const picks=r.picks.filter((_,i)=>i!==idx);
      const validos=picks.filter(p=>p.odd_final);
      let tot=1; validos.forEach(p=>tot*=p.odd_final);
      return {...r, picks,
        picks_total: picks.length,
        picks_ok: validos.length,
        cuota_total: validos.length?Math.round(tot*100)/100:null};
    });
    setCorrigiendo(null);
  };

  const elegirArchivo=(e)=>{
    const files=Array.from(e.target.files||[]);
    if(!files.length) return;
    setErr(""); setRes(null); setOkMsg("");
    files.forEach(file=>{
      if(file.size>8*1024*1024){ setErr("Una imagen supera los 8MB"); return; }
      const reader=new FileReader();
      reader.onload=()=>{
        const dataUrl=reader.result;
        setImagenes(prev=>[...prev,
          {b64:dataUrl.split(",")[1], tipo:file.type||"image/jpeg", preview:dataUrl}]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value="";   // permite volver a elegir el mismo archivo
  };
  const quitarImagen=(i)=>setImagenes(prev=>prev.filter((_,k)=>k!==i));

  // Una sola función para las dos entradas. El endpoint acepta `codigo` o
  // `imagenes` y devuelve lo mismo, así que acá tampoco hay dos caminos: lo
  // único que cambia es qué se manda.
  const pedirMejora=async(entrada)=>{
    if(analizando) return;
    setAnalizando(true); setErr(""); setRes(null);
    try{
      const r=await fetch(`${API_URL}/api/mejorar-combinada`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(entrada),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      if(!d.ok){ setErr(d.mensaje||"No se pudo leer la apuesta"); }
      else setRes(d);
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message); }
    setAnalizando(false);
  };

  const analizar=()=>{
    if(!imagenes.length) return;
    return pedirMejora({imagenes:imagenes.map(im=>({data:im.b64,media_type:im.tipo}))});
  };

  const analizarCodigo=()=>{
    const limpio=codigo.trim().toUpperCase();
    if(!limpio) return;
    return pedirMejora({codigo:limpio});
  };

  const cobrar=async()=>{
    if(!res||cobrando) return;
    const validos=res.picks.filter(p=>p.odd_final);
    if(!validos.length){ setErr("No hay picks que podamos tomar"); return; }
    setCobrando(true); setErr(""); setOkMsg("");
    try{
      const r=await fetch(`${API_URL}/api/betslip`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(conRef("agencia-escaner", {
          // `sel` es lo que se liquida, así que gana el nombre que EMITE GR8
          // —el que puso el corrector— sobre la etiqueta de pantalla. Y
          // `market` se agrega: antes este cuerpo no lo mandaba en absoluto,
          // aunque `/api/betslip` ya lo guarda (casino_api.py:17356). Sin
          // mercado el reconocedor tiene que buscar el nombre en TODOS los
          // mercados del evento, y el nombre pelado de un equipo lo producen
          // dos (`h2h` y `h2h_3_way`): queda ambiguo, que es sin clave igual
          // que sin nombre.
          //
          // `sport:p.market` queda como estaba: ese campo nunca fue un
          // deporte y no se valida contra nada, pero cambiarlo acá sería otra
          // tarea. Está anotado en la tarea.
          picks: validos.map(p=>({
            home:p.home, away:p.away, sel:p.nombre||p.selection,
            odd:p.odd_final, market:p.market||"",
            sport:p.market, event_id:p.event_id||null, sport_key:p.sport_key||null,
          })),
          cliente: cliente||null,
          agencia_code: agencia.code,
        })),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(mensajeDeConflicto("agencia-escaner", r.status, e.detail) || e.detail||`Error ${r.status}`); }
      const creado=await r.json();
      const pago=await payBetslip(creado.code, monto, agencia.token);
      envioCumplido("agencia-escaner");
      printTicket({
        code:creado.code, agencia:agencia.code,
        user:cliente||"Cliente mostrador",
        created_at:nowStr(), expires_at:expires24(),
        picks:validos.map(p=>({home:p.home,away:p.away,sel:p.selection,
          odd:p.odd_final,sport:p.market})),
        stake:pago.stake, odd_total:pago.odd_total,
        potential_win:pago.potential_win,
        // El ticket se imprime y se lo lleva el cliente: la moneda va
        // adentro del papel, no en la pantalla que lo genero.
        moneda:agencia?.moneda,
      },"apuesta");
      setOkMsg(`✅ Cobrado · ${creado.code} · ret ${money(pago.potential_win, mon)}`);
      setImagenes([]); setRes(null); setCliente("");
    }catch(e){ setErr("⚠️ "+e.message); }
    setCobrando(false);
  };

  const estados={
    ok:{t:"Tenemos igual o mejor",c:Q.green},
    igualada:{t:"Igualamos su cuota",c:Q.green},
    mejorada_parcial:{t:"Máximo que podemos",c:Q.amber},
    sin_mercado:{t:"No tenemos ese mercado",c:Q.red},
    sin_partido:{t:"No tenemos ese partido",c:Q.red},
  };

  return(
    <div>
      <PageHeader icon={<Icon name="scan-line"/>} title="Mejorar combinada"
        description={<>Pedile al cliente el código de su cupón, o subí una
        captura. La leemos y la replicamos con nuestras cuotas, mejorándola
        hasta {res?.tope_ajuste_pct||6}%.</>}/>

      {/* EL CÓDIGO VA PRIMERO, y en el mostrador más que en ninguna otra
          pantalla: el cajero teclea cinco caracteres mientras el cliente
          sostiene el teléfono, en vez de pedírselo para fotografiarle la
          pantalla. La foto además depende de que entre la combinada entera,
          y por eso existe el aviso de abajo sobre scrollear el cupón. */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <input value={codigo}
          onChange={e=>setCodigo(e.target.value.toUpperCase())}
          onKeyDown={e=>{ if(e.key==="Enter") analizarCodigo(); }}
          placeholder="Código del cupón"
          aria-label="Código del cupón del cliente"
          maxLength={16}
          style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.02)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"16px 12px",color:Q.text,fontSize:15,fontWeight:700,
            letterSpacing:2,fontFamily:F_NUM,outline:"none"}}/>
        <Btn label="Buscar" onClick={analizarCodigo} color={Q.violet}
          size="lg" disabled={!codigo.trim()||analizando}/>
      </div>

      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
        marginBottom:10}}>
        <div style={{flex:1,height:1,background:Q.border}}/>
        <div style={{color:Q.dim,fontSize:12,fontFamily:F_BODY}}>o con una foto</div>
        <div style={{flex:1,height:1,background:Q.border}}/>
      </div>

      <div style={{color:Q.muted,fontSize:12,marginBottom:8,lineHeight:1.4,
        fontFamily:F_BODY}}>
        Si la combinada es larga y no entra en una foto, agregá varias
        (scrolleá el cupón entre cada captura).
      </div>

      {/* Miniaturas de las fotos cargadas */}
      {imagenes.length>0&&(
        <div style={{display:"flex",gap:SPACING[8],flexWrap:"wrap",marginBottom:10}}>
          {imagenes.map((im,i)=>(
            <div key={i} style={{position:"relative"}}>
              <img src={im.preview} alt={"foto"+i} style={{width:70,height:70,
                objectFit:"cover",borderRadius:RADII.md,border:`1px solid ${Q.border}`}}/>
              <button onClick={()=>quitarImagen(i)} style={{position:"absolute",
                top:-6,right:-6,width:20,height:20,borderRadius:"50%",
                background:Q.red,border:"none",color:"#fff",fontSize:12,
                cursor:"pointer",lineHeight:1}}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
        <label style={{flex:1,border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
          padding:"20px 12px",textAlign:"center",cursor:"pointer",
          background:"rgba(255,255,255,0.02)"}}>
          <input type="file" accept="image/*" capture="environment"
            onChange={elegirArchivo} style={{display:"none"}}/>
          <div style={{marginBottom:4}}><Icon name="camera" size={24}/></div>
          <div style={{color:Q.text,fontWeight:700,fontSize:12,
            fontFamily:F_BODY}}>Sacar foto</div>
        </label>
        <label style={{flex:1,border:`2px dashed ${Q.border}`,borderRadius:RADII.lg,
          padding:"20px 12px",textAlign:"center",cursor:"pointer",
          background:"rgba(255,255,255,0.02)"}}>
          <input type="file" accept="image/*" multiple onChange={elegirArchivo}
            style={{display:"none"}}/>
          <div style={{marginBottom:4}}><ImageIcon size={26}/></div>
          <div style={{color:Q.text,fontWeight:700,fontSize:12,
            fontFamily:F_BODY}}>
            {imagenes.length>0?"Agregar más":"Desde galería"}</div>
        </label>
      </div>

      {imagenes.length>0&&(
        <Btn label={analizando?"LEYENDO...":<><Icon name="search" size={13}/> Analizar {imagenes.length} foto{imagenes.length>1?"s":""}</>}
          onClick={analizar} color={Q.violet} full size="lg" disabled={analizando}
          style={{marginBottom:12}}/>
      )}

      <AlertaError mensaje={err}/>
      {okMsg&&<div style={{color:Q.green,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>{okMsg}</div>}

      {analizando&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.violet2,fontSize:13,
            fontFamily:F_BODY}}>
            La IA está leyendo la apuesta...
          </div>
        </GCard>
      )}

      {res&&(
        <div>
          <div style={{color:Q.muted,fontSize:12,margin:"4px 0 10px",
            fontFamily:F_BODY}}>
            Leímos {res.picks_total} selecciones · podemos tomar {res.picks_ok}
          </div>

          {res.faltan_picks&&(
            <div style={{background:`${Q.red}12`,border:`1px solid ${Q.red}66`,
              borderRadius:RADII.md,padding:"12px 12px",marginBottom:12,color:Q.red,
              fontSize:12,lineHeight:1.4,fontFamily:F_BODY}}>
              <Icon name="triangle-alert" size={13}/> El cupón marca cuota total {fmt(res.total_odd_cupon)} pero con lo
              leído no llegamos. Probablemente falta algún partido — agregá otra
              foto scrolleando el cupón.
            </div>
          )}

          {/* Un solo aviso arriba: el botón de corregir ya está en
              cada pick y repetirlo no agrega nada. */}
          <div style={{background:`${Q.amber}12`,
            border:`1px solid ${Q.amber}44`,borderRadius:RADII.md,
            padding:"12px 12px",marginBottom:10,color:Q.amber,
            fontSize:12,lineHeight:1.5,
            fontFamily:F_BODY}}>
            Revisá que los partidos y las selecciones sean los
            correctos antes de confirmar. Si alguno no coincide, tocá
            "corregir" en ese pick.</div>

          {res.picks.map((p,i)=>{
            const est=estados[p.estado]||{t:p.estado,c:Q.muted};
            return(
            <GCard key={i} glow={p.odd_final?est.c:undefined}
              style={{padding:"12px 16px",marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",gap:SPACING[8],marginBottom:6}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:600,fontSize:13,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                    fontFamily:F_BODY}}>{p.home} vs {p.away}</div>
                  <div style={{color:Q.muted,fontSize:12}}>{p.selection}</div>
                  {/* El partido ya arrancó: la cuota se mueve sola mientras el
                      cajero cobra. No bloquea nada —la venta valida el precio
                      contra el feed— pero el rechazo le llega al cajero con el
                      cliente adelante, y conviene que lo vea venir. */}
                  {p.en_vivo&&(
                    <div style={{color:Q.amber,fontSize:12,lineHeight:1.35,
                      fontFamily:F_BODY}}>
                      En vivo · la cuota puede cambiar antes de cobrar
                    </div>
                  )}
                </div>
                <span style={{background:`${est.c}22`,border:`1px solid ${est.c}`,
                  borderRadius:RADII.xl,padding:"4px 8px",fontSize:12,fontWeight:700,
                  color:est.c,flexShrink:0,whiteSpace:"nowrap",
                  fontFamily:F_BODY}}>{est.t}</span>
              </div>

              <div style={{display:"flex",gap:SPACING[12],fontSize:12,marginBottom:8,
                flexWrap:"wrap",fontFamily:F_BODY}}>
                {p.odd_original&&<span style={{color:Q.dim}}>
                  Origen: <span style={{color:Q.muted}}>{fmt(p.odd_original)}</span></span>}
                {/* LA CUOTA DEL CUPÓN NO ES "ORIGEN", y acá importa el doble
                    porque el cajero tiene al cliente enfrente. "Origen" es el
                    precio contra el que se compara; el del cupón estaba
                    CONGELADO cuando se generó el código y no se puede fechar.
                    Decirle al cliente "allá pagaban 8.5" con ese número es
                    arriesgarse a que él mire su pantalla y vea 9.00. */}
                {p.odd_cupon&&<span style={{color:Q.dim}}>
                  En su cupón: <span style={{color:Q.muted}}>{fmt(p.odd_cupon)}</span></span>}
                {p.odd_nuestra&&<span style={{color:Q.dim}}>
                  Nuestra: <span style={{color:Q.muted}}>{fmt(p.odd_nuestra)}</span></span>}
                {p.odd_final&&<span style={{color:Q.dim}}>
                  Final: <span style={{color:est.c,fontWeight:700}}>{fmt(p.odd_final)}</span>
                  {p.ajustada&&<span style={{color:Q.amber,fontSize:12}}> ↑ajustada</span>}</span>}
              </div>
              <button onClick={()=>setCorrigiendo(corrigiendo===i?null:i)} style={{
                background:corrigiendo===i?`${Q.cyan}22`:"transparent",
                border:`1px solid ${corrigiendo===i?Q.cyan:Q.dim}`,borderRadius:RADII.md,
                padding:"8px 12px",cursor:"pointer",color:corrigiendo===i?Q.cyan:Q.muted,
                fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                {corrigiendo===i?"✕ Cerrar":<><Pencil size={12}/> Está mal / Corregir</>}
              </button>
              {corrigiendo===i&&(
                <CorregirPick pick={p} token={agencia.token}
                  onAplicar={(nuevo)=>aplicarCorreccion(i,nuevo)}
                  onQuitar={()=>quitarPick(i)}
                  onSesionExpirada={onSesionExpirada}/>
              )}
            </GCard>
            );
          })}

          {res.picks_ok<res.picks_total&&(
            <div style={{background:`${Q.amber}12`,border:`1px solid ${Q.amber}55`,
              borderRadius:RADII.md,padding:"12px 12px",marginBottom:12,color:Q.amber,
              fontSize:12,lineHeight:1.4,fontFamily:F_BODY}}>
              <Icon name="triangle-alert" size={13}/> {res.picks_total-res.picks_ok} selección(es) no las podemos tomar
              (partido o mercado que no tenemos). Se cobra solo con las
              {" "}{res.picks_ok} que sí, si estás de acuerdo.
            </div>
          )}

          {res.picks_ok>0&&(
            <GCard style={{padding:SPACING[16],marginTop:4}}>
              <div style={{display:"flex",justifyContent:"space-between",
                marginBottom:12}}>
                <span style={{color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}>Cuota total nuestra</span>
                <span style={{color:Q.gold,fontWeight:900,fontSize:20,
                  fontFamily:F_BODY}}>{fmt(res.cuota_total)}x</span>
              </div>
              <input value={cliente} onChange={e=>setCliente(e.target.value)}
                placeholder="Cliente (opcional)"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                  color:Q.text,fontSize:16,marginBottom:8,
                  fontFamily:F_BODY}}/>
              <div style={{display:"flex",gap:SPACING[4],marginBottom:10}}>
                {montosDe(agencia?.moneda).map(v=>(
                  <button key={v} onClick={()=>setMonto(v)} style={{
                    flex:1,background:monto===v?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                    border:`1px solid ${monto===v?Q.violet:Q.border}`,borderRadius:RADII.md,
                    padding:"8px 4px",cursor:"pointer",color:monto===v?Q.cyan:Q.muted,
                    fontSize:12,fontWeight:monto===v?700:400,
                    fontFamily:F_BODY,
                  }}>{v>=1000?`$${v/1000}K`:v}</button>
                ))}
              </div>
        {/* Campo libre: las fichas son atajos, no un límite */}
        <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:8}}>
          <span style={{color:Q.muted,fontSize:12,flexShrink:0,
            fontFamily:F_BODY}}>Otro:</span>
          <input type="number" inputMode="numeric" min="0" value={monto}
            onChange={e=>setMonto(Math.max(0,Number(e.target.value)||0))}
            aria-label="Monto a apostar"
            style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 8px",
              color:Q.text,fontSize:13,fontWeight:700,
              fontFamily:F_BODY}}/>
        </div>
              <div style={{display:"flex",justifyContent:"space-between",
                marginBottom:10,padding:"8px 0",borderTop:`1px solid ${Q.dim}`}}>
                <span style={{color:Q.muted,fontSize:12}}>Retorno {money(monto, mon)}</span>
                <span style={{color:Q.green,fontWeight:700,fontSize:15,
                  fontFamily:F_BODY}}>
                  {money(Math.round(monto*res.cuota_total), mon)}</span>
              </div>
              <button onClick={cobrar} disabled={cobrando} style={{
                width:"100%",
                background:cobrando?"rgba(255,255,255,0.06)"
                  :`linear-gradient(135deg,${Q.green},#00a854)`,
                border:"none",borderRadius:RADII.md,padding:"12px",
                cursor:cobrando?"wait":"pointer",
                color:cobrando?Q.muted:"#04120a",fontWeight:900,fontSize:14,
                fontFamily:F_BODY,textTransform:"uppercase",
              }}>{cobrando?"Cobrando...":`Cobrar e imprimir ${money(monto, mon)}`}</button>
            </GCard>
          )}
        </div>
      )}
    </div>
  );
}

// ── CREAR COMBO (agencia) ─────────────────────────────────────
function CrearComboAgencia({ agencia, onVolver, onSesionExpirada }){
  const [deportes,setDeportes]=useState(null);
  // Combos ya creados, para verlos debajo del formulario
  const [lista,setLista]=useState([]);
  const [nombre,setNombre]=useState("");
  const [picks,setPicks]=useState([]);
  const [busqueda,setBusqueda]=useState("");
  // Deporte y liga son dos ejes, y acá eran uno solo mal nombrado: la tira
  // de chips decía "liga" y comparaba contra `d.name`, que en el catálogo
  // de `/api/live/prematch` es el DEPORTE. Con 68 torneos, el que arma el
  // combo del día no tenía filtro de liga en absoluto. Es el mismo defecto
  // que el T3 sacó de la pestaña Prematch de `FlujoManual`.
  const [deporteSel,setDeporteSel]=useState(null);
  const [liga,setLiga]=useState(null);
  const [guardando,setGuardando]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [infList,setInfList]=useState([]);
  const [infCode,setInfCode]=useState("");
  const [codigoSalida,setCodigoSalida]=useState("");
  // compartir con varios
  const [destSubs,setDestSubs]=useState([]);       // codes de subs
  const [destInfs,setDestInfs]=useState([]);       // codes de influencers
  const [todosSubs,setTodosSubs]=useState(false);
  const [todosInfs,setTodosInfs]=useState(false);
  const [destinatarios,setDestinatarios]=useState({subs:[],influencers:[]});

  useEffect(()=>{
    fetch(`${API_URL}/api/live/prematch`)
      .then(r=>r.ok?r.json():null)
      .then(d=>setDeportes(d?.sports||[]))
      .catch(()=>setDeportes([]));
    fetch(`${API_URL}/api/influencers`,{headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():{influencers:[]}).then(d=>setInfList(d.influencers||[]))
      .catch(()=>{});
    fetch(`${API_URL}/api/agencias/me/destinatarios`,{headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():{subs:[],influencers:[]})
      .then(d=>setDestinatarios({subs:d.subs||[],influencers:d.influencers||[]}))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const toggleDest=(code,lista,setLista)=>{
    setLista(prev=>prev.includes(code)?prev.filter(c=>c!==code):[...prev,code]);
  };

  // `market` y `nombre` son el mercado y el nombre del resultado TAL COMO LOS
  // EMITE GR8, y los manda el carrusel cuarto y quinto. Este `toggle`
  // declaraba CUATRO argumentos y no guardaba ninguno de los dos, así que el
  // combo que la agencia publicaba quedaba con la etiqueta de pantalla donde
  // va la clave y sin mercado: se vende y no se puede pagar solo.
  //
  // Van ADEMÁS de `sel`, que sigue siendo la etiqueta —lo que el jugador lee
  // en la tarjeta del combo— y la identidad del carrito (`id`).
  const toggle=(ev,dep,label,odd,market,nombre)=>{
    const id=`${ev.h}-${ev.a}-${label}`;
    setPicks(p=>{
      const w=p.filter(x=>x.id!==id);
      if(p.find(x=>x.id===id)) return w;
      return[...w,{id,home:ev.h,away:ev.a,sel:label,odd,sport:dep,
        market:market||"",nombre:nombre||"",
        event_id:ev.id,sport_key:ev.sport_key}];
    });
  };
  // El carrusel pregunta por `bets`, no por un ayudante propio: el
  // `tiene(ev,l)` que pintaba la fila fija de 1X2 se fue con ella. La
  // regla de "este pick es de este evento" vive en `agenciaPicks.js`, una
  // sola vez para las cuatro pantallas que la necesitan.
  //
  // La cuota la cotiza el SERVIDOR, igual que en `FlujoManual`.
  //
  // Acá había un producto pelado, y era el defecto: el servidor guarda el
  // combo con `_odd_del_combo` —que cobra el margen de correlación por
  // partido— así que el que armaba el combo leía un precio más alto que el que
  // quedaba guardado. Dos mercados del mismo partido entran en este armador
  // (el id del pick lleva la etiqueta adentro), así que no era un caso de
  // laboratorio.
  //
  // `useCuotaCombinada` es el ÚNICO lugar que llama a
  // `/api/bet-builder/cotizar`, y eso es lo que `combosMismoPartido.test.js`
  // protege: prohíbe que esta pantalla abra su propia llamada, no que use el
  // módulo. Manda `home`/`away` con cada selección, que es lo que el carrito
  // de acá ya guarda. Ver `odd/tasks/cuota-del-combo.md`.
  const {totOdd}=useCuotaCombinada(picks);

  const guardar=async()=>{
    if(!picks.length||guardando) return;
    setGuardando(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/combos`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        // `sel` se queda como está —es la etiqueta que el jugador lee en la
        // tarjeta del combo— y el mercado y el nombre del emisor viajan AL
        // LADO. Son los dos campos con los que GR8 liquida, y hasta L4a
        // `/api/agencias/me/combos` los descartaba de la lista blanca: un
        // combo publicado no podía liquidar NUNCA, por mucho que esta pantalla
        // los mandara. Las dos mitades son una sola tanda.
        body:JSON.stringify({nombre:nombre||"Combo de la agencia",
          picks:picks.map(p=>({home:p.home,away:p.away,sel:p.sel,odd:p.odd,
            sport:p.sport,event_id:p.event_id,sport_key:p.sport_key,
            market:p.market||"",nombre:p.nombre||""})),
          influencer_code: infCode||null, codigo: codigoSalida||null,
          compartir_subs: todosSubs ? "todos" : destSubs,
          compartir_influencers: todosInfs ? "todos" : destInfs}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const nComp=(todosSubs?destinatarios.subs.length:destSubs.length)
                 +(todosInfs?destinatarios.influencers.length:destInfs.length);
      setMsg({text:`Combo creado.${nComp>0?` Compartido con ${nComp} cuenta(s).`:" Ya aparece en tus terminales."}`, ok:true});
      setPicks([]); setNombre("");
      setDestSubs([]); setDestInfs([]); setTodosSubs(false); setTodosInfs(false);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setGuardando(false);
  };

  // ── Los filtros, en cascada ─────────────────────────────────────
  // El mismo cableado que la pestaña Prematch de `FlujoManual`, que lee
  // este mismo endpoint: cada desplegable se arma sobre el catálogo
  // filtrado por TODO lo anterior menos por sí mismo. Si la liga elegida
  // entrara en el cálculo de su propia lista, al elegirla desaparecerían
  // las demás y no habría forma de volver; y si la lista se armara sobre
  // el catálogo completo, ofrecería ligas que llevan a una lista vacía.
  //
  // El buscador se queda: `SelectorDesplegable` trae uno adentro, pero
  // solo con más de 12 opciones y solo sobre los rótulos del propio
  // filtro —nombres de deporte o de liga—. Buscar un EQUIPO no lo cubre
  // ninguno de los dos, y es la forma más rápida de llegar a un partido.
  // `coincideBusqueda` mira equipos y liga, así que ahora también
  // encuentra por torneo, que antes no.
  const catalogo = useMemo(
    ()=>filtrarCatalogo(deportes||[],{busqueda}),
    [deportes,busqueda]);
  const deportesCombo = useMemo(()=>deportesDisponibles(catalogo),[catalogo]);
  // Una selección que ya no existe —cambió la oferta, terminó el partido—
  // vuelve sola a "todos" en vez de dejar la lista vacía con un filtro
  // puesto que no se ve.
  const deporteActivo = deportesCombo.some(d=>d.name===deporteSel)?deporteSel:null;
  const porDeporte = useMemo(
    ()=>filtrarCatalogo(catalogo,{deporte:deporteActivo}),
    [catalogo,deporteActivo]);
  const ligasCombo = useMemo(()=>ligasDisponibles(porDeporte),[porDeporte]);
  const ligaActiva = ligasCombo.some(l=>l.liga===liga)?liga:null;
  const comboVisible = useMemo(
    ()=>filtrarCatalogo(porDeporte,{liga:ligaActiva}),
    [porDeporte,ligaActiva]);
  const totalCombo = catalogo.reduce((n,s)=>n+s.events.length,0);
  const enEstaVista = porDeporte.reduce((n,s)=>n+s.events.length,0);
  // Las dos listas en la forma que entiende el desplegable. El orden lo
  // siguen decidiendo `deportesDisponibles()` y `ligasDisponibles()`: acá
  // solo se renombran los campos.
  const opcionesDeporte = useMemo(()=>deportesCombo.map(d=>({
    clave:d.name, rotulo:d.name, eventos:d.eventos })),[deportesCombo]);
  const opcionesLiga = useMemo(()=>ligasCombo.map(l=>({
    clave:l.liga, rotulo:l.liga, eventos:l.eventos })),[ligasCombo]);

  return(
    <div>
      <button onClick={onVolver} style={{background:"transparent",border:"none",
        color:Q.muted,fontSize:22,cursor:"pointer",marginBottom:6,padding:0}}>‹ Volver</button>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:10,
        fontFamily:F_BODY}}>Crear combo</div>

      <input value={nombre} onChange={e=>setNombre(e.target.value)}
        placeholder="Nombre del combo (opcional)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
          color:Q.text,fontSize:16,marginBottom:10,
          fontFamily:F_BODY}}/>

      {infList.length>0&&<select value={infCode} onChange={e=>setInfCode(e.target.value)}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${infCode?Q.violet:Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
          color:infCode?Q.text:Q.muted,fontSize:15,marginBottom:10,
          fontFamily:F_BODY}}>
        <option value="">Asignar a influencer (opcional)</option>
        {infList.map(inf=>(
          <option key={inf.code} value={inf.code}>{inf.name} · {inf.codigo_ref}</option>
        ))}
      </select>}
      {infCode&&<input value={codigoSalida} onChange={e=>setCodigoSalida(e.target.value.toUpperCase())}
        placeholder="Código de salida del combo (ej: COMBODELDIA)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
          color:Q.text,fontSize:15,marginBottom:10,
          fontFamily:F_BODY}}/>}

      {/* Compartir con subs e influencers */}
      {(destinatarios.subs.length>0||destinatarios.influencers.length>0)&&(
        <GCard style={{padding:SPACING[12],marginBottom:10}}>
          <div style={{color:Q.violet2,fontWeight:700,fontSize:12,marginBottom:8,
            fontFamily:F_BODY}}><Icon name="arrow-up-right" size={13}/> Compartir este combo</div>

          {destinatarios.subs.length>0&&(
            <div style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                  fontFamily:F_BODY}}><Building2 size={12}/> Sub-agencias</span>
                <button onClick={()=>{setTodosSubs(v=>!v); if(!todosSubs)setDestSubs([]);}}
                  style={{background:todosSubs?`${Q.green}22`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${todosSubs?Q.green:Q.border}`,borderRadius:RADII.md,
                    padding:"4px 12px",cursor:"pointer",color:todosSubs?Q.green:Q.muted,
                    fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                  {todosSubs?"✓ Todas":"Todas"}</button>
              </div>
              {!todosSubs&&<div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8]}}>
                {destinatarios.subs.map(x=>(
                  <button key={x.code} onClick={()=>toggleDest(x.code,destSubs,setDestSubs)}
                    style={{background:destSubs.includes(x.code)?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                      border:`1px solid ${destSubs.includes(x.code)?Q.violet:Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",cursor:"pointer",color:destSubs.includes(x.code)?Q.text:Q.muted,
                      fontSize:12,fontFamily:F_BODY}}>
                    {destSubs.includes(x.code)?"✓ ":""}{x.name}</button>
                ))}
              </div>}
            </div>
          )}

          {destinatarios.influencers.length>0&&(
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                  fontFamily:F_BODY}}><Star size={12}/> Influencers</span>
                <button onClick={()=>{setTodosInfs(v=>!v); if(!todosInfs)setDestInfs([]);}}
                  style={{background:todosInfs?`${Q.green}22`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${todosInfs?Q.green:Q.border}`,borderRadius:RADII.md,
                    padding:"4px 12px",cursor:"pointer",color:todosInfs?Q.green:Q.muted,
                    fontSize:12,fontWeight:700,fontFamily:F_BODY}}>
                  {todosInfs?"✓ Todos":"Todos"}</button>
              </div>
              {!todosInfs&&<div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8]}}>
                {destinatarios.influencers.map(x=>(
                  <button key={x.code} onClick={()=>toggleDest(x.code,destInfs,setDestInfs)}
                    style={{background:destInfs.includes(x.code)?`${Q.violet}33`:"rgba(255,255,255,0.04)",
                      border:`1px solid ${destInfs.includes(x.code)?Q.violet:Q.border}`,borderRadius:RADII.md,
                      padding:"8px 12px",cursor:"pointer",color:destInfs.includes(x.code)?Q.text:Q.muted,
                      fontSize:12,fontFamily:F_BODY}}>
                    {destInfs.includes(x.code)?"✓ ":""}{x.name}</button>
                ))}
              </div>}
            </div>
          )}
        </GCard>
      )}

      <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
        background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
        borderRadius:RADII.md,padding:"8px 12px",marginBottom:8}}>
        <Icon name="search" size={14} color={Q.muted}/>
        <input value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          placeholder="Buscar equipo o liga..."
          style={{background:"transparent",border:"none",color:Q.text,fontSize:14,
            flex:1,minWidth:0,fontFamily:F_BODY}}/>
      </div>

      {/* Deporte y liga, uno al lado del otro: los mismos dos desplegables
          que ve el jugador y que ya tiene la pestaña Prematch del
          sportsbook de la agencia. Eran UNA tira de chips que se deslizaba
          de costado, con lo que de los 12 deportes se veían los cuatro que
          entraban en el ancho de la pantalla y de las 68 ligas ninguna.
          Son las listas de ESTA vista, no las del catálogo entero, así que
          un filtro nunca lleva a una lista vacía. */}
      {(deportesCombo.length>1||ligasCombo.length>1)&&(
        <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
          marginBottom:10}}>
          {deportesCombo.length>1&&(
            <SelectorDesplegable rotulo="Deporte" todas="Todos los deportes"
              opciones={opcionesDeporte} activa={deporteActivo}
              total={totalCombo} color={Q.violet}
              onElegir={clave=>setDeporteSel(clave)}/>
          )}
          {ligasCombo.length>1&&(
            <SelectorDesplegable rotulo="Liga" todas="Todas las ligas"
              opciones={opcionesLiga} activa={ligaActiva}
              total={enEstaVista} color={Q.violet}
              onElegir={clave=>setLiga(clave)}/>
          )}
        </div>
      )}

      {!deportes&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando partidos...</div>}

      {/* La caja con scroll se QUEDA, y sube de 40vh a 60vh. A diferencia
          del sportsbook, acá debajo de la lista hay un formulario —el
          nombre, el influencer, los destinatarios y el botón de guardar—
          que tiene que seguir alcanzable sin recorrer toda la oferta. Pero
          los 40vh estaban medidos para una columna de `div` pelados: con
          la rejilla a tres columnas mostraban una fila y media. */}
      <div style={{maxHeight:"60vh",overflowY:"auto",marginBottom:12}}>
        {comboVisible.map(d=>(
          <div key={d.name} style={{marginBottom:14}}>
            {/* El deporte es un encabezado, no una tarjeta: la misma forma
                de fila que el jugador y que la pestaña Prematch de
                `FlujoManual`, con el contador de eventos a la derecha. */}
            <div style={{display:"flex",alignItems:"center",gap:SPACING[8],
              marginBottom:8}}>
              <span style={{color:Q.violet2,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>{d.icon} {d.name}</span>
              <span style={{color:Q.dim,fontSize:12,
                fontFamily:F_NUM}}>{(d.events||[]).length}</span>
            </div>
            {/* La misma rejilla y el mismo piso que las otras cinco listas
                del repo. Y sin la raya de abajo de cada evento: en una
                rejilla, una línea debajo de cada celda es una línea
                perdida, y el `gap` ya separa. Lo que faltaba no era la
                raya —era que el evento fuera una tarjeta, que trae su
                propio fondo. */}
            <div style={{display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(min(360px,100%),1fr))",
              gap:SPACING[12]}}>
              {(d.events||[]).map(ev=>(
                <GCard key={ev.id||(ev.h+ev.a)} style={{padding:SPACING[16]}}>
                  <div style={{color:Q.text,fontSize:13,fontWeight:600,
                    marginBottom:6,fontFamily:F_BODY,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                    {ev.h} <span style={{color:Q.dim}}>vs</span> {ev.a}
                  </div>
                  {/* El carrusel del jugador, no una fila fija de 1X2 leída
                      de `ev.odds.L/E/V`. Esta pantalla pasa a ofrecer TODOS
                      los mercados del partido y no solo el ganador, que es
                      lo que significa "el mismo sportsbook": más mercados
                      son más combos para armar.
                      `/api/live/prematch` es un alias de `all_markets()`
                      (casino_api.py:18863), así que `ev.markets` ya venía
                      completo en la respuesta y nadie lo miraba. Sin la
                      prop `live`: acá no hay partidos en curso.

                      La adaptación del `onToggle` es obligatoria y no
                      cosmética: el carrusel avisa con
                      `(ev, etiqueta, cuota, mercadoKey)` y este `toggle`
                      espera el deporte en segundo lugar. Cableado directo,
                      la clave del mercado entraría como deporte y la cuota
                      se perdería. El deporte sale de `d.name`, que es como
                      el catálogo agrupa.

                      Y el adaptador tiene que reenviar los CINCO: declaraba
                      tres y descartaba el mercado y el nombre del emisor, así
                      que el combo se publicaba con la etiqueta de pantalla
                      donde va la clave. Es el mismo olvido que L2 y L3 ya
                      arreglaron en los otros cuatro adaptadores: mandar el
                      quinto argumento desde el botón no sirve de nada si el
                      adaptador del medio no lo pasa. */}
                  <CuotasCarrusel ev={ev} bets={picksDelEvento(picks,ev)}
                    onToggle={(e,etiqueta,cuota,mercado,nombre)=>
                      toggle(ev,d.name,etiqueta,cuota,mercado,nombre)}/>
                </GCard>
              ))}
            </div>
          </div>
        ))}
      </div>

      {msg&&<div style={{fontSize:12,marginBottom:10,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {picks.length>0&&(
        <GCard style={{padding:SPACING[16],marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <span style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>{picks.length} picks</span>
            <span style={{color:Q.gold,fontWeight:900,fontSize:18,
              fontFamily:F_BODY}}>{fmt(totOdd)}x</span>
          </div>
          <Btn label={guardando?"GUARDANDO...":"Guardar combo"} onClick={guardar}
            color={Q.violet} full size="lg" disabled={guardando}/>
        </GCard>
      )}
    </div>
  );
}

function MisCombosAgencia({ agencia, onVolver, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [combos,setCombos]=useState(null);
  const [err,setErr]=useState("");
  const [abierto,setAbierto]=useState(null);
  const [cobrando,setCobrando]=useState(null);
  const [cliente,setCliente]=useState("");
  const [monto,setMonto]=useState(montoInicial(agencia?.moneda));
  const [okMsg,setOkMsg]=useState("");

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/combos`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      setCombos((await r.json()).combos||[]);
    }catch(e){ setErr(e.message); setCombos([]); }
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const borrar=async(id)=>{
    try{
      await fetch(`${API_URL}/api/agencias/me/combos/${id}`,
        {method:"DELETE",headers:authHeaders(agencia.token)});
      cargar();
    }catch(e){}
  };

  // normaliza un pick venga como {h,a} o {home,away}
  // `pk` sirve para DOS cosas: la lista que la agencia LEE (`{p.sel}`, más
  // abajo) y el cuerpo que `cobrarCombo` manda a vender. Por eso `sel` se
  // queda con la etiqueta y el nombre del emisor viaja AL LADO, en `nombre`,
  // junto con su mercado. Los dos vienen guardados en el combo desde L4a
  // (`casino_api.py`, las listas blancas que escriben `combos_manuales.picks`)
  // y este mapa los tiraba: la agencia publicaba un combo liquidable y después
  // lo vendía sin clave por su propia puerta.
  const pk=(p)=>({
    home:p.home||p.h||"", away:p.away||p.a||"",
    sel:p.sel||p.label||"", odd:parseFloat(p.odd)||1,
    nombre:p.nombre||"", market:p.market||"",
    sport:p.sport||"", event_id:p.event_id||null, sport_key:p.sport_key||null,
  });

  const cobrarCombo=async(combo)=>{
    if(cobrando) return;
    setCobrando(combo.id); setOkMsg(""); setErr("");
    try{
      // Acá se elige qué viaja a la VENTA: gana el nombre del emisor sobre la
      // etiqueta, porque es el único de los dos que `resolver_una` encuentra
      // en el índice de claves de GR8. `nombre` se saca del cuerpo una vez
      // usado —el servidor ya lo tiene en `sel`— y la lista de la pantalla
      // sigue leyendo la etiqueta, que no se toca.
      const picks=(combo.picks||[]).map(pk)
        .map(({nombre,...p})=>({...p, sel:nombre||p.sel}));
      const r=await fetch(`${API_URL}/api/betslip`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(conRef("agencia-mis-combos", {picks, cliente:cliente||null, agencia_code:agencia.code})),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(mensajeDeConflicto("agencia-mis-combos", r.status, e.detail) || e.detail||`Error ${r.status}`); }
      const creado=await r.json();
      const pago=await payBetslip(creado.code, monto, agencia.token);
      envioCumplido("agencia-mis-combos");
      printTicket({
        code:creado.code, agencia:agencia.code,
        user:cliente||"Cliente mostrador", created_at:nowStr(), expires_at:expires24(),
        picks:picks.map(p=>({home:p.home,away:p.away,sel:p.sel,odd:p.odd,sport:p.sport})),
        stake:pago.stake, odd_total:pago.odd_total, potential_win:pago.potential_win,
        moneda:agencia?.moneda,
      },"apuesta");
      setOkMsg(`✅ Combo cobrado · ${creado.code} · ret ${money(pago.potential_win, mon)}`);
      setCliente("");
    }catch(e){ setErr("⚠️ "+e.message); }
    setCobrando(null);
  };

  return(
    <div>
      <button onClick={onVolver} style={{background:"transparent",border:"none",
        color:Q.muted,fontSize:22,cursor:"pointer",marginBottom:6,padding:0}}>‹ Volver</button>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:12,
        fontFamily:F_BODY}}>Mis combos</div>

      <AlertaError mensaje={err}/>
      {okMsg&&<div style={{fontSize:12,marginBottom:10,color:Q.green,
        fontFamily:F_BODY}}>{okMsg}</div>}
      {combos===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {combos&&combos.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Icon name="clipboard-list" size={26} color={Q.muted}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            Todavía no hay combos</div>
        </GCard>
      )}
      {(combos||[]).map(c=>{
        const abierta=abierto===c.id;
        const picks=(c.picks||[]).map(pk);
        const esCasa=c.de_la_casa||c.origen==="admin";
        return(
          <GCard key={c.id} glow={esCasa?Q.gold:undefined}
            style={{padding:"12px 16px",marginBottom:8}}>
            <div onClick={()=>setAbierto(abierta?null:c.id)}
              style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",gap:SPACING[8],cursor:"pointer"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:600,fontSize:13,
                  fontFamily:F_BODY}}>{c.nombre}
                  {esCasa&&<span style={{background:`${Q.gold}22`,border:`1px solid ${Q.gold}`,
                    borderRadius:RADII.md,padding:"4px 8px",fontSize:12,color:Q.gold,marginLeft:6}}><Icon name="landmark" size={11}/> De la casa</span>}</div>
                <div style={{color:Q.muted,fontSize:12}}>
                  {picks.length} picks · {fmt(c.odd_total)}x</div>
              </div>
              <span style={{color:Q.muted,fontSize:16}}>{abierta?"▾":"›"}</span>
            </div>

            {abierta&&(
              <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                {picks.map((p,j)=>(
                  <div key={j} style={{display:"flex",justifyContent:"space-between",
                    padding:"4px 0",borderBottom:j<picks.length-1?`1px solid ${Q.dim}`:"none"}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:12,fontWeight:600,
                        fontFamily:F_BODY}}>{p.sel}</div>
                      <div style={{color:Q.muted,fontSize:12}}>{p.home} vs {p.away}</div>
                    </div>
                    <span style={{color:Q.cyan,fontWeight:700,fontSize:13,
                      fontFamily:F_BODY}}>{fmt(p.odd)}</span>
                  </div>
                ))}
                {/* Ganancias */}
                <div style={{marginTop:8}}>
                  <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                    marginBottom:4,fontFamily:F_BODY}}>Ganancias</div>
                  {montosDe(agencia?.moneda).slice(0,3).map(m=>(
                    <div key={m} style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                      <span style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>Apostás {money(m, mon)}</span>
                      <span style={{color:Q.green,fontWeight:700,fontSize:12,
                        fontFamily:F_BODY}}>→ {money(Math.round(m*(c.odd_total||1)), mon)}</span>
                    </div>
                  ))}
                </div>

                {/* Cobrar para un cliente */}
                <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.violet}44`}}>
                  <input value={cliente} onChange={e=>setCliente(e.target.value)}
                    placeholder="Cliente (opcional)"
                    style={{width:"100%",background:"rgba(255,255,255,0.05)",
                      border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                      color:Q.text,fontSize:16,marginBottom:8,
                      fontFamily:F_BODY}}/>
                  <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
                    {montosDe(agencia.moneda).map(m=>(
                      <button key={m} onClick={()=>setMonto(m)} style={{flex:1,
                        background:monto===m?`${Q.gold}22`:"rgba(255,255,255,0.04)",
                        border:`1px solid ${monto===m?Q.gold:Q.border}`,borderRadius:RADII.md,
                        padding:"8px 4px",cursor:"pointer",color:monto===m?Q.gold:Q.muted,
                        fontSize:12,fontWeight:monto===m?700:400,
                        fontFamily:F_BODY}}>{money(m, mon)}</button>
                    ))}
                  </div>
                  {/* Campo libre: los montos fijos son atajos, no un límite */}
                  <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:8}}>
                    <span style={{color:Q.muted,fontSize:12,flexShrink:0,
                      fontFamily:F_BODY}}>Otro:</span>
                    <input type="number" inputMode="numeric" min="0" value={monto}
                      onChange={e=>setMonto(Math.max(0,Number(e.target.value)||0))}
                      aria-label="Monto a apostar"
                      style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
                        border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 8px",
                        color:Q.text,fontSize:13,fontWeight:700,
                        fontFamily:F_BODY}}/>
                  </div>
                  <Btn label={cobrando===c.id?"COBRANDO...":<><Icon name="wallet-cards" size={13}/> Cobrar {money(monto, mon)}</>}
                    onClick={()=>cobrarCombo(c)} color={Q.green} full disabled={cobrando===c.id}/>
                  {!esCasa&&(
                    <button onClick={()=>borrar(c.id)} style={{width:"100%",marginTop:8,
                      background:`${Q.red}18`,border:`1px solid ${Q.red}`,borderRadius:RADII.md,
                      padding:"8px",cursor:"pointer",color:Q.red,fontSize:12,fontWeight:700,
                      fontFamily:F_BODY}}>Borrar combo</button>
                  )}
                </div>
              </div>
            )}
          </GCard>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════
// COMBOS IA — sugerencias automáticas + armado manual
// ═══════════════════════════════════════════════════════════════
function CombosIA({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [modo,setModo]=useState("ver");   // ver | crear | mios
  const [combos,setCombos]=useState(null);
  const [cargando,setCargando]=useState(true);
  const [err,setErr]=useState("");
  const [gen,setGen]=useState("");
  const [cobrando,setCobrando]=useState(null);   // id combo en proceso
  const [cliente,setCliente]=useState("");
  const [monto,setMonto]=useState(montoInicial(agencia?.moneda));
  const [abierto,setAbierto]=useState(null);      // combo desplegado
  const [okMsg,setOkMsg]=useState("");

  const cargar=async()=>{
    setCargando(true); setErr("");
    try{
      const r=await fetch(`${API_URL}/api/ai/combos`);
      if(!r.ok) throw new Error(`Error ${r.status}`);
      const d=await r.json();
      setCombos(d.combos||[]);
      setGen(d.generated_at||"");
      if(d.error) setErr(d.error);
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message);
      setCombos([]); }
    setCargando(false);
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  // Los sub-modos se muestran DESPUÉS de declarar todos los hooks
  // (si no, React tira el error #300 por número de hooks distinto).
  if(modo==="crear")
    return <CrearComboAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}
      onVolver={()=>setModo("ver")}/>;
  if(modo==="mios")
    return <MisCombosAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}
      onVolver={()=>setModo("ver")}/>;

  // Cobra el combo: crea el boleto con sus picks y lo paga
  const cobrarCombo=async(combo)=>{
    if(cobrando) return;
    setCobrando(combo.id); setOkMsg(""); setErr("");
    try{
      const r=await fetch(`${API_URL}/api/betslip`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(conRef("agencia-combos", {
          picks: combo.picks.map(p=>({
            home:p.h, away:p.a, sel:p.sel, odd:p.odd, sport:p.sport,
            event_id:p.event_id||null, sport_key:p.sport_key||null,
          })),
          cliente: cliente||null,
          agencia_code: agencia.code,
        })),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(mensajeDeConflicto("agencia-combos", r.status, e.detail) || e.detail||`Error ${r.status}`); }
      const creado=await r.json();
      const pago=await payBetslip(creado.code, monto, agencia.token);
      envioCumplido("agencia-combos");
      // Imprimir el ticket, igual que en apuesta manual
      printTicket({
        code: creado.code,
        agencia: agencia.code,
        user: cliente || "Cliente mostrador",
        created_at: nowStr(),
        expires_at: expires24(),
        picks: combo.picks.map(p=>({
          home:p.h, away:p.a, sel:p.sel, odd:p.odd, sport:p.sport,
        })),
        stake: pago.stake,
        odd_total: pago.odd_total,
        potential_win: pago.potential_win,
        moneda: agencia?.moneda,
      }, "apuesta");
      setOkMsg(`✅ Combo cobrado · ${creado.code} · ret ${money(pago.potential_win, mon)}`);
      setCliente("");
    }catch(e){ setErr("⚠️ "+e.message); }
    setCobrando(null);
  };

  return(
    <div>
      <PageHeader icon={<Zap/>} title="Combos IA"
        action={<Btn label={cargando?"...":"↻ Actualizar"} onClick={cargar}
          outline color={Q.muted} size="sm"/>}/>
      {gen&&<div style={{color:Q.muted,fontSize:12,marginBottom:12}}>Generados {gen}</div>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <Btn label="+ Crear combo" onClick={()=>setModo("crear")}
          color={Q.violet} full/>
        <Btn label="Mis combos" onClick={()=>setModo("mios")}
          outline color={Q.cyan} full/>
      </div>

      <GCard style={{padding:SPACING[16],marginBottom:12,
        background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}08)`}}>
        <input value={cliente} onChange={e=>setCliente(e.target.value)}
          placeholder="Cliente (opcional)"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>

        {/* Monto: va ARRIBA de las fichas, no debajo. Abajo quedaba
            fuera de la tarjeta y no se veía. */}
        <div style={{color:Q.muted,fontSize:12,marginBottom:4,
          fontFamily:F_BODY}}>MONTO A APOSTAR</div>
        <input type="number" inputMode="numeric" min="0" value={monto}
          onChange={e=>setMonto(Math.max(0,Number(e.target.value)||0))}
          aria-label="Monto a apostar"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:18,fontWeight:700,marginBottom:8,
            fontFamily:F_BODY}}/>

        <div style={{display:"flex",gap:SPACING[4]}}>
          {montosDe(agencia?.moneda).map(v=>(
            <button key={v} onClick={()=>setMonto(v)} style={{
              flex:1,background:monto===v?`${Q.violet}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${monto===v?Q.violet:Q.border}`,borderRadius:RADII.md,
              padding:"8px 4px",cursor:"pointer",color:monto===v?Q.cyan:Q.muted,
              fontSize:12,fontWeight:monto===v?700:400,
              fontFamily:F_BODY,
            }}>{v>=1000?`$${v/1000}K`:v}</button>
          ))}
        </div>
      </GCard>

      <AlertaError mensaje={err}/>
      {okMsg&&<div style={{color:Q.green,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>{okMsg}</div>}

      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Generando combos...</div>}

      {combos&&combos.length===0&&!cargando&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Moon size={26} color={Q.muted}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            No hay combos disponibles ahora
          </div>
        </GCard>
      )}

      {(combos||[]).map(combo=>{
        const ret=Math.round(monto*combo.odd_total);
        const abiertoEste=abierto===combo.id;
        return(
        <GCard key={combo.id} glow={combo.tagColor}
          style={{padding:SPACING[16],marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",marginBottom:8,gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:14,
                fontFamily:F_BODY}}>{combo.name}</div>
              <div style={{color:combo.tagColor,fontSize:12,marginTop:2}}>{combo.tag}</div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{color:Q.gold,fontWeight:900,fontSize:18,
                fontFamily:F_BODY}}>{fmt(combo.odd_total)}x</div>
              <div style={{color:Q.muted,fontSize:12}}>{combo.picks.length} picks</div>
            </div>
          </div>

          <button onClick={()=>setAbierto(abiertoEste?null:combo.id)} style={{
            width:"100%",background:"transparent",border:`1px solid ${Q.dim}`,
            borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.dim,fontSize:12,
            marginBottom:8,fontFamily:F_BODY,
          }}>{abiertoEste?"▲ Ocultar picks":"▼ Ver picks"}</button>

          {abiertoEste&&(
            <div style={{marginBottom:10}}>
              {combo.picks.map((p,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",
                  padding:"8px 0",gap:SPACING[8],
                  borderBottom:i<combo.picks.length-1?`1px solid ${Q.dim}`:"none"}}>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{color:Q.text,fontSize:12,fontWeight:600,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                      fontFamily:F_BODY}}>{p.h} vs {p.a}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{p.sel}</div>
                  </div>
                  <span style={{color:Q.cyan,fontWeight:700,fontSize:12,flexShrink:0,
                    fontFamily:F_BODY}}>{fmt(p.odd)}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",padding:"8px 0",marginBottom:8,
            borderTop:`1px solid ${Q.dim}`}}>
            <span style={{color:Q.muted,fontSize:12}}>Retorno {money(monto, mon)}</span>
            <span style={{color:Q.green,fontWeight:700,fontSize:15,
              fontFamily:F_BODY}}>{money(ret, mon)}</span>
          </div>

          <button onClick={()=>cobrarCombo(combo)} disabled={cobrando===combo.id}
            style={{
              width:"100%",
              background:cobrando===combo.id?"rgba(255,255,255,0.06)"
                :`linear-gradient(135deg,${combo.tagColor},${Q.violet})`,
              border:"none",borderRadius:RADII.md,padding:"12px",
              cursor:cobrando===combo.id?"wait":"pointer",
              color:cobrando===combo.id?Q.muted:"#fff",fontWeight:700,fontSize:13,
              fontFamily:F_BODY,textTransform:"uppercase",
            }}>
            {cobrando===combo.id?"Cobrando...":`Cobrar e imprimir ${money(monto, mon)}`}
          </button>
        </GCard>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CLIENTES — alta, búsqueda, carga de saldo, historial
// ═══════════════════════════════════════════════════════════════
function Clientes({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [q,setQ]=useState("");
  const [lista,setLista]=useState([]);
  const [cargando,setCargando]=useState(false);
  const [sel,setSel]=useState(null);      // usuario abierto
  const [alta,setAlta]=useState(false);   // form de alta
  const [msg,setMsg]=useState("");
  const [verBloqueos,setVerBloqueos]=useState(false);

  const buscar=async(texto)=>{
    setCargando(true); setMsg("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/usuarios?q=${encodeURIComponent(texto||"")}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      setLista((await r.json()).usuarios||[]);
    }catch(e){ setMsg(e.message==="Failed to fetch"?"Sin conexión":e.message); }
    setCargando(false);
  };

  useEffect(()=>{ buscar(""); // eslint-disable-next-line
  },[]);

  // Deliberate exception to the sheet/modal convention used everywhere
  // else in this file (and to admin's own FichaCliente, which genuinely
  // is a short sheet and stays one). This detail view is dense — the
  // cash desk, the stats row, the header actions and two logs — and a
  // modal squeezed all of it into one narrow column. A full view gives
  // it the width it needs, the way it worked before it was briefly
  // converted to a modal; a modal is right for a short detail, not a
  // workspace. `buscar(q)` on the way back re-fetches the list with
  // whatever search text was active.
  if(sel) return <FichaCliente agencia={agencia} user={sel}
    onVolver={()=>{setSel(null);buscar(q);}} onSesionExpirada={onSesionExpirada}/>;

  return(
    <div>
      <PageHeader icon={<Icon name="users"/>} title="Clientes"
        description="Buscá, dá de alta y administrá los clientes de tu agencia."/>

      {alta&&<AltaCliente agencia={agencia}
        onListo={(u)=>{setAlta(false);buscar("");if(u)setSel(u);}}
        onCancel={()=>setAlta(false)} onSesionExpirada={onSesionExpirada}/>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:SPACING[8],
          background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"8px 12px"}}>
          <Icon name="search" size={14} color={Q.muted}/>
          <input value={q}
            onChange={e=>{setQ(e.target.value);buscar(e.target.value);}}
            placeholder="Buscar por nombre o documento..."
            style={{background:"transparent",border:"none",color:Q.text,
              fontSize:14,flex:1,minWidth:0,
              fontFamily:F_BODY}}/>
        </div>
        <Btn label={<Lock size={14} role="img" aria-label="Bloqueos"/>} onClick={()=>setVerBloqueos(true)} color={Q.red} size="sm"/>
        <Btn label="+ Nuevo" onClick={()=>setAlta(true)} color={Q.violet} size="sm"/>
      </div>

      <AlertaError mensaje={msg}/>

      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Buscando...</div>}

      {!cargando&&lista.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Icon name="users" size={26}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            {q?"No se encontró ningún cliente":"Todavía no cargaste clientes"}
          </div>
        </GCard>
      )}

      {/* A side-to-side stacked list read as a spreadsheet on desktop;
          an auto-fit card grid gives each client room for name, contact
          and balance without a horizontal scan, and still stacks to one
          column on a phone with no breakpoint logic. */}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
        gap:SPACING[12]}}>
        {lista.map(u=>(
          <GCard key={u.id} onClick={()=>setSel(u)}
            style={{padding:SPACING[12],cursor:"pointer"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:2}}>
                  <span style={{color:Q.text,fontWeight:600,fontSize:14,
                    fontFamily:F_BODY,overflow:"hidden",
                    textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.nombre}</span>
                  {u.tiene_telegram&&<span style={{color:Q.cyan,fontSize:12}}><Send size={11}/></span>}
                </div>
                {/* El usuario con el que entra al sitio: el cajero lo
                    necesita a mano cuando el cliente lo olvida. */}
                {u.username&&!u.username.startsWith("loc_")&&(
                  <div style={{color:Q.cyan,fontSize:12,marginTop:1,
                    fontFamily:F_BODY}}>
                    @{u.username}</div>
                )}
                <div style={{color:Q.muted,fontSize:12}}>
                  {u.documento?`Doc ${u.documento}`:"Sin documento"}
                  {u.telefono?` · ${u.telefono}`:""}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:u.saldo>0?Q.green:Q.muted,fontWeight:700,fontSize:15,
                  fontFamily:F_BODY}}>{money(u.saldo, mon)}</div>
                <span style={{color:Q.muted,fontSize:16}}>›</span>
              </div>
            </div>
          </GCard>
        ))}
      </div>

      {verBloqueos&&<BloqueosRama agencia={agencia}
        onCerrar={()=>setVerBloqueos(false)} onSesionExpirada={onSesionExpirada}/>}
    </div>
  );
}

function ImpresionesRama({ agencia, onCerrar, onSesionExpirada }){
  const [data,setData]=useState(null);
  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/historial/impresiones`,{headers:authHeaders(agencia.token)})
      .then(r=>{ if(r.status===401){ onSesionExpirada(); return {impresiones:[]}; } return r.json(); })
      .then(d=>setData(d.impresiones||[])).catch(()=>setData([]));
  // eslint-disable-next-line
  },[]);
  const tipoTxt={ticket:"Ticket",apuesta:"Apuesta",cobro:"Cobro",cierre:"Cierre de caja",combo:"Combo"};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
      zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
        border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Printer size={16}/> Impresiones de mi rama</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {data&&data.length===0&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Todavía no hubo impresiones</div>}
        {(data||[]).map((im,i)=>(
          <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:12,
                  fontFamily:F_BODY}}>{tipoTxt[im.tipo]||im.tipo}
                  {im.referencia?<span style={{color:Q.cyan,fontWeight:400}}> · {im.referencia}</span>:null}</div>
                <div style={{color:Q.muted,fontSize:12}}>{im.agencia} · por {im.quien} · {fechaHora(im.fecha)}</div>
              </div>
              <Printer size={16}/>
            </div>
          </GCard>
        ))}
      </div>
    </div>
  );
}

function BloqueosRama({ agencia, onCerrar, onSesionExpirada }){
  const [data,setData]=useState(null);
  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/bloqueos`,{headers:authHeaders(agencia.token)})
      .then(r=>{ if(r.status===401){ onSesionExpirada(); return {bloqueos:[]}; } return r.json(); })
      .then(d=>setData(d.bloqueos||[])).catch(()=>setData([]));
  // eslint-disable-next-line
  },[]);
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",
      zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
      onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
        border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Lock size={16}/> Bloqueos de mi rama</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {data&&data.length===0&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Todavía no hubo bloqueos</div>}
        {(data||[]).map((b,i)=>{
          const esBloqueo=b.accion==="bloqueo";
          return(
            <GCard key={i} glow={esBloqueo?Q.red:null} style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{color:esBloqueo?Q.red:Q.green,fontWeight:700,fontSize:12,
                fontFamily:F_BODY}}>
                {esBloqueo?<><Lock size={12}/> Bloqueó</>:<><Icon name="circle-check" size={12}/> Desbloqueó</>}{" "}
                <span style={{color:Q.text}}>{b.nombre}</span>
                <span style={{color:Q.muted,fontWeight:400}}> ({b.tipo})</span></div>
              <div style={{color:Q.muted,fontSize:12,marginTop:2}}>por {b.quien} · {fechaHora(b.fecha)}</div>
              {b.motivo&&<div style={{color:Q.dim,fontSize:12,marginTop:4,fontStyle:"italic",
                fontFamily:F_BODY}}>"{b.motivo}"</div>}
            </GCard>
          );
        })}
      </div>
    </div>
  );
}

function AltaCliente({ agencia, onListo, onCancel, onSesionExpirada }){
  const [nombre,setNombre]=useState("");
  const [doc,setDoc]=useState("");
  const [tel,setTel]=useState("");
  // Usuario y clave: son las credenciales con las que el cliente entra
  // al sitio web. Las elige el cajero al darlo de alta.
  const [usuario,setUsuario]=useState("");
  const [clave,setClave]=useState("");
  const [msg,setMsg]=useState("");
  const [guardando,setGuardando]=useState(false);

  const crear=async()=>{
    if(!nombre.trim()){ setMsg("El nombre es obligatorio"); return; }
    if(!usuario.trim()){ setMsg("El usuario es obligatorio"); return; }
    if(!/^[a-z0-9._-]{3,40}$/.test(usuario.trim().toLowerCase())){
      setMsg("El usuario admite letras, números, punto, guion y guion bajo (3 a 40)");
      return;
    }
    if(clave.length<6){ setMsg("La clave debe tener al menos 6 caracteres"); return; }
    setGuardando(true); setMsg("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/usuarios`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({nombre,documento:doc,telefono:tel,
          username:usuario.trim().toLowerCase(), password:clave}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      onListo({id:d.id,nombre:d.nombre,documento:d.documento,saldo:0,
        telefono:tel,username:d.username,tiene_telegram:false,
        de_esta_agencia:true});
    }catch(e){ setMsg(e.message); setGuardando(false); }
  };

  return(
    <div>
      <button onClick={onCancel} style={{background:"transparent",border:"none",
        color:Q.muted,fontSize:22,cursor:"pointer",marginBottom:10,padding:0}}>‹ Volver</button>
      <GCard style={{padding:SPACING[20]}}>
        <div style={{color:Q.violet2,fontWeight:700,fontSize:15,marginBottom:14,
          fontFamily:F_BODY}}>Nuevo cliente</div>
        {[["Nombre y apellido",nombre,setNombre,"Juan Pérez","text"],
          ["Usuario para el sitio web",usuario,setUsuario,"juanperez","text"],
          ["Clave (mínimo 6)",clave,setClave,"••••••","text"],
          ["Documento (opcional)",doc,setDoc,"30111222","text"],
          ["Teléfono (opcional)",tel,setTel,"+54 11...","text"],
        ].map(([l,v,sv,ph,t])=>(
          <div key={l} style={{marginBottom:12}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,fontFamily:F_BODY,
              marginBottom:5}}>{l}</div>
            <input type={t} value={v} onChange={e=>sv(e.target.value)} placeholder={ph}
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 16px",
                color:Q.text,fontSize:16,fontFamily:F_BODY}}/>
          </div>
        ))}
        <AlertaError mensaje={msg}/>
        <div style={{display:"flex",gap:SPACING[8],marginTop:4}}>
          <Btn label="Cancelar" onClick={onCancel} outline color={Q.muted} full disabled={guardando}/>
          <Btn label={guardando?"CREANDO...":"CREAR"} onClick={crear} color={Q.violet} full disabled={guardando}/>
        </div>
        <div style={{color:Q.muted,fontSize:12,marginTop:10,lineHeight:1.5,
          fontFamily:F_BODY}}>
          Con ese usuario y clave el cliente entra al sitio web a consultar
          su cuenta y apostar. La carga y el retiro de saldo siguen siendo
          en la agencia. Anotale los datos antes de cerrar.
        </div>
      </GCard>
    </div>
  );
}

// Used to manage its own trigger button and reveal its form inline below
// it. The trigger now lives in FichaCliente's header row, so this opens
// as its own modal instead — same centered-dialog convention as
// ResetPassword (fixed backdrop, GCard, × close) — controlled from the
// caller via `open`/`onCerrar`.
function OtorgarBonoCliente({ agencia, userId, open, onCerrar }){
  const mon = agencia?.moneda || "ARS";
  const [bonos,setBonos]=useState([]);
  const [sel,setSel]=useState("");
  const [montoCarga,setMontoCarga]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  useEffect(()=>{
    if(!open) return;
    fetch(`${API_URL}/api/agencias/me/bonos`,{headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():{bonos:[]}).then(d=>setBonos(d.bonos||[])).catch(()=>{});
  },[open,agencia.token]);

  const bonoSel = bonos.find(b=>String(b.id)===String(sel));
  const otorgar=async()=>{
    if(!sel){ setMsg({text:"Elegí un bono", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const body={user_id:userId,bono_id:parseInt(sel,10)};
      if(bonoSel&&bonoSel.tipo==="carga") body.monto_carga=parseInt(montoCarga||"0",10);
      const r=await fetch(`${API_URL}/api/agencias/me/otorgar-bono`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(body),
      });
      const d=await r.json();
      if(r.ok&&d.ok){ setMsg({text:`Bono otorgado · ${money(d.monto, mon)}`, ok:true}); setSel(""); setMontoCarga(""); }
      else setMsg({text:d.detail||"No se pudo", ok:false});
    }catch(e){ setMsg({text:"Error", ok:false}); }
    setProc(false);
  };

  if(!open) return null;

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:220,
      display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}}
      onClick={onCerrar}>
      <GCard onClick={e=>e.stopPropagation()}
        style={{padding:SPACING[20],maxWidth:420,width:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:Q.gold,fontWeight:700,fontSize:15,
            fontFamily:F_BODY}}><Gift size={15}/> Otorgar bono</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {bonos.length===0?(
          <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
            No hay bonos habilitados para tu agencia.</div>
        ):(
          <>
            <select value={sel} onChange={e=>setSel(e.target.value)}
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px",color:Q.text,
                fontSize:13,marginBottom:8,fontFamily:F_BODY}}>
              <option value="">Elegí un bono</option>
              {/* EL MONTO DEL BONO LLEVA LA MONEDA DEL BONO, no la del libro
                  de esta agencia. Después del filtro de asignación coinciden
                  siempre; si alguna vez difieren es una asignación vieja
                  —escrita antes de que el filtro existiera— y entonces lo
                  honesto es que el desplegable diga la verdad en vez de
                  renombrar el número con la moneda de acá. El backend no va a
                  otorgar ese bono. */}
              {bonos.map(b=>(<option key={b.id} value={b.id}>
                {b.nombre} ({b.tipo==="bienvenida"?money(b.monto_fijo||0, b.moneda):`${b.porcentaje}%`})</option>))}
            </select>
            {bonoSel&&bonoSel.tipo==="carga"&&(
              <input value={montoCarga} onChange={e=>setMontoCarga(e.target.value.replace(/\D/g,""))}
                placeholder="Monto de la carga" inputMode="numeric"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px",color:Q.text,
                  fontSize:13,marginBottom:8,fontFamily:F_BODY}}/>
            )}
            <div style={{display:"flex",gap:SPACING[8]}}>
              <Btn label="Cerrar" onClick={onCerrar} outline color={Q.muted} full/>
              <Btn label={proc?"...":"Otorgar"} onClick={otorgar} color={Q.gold} full disabled={proc}/>
            </div>
          </>
        )}
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>
    </div>
  );
}

function FichaCliente({ agencia, user, onVolver, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [saldo,setSaldo]=useState(user.saldo);
  const [resetOpen,setResetOpen]=useState(false);
  const [bonoOpen,setBonoOpen]=useState(false);
  const [tgLink,setTgLink]=useState(null);
  const [tgProc,setTgProc]=useState(false);
  const [tgCopiado,setTgCopiado]=useState(false);

  const conectarTelegram=async()=>{
    setTgProc(true);
    try{
      const r=await fetch(`${API_URL}/api/cliente/${user.id}/vincular-telegram`,{
        method:"POST",headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok){ const d=await r.json(); setTgLink(d); }
    }catch(e){}
    setTgProc(false);
  };
  const [movs,setMovs]=useState(null);
  const isDesktop=useDesktopShellWidth();
  const [monto,setMonto]=useState(montoInicial(agencia?.moneda));
  const [modo,setModo]=useState("carga");   // carga | retiro
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [ficha,setFicha]=useState(null);     // rendimiento + apuestas + bloqueo
  const [motivo,setMotivo]=useState("");
  const [confirmBloq,setConfirmBloq]=useState(false);

  const cargarFicha=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/clientes/${user.id}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setFicha(await r.json());
    }catch(e){}
  };

  const toggleBloqueo=async()=>{
    const bloquear=!ficha?.bloqueado;
    if(bloquear && !confirmBloq){ setConfirmBloq(true); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/bloquear`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({tipo:"cliente",objetivo:user.id,bloquear,motivo}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      setConfirmBloq(false); setMotivo("");
      // Blocking and unblocking are the same operation with opposite sign,
      // and both report success. Under the old sniffing render the block
      // confirmation showed red only because its text carried no ✅ prefix,
      // which told the agency an action that worked had failed.
      setMsg({text: bloquear?"🔒 Cliente bloqueado":"Cliente desbloqueado", ok: true});
      cargarFicha();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const cargarMovs=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/usuarios/${user.id}/movimientos`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok){ const d=await r.json(); setMovs(d.movimientos||[]); setSaldo(d.saldo); }
    }catch(e){ setMovs([]); }
  };
  useEffect(()=>{ cargarMovs(); cargarFicha(); // eslint-disable-next-line
  },[]);

  const aplicar=async()=>{
    if(proc||!monto) return;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/cargar`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({user_id:user.id,
          monto: modo==="carga"? monto : -monto}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setSaldo(d.saldo);
      setMsg({text:`${modo==="carga"?"Cargado":"Retirado"} ${money(monto, mon)}`, ok:true});
      cargarMovs();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const tipoTxt={carga:"Carga",retiro:"Retiro",pago_premio:"Premio pagado",ajuste:"Ajuste"};
  const tipoColor={carga:Q.green,retiro:Q.amber,pago_premio:Q.violet2,ajuste:Q.muted};

  // Deliberate exception: this renders as a full view, not the
  // fixed-position sheet the rest of this file (and admin's own
  // FichaCliente) uses. A modal squeezed the cash desk, the stats row,
  // the header actions and two logs into one narrow column — too dense
  // a detail view for that width. The caller (`Clientes`) opens this
  // with an early return, same as before the sheet conversion.
  return(
    <div>
      {/* Account actions moved up next to Volver, small and right-aligned,
          the way the original reference had them — instead of their own
          card near the bottom. The row wraps on a narrow phone rather
          than squashing four buttons into one line. */}
      <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",
        justifyContent:"space-between",gap:SPACING[8],marginBottom:12}}>
        <button onClick={onVolver} style={{background:"transparent",border:"none",
          color:Q.muted,fontSize:22,cursor:"pointer",padding:0}}>‹ Volver</button>
        {ficha&&(
          <div style={{display:"flex",flexWrap:"wrap",gap:SPACING[8],justifyContent:"flex-end"}}>
            {ficha.bloqueado?(
              <Btn label={proc?"...":"Desbloquear cliente"} onClick={toggleBloqueo}
                color={Q.green} size="sm" disabled={proc}/>
            ):(
              <Btn label={<><Lock size={13}/> Bloquear cliente</>} onClick={toggleBloqueo}
                color={Q.red} size="sm" disabled={proc}/>
            )}
            <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
              color={Q.amber} size="sm"/>
            <Btn label={tgProc?"...":<><Smartphone size={13}/> Conectar Telegram</>} onClick={conectarTelegram}
              color={Q.cyan} size="sm" disabled={tgProc}/>
            <Btn label={<><Gift size={13}/> Otorgar bono</>} onClick={()=>setBonoOpen(true)}
              color={Q.gold} size="sm"/>
          </div>
        )}
      </div>

      <GCard style={{padding:SPACING[20],marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:17,
              fontFamily:F_BODY,overflow:"hidden",
              textOverflow:"ellipsis"}}>{user.nombre}</div>
            <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
              {user.documento?`Doc ${user.documento}`:"Sin documento"}
              {user.tiene_telegram?<> · Telegram <Send size={11}/></>:""}
            </div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,display:"flex",alignItems:"center",justifyContent:"flex-end",
              gap:SPACING[4]}}><Icon name="wallet-cards" size={12}/> Saldo</div>
            <div style={{color:saldo>0?Q.green:Q.text,fontWeight:900,fontSize:22,
              fontFamily:F_BODY}}>{money(saldo, mon)}</div>
          </div>
        </div>
        {ficha?.bloqueado&&(
          <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`,
            color:Q.red,fontSize:12,fontFamily:F_BODY}}>
            <Lock size={12}/> Bloqueado por {ficha.bloqueado_por||"—"}
            {ficha.bloqueado_motivo?` · ${ficha.bloqueado_motivo}`:""}
          </div>
        )}
      </GCard>

      {/* Rendimiento — an icon per figure so this reads as three
          distinct metrics at a glance, not a row of plain numbers.
          Auto-fit lets each stat keep a sane width instead of squeezing
          three into one row on a narrow phone. */}
      {ficha&&(
        <div style={{display:"grid",
          gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
          gap:SPACING[12],marginBottom:12}}>
          <GCard style={{padding:"8px",textAlign:"center"}}>
            <div style={{color:Q.cyan,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{money(ficha.rendimiento.apostado, mon)}</div>
            <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
              justifyContent:"center",gap:SPACING[4]}}><Icon name="ticket" size={12}/> Apostado</div>
          </GCard>
          <GCard style={{padding:"8px",textAlign:"center"}}>
            <div style={{color:Q.violet2,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{money(ficha.rendimiento.ganado, mon)}</div>
            <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
              justifyContent:"center",gap:SPACING[4]}}><Icon name="trophy" size={12}/> Ganado</div>
          </GCard>
          <GCard style={{padding:"8px",textAlign:"center"}}>
            <div style={{color:ficha.rendimiento.neto_cliente>=0?Q.green:Q.red,
              fontWeight:700,fontSize:13,fontFamily:F_BODY}}>
              {money(ficha.rendimiento.neto_cliente, mon)}</div>
            <div style={{color:Q.muted,fontSize:12,display:"flex",alignItems:"center",
              justifyContent:"center",gap:SPACING[4]}}><Scale size={12}/> Neto</div>
          </GCard>
        </div>
      )}
      {ficha&&(
        <div style={{display:"flex",gap:SPACING[8],marginBottom:12,fontSize:12,
          fontFamily:F_BODY}}>
          <span style={{color:Q.green}}>{ficha.rendimiento.ganadas} ganadas</span>
          <span style={{color:Q.red}}>{ficha.rendimiento.perdidas} perdidas</span>
          <span style={{color:Q.amber}}>{ficha.rendimiento.pendientes} pendientes</span>
        </div>
      )}

      {/* The cash desk. Left composes the figure, right decides and
          commits it — the order the work actually happens in at a
          counter. Below the shell breakpoint it stacks into one column,
          because the agency runs this screen from a phone. */}
      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{display:"grid",
          gridTemplateColumns:isDesktop?"minmax(0,1fr) 260px":"minmax(0,1fr)",
          gap:SPACING[16],alignItems:"start"}}>

          <div>
            {/* The amount is a readout, not a field with a frame around it.
                Monospace with tabular figures so the digits keep their
                place while the number is being typed — on a screen where
                cash moves, a digit that shifts is a digit misread. This
                input is also the free-entry field: there used to be a
                second one labelled "Otro" bound to the same `monto`, so
                the same number appeared twice and either copy could be
                edited. */}
            <div style={{background:Q.inset,borderRadius:RADII.md,
              padding:"12px 16px",marginBottom:SPACING[12]}}>
              <input type="number" inputMode="numeric" min="0" value={monto}
                onChange={e=>setMonto(Math.max(0,Number(e.target.value)||0))}
                aria-label={modo==="carga"?"Monto a cargar":"Monto a retirar"}
                style={{width:"100%",minWidth:0,background:"transparent",
                  border:"none",outline:"none",padding:0,
                  color:modo==="carga"?Q.green:Q.amber,
                  fontSize:TEXT[32],fontWeight:700,fontFamily:F_MONO,
                  fontVariantNumeric:"tabular-nums",letterSpacing:0.5}}/>
            </div>

            {/* Shortcut keys, sized for a thumb rather than drawn as
                outlined pills. They fit as many per row as the column
                allows instead of being squeezed into four. */}
            <div style={{display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(72px,1fr))",
              gap:SPACING[8]}}>
              {montosDe(agencia?.moneda).map(v=>(
                <button key={v} onClick={()=>setMonto(v)} style={{
                  background:monto===v
                    ? `linear-gradient(135deg,${Q.violet},${Q.violet}CC)`
                    : Q.raised,
                  border:"none",borderRadius:RADII.md,padding:"16px 8px",
                  cursor:"pointer",color:monto===v?"#fff":Q.text,
                  fontSize:TEXT[16],fontWeight:700,fontFamily:F_MONO,
                  fontVariantNumeric:"tabular-nums",
                }}>{v>=1000?`${v/1000}K`:v}</button>
              ))}
            </div>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:SPACING[8]}}>
            {/* A segmented control, because this picks a mode. Drawn as
                two equal buttons it read as two actions, which is what
                made the card confusing: three things looked clickable
                and only the last one moved money. */}
            <div style={{display:"flex",background:Q.inset,
              borderRadius:RADII.md,padding:SPACING[4],gap:SPACING[4]}}>
              {[["carga",<><Icon name="plus" size={13}/> Cargar</>,Q.green],
                ["retiro",<><Minus size={13}/> Retirar</>,Q.amber]].map(([k,l,c])=>(
                <button key={k} onClick={()=>setModo(k)} style={{
                  flex:1,background:modo===k?c:"transparent",border:"none",
                  borderRadius:RADII.sm,padding:"8px 8px",cursor:"pointer",
                  color:modo===k?inkOn(c):Q.muted,
                  fontSize:TEXT[13],fontWeight:700,fontFamily:F_BODY,
                }}>{l}</button>
              ))}
            </div>

            {msg&&<div style={{fontSize:TEXT[12],
              color:msg.ok?Q.green:Q.red,
              fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

            <Btn label={proc?"PROCESANDO...":
              `${modo==="carga"?"CARGAR":"RETIRAR"} ${money(monto, mon)}`}
              onClick={aplicar} color={modo==="carga"?Q.green:Q.amber} full size="lg"
              disabled={proc||!monto}/>
          </div>
        </div>
      </GCard>

      {/* Blocking needs a confirmation, so it gets its own modal — the
          same centered-dialog convention ResetPassword already uses in
          this file (fixed backdrop, GCard, × close). Unblocking has no
          confirmation step and applies straight from the header button. */}
      {confirmBloq&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:220,
          display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}}
          onClick={()=>{setConfirmBloq(false);setMotivo("");}}>
          <GCard glow={Q.red} onClick={e=>e.stopPropagation()}
            style={{padding:SPACING[20],maxWidth:400,width:"100%"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:15,
                fontFamily:F_BODY}}><Lock size={15}/> Bloquear cliente</div>
              <button onClick={()=>{setConfirmBloq(false);setMotivo("");}} style={{background:"transparent",
                border:"none",color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{color:Q.red,fontWeight:700,fontSize:13,marginBottom:6,
              fontFamily:F_BODY}}>¿Bloquear a {user.nombre}?</div>
            <div style={{color:Q.muted,fontSize:12,marginBottom:8,
              fontFamily:F_BODY}}>
              No va a poder apostar hasta que lo desbloquees.</div>
            <input value={motivo} onChange={e=>setMotivo(e.target.value)}
              placeholder="Motivo del bloqueo"
              style={{width:"100%",background:"rgba(255,255,255,0.05)",
                border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                color:Q.text,fontSize:14,marginBottom:10,
                fontFamily:F_BODY}}/>
            <div style={{display:"flex",gap:SPACING[8]}}>
              {/* Cancelar stays outline: it is the quiet half of this
                  confirm pair, next to a filled "Sí, bloquear". */}
              <Btn label="Cancelar" onClick={()=>{setConfirmBloq(false);setMotivo("");}}
                outline color={Q.muted} full/>
              <Btn label="Sí, bloquear" onClick={toggleBloqueo} color={Q.red}
                full disabled={proc}/>
            </div>
            {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
              color:msg.ok?Q.green:Q.red,
              fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
          </GCard>
        </div>
      )}

      {/* Telegram's result used to reveal as a panel under the action
          row; now that the action lives in the header, its result opens
          as its own modal instead — same convention as above. */}
      {tgLink&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:220,
          display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}}
          onClick={()=>setTgLink(null)}>
          <GCard onClick={e=>e.stopPropagation()}
            style={{padding:SPACING[20],maxWidth:420,width:"100%"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:15,
                fontFamily:F_BODY}}><Smartphone size={15}/> Conectar Telegram</div>
              <button onClick={()=>setTgLink(null)} style={{background:"transparent",border:"none",
                color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <div style={{color:Q.text,fontSize:12,marginBottom:8,lineHeight:1.5,
              fontFamily:F_BODY}}>
              Mandale este link al cliente. Cuando lo abra en Telegram, su cuenta
              de mostrador y su Telegram quedan unificadas (saldo sumado). Vence en 24hs.</div>
            <div style={{display:"flex",gap:SPACING[8]}}>
              <input readOnly value={tgLink.link} style={{flex:1,minWidth:0,
                background:"rgba(0,0,0,0.3)",border:`1px solid ${Q.border}`,borderRadius:RADII.md,
                padding:"8px 12px",color:Q.cyan,fontSize:12,
                fontFamily:F_BODY}}/>
              <button onClick={()=>{try{navigator.clipboard.writeText(tgLink.link);
                setTgCopiado(true);setTimeout(()=>setTgCopiado(false),1500);}catch(e){}}}
                style={{background:`${Q.cyan}22`,border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,
                padding:"8px 12px",cursor:"pointer",color:Q.cyan,fontSize:12,fontWeight:700,
                fontFamily:F_BODY}}>{tgCopiado?"✓":"Copiar"}</button>
            </div>
          </GCard>
        </div>
      )}

      <OtorgarBonoCliente agencia={agencia} userId={user.id}
        open={bonoOpen} onCerrar={()=>setBonoOpen(false)}/>

      {resetOpen&&<ResetPassword agencia={agencia} userId={user.id}
        nombre={user.nombre_completo||user.username||"el cliente"}
        onCerrar={()=>setResetOpen(false)}/>}

      {/* Apuestas and Movimientos are logs — read in time order, not
          navigated into — so they get the row standard: off the rounded
          GCard, on the page background, hairline separators. The whole
          sheet scrolls as one, so raising row padding here does not
          shrink a fixed-height viewport. */}
      {ficha&&ficha.apuestas.length>0&&(
        <div style={{marginBottom:SPACING[24]}}>
          <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
            textTransform:"uppercase",marginBottom:SPACING[12],
            fontFamily:F_BODY}}>Últimas apuestas</div>
          {ficha.apuestas.slice(0,10).map((b,i,arr)=>{
            const est={won:Q.green,ganada:Q.green,lost:Q.red,perdida:Q.red,
              pending:Q.amber,pendiente:Q.amber,active:Q.cyan}[(b.status||"").toLowerCase()]||Q.muted;
            return(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
                borderBottom:i<arr.length-1?`1px solid ${Q.border}55`:"none"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontSize:TEXT[15],fontWeight:600,
                    fontFamily:F_BODY}}>{b.code}</div>
                  <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                    fontFamily:F_BODY}}>{money(b.stake, mon)} · {fmt(b.odd)}x · {fechaHora(b.fecha)}</div>
                </div>
                <span style={{color:est,fontSize:TEXT[13],fontWeight:700,flexShrink:0,
                  fontFamily:F_BODY}}>{b.status}</span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
        textTransform:"uppercase",marginBottom:SPACING[12],
        fontFamily:F_BODY}}>
        Movimientos
      </div>
      {movs===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[12],
        fontFamily:F_BODY}}>Cargando...</div>}
      {movs&&movs.length===0&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12,
            fontFamily:F_BODY}}>Sin movimientos</div>
        </GCard>
      )}
      {(movs||[]).map((m,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
          borderBottom:i<movs.length-1?`1px solid ${Q.border}55`:"none"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:tipoColor[m.tipo]||Q.text,fontWeight:600,fontSize:TEXT[15],
              fontFamily:F_BODY}}>{tipoTxt[m.tipo]||m.tipo}</div>
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
              fontFamily:F_BODY}}>{fechaHora(m.fecha)}{m.betslip?` · ${m.betslip}`:""}</div>
          </div>
          <div style={{color:m.tipo==="retiro"?Q.amber:Q.green,fontWeight:700,fontSize:TEXT[16],
            flexShrink:0,fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>
            {m.tipo==="retiro"?"-":"+"}{money(m.monto, mon)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// HISTORIAL
// ═══════════════════════════════════════════════════════════════
// Liquida en bloque los boletos de ESTA agencia con los resultados de GR8,
// cruzando por la clave con la que GR8 liquida —la misma que la apuesta guardó
// al venderse—.
//
// Antes le pedía los resultados a The Odds API y resolvía el pick parseando el
// texto de la selección contra el marcador. Con el catálogo de GR8 eso no
// resolvía NI UN boleto: devolvía "0 ganadas" siempre y sin avisar.
//
// Las tres cuentas se muestran SEPARADAS porque son tres acciones distintas del
// cajero: las liquidadas ya están, las que esperan se resuelven solas cuando
// llegue el resultado, y las que no tienen clave de liquidación —las del
// formato viejo— no se van a resolver nunca solas y hay que liquidarlas a mano.
// Mostrar un solo número las confunde y el boleto trabado se queda sin pagar.
//
// Liquidar NO es pagar: el premio se cobra por la caja, con el botón de pago
// del ticket, que es el que mueve la cuenta corriente.
function AutoLiquidar({ agencia, onListo, onSesionExpirada }){
  const [proc,setProc]=useState(false);
  const [res,setRes]=useState(null);

  const correr=async()=>{
    if(proc) return;
    setProc(true); setRes(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/auto-liquidar`,{
        method:"POST",headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      const d=await r.json();
      setRes(d);
      onListo&&onListo();
    }catch(e){ setRes({error:"No se pudo liquidar automáticamente"}); }
    setProc(false);
  };

  const liquidadas=res
    ?(res.ganadas||0)+(res.perdidas||0)+(res.devueltas||0):0;

  return(
    <GCard style={{padding:"12px 16px",marginBottom:12,
      background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}08)`}}>
      <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
        justifyContent:"space-between"}}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,
            fontFamily:F_BODY}}><Zap size={13}/> Liquidar automático</div>
          <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
            Resuelve tus boletos con el resultado de GR8. El premio se cobra
            aparte, por caja.
          </div>
        </div>
        <button onClick={correr} disabled={proc} style={{
          background:proc?"rgba(255,255,255,0.06)"
            :`linear-gradient(135deg,${Q.violet},${Q.cyan})`,
          border:"none",borderRadius:RADII.md,padding:"8px 16px",
          cursor:proc?"wait":"pointer",color:proc?Q.muted:"#fff",
          fontWeight:700,fontSize:12,flexShrink:0,
          fontFamily:F_BODY}}>
          {proc?"Buscando...":"Ejecutar"}
        </button>
      </div>
      {res&&res.error&&<div style={{color:Q.red,fontSize:12,marginTop:8,
        fontFamily:F_BODY}}>{res.error}</div>}
      {res&&!res.error&&(
        <div style={{marginTop:8,fontSize:12,fontFamily:F_BODY}}>
          <div style={{color:Q.cyan}}>
            {liquidadas} liquidadas · {res.ganadas||0} ganadas ·{" "}
            {res.perdidas||0} perdidas · {res.devueltas||0} devueltas
          </div>
          <div style={{color:Q.muted,marginTop:2}}>
            {res.esperando||0} esperando el resultado de GR8
          </div>
          {(res.sin_clave||0)>0&&(
            <div style={{color:Q.amber,marginTop:2}}>
              {res.sin_clave} sin clave de liquidación: a esas hay que
              liquidarlas a mano
            </div>
          )}
        </div>
      )}
    </GCard>
  );
}

// Ticket del historial con liquidación (Ganó/Perdió) y pago de premio
//
// `/api/betslip/{code}` no es público: pide la clave de admin o una sesión de
// agencia, y además exige que el boleto sea de la rama. Esta llamada iba sin
// cabeceras y recibía 401 contra producción, así que el detalle no cargaba
// nunca; el `token` hay que bajarlo desde `TicketHistorial`, que ya lo usa
// para liquidar y para anular.
function DetalleTicket({ code, token, onCerrar }){
  const [d,setD]=useState(null);
  const [err,setErr]=useState("");

  useEffect(()=>{
    let vivo=true;
    fetch(`${API_URL}/api/betslip/${encodeURIComponent(code)}`,
      {headers:authHeaders(token)})
      .then(r=>r.ok?r.json():Promise.reject(new Error(`Error ${r.status}`)))
      .then(x=>vivo&&setD(x))
      .catch(e=>vivo&&setErr(e.message==="Failed to fetch"
        ? "Sin conexión" : e.message));
    return()=>{ vivo=false; };
  },[code,token]);

  const ESTADOS={pending:"Reservada, sin pagar", active:"En juego",
    won:"Ganada", lost:"Perdida", paid:"Pagada",
    cashed_out:"Cerrada con cash out", cancelled:"Anulada"};

  return(
    <div onClick={onCerrar} style={{position:"fixed",inset:0,zIndex:300,
      background:"rgba(2,2,8,.9)",display:"flex",alignItems:"center",
      justifyContent:"center",padding:SPACING[16]}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:420,
        maxHeight:"85dvh",overflowY:"auto",background:Q.deep,
        border:`1px solid ${Q.border}`,borderRadius:RADII.lg,padding:SPACING[16]}}>

        <div style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:800,fontSize:16,
            fontFamily:F_BODY}}>{code}</div>
          <button onClick={onCerrar} style={{background:"transparent",
            border:"none",color:Q.muted,fontSize:22,cursor:"pointer",
            padding:0}}>×</button>
        </div>

        {err&&<div style={{color:Q.red,fontSize:12.5}}>{err}</div>}
        {!d&&!err&&<div style={{color:Q.muted,fontSize:12.5}}>Cargando…</div>}

        {d&&<>
          {/* Si está reservada, el código va grande: es lo que el
              cliente muestra en el mostrador para pagarla. */}
          {(d.status||"").toLowerCase()==="pending"&&(
            <CodigoReserva code={code} compacto/>
          )}
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"baseline",marginBottom:10,marginTop:10,fontSize:12}}>
            <span style={{color:Q.muted}}>
              {ESTADOS[(d.status||"").toLowerCase()]||d.status}</span>
            <span style={{color:Q.gold,fontWeight:800,fontSize:18,
              fontFamily:F_BODY}}>
              {Number(d.odd_total||0).toFixed(2)}</span>
          </div>

          {(d.picks||[]).map((p,i)=>(
            <div key={i} style={{padding:"8px 0",
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{color:Q.muted,fontSize:12}}>
                {p.home}{p.away?` — ${p.away}`:""}</div>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"baseline",gap:SPACING[8],marginTop:2}}>
                <span style={{color:Q.text,fontSize:12.5,fontWeight:600}}>
                  {p.sel}</span>
                <span style={{color:Q.gold,fontSize:13,
                  fontFamily:F_BODY}}>
                  {Number(p.odd||0).toFixed(2)}</span>
              </div>
            </div>
          ))}

          <div style={{borderTop:`1px solid ${Q.border}`,marginTop:10,
            paddingTop:SPACING[12],display:"flex",justifyContent:"space-between",
            fontSize:12}}>
            <span style={{color:Q.muted}}>Apostado</span>
            <span style={{color:Q.text}}>{money(d.stake, d?.moneda)}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",
            fontSize:12,marginTop:4}}>
            <span style={{color:Q.muted}}>A cobrar</span>
            <span style={{color:Q.gold,fontWeight:700}}>
              {money(d.potential_win, d?.moneda)}</span>
          </div>
        </>}
      </div>
    </div>
  );
}

// ── Código de reserva ─────────────────────────────────────────
// Se muestra grande y con QR en todas las pantallas donde aparece un
// boleto pendiente: es lo que el cliente lleva al mostrador. Tenerlo
// escondido en un detalle obligaba a buscarlo con el cajero esperando.
function CodigoReserva({ code, compacto=false, vence=null }){
  const [copiado,setCopiado]=useState(false);
  if(!code) return null;

  const copiar=()=>{
    try{
      navigator.clipboard.writeText(code);
      setCopiado(true);
      setTimeout(()=>setCopiado(false),1500);
    }catch(e){}
  };

  return(
    <div style={{background:Q.inset||"rgba(255,255,255,0.04)",
      border:`1px solid ${Q.gold}`,borderRadius:RADII.lg,
      padding:compacto?"10px 12px":"14px",marginTop:8,
      display:"flex",alignItems:"center",gap:SPACING[12]}}>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,letterSpacing:1.4,color:Q.dim,
          fontWeight:700}}>CÓDIGO DE RESERVA</div>
        <div onClick={copiar} style={{fontFamily:F_BODY,
          fontSize:compacto?26:32,fontWeight:700,color:Q.gold,
          letterSpacing:1.5,lineHeight:1.1,cursor:"pointer"}}>
          {code}</div>
        <div style={{fontSize:12,color:copiado?Q.green:Q.muted,marginTop:2}}>
          {copiado?"✓ Copiado":"Tocá para copiar · Presentalo en la agencia"}</div>
        {vence&&<div style={{fontSize:12,color:Q.amber,marginTop:2}}>
          Vence {vence}</div>}
      </div>
      <img alt={`Código QR ${code}`} loading="lazy"
        src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=0&data=${encodeURIComponent(code)}`}
        style={{width:compacto?64:84,height:compacto?64:84,borderRadius:RADII.md,
          background:"#fff",padding:SPACING[4],flexShrink:0}}/>
    </div>
  );
}

function TicketHistorial({ t, agencia, anular, onCambio, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [verDetalle,setVerDetalle]=useState(false);
  const [proc,setProc]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [res,setRes]=useState(t.resultado||null);
  const [pagado,setPagado]=useState(!!t.pagado);
  const [anulado,setAnulado]=useState(false);
  const [confirmando,setConfirmando]=useState(false);
  const [motivo,setMotivo]=useState("");

  // Se muestra el botón solo mientras la ventana sigue abierta. El
  // servidor vuelve a validar igual: esto es comodidad, no seguridad.
  const minutosDesde=(()=>{
    const crudo=t.pagado_at||t.fecha_iso||t.creado_at;
    if(!crudo) return null;
    const d=new Date(crudo);
    if(isNaN(d)) return null;
    return (Date.now()-d.getTime())/60000;
  })();
  const dentroDeVentana = minutosDesde==null
    || minutosDesde <= (anular?.minutos_tras_pago ?? 5);
  const sePuedeAnular = anular?.puede_anular && !res && !pagado
    && !anulado && dentroDeVentana;

  const accion=async(ruta,body,okMsg)=>{
    if(proc) return null;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/${ruta}`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(body),
      });
      if(r.status===401){ onSesionExpirada(); return null; }
      if(!r.ok){ const e=await r.json().catch(()=>({}));
        throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      if(okMsg) setMsg({text:okMsg, ok:true});
      return d;
    }catch(e){ setMsg({text:e.message, ok:false}); return null; }
    finally{ setProc(false); }
  };

  const liquidar=async(resultado)=>{
    const d=await accion("liquidar",{code:t.code,resultado},
      resultado==="ganada"?"Marcada como ganada":"Marcada como perdida");
    if(d){ setRes(resultado); onCambio&&onCambio(); }
  };
  const pagar=async()=>{
    const d=await accion("pagar-premio",{code:t.code},"Premio pagado");
    if(d){ setPagado(true); onCambio&&onCambio(); }
  };

  const confirmarAnular=async()=>{
    if(proc) return;
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/betslip/${t.code}/anular`,{
        method:"POST",
        headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({motivo}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      const d=await r.json().catch(()=>({}));
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setAnulado(true); setConfirmando(false);
      setMsg({text: d.efectivo_a_devolver
        ? `Anulada. Devolvé ${money(d.efectivo_a_devolver, mon)} al cliente.`
        : "Anulada. El saldo volvió a la cuenta del cliente.", ok:true});
      onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    finally{ setProc(false); }
  };

  const colorEstado = pagado?Q.violet2 : res==="ganada"?Q.green
    : res==="perdida"?Q.red : t.tipo==="bot"?Q.violet:Q.amber;
  const textoEstado = pagado?"PAGADA" : res==="ganada"?"GANADA"
    : res==="perdida"?"PERDIDA" : (t.tipo||"").toUpperCase();

  return(
    <GCard glow={res==="ganada"&&!pagado?Q.green:undefined}
      style={{padding:"12px 16px",marginBottom:8}}>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"center",gap:SPACING[8]}}>
        {/* Tocar los datos del ticket abre el detalle con las
            selecciones. Los botones de acción quedan aparte para que
            no se dispare sin querer al liquidar o pagar. */}
        <div onClick={()=>setVerDetalle(true)}
          style={{minWidth:0,flex:1,cursor:"pointer"}}>
          <div style={{display:"flex",alignItems:"center",gap:SPACING[8],marginBottom:3}}>
            <span style={{color:Q.cyan,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{t.code}</span>
            <span style={{color:Q.dim,fontSize:12}}>ver ›</span>
            <span style={{background:`${colorEstado}22`,
              border:`1px solid ${colorEstado}`,borderRadius:RADII.xl,padding:"4px 8px",
              fontSize:12,fontWeight:700,color:colorEstado,
              fontFamily:F_BODY}}>{textoEstado}</span>
          </div>
          <div style={{color:Q.muted,fontSize:12,overflow:"hidden",
            textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
            {t.cliente} · {fechaHora(t.fecha)}{t.odd_total?` · ${fmt(t.odd_total)}x`:""}
          </div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:13,
            fontFamily:F_BODY}}>{money(t.stake, mon)}</div>
          <div style={{color:Q.green,fontSize:12}}>ret: {money(t.potential_win, mon)}</div>
        </div>
      </div>

      {!pagado&&(
        <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
          {!res&&(
            <div style={{display:"flex",gap:SPACING[8]}}>
              <button disabled={proc} onClick={()=>liquidar("ganada")} style={{
                flex:1,background:`${Q.green}18`,border:`1px solid ${Q.green}`,
                borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.green,
                fontSize:12,fontWeight:700,
                fontFamily:F_BODY}}>✓ Ganó</button>
              <button disabled={proc} onClick={()=>liquidar("perdida")} style={{
                flex:1,background:`${Q.red}18`,border:`1px solid ${Q.red}`,
                borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.red,
                fontSize:12,fontWeight:700,
                fontFamily:F_BODY}}>✗ Perdió</button>
            </div>
          )}
          {res==="ganada"&&(
            <button disabled={proc} onClick={pagar} style={{
              width:"100%",background:`linear-gradient(135deg,${Q.green},#00a854)`,
              border:"none",borderRadius:RADII.md,padding:"12px",cursor:"pointer",
              color:"#04120a",fontWeight:900,fontSize:14,
              fontFamily:F_BODY,textTransform:"uppercase",
            }}><Icon name="wallet-cards" size={14}/> Pagar premio {money(t.potential_win, mon)}</button>
          )}
          {sePuedeAnular&&!confirmando&&(
            <button disabled={proc} onClick={()=>setConfirmando(true)} style={{
              width:"100%",marginTop:6,background:"transparent",
              border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px",
              cursor:"pointer",color:Q.muted,fontSize:12,fontWeight:700,
              fontFamily:F_BODY}}>
              Anular apuesta</button>
          )}

          {confirmando&&(
            <div style={{marginTop:8,padding:"12px 12px",
              background:`${Q.red}12`,border:`1px solid ${Q.red}`,
              borderRadius:RADII.md}}>
              <div style={{color:Q.text,fontSize:12,marginBottom:8,
                lineHeight:1.5,fontFamily:F_BODY}}>
                Anular {t.code} por {money(t.stake, mon)}. Si se cobró en efectivo,
                hay que devolvérselo al cliente. No se puede deshacer.</div>
              <input value={motivo} onChange={e=>setMotivo(e.target.value)}
                placeholder="Motivo (ej: cargué mal el monto)"
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px 12px",
                  color:Q.text,fontSize:12,marginBottom:8,
                  fontFamily:F_BODY}}/>
              <div style={{display:"flex",gap:SPACING[8]}}>
                <button disabled={proc||!motivo.trim()}
                  onClick={confirmarAnular} style={{flex:1,
                  background:motivo.trim()?Q.red:"rgba(255,255,255,0.05)",
                  border:"none",borderRadius:RADII.md,padding:"8px",
                  cursor:motivo.trim()?"pointer":"default",
                  color:motivo.trim()?"#fff":Q.dim,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>
                  {proc?"Anulando…":"Sí, anular"}</button>
                <button disabled={proc} onClick={()=>setConfirmando(false)}
                  style={{flex:1,background:"transparent",
                  border:`1px solid ${Q.dim}`,borderRadius:RADII.md,padding:"8px",
                  cursor:"pointer",color:Q.muted,fontSize:12,
                  fontFamily:F_BODY}}>Volver</button>
              </div>
            </div>
          )}

          {anular?.puede_anular&&!sePuedeAnular&&!res&&!pagado&&!anulado&&(
            <div style={{marginTop:6,textAlign:"center",color:Q.dim,
              fontSize:12,fontFamily:F_BODY}}>
              Venció el plazo para anular
              ({anular.minutos_tras_pago ?? 5} min desde el cobro)</div>
          )}

          {res==="perdida"&&(
            <div style={{textAlign:"center",color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>Sin premio a pagar</div>
          )}
        </div>
      )}

      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={12}/> {msg.text}</div>}

      {verDetalle&&<DetalleTicket code={t.code} token={agencia.token}
        onCerrar={()=>setVerDetalle(false)}/>}
    </GCard>
  );
}

// Sonido de las alertas. Lo configura quien lo recibe, no quien lo
// manda: si el ruido molesta, la gente silencia el navegador entero
// y se pierde también lo que sí importa.
//
// Se genera con el propio navegador en vez de cargar un archivo: no
// suma peso ni depende de que un audio esté disponible.
const SONIDO_KEY = "qp_sonido_alertas";

function nivelSonido(){
  try{ return localStorage.getItem(SONIDO_KEY) || "suave"; }
  catch(e){ return "suave"; }
}

function guardarNivelSonido(v){
  try{ localStorage.setItem(SONIDO_KEY, v); }catch(e){}
}

function sonarAlerta(urgente){
  const nivel = nivelSonido();
  if(nivel === "off") return;
  // 'urgente' solo suena para lo crítico; el resto queda en silencio
  if(nivel === "urgente" && !urgente) return;
  try{
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if(!Ctx) return;
    const ctx = new Ctx();
    // Dos tonos para lo urgente, uno solo para lo normal
    const notas = urgente ? [880, 660] : [660];
    notas.forEach((hz, i) => {
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = hz;
      // Sube y baja suave: un tono seco resulta molesto
      const t0 = ctx.currentTime + i * 0.22;
      vol.gain.setValueAtTime(0, t0);
      vol.gain.linearRampToValueAtTime(0.14, t0 + 0.04);
      vol.gain.linearRampToValueAtTime(0, t0 + 0.2);
      osc.connect(vol); vol.connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.22);
    });
    setTimeout(()=>{ try{ ctx.close(); }catch(e){} }, 900);
  }catch(e){}
}

// Selector para poner donde haga falta
function SelectorSonido(){
  const [nivel,setNivel]=useState(nivelSonido());
  const cambiar=(v)=>{
    setNivel(v); guardarNivelSonido(v);
    if(v!=="off") sonarAlerta(v==="urgente");
  };
  return(
    <div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:5,
        fontFamily:F_BODY}}>Sonido de alertas</div>
      <div style={{display:"flex",gap:SPACING[8]}}>
        {[["off",<><VolumeX size={13}/> Sin sonido</>],["urgente",<><Bell size={13}/> Solo urgentes</>],
          ["suave",<><Volume2 size={13}/> Todas</>]].map(([k,l])=>(
          <button key={k} onClick={()=>cambiar(k)} style={{flex:1,
            background:nivel===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${nivel===k?Q.violet:Q.border}`,
            borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
            color:nivel===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:nivel===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>
      <div style={{color:Q.dim,fontSize:12,marginTop:5,lineHeight:1.4}}>
        Tocá una opción para escucharla.</div>
    </div>
  );
}

// Los avisos que bajan del admin. Rota solo entre los que hay, y
// los urgentes no se pueden descartar sin leerlos: son los que
// importan de verdad.
function CarruselAvisos({ agencia, onSesionExpirada }){
  const [avisos,setAvisos]=useState([]);
  const [i,setI]=useState(0);
  const [abierto,setAbierto]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/avisos`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada&&onSesionExpirada(); return; }
      if(r.ok){
        const nuevos=(await r.json()).avisos||[];
        // Suena solo si hay uno urgente sin ver que antes no estaba
        const urgenteNuevo=nuevos.some(a=>a.nivel==="urgente"&&!a.visto
          &&!avisos.some(v=>v.id===a.id));
        if(urgenteNuevo) sonarAlerta(true);
        setAvisos(nuevos);
      }
    }catch(e){}
  };

  useEffect(()=>{
    cargar();
    // Cada 40 segundos: un aviso urgente que tarda dos minutos en
    // aparecer llega tarde para lo que suele avisarse.
    const t=setInterval(cargar,40000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[]);

  // Rota cada 6 segundos si hay más de uno
  useEffect(()=>{
    if(avisos.length<2||abierto) return;
    const t=setInterval(()=>setI(x=>(x+1)%avisos.length),6000);
    return()=>clearInterval(t);
  },[avisos.length,abierto]);

  const marcarVisto=async(id)=>{
    try{
      await fetch(`${API_URL}/api/agencias/me/avisos/${id}/visto`,
        {method:"POST",headers:authHeaders(agencia.token)});
      setAvisos(a=>a.map(x=>x.id===id?{...x,visto:true}:x));
    }catch(e){}
  };

  if(!avisos.length) return null;
  const a=avisos[Math.min(i,avisos.length-1)];
  const COLOR={info:Q.cyan, aviso:Q.gold, urgente:Q.red};
  const c=COLOR[a.nivel]||Q.cyan;

  return(
    <div onClick={()=>{setAbierto(v=>!v); if(!a.visto) marcarVisto(a.id);}}
      style={{background:`${c}12`,border:`1px solid ${c}55`,
        borderRadius:RADII.md,padding:"12px 12px",marginBottom:12,
        cursor:"pointer"}}>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"flex-start",gap:SPACING[8]}}>
        <div style={{minWidth:0,flex:1}}>
          <div style={{color:c,fontWeight:700,fontSize:12.5,
            fontFamily:F_BODY}}>
            <Icon name="circle-dot" size={11} color={c}/> {a.titulo}
            {!a.visto&&(
              <span style={{background:c,color:"#000",borderRadius:RADII.sm,
                padding:"4px 4px",fontSize:12,marginLeft:6,
                fontWeight:800}}>NUEVO</span>
            )}
          </div>
          {abierto&&a.cuerpo&&(
            <div style={{color:Q.text,fontSize:12,marginTop:6,
              lineHeight:1.55,whiteSpace:"pre-wrap"}}>{a.cuerpo}</div>
          )}
          {!abierto&&a.cuerpo&&(
            <div style={{color:Q.muted,fontSize:12,marginTop:2,
              overflow:"hidden",textOverflow:"ellipsis",
              whiteSpace:"nowrap"}}>{a.cuerpo}</div>
          )}
          <div style={{color:Q.dim,fontSize:12,marginTop:3}}>{fechaHora(a.fecha)}</div>
        </div>
        {avisos.length>1&&(
          <div style={{display:"flex",gap:SPACING[4],flexShrink:0,paddingTop:SPACING[4]}}>
            {avisos.map((_,k)=>(
              <span key={k} style={{width:5,height:5,borderRadius:RADII.sm,
                background:k===i?c:Q.border}}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Soporte: los clientes que escribieron ─────────────────────
// La IA resuelve lo repetitivo. Acá llegan los que necesitan a una
// persona: reclamos de plata y todo lo que la IA no pudo cerrar.
function SoporteAgencia({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [tickets,setTickets]=useState(null);
  const [filtro,setFiltro]=useState("abierto");
  const [abierto,setAbierto]=useState(null);
  const [det,setDet]=useState(null);
  const [texto,setTexto]=useState("");
  const [proc,setProc]=useState(false);
  const [sonido,setSonido]=useState(()=>{
    try{ return localStorage.getItem("qp_sop_sonido")!=="0"; }
    catch(e){ return true; }
  });
  const derivRef=useRef(0);
  const finRef=useRef(null);

  const sonar=()=>{
    if(!sonido) return;
    try{
      const ctx=new (window.AudioContext||window.webkitAudioContext)();
      const osc=ctx.createOscillator(), g=ctx.createGain();
      osc.connect(g); g.connect(ctx.destination);
      osc.frequency.value=660; osc.type="sine";
      g.gain.setValueAtTime(0.0001,ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.15,ctx.currentTime+0.02);
      g.gain.exponentialRampToValueAtTime(0.0001,ctx.currentTime+0.35);
      osc.start(); osc.stop(ctx.currentTime+0.37);
    }catch(e){}
  };

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/soporte?estado=${filtro}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok){
        const d=await r.json();
        // Suena cuando aparece un caso nuevo que necesita a alguien
        if(derivRef.current && (d.derivados||0)>derivRef.current) sonar();
        derivRef.current=d.derivados||0;
        setTickets(d.tickets||[]);
      }
    }catch(e){ setTickets([]); }
  };
  useEffect(()=>{
    cargar();
    const t=setInterval(cargar,40000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[filtro]);

  const abrir=async(id)=>{
    setAbierto(id); setDet(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/soporte/${id}`,
        {headers:authHeaders(agencia.token)});
      if(r.ok){ setDet(await r.json()); cargar(); }
    }catch(e){}
  };
  useEffect(()=>{ finRef.current?.scrollIntoView({behavior:"smooth"}); },[det]);

  const responder=async(cerrar)=>{
    const t=texto.trim();
    if((!t&&!cerrar)||proc) return;
    setProc(true); setTexto("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/soporte/${abierto}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({texto:t,cerrar})});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok){
        if(cerrar){ setAbierto(null); setDet(null); }
        else abrir(abierto);
        cargar();
      }
    }catch(e){}
    setProc(false);
  };

  const AUTOR={cliente:"CLIENTE", ia:"ASISTENTE",
               agencia:"VOS", admin:"ADMINISTRACIÓN"};

  if(abierto&&det){
    return(
      <div style={{display:"flex",flexDirection:"column",
        height:"calc(100dvh - 230px)",minHeight:320}}>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12],
          marginBottom:10}}>
          <button onClick={()=>{setAbierto(null);setDet(null);}}
            style={{background:"transparent",border:"none",color:Q.cyan,
              fontSize:20,cursor:"pointer",padding:0}}>‹</button>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:14,
              fontFamily:F_BODY}}>{det.cliente}</div>
            <div style={{color:Q.muted,fontSize:12}}>
              Saldo {money(det.saldo, mon)} · {det.estado}</div>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",marginBottom:10}}>
          {(det.mensajes||[]).map((m,i)=>(
            <div key={i} style={{display:"flex",
              justifyContent:m.autor==="agencia"?"flex-end":"flex-start",
              marginBottom:8}}>
              <div style={{maxWidth:"85%",
                background:m.autor==="agencia"?`${Q.violet}28`
                  :(m.autor==="ia"?"rgba(255,255,255,0.03)"
                    :"rgba(255,255,255,0.06)"),
                border:`1px solid ${m.autor==="agencia"?Q.violet:Q.border}`,
                borderRadius:RADII.lg,padding:"8px 12px",color:Q.text,
                fontSize:12.5,lineHeight:1.55,whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                <div style={{color:m.autor==="ia"?Q.dim:Q.cyan,fontSize:12,
                  fontWeight:700,marginBottom:3}}>{AUTOR[m.autor]}</div>
                {m.texto}
                <div style={{color:Q.dim,fontSize:12,marginTop:4}}>
                  {fechaHora(m.fecha)}</div>
              </div>
            </div>
          ))}
          <div ref={finRef}/>
        </div>

        <div style={{display:"flex",gap:SPACING[8],marginBottom:7}}>
          <input value={texto} onChange={e=>setTexto(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&responder(false)}
            placeholder="Escribí tu respuesta…"
            style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.md,
              padding:"12px 12px",color:Q.text,fontSize:14,
              fontFamily:F_BODY}}/>
          <button onClick={()=>responder(false)}
            disabled={proc||!texto.trim()}
            style={{background:texto.trim()?Q.violet:"rgba(255,255,255,0.05)",
              border:"none",borderRadius:RADII.md,padding:"0 16px",
              cursor:texto.trim()?"pointer":"default",
              color:texto.trim()?"#fff":Q.dim,fontSize:14,fontWeight:700,
              flexShrink:0}}>↑</button>
        </div>
        <button onClick={()=>responder(true)} style={{width:"100%",
          background:"transparent",border:`1px solid ${Q.green}55`,
          borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.green,
          fontSize:12,fontFamily:F_BODY}}>
          Marcar como resuelto</button>
      </div>
    );
  }

  const derivados=(tickets||[]).filter(t=>t.derivado).length;

  return(
    <div>
      <PageHeader icon={<Headphones/>} title="Soporte"
        description="Consultas de tus clientes"
        action={<button onClick={()=>setSonido(v=>{
            const n=!v;
            try{ localStorage.setItem("qp_sop_sonido", n?"1":"0"); }catch(e){}
            return n;
          })} title="Sonido al llegar un caso"
          style={{background:"transparent",border:"none",
            cursor:"pointer",opacity:sonido?1:0.4}}>
          <Bell size={17}/></button>}/>

      {derivados>0&&(
        <div style={{background:`${Q.gold}12`,border:`1px solid ${Q.gold}55`,
          borderRadius:RADII.md,padding:"8px 12px",marginBottom:10,
          color:Q.gold,fontSize:12,
          fontFamily:F_BODY}}>
          <Icon name="triangle-alert" size={13}/> {derivados} {derivados===1?"consulta necesita":"consultas necesitan"}
          {" "}que las veas vos</div>
      )}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        {[["abierto","Abiertos"],["atendido","Atendidos"],
          ["todos","Todos"]].map(([k,l])=>(
          <button key={k} onClick={()=>setFiltro(k)} style={{flex:1,
            background:filtro===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${filtro===k?Q.violet:Q.border}`,
            borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",
            color:filtro===k?Q.cyan:Q.muted,fontSize:12,
            fontWeight:filtro===k?700:400,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {!tickets&&<div style={{color:Q.muted,textAlign:"center",
        padding:SPACING[20]}}>Cargando…</div>}
      {tickets&&tickets.length===0&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12.5,
            fontFamily:F_BODY}}>
            No hay consultas en este filtro</div>
        </GCard>
      )}

      {(tickets||[]).map(t=>(
        <GCard key={t.id} glow={t.derivado?Q.gold:null}
          onClick={()=>abrir(t.id)}
          style={{padding:"12px 12px",marginBottom:7,cursor:"pointer"}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                fontFamily:F_BODY}}>
                {t.derivado&&<span style={{color:Q.gold}}><Icon name="triangle-alert" size={12}/> </span>}
                {t.cliente}
                {t.sin_leer>0&&(
                  <span style={{background:Q.red,color:"#fff",borderRadius:RADII.md,
                    padding:"4px 8px",fontSize:12,marginLeft:6}}>
                    {t.sin_leer}</span>
                )}
              </div>
              <div style={{color:Q.muted,fontSize:12,marginTop:2,
                overflow:"hidden",textOverflow:"ellipsis",
                whiteSpace:"nowrap"}}>{t.ultimo}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                {fechaHora(t.fecha)} · saldo {money(t.saldo, mon)}
                {t.motivo?` · ${t.motivo}`:""}</div>
            </div>
          </div>
        </GCard>
      ))}
    </div>
  );
}

// ── Mensajes con el admin ─────────────────────────────────────
// Los canales por donde los clientes pueden escribirle a la agencia.
// Aparecen como botones en el chat de soporte del jugador: si están
// vacíos, el cliente no tiene forma de llegar a una persona.
// Qué productos ofrece cada agencia de su red. Puede quitárselos,
// pero no darles algo que ella misma no tenga: si la casa le sacó
// casino a la rama, no puede devolvérselo a nadie.
// Los proveedores de casino de tu red. Podés quitárselos a tus
// subagencias, pero no darles algo que vos no tengas: si la casa lo
// apagó para tu rama, nadie del medio puede devolverlo.
function ProveedoresAgencia({ agencia, onSesionExpirada }){
  const [d,setD]=useState(null);
  const [abierta,setAbierta]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [filtro,setFiltro]=useState("todos");

  const cargar=()=>{
    fetch(`${API_URL}/api/agencias/me/proveedores`,
      {headers:authHeaders(agencia.token)})
      .then(r=>{ if(r.status===401){onSesionExpirada();return null;}
        return r.ok?r.json():null; })
      .then(x=>x&&setD(x)).catch(()=>{});
  };
  useEffect(cargar,[]);

  const cambiar=async(code,marca,activo)=>{
    let motivo=null;
    if(!activo){
      motivo=window.prompt(`¿Por qué le quitás ${marca}?`);
      if(motivo===null) return;
    }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/proveedores`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({agencia_code:code, marca, activo, motivo})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||`Error ${r.status}`);
      setMsg({text:activo?"Habilitado":"Quitado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!d) return null;
  if(!(d.hijas||[]).length) return null;

  const lista=(d.proveedores||[]).filter(p=>{
    if(filtro==="vivo") return p.es_vivo;
    if(filtro==="slots") return !p.es_vivo;
    return true;
  });

  return(
    <div style={{marginTop:16,paddingTop:SPACING[16],
      borderTop:`1px solid ${Q.border}`}}>
      <div style={{color:Q.text,fontWeight:700,fontSize:13,marginBottom:3,
        fontFamily:F_BODY}}>
        Proveedores de casino</div>
      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        lineHeight:1.5,fontFamily:F_BODY}}>
        Podés quitárselos a tus subagencias. Los que vos no tengas
        aparecen como no disponibles.</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:9,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {(d.mis_apagados||[]).length>0&&(
        <div style={{color:Q.amber,fontSize:12,marginBottom:10,
          lineHeight:1.5,fontFamily:F_BODY}}>
          Tu agencia no tiene: {d.mis_apagados.join(", ")}</div>
      )}

      {d.hijas.map(h=>(
        <div key={h.code} style={{marginBottom:7}}>
          <div onClick={()=>setAbierta(abierta===h.code?null:h.code)}
            style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",cursor:"pointer",padding:"8px 0"}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontSize:12.5,fontWeight:600,
                fontFamily:F_BODY}}>{h.nombre}</div>
              <div style={{color:Q.dim,fontSize:12}}>
                {h.apagados.length===0
                  ? "todos los proveedores"
                  : `sin ${h.apagados.length} proveedor${h.apagados.length>1?"es":""}`}
                {h.apagados_por_mi.length>0&&(
                  <span style={{color:Q.amber}}>
                    {" "}· {h.apagados_por_mi.length} quitado
                    {h.apagados_por_mi.length>1?"s":""} por vos</span>
                )}</div>
            </div>
            <span style={{color:Q.dim,fontSize:12}}>
              {abierta===h.code?"▲":"▼"}</span>
          </div>

          {abierta===h.code&&(
            <div style={{paddingLeft:SPACING[8],paddingBottom:SPACING[8]}}>
              <div style={{display:"flex",gap:SPACING[4],marginBottom:8}}>
                {[["todos","Todos"],["slots",<Icon name="spade" size={13}/>],["vivo",<Video size={13}/>]]
                  .map(([k,l])=>(
                  <button key={k} onClick={()=>setFiltro(k)}
                    style={{background:filtro===k?`${Q.violet}28`:"transparent",
                      border:`1px solid ${filtro===k?Q.violet:Q.border}`,
                      borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                      color:filtro===k?Q.cyan:Q.muted,fontSize:12,
                      fontFamily:F_BODY}}>{l}</button>
                ))}
              </div>

              {lista.map(p=>{
                const tiene=!h.apagados.includes(p.marca);
                const puedoTocar=p.lo_tengo&&(
                  !tiene ? h.apagados_por_mi.includes(p.marca) : true);
                return(
                  <button key={p.marca}
                    onClick={()=>puedoTocar&&cambiar(h.code,p.marca,!tiene)}
                    disabled={proc||!puedoTocar}
                    style={{width:"100%",display:"flex",
                      justifyContent:"space-between",alignItems:"center",
                      background:!p.lo_tengo?"rgba(255,255,255,0.02)"
                        :(tiene?`${Q.green}0D`:`${Q.red}0D`),
                      border:`1px solid ${!p.lo_tengo?Q.border
                        :(tiene?`${Q.green}33`:`${Q.red}33`)}`,
                      borderRadius:RADII.md,padding:"8px 12px",marginBottom:5,
                      cursor:puedoTocar?"pointer":"not-allowed",
                      fontFamily:F_BODY}}>
                    <span style={{color:p.lo_tengo?Q.text:Q.dim,
                      fontSize:12,minWidth:0,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {p.es_vivo&&<><Video size={11}/> </>}{p.marca}
                      <span style={{color:Q.dim,fontSize:12,marginLeft:5}}>
                        {p.juegos}</span></span>
                    <span style={{fontSize:12,fontWeight:700,flexShrink:0,
                      color:!p.lo_tengo?Q.dim:(tiene?Q.green:Q.red)}}>
                      {!p.lo_tengo?"no disponible"
                        :(tiene?"lo tiene":"sin acceso")}</span>
                  </button>
                );
              })}

              {lista.some(p=>!p.lo_tengo)&&(
                <div style={{color:Q.dim,fontSize:12,marginTop:4,
                  lineHeight:1.45}}>
                  Los que dicen "no disponible" no los tenés vos.
                  Pedíselos a la administración.</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ProductosRed({ agencia, onSesionExpirada }){
  const [datos,setDatos]=useState(null);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [abierta,setAbierta]=useState(null);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/productos`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setDatos(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const cambiar=async(code,producto,activo)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/productos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({agencia_code:code,producto,activo})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:activo?"Habilitado":"Quitado", ok:true}); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  if(!datos) return null;
  if(!datos.hijas?.length) return null;

  return(
    <div style={{marginBottom:12,paddingBottom:SPACING[12],
      borderBottom:`1px solid ${Q.border}`}}>
      <div style={{color:Q.muted,fontSize:12,marginBottom:6,
        fontFamily:F_BODY}}>
        Productos de tu red</div>

      <div style={{color:Q.dim,fontSize:12,marginBottom:9,
        lineHeight:1.5}}>
        Vos tenés: {(datos.mis_productos||[]).map(p=>{
          const c=datos.catalogo.find(x=>x.codigo===p);
          return c?`${c.icono} ${c.nombre}`:p;
        }).join(" · ")||"ninguno"}</div>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:8,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {datos.hijas.map(h=>(
        <div key={h.code} style={{marginBottom:7}}>
          <div onClick={()=>setAbierta(abierta===h.code?null:h.code)}
            style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",cursor:"pointer",padding:"8px 0"}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontSize:12.5,fontWeight:600,
                fontFamily:F_BODY}}>{h.nombre}</div>
              <div style={{color:Q.dim,fontSize:12}}>
                {h.productos.length} de {datos.catalogo.length} productos
                {h.apagados_aca?.length>0&&(
                  <span style={{color:Q.amber}}>
                    {" "}· {h.apagados_aca.length} quitado
                    {h.apagados_aca.length>1?"s":""} por vos</span>
                )}</div>
            </div>
            <span style={{color:Q.dim,fontSize:12}}>
              {abierta===h.code?"▲":"▼"}</span>
          </div>

          {abierta===h.code&&(
            <div style={{paddingLeft:SPACING[12],paddingBottom:SPACING[8]}}>
              {datos.catalogo.map(c=>{
                const loTengo=(datos.mis_productos||[]).includes(c.codigo);
                const loTiene=h.productos.includes(c.codigo);
                // Si yo no lo tengo, no puedo dárselo: el botón queda
                // inerte y se explica por qué.
                return(
                  <button key={c.codigo}
                    onClick={()=>loTengo&&cambiar(h.code,c.codigo,!loTiene)}
                    disabled={proc||!loTengo}
                    style={{width:"100%",display:"flex",
                      justifyContent:"space-between",alignItems:"center",
                      background:!loTengo?"rgba(255,255,255,0.02)"
                        :(loTiene?`${Q.green}10`:`${Q.red}10`),
                      border:`1px solid ${!loTengo?Q.border
                        :(loTiene?`${Q.green}44`:`${Q.red}44`)}`,
                      borderRadius:RADII.md,padding:"8px 12px",marginBottom:5,
                      cursor:loTengo?"pointer":"not-allowed",
                      fontFamily:F_BODY}}>
                    <span style={{color:loTengo?Q.text:Q.dim,fontSize:12}}>
                      {c.icono} {c.nombre}</span>
                    <span style={{fontSize:12,fontWeight:700,
                      color:!loTengo?Q.dim:(loTiene?Q.green:Q.red)}}>
                      {!loTengo?"no disponible"
                        :(loTiene?"lo tiene":"sin acceso")}</span>
                  </button>
                );
              })}
              {(datos.catalogo||[]).some(c=>
                !(datos.mis_productos||[]).includes(c.codigo))&&(
                <div style={{color:Q.dim,fontSize:12,marginTop:4,
                  lineHeight:1.45}}>
                  Los que dicen "no disponible" no los tenés vos, así que
                  no se los podés dar. Pedíselos a la administración.</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Las pantallas del local. Cada una tiene su QR: el cliente lo
// escanea, arma el boleto en su teléfono mientras espera, y llega al
// mostrador con el código listo. El cajero solo cobra.
function Terminales({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [lista,setLista]=useState(null);
  const [nombre,setNombre]=useState("");
  const [ubic,setUbic]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [verQR,setVerQR]=useState(null);
  // El código de alta recién emitido, por terminal: {id, codigo, horas}.
  // Vive en memoria del componente a propósito. No se guarda y no se puede
  // volver a mirar: si se pierde, se emite otro. Un código de alta que se
  // puede releer en el panel es un código que queda a la vista del próximo que
  // abra la pantalla, y no hace falta — emitir otro cuesta un clic y el viejo
  // sigue venciendo solo.
  const [alta,setAlta]=useState(null);
  const [altaProc,setAltaProc]=useState(false);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/terminales`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setLista((await r.json()).terminales||[]);
    }catch(e){ setLista([]); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const crear=async()=>{
    if(!nombre.trim()){ setMsg({text:"Poné un nombre", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/terminales`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({nombre:nombre.trim(),ubicacion:ubic.trim()})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Terminal creada", ok:true}); setNombre(""); setUbic(""); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const cambiar=async(t,campo,valor)=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/terminales/${t.id}`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({[campo]:valor})});
      if(r.ok){
        // Apagar una terminal le corta el acceso EN EL ACTO: el servidor mira
        // `activa` en el mismo UPDATE con el que autoriza, así que no espera a
        // que su token venza. Si estaba apagándose, el código de alta que
        // estuviera a la vista deja de servir, y mostrarlo igual sería mentira.
        if(campo==="activa"&&!valor&&alta&&alta.id===t.id) setAlta(null);
        cargar();
      }
    }catch(e){}
  };

  // El alta de la terminal: un código de un solo uso que vence en 24 h.
  //
  // Es lo que le da identidad a la pantalla del mostrador. Hasta que existió,
  // el Box no era nadie —solo sabía el código de su dirección— y el cash out
  // tenía que estar abierto para que funcionara. Ese era el agujero.
  //
  // NO ES EL CÓDIGO DEL QR. El del QR va impreso en la pared y lo lee
  // cualquiera que escanee: si sirviera para darse de alta, cualquiera que
  // mirara la pared se haría pasar por la terminal.
  const generarAlta=async(t)=>{
    if(altaProc) return;
    setAltaProc(true); setMsg(null); setAlta(null);
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/terminales/${t.id}/credencial`,{
        method:"POST",headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setAlta({id:t.id, codigo:d.codigo, horas:d.expira_en_horas});
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setAltaProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  return(
    <div>
      <PageHeader icon={<Monitor/>} title="Terminales"
        description={<>Cada pantalla del local con su QR. El cliente lo escanea, arma
        el boleto en su teléfono mientras espera, y llega al mostrador
        con el código listo.</>}/>

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <GCard style={{padding:SPACING[16],marginBottom:12}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
          marginBottom:8,fontFamily:F_BODY}}>
          Nueva terminal</div>
        <input value={nombre} onChange={e=>setNombre(e.target.value)}
          placeholder="Nombre: Terminal 1, Barra, Entrada…" style={inp}/>
        <input value={ubic} onChange={e=>setUbic(e.target.value)}
          placeholder="Dónde está (opcional)" style={inp}/>
        <Btn label={proc?"Creando…":"+ Crear terminal"} onClick={crear}
          color={Q.violet} full disabled={proc}/>
      </GCard>

      {!lista&&<div style={{color:Q.muted,textAlign:"center",
        padding:SPACING[20]}}>Cargando…</div>}

      {lista&&lista.length===0&&(
        <GCard style={{padding:SPACING[24],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:13,lineHeight:1.55,
            fontFamily:F_BODY}}>
            Todavía no tenés terminales.<br/>
            Creá una por cada pantalla del local.</div>
        </GCard>
      )}

      {(lista||[]).map(t=>(
        <GCard key={t.id} glow={t.activa?null:Q.red}
          style={{padding:SPACING[12],marginBottom:8,opacity:t.activa?1:0.6}}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:SPACING[8]}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:13,
                fontFamily:F_BODY}}>
                {t.nombre}
                {!t.activa&&<span style={{color:Q.red,fontSize:12,
                  marginLeft:6}}>apagada</span>}</div>
              {t.ubicacion&&(
                <div style={{color:Q.dim,fontSize:12}}>{t.ubicacion}</div>
              )}
              <div style={{color:Q.muted,fontSize:12,marginTop:3}}>
                {t.escaneos} escaneos · {t.boletos} boletos ·{" "}
                {money(t.vendido, mon)}</div>
              {/* Cuándo operó por última vez. Es lo que deja ver una terminal
                  robada —opera con el local cerrado— o una que dejó de usarse
                  con la credencial todavía viva. */}
              <div style={{color:Q.dim,fontSize:12,marginTop:2}}>
                {t.ultimo_uso?`Último uso: ${fechaHora(t.ultimo_uso)}`:"Sin uso todavía"}</div>
            </div>
            <button onClick={()=>setVerQR(verQR===t.codigo?null:t.codigo)}
              style={{background:`${Q.cyan}18`,
                border:`1px solid ${Q.cyan}66`,borderRadius:RADII.md,
                padding:"8px 12px",cursor:"pointer",color:Q.cyan,
                fontSize:12,flexShrink:0,fontWeight:700,
                fontFamily:F_BODY}}>
              {verQR===t.codigo?"Ocultar":"Ver QR"}</button>
          </div>

          {verQR===t.codigo&&(
            <div style={{marginTop:11,paddingTop:SPACING[12],
              borderTop:`1px solid ${Q.border}`,textAlign:"center"}}>
              {/* El QR se genera al vuelo: no hace falta guardar una
                  imagen ni depender de que exista un archivo. */}
              <img alt={`QR de ${t.nombre}`}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=${encodeURIComponent(t.url)}`}
                style={{width:"100%",maxWidth:220,borderRadius:RADII.md,
                  background:"#fff",padding:SPACING[8]}}/>
              <div style={{color:Q.gold,fontSize:14,fontWeight:800,
                marginTop:8,letterSpacing:1,
                fontFamily:F_BODY}}>{t.codigo}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:4,
                wordBreak:"break-all"}}>{t.url}</div>
              <div style={{color:Q.muted,fontSize:12,marginTop:8,
                lineHeight:1.5}}>
                Imprimilo y pegalo cerca de la pantalla, o mostralo en
                la pantalla misma.</div>

              <div style={{display:"flex",gap:SPACING[8],marginTop:11}}>
                <button onClick={()=>cambiar(t,"activa",!t.activa)}
                  style={{flex:1,background:"transparent",
                    border:`1px solid ${t.activa?Q.red:Q.green}66`,
                    borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:t.activa?Q.red:Q.green,fontSize:12,
                    fontFamily:F_BODY}}>
                  {t.activa?"Apagar":"Encender"}</button>
              </div>

              {/* ── EL ALTA DE LA PANTALLA ──
                  Separado del QR por una línea, porque son dos cosas opuestas y
                  confundirlas es el riesgo: el de arriba se imprime y se pega en
                  la pared, este se tipea una vez en la pantalla y no se vuelve a
                  mostrar. Apagada no se da de alta: el botón no está. */}
              <div style={{marginTop:14,paddingTop:SPACING[12],
                borderTop:`1px solid ${Q.border}`,textAlign:"left"}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
                  fontFamily:F_BODY}}>Credencial de la pantalla</div>
                <div style={{color:Q.muted,fontSize:12,marginTop:4,
                  lineHeight:1.5}}>
                  Para que esta terminal pueda cerrar un cash out en el
                  mostrador. Generá el código, escribilo una vez en la pantalla
                  y listo. No es el código del QR de arriba: este no se pega en
                  ninguna parte.
                </div>

                {!t.activa&&(
                  <div style={{color:Q.red,fontSize:12,marginTop:8,
                    lineHeight:1.45}}>
                    Está apagada. Encendela para darla de alta — apagada no
                    tiene acceso, aunque ya se haya dado de alta antes.</div>
                )}

                {t.activa&&(
                  <button onClick={()=>generarAlta(t)} disabled={altaProc}
                    style={{width:"100%",marginTop:10,
                      background:`${Q.violet}22`,
                      border:`1px solid ${Q.violet}`,borderRadius:RADII.md,
                      padding:SPACING[8],cursor:altaProc?"default":"pointer",
                      color:Q.text,fontSize:12,fontWeight:700,
                      opacity:altaProc?0.6:1,fontFamily:F_BODY}}>
                    {altaProc?"Generando…":"Generar código de alta"}</button>
                )}

                {alta&&alta.id===t.id&&(
                  <div style={{marginTop:11,background:`${Q.violet}14`,
                    border:`1px solid ${Q.violet}`,borderRadius:RADII.md,
                    padding:SPACING[12],textAlign:"center"}}>
                    <div style={{color:Q.muted,fontSize:12}}>
                      Escribilo en la pantalla</div>
                    <div style={{color:Q.text,fontWeight:800,fontSize:14,
                      marginTop:6,wordBreak:"break-all",letterSpacing:0.5,
                      fontFamily:F_BODY}}>{alta.codigo}</div>
                    <div style={{color:Q.muted,fontSize:12,marginTop:8,
                      lineHeight:1.5}}>
                      Vence en {alta.horas} horas y sirve una sola vez. No se
                      vuelve a mostrar: si lo perdés, generá otro.</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </GCard>
      ))}
    </div>
  );
}

// Los desafíos de la red: cuánto se juega, qué comisión deja, y qué
// subagencias lo tienen habilitado.
//
// Una agencia puede QUITARLE el producto a sus subagencias, pero no
// dárselo si ella misma no lo tiene: si la casa se lo sacó a la
// rama, nadie del medio puede devolverlo.
// Le explica al agente cómo viene su negocio con sus propios
// números. No inventa datos ni promete resultados: un agente que
// decide sobre una proyección optimista termina peor que sin asesor.
function AsesorAgencia({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [d,setD]=useState(null);
  const [cargando,setCargando]=useState(false);
  const [pregunta,setPregunta]=useState("");
  const [respuesta,setRespuesta]=useState("");
  const [proc,setProc]=useState(false);

  const analizar=async()=>{
    setCargando(true); setRespuesta("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/asesor`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setD(await r.json());
    }catch(e){}
    setCargando(false);
  };
  useEffect(()=>{ analizar(); /* eslint-disable-next-line */ },[]);

  const preguntar=async()=>{
    if(pregunta.trim().length<5) return;
    setProc(true); setRespuesta("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/asesor/preguntar`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({pregunta:pregunta.trim()})});
      const x=await r.json();
      if(!r.ok) throw new Error(x.detail||"Error");
      setRespuesta(x.respuesta||"");
    }catch(e){ setRespuesta("⚠️ "+e.message); }
    setProc(false);
  };

  const SUGERIDAS=[
    "¿Qué hago con los clientes que dejaron de jugar?",
    "¿Cómo vengo comparado con el mes pasado?",
    "¿Me conviene abrir otra subagencia?",
  ];

  return(
    <div>
      <PageHeader icon={<Bot/>} title="Tu asesor"
        description="Lee tus números y te dice qué mirar. Solo ve lo de tu red."/>

      <Btn label={cargando?"Analizando…":<><RefreshCw size={13}/> Analizar mi negocio</>}
        onClick={analizar} color={Q.violet} full disabled={cargando}/>

      {d&&(
        <>
          <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
            <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
              marginBottom:9}}>ÚLTIMOS {d.datos.dias} DÍAS</div>
            <div style={{display:"flex",gap:SPACING[12],marginBottom:9}}>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Apostado</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:16,
                  fontFamily:F_BODY}}>
                  {money(d.datos.apostado, mon)}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>GGR</div>
                <div style={{color:d.datos.ggr>=0?Q.green:Q.red,
                  fontWeight:800,fontSize:16,
                  fontFamily:F_BODY}}>
                  {money(d.datos.ggr, mon)}</div>
              </div>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Margen</div>
                <div style={{color:Q.text,fontWeight:800,fontSize:16,
                  fontFamily:F_BODY}}>
                  {d.datos.margen!=null?`${d.datos.margen}%`:"—"}</div>
              </div>
            </div>
            <div style={{display:"flex",gap:SPACING[12],paddingTop:SPACING[8],
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{flex:1}}>
                <div style={{color:Q.muted,fontSize:12}}>Clientes</div>
                <div style={{color:Q.text,fontSize:14,fontWeight:700,
                  fontFamily:F_BODY}}>
                  {d.datos.clientes}
                  <span style={{color:Q.green,fontSize:12,marginLeft:5,
                    fontWeight:400}}>{d.datos.activos} activos</span></div>
              </div>
              {d.datos.dormidos>0&&(
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Dormidos</div>
                  <div style={{color:Q.amber,fontSize:14,fontWeight:700,
                    fontFamily:F_BODY}}>
                    {d.datos.dormidos}</div>
                </div>
              )}
            </div>
          </GCard>

          {d.analisis&&(
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.text,fontSize:13,lineHeight:1.65,
                whiteSpace:"pre-wrap",
                fontFamily:F_BODY}}>
                {d.analisis}</div>
            </GCard>
          )}
        </>
      )}

      <div style={{color:Q.muted,fontSize:12,marginBottom:5,
        fontFamily:F_BODY}}>Preguntale algo</div>
      <input value={pregunta} onChange={e=>setPregunta(e.target.value)}
        onKeyDown={e=>{ if(e.key==="Enter") preguntar(); }}
        placeholder="¿Qué me conviene hacer esta semana?"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,
          padding:"12px 12px",color:Q.text,fontSize:13.5,marginBottom:8,
          fontFamily:F_BODY}}/>

      <div style={{display:"flex",gap:SPACING[4],flexWrap:"wrap",marginBottom:10}}>
        {SUGERIDAS.map(q=>(
          <button key={q} onClick={()=>{ setPregunta(q); }}
            style={{background:"rgba(255,255,255,0.04)",
              border:`1px solid ${Q.border}`,borderRadius:RADII.sm,
              padding:"8px 12px",cursor:"pointer",color:Q.muted,
              fontSize:12,lineHeight:1.3,textAlign:"left",
              fontFamily:F_BODY}}>{q}</button>
        ))}
      </div>

      <Btn label={proc?"Pensando…":"Preguntar"} onClick={preguntar}
        color={Q.cyan} full disabled={proc||pregunta.trim().length<5}/>

      {respuesta&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}>
          <div style={{color:Q.text,fontSize:13,lineHeight:1.65,
            whiteSpace:"pre-wrap",
            fontFamily:F_BODY}}>{respuesta}</div>
        </GCard>
      )}
    </div>
  );
}

function DesafiosAgencia({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const hoy=new Date().toISOString().slice(0,10);
  const hace30=new Date(Date.now()-30*86400000).toISOString().slice(0,10);
  const [desde,setDesde]=useState(hace30);
  const [hasta,setHasta]=useState(hoy);
  const [rep,setRep]=useState(null);
  const [prods,setProds]=useState(null);
  const [cargando,setCargando]=useState(false);
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);

  const [cc,setCc]=useState(null);
  const [liq,setLiq]=useState(null);

  const cargarLiq=async()=>{
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/liquidacion?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.ok) setLiq(await r.json());
    }catch(e){}
  };

  const cargarCC=async()=>{
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/desafios/cuenta?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.ok) setCc(await r.json());
    }catch(e){}
  };

  const cargarReporte=async()=>{
    setCargando(true);
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/desafios/reporte?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setRep(await r.json());
    }catch(e){}
    setCargando(false);
  };

  const cargarProds=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/productos`,
        {headers:authHeaders(agencia.token)});
      if(r.ok) setProds(await r.json());
    }catch(e){}
  };

  useEffect(()=>{ cargarReporte(); cargarProds(); cargarCC(); cargarLiq();
    /* eslint-disable-next-line */ },[]);

  const cambiar=async(code,activo)=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/productos`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({agencia_code:code, producto:"desafios",
                             activo})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:activo?"Habilitado":"Quitado", ok:true}); cargarProds();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const loTengo=(prods?.mis_productos||[]).includes("desafios");

  const inp={background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
    color:Q.text,fontSize:13,flex:1,minWidth:0,
    fontFamily:F_BODY};

  // ── LA LIQUIDACIÓN SON DOS NÚMEROS, NO UN TOTAL ──────────────
  //
  // Los desafíos se juegan en IACOIN y el resto en la moneda de la agencia.
  // Hasta hoy el backend las sumaba en un solo `total_liquidar` y esta tarjeta
  // lo dibujaba con `money(..., mon)`: un número con dos escalas adentro y el
  // símbolo de una sola. Ahora llegan separados —`total_liquidar_pesos` y
  // `desafios.total_iacoin`— y se dibujan separados.
  //
  // NO SE SUMAN ACÁ TAMPOCO. Si esta pantalla los sumara para mostrar "un
  // total", el arreglo del backend no habría servido de nada: el número
  // equivocado volvería por el frente.
  const liqPesos = liq?.total_liquidar_pesos || 0;
  const liqIacoin = liq?.desafios?.total_iacoin || 0;
  // ── Y CUÁNTO SON ESOS IACOIN EN TU MONEDA, HOY ───────────────
  //
  // La agencia no tiene cuenta en IACOIN: `saldo_iacoin` existe sólo en
  // `users` y su saldo es `saldo_cc`, en moneda local. Desde el 2026-10-27 la
  // comisión de desafíos se le paga en su moneda, a la cotización del día, y
  // el backend manda ese equivalente ya convertido con su rótulo.
  //
  // NO SE SUMA AL NÚMERO DE ARRIBA, ni acá ni en el backend. Son tres cosas:
  // lo que se debe en moneda local, lo que se debe en IACOIN y cuánto son
  // esos IACOIN hoy. Juntar dos de las tres es el defecto que se arregló.
  //
  // `null` NO ES CERO. Sin cotización vigente para su moneda el backend manda
  // `equivalente_hoy` en `null` y el motivo en `equivalente_hoy_error`: por
  // eso se pregunta por el TIPO y no se escribe `|| 0`, que volvería a
  // dibujar "no se te debe nada" sobre plata que sí se debe.
  const equivHoy = liq?.desafios?.equivalente_hoy;
  const hayEquiv = typeof equivHoy === "number";
  // La moneda la declara el backend con el campo, y sale de `agencias.moneda`.
  // Si faltara, la de la agencia, que es la misma de la que salió.
  const equivMoneda = liq?.desafios?.equivalente_hoy_moneda || mon;
  const equivError = liq?.desafios?.equivalente_hoy_error;
  const cotizacionDeHoy = liq?.desafios?.cotizacion_de_hoy;
  // La moneda la declara el backend con el bloque. Si faltara, IACOIN: es la
  // única escala en la que se juegan los desafíos.
  const monDesafios = liq?.desafios?.moneda || "IACOIN";
  // Cada pata con su propio detalle. Mezclar una fila de IACOIN en la lista
  // de pesos es cómo se veía antes, y era lo mismo que sumarlas.
  const detallePesos = liq ? [
    ["Deportivas", liq.deportivas?.comision || 0],
    ["Casino", liq.casino?.comision || 0],
    ["Cuenta corriente anterior", liq.cuenta_corriente || 0],
  ].filter(([,v])=>v!==0) : [];
  const detalleIacoin = liq ? [
    ["Comisión de desafíos", liq.desafios?.comision || 0],
    ["Movimiento de desafíos", liq.desafios?.saldo_movimiento || 0],
  ].filter(([,v])=>v!==0) : [];

  return(
    <div>
      <PageHeader icon={<Handshake/>} title="Desafíos"
        description={<>Tus clientes apuestan entre ellos en IACOIN. La casa no
        arriesga: cobra comisión y vos cobrás una parte.</>}/>

      {!loTengo&&(
        <GCard glow={Q.red} style={{padding:SPACING[16],marginBottom:12}}>
          <div style={{color:Q.red,fontSize:13,fontWeight:700,
            marginBottom:5,fontFamily:F_BODY}}>
            No tenés este producto</div>
          <div style={{color:Q.muted,fontSize:12,lineHeight:1.55}}>
            Pedíselo a la administración para que tus clientes puedan
            desafiarse.</div>
        </GCard>
      )}

      {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12.5,marginBottom:10,
        textAlign:"center"}}>
        <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        <input type="date" value={desde}
          onChange={e=>setDesde(e.target.value)} style={inp}/>
        <input type="date" value={hasta}
          onChange={e=>setHasta(e.target.value)} style={inp}/>
      </div>
      <Btn label={cargando?"Cargando…":"Ver"} onClick={cargarReporte}
        color={Q.violet} full disabled={cargando}/>

      {/* El número final: qué se le paga cruzando todo. Es lo
          primero que quiere ver el agente. */}
      {liq&&(
        <GCard glow={liqPesos>=0?Q.green:Q.amber}
          style={{padding:SPACING[16],margin:"12px 0"}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
            marginBottom:7}}>TU LIQUIDACIÓN</div>

          {/* En la moneda de la agencia: deportivas, casino y la cuenta
              corriente que ya venía. */}
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",marginBottom:9}}>
            <span style={{color:Q.text,fontSize:13,fontWeight:700,
              fontFamily:F_BODY}}>
              {liqPesos>=0?"Te pagamos":"Le debés a la casa"}</span>
            <span style={{color:liqPesos>=0?Q.green:Q.amber,
              fontWeight:900,fontSize:22,
              fontFamily:F_BODY}}>
              {money(Math.abs(liqPesos), mon, {conCodigo:true})}</span>
          </div>
          {detallePesos.map(([l,v])=>(
            <div key={l} style={{display:"flex",
              justifyContent:"space-between",fontSize:12,
              padding:"4px 0",borderTop:`1px solid ${Q.border}`,
              fontFamily:F_BODY}}>
              <span style={{color:Q.muted}}>{l}</span>
              <span style={{color:v>=0?Q.text:Q.amber}}>
                {v>=0?"+":""}{money(v, mon)}</span>
            </div>
          ))}

          {/* Y los desafíos, APARTE y en IACOIN. Es otra moneda: un IACOIN
              vale un dólar de referencia y no se convierte acá. Se muestra
              siempre que haya movimiento, incluso con la pata de arriba en
              cero, porque es plata que se debe igual. */}
          {(liqIacoin!==0||detalleIacoin.length>0)&&(
            <div style={{marginTop:12,paddingTop:SPACING[12],
              borderTop:`1px solid ${Q.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",marginBottom:9}}>
                <span style={{color:Q.text,fontSize:13,fontWeight:700,
                  fontFamily:F_BODY}}>
                  Desafíos · {liqIacoin>=0?"te pagamos":"le debés a la casa"}
                  </span>
                <span style={{color:liqIacoin>=0?Q.green:Q.amber,
                  fontWeight:900,fontSize:19,
                  fontFamily:F_BODY}}>
                  {money(Math.abs(liqIacoin), monDesafios)}</span>
              </div>
              {detalleIacoin.map(([l,v])=>(
                <div key={l} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,
                  padding:"4px 0",borderTop:`1px solid ${Q.border}`,
                  fontFamily:F_BODY}}>
                  <span style={{color:Q.muted}}>{l}</span>
                  <span style={{color:v>=0?Q.text:Q.amber}}>
                    {v>=0?"+":""}{money(v, monDesafios)}</span>
                </div>
              ))}
              {/* El equivalente de hoy: un TERCER renglón, con la fecha en el
                  rótulo. Un valor convertido que no dice a qué día se
                  convirtió es un número que miente mañana, porque la
                  cotización la fija un humano y cambia. */}
              {hayEquiv&&(
                <div style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,
                  padding:"4px 0",borderTop:`1px solid ${Q.border}`,
                  fontFamily:F_BODY}}>
                  <span style={{color:Q.muted,minWidth:0,flex:1}}>
                    Hoy son
                    {cotizacionDeHoy?(
                      <span style={{color:Q.dim,fontSize:12,display:"block",
                        lineHeight:1.35}}>
                        a la cotización de hoy: 1 IACOIN
                        = {money(cotizacionDeHoy, equivMoneda)}</span>
                    ):null}</span>
                  <span style={{color:Q.text,flexShrink:0,marginLeft:8,
                    fontWeight:700}}>
                    {money(equivHoy, equivMoneda, {conCodigo:true})}</span>
                </div>
              )}
              {/* Y SI NO SE PUDO CONVERTIR, SE DICE. Nunca un cero: se vería
                  igual que "no se te debe nada" sobre plata que se debe. */}
              {!hayEquiv&&equivError&&(
                <div style={{color:Q.amber,fontSize:12,marginTop:7,
                  lineHeight:1.45,fontFamily:F_BODY}}>
                  <Icon name="triangle-alert" size={12}/> No pudimos convertir
                  esto a {equivMoneda}: {equivError}. Avisale a la
                  administración.</div>
              )}
              <div style={{color:Q.dim,fontSize:12,marginTop:7,
                lineHeight:1.45,fontFamily:F_BODY}}>
                Los desafíos se juegan en IACOIN y se liquidan aparte: no se
                suman con lo de arriba. Se te pagan en {equivMoneda} a la
                cotización del día en que se cobran.</div>
            </div>
          )}
        </GCard>
      )}

      {cc&&(cc.desafios>0||cc.saldo!==0)&&(
        <GCard glow={cc.saldo>=0?Q.green:Q.amber}
          style={{padding:SPACING[16],margin:"12px 0"}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
            marginBottom:8}}>CUENTA CON EL SISTEMA</div>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center"}}>
            <span style={{color:Q.text,fontSize:13,fontWeight:700,
              fontFamily:F_BODY}}>
              {cc.saldo>=0?"Te transferimos":"Le debés al sistema"}</span>
            <span style={{color:cc.saldo>=0?Q.green:Q.amber,
              fontWeight:900,fontSize:20,
              fontFamily:F_BODY}}>
              {Math.abs(cc.saldo).toLocaleString("es-AR",
                {maximumFractionDigits:2})}</span>
          </div>
          {/* Los cuatro montos de esta tarjeta salen de `_cc_desafios`, o
              sea de `desafios_comisiones`: están en IACOIN, igual que los de
              la tarjeta de liquidación de arriba. Dos de ellos se dibujaban
              con `money(..., mon)`, con el símbolo de la moneda de la
              agencia, mientras el saldo y el rake de al lado iban sin símbolo:
              la misma plata con dos unidades distintas en la misma tarjeta.
              El endpoint ya declara `moneda: "IACOIN"` y es lo que se usa. */}
          <div style={{color:Q.dim,fontSize:12,marginTop:7,
            lineHeight:1.5,fontFamily:F_BODY}}>
            Cobraste {money(cc.cobrado_a_jugadores, cc.moneda||"IACOIN")} de
            tus jugadores
            {cc.premios_a_pagar>0&&
              `, tenés que pagar ${money(cc.premios_a_pagar,
                                         cc.moneda||"IACOIN")}`}
            . Ganaste {cc.rake.toLocaleString("es-AR",
              {maximumFractionDigits:2})} de comisión.</div>
        </GCard>
      )}

      {rep&&(
        <GCard style={{padding:SPACING[16],margin:"12px 0"}}>
          <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
            marginBottom:9}}>EN IACOIN</div>
          <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Desafíos</div>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>
                {rep.desafios}</div>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Jugadores</div>
              <div style={{color:Q.text,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>
                {rep.jugadores}</div>
            </div>
            <div style={{flex:1}}>
              <div style={{color:Q.muted,fontSize:12}}>Jugado</div>
              <div style={{color:Q.gold,fontWeight:800,fontSize:18,
                fontFamily:F_BODY}}>
                {rep.jugado.toLocaleString("es-AR",
                  {maximumFractionDigits:0})}</div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",paddingTop:SPACING[8],
            borderTop:`1px solid ${Q.border}`}}>
            <span style={{color:Q.text,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>
              Tu comisión
              {rep.mi_pct>0&&(
                <span style={{color:Q.dim,fontSize:12,marginLeft:6}}>
                  {rep.mi_pct}%</span>
              )}</span>
            <span style={{color:Q.green,fontWeight:900,fontSize:20,
              fontFamily:F_BODY}}>
              {rep.mi_comision.toLocaleString("es-AR",
                {maximumFractionDigits:2})}</span>
          </div>
          {rep.comision_generada>rep.mi_comision&&(
            <div style={{color:Q.dim,fontSize:12,marginTop:6,
              lineHeight:1.45}}>
              Tu red generó {rep.comision_generada.toLocaleString("es-AR",
                {maximumFractionDigits:2})} en total. El resto va a las
              subagencias y a la casa.</div>
          )}
        </GCard>
      )}

      {/* Quitárselo o dárselo a las subagencias */}
      {prods&&(prods.hijas||[]).length>0&&(
        <GCard style={{padding:SPACING[16],marginTop:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:12.5,
            marginBottom:4,fontFamily:F_BODY}}>
            Tus subagencias</div>
          <div style={{color:Q.dim,fontSize:12,marginBottom:10,
            lineHeight:1.5}}>
            {loTengo
              ?"Alcanza también a las que cuelgan de cada una."
              :"No podés habilitarlo porque vos no lo tenés."}</div>

          {prods.hijas.map(h=>{
            const tiene=(h.productos||[]).includes("desafios");
            return(
              <button key={h.code}
                onClick={()=>loTengo&&cambiar(h.code,!tiene)}
                disabled={proc||!loTengo}
                style={{width:"100%",display:"flex",
                  justifyContent:"space-between",alignItems:"center",
                  background:!loTengo?"rgba(255,255,255,0.02)"
                    :(tiene?`${Q.green}10`:`${Q.red}10`),
                  border:`1px solid ${!loTengo?Q.border
                    :(tiene?`${Q.green}44`:`${Q.red}44`)}`,
                  borderRadius:RADII.md,padding:"12px 12px",marginBottom:6,
                  cursor:loTengo?"pointer":"not-allowed",
                  fontFamily:F_BODY}}>
                <span style={{color:loTengo?Q.text:Q.dim,fontSize:12.5,
                  minWidth:0,overflow:"hidden",textOverflow:"ellipsis",
                  whiteSpace:"nowrap"}}>{h.nombre}</span>
                <span style={{fontSize:12,fontWeight:700,flexShrink:0,
                  color:!loTengo?Q.dim:(tiene?Q.green:Q.red)}}>
                  {!loTengo?"—":(tiene?"lo tiene":"sin acceso")}</span>
              </button>
            );
          })}
        </GCard>
      )}
    </div>
  );
}

function MisCanales({ agencia, onSesionExpirada }){
  const [wa,setWa]=useState("");
  const [tg,setTg]=useState("");
  const [hor,setHor]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [abierto,setAbierto]=useState(false);

  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/contacto`,
      {headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d){ setWa(d.whatsapp||""); setTg(d.telegram_url||"");
                        setHor(d.horario||""); } })
      .catch(()=>{});
    // eslint-disable-next-line
  },[]);

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/contacto`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},
        body:JSON.stringify({whatsapp:wa,telegram_url:tg,horario:hor})});
      if(r.status===401){ onSesionExpirada(); return; }
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||`Error ${r.status}`);
      setMsg({text:"Guardado", ok:true}); if(d.telegram_url) setTg(d.telegram_url);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:14,marginBottom:8,
    fontFamily:F_BODY};

  return(
    <div style={{marginBottom:12,paddingBottom:SPACING[12],
      borderBottom:`1px solid ${Q.border}`}}>
      <div onClick={()=>setAbierto(v=>!v)} style={{cursor:"pointer",
        display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{color:Q.muted,fontSize:12,
            fontFamily:F_BODY}}>
            Cómo te contactan tus clientes</div>
          {!abierto&&(
            <div style={{color:(wa||tg)?Q.green:Q.amber,fontSize:12,
              marginTop:2}}>
              {(wa||tg)
                ? `${wa?"WhatsApp":""}${wa&&tg?" · ":""}${tg?"Telegram":""}`
                : "Sin canales cargados"}</div>
          )}
        </div>
        <span style={{color:Q.dim,fontSize:12}}>{abierto?"▲":"▼"}</span>
      </div>

      {abierto&&(
        <div style={{marginTop:9}}>
          <div style={{color:Q.dim,fontSize:12,marginBottom:8,
            lineHeight:1.5}}>
            Estos botones aparecen en el chat de soporte de tus
            clientes. Sin esto, no tienen cómo llegar a vos.</div>

          <input value={wa} onChange={e=>setWa(e.target.value)}
            placeholder="WhatsApp: +54 9 11 1234 5678" style={inp}/>
          <input value={tg} onChange={e=>setTg(e.target.value)}
            placeholder="Telegram: @tuusuario" style={inp}/>
          <input value={hor} onChange={e=>setHor(e.target.value)}
            placeholder="Horario: lunes a sábado de 9 a 22" style={inp}/>

          {msg&&<div style={{color:msg.ok?Q.green:Q.red,fontSize:12,marginBottom:8,
            textAlign:"center"}}>
            <Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

          <Btn label={proc?"Guardando…":"Guardar"} onClick={guardar}
            color={Q.violet} full disabled={proc}/>
        </div>
      )}
    </div>
  );
}

function MensajesAgencia({ agencia, onSesionExpirada }){
  const [msgs,setMsgs]=useState(null);
  const [texto,setTexto]=useState("");
  const [proc,setProc]=useState(false);
  const finRef=useRef(null);

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/mensajes`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setMsgs((await r.json()).mensajes||[]);
    }catch(e){ setMsgs([]); }
  };
  useEffect(()=>{
    cargar();
    const t=setInterval(cargar,45000);
    return()=>clearInterval(t);
    // eslint-disable-next-line
  },[]);
  useEffect(()=>{ finRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs]);

  const enviar=async()=>{
    const t=texto.trim();
    if(!t||proc) return;
    setProc(true); setTexto("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/mensajes`,{
        method:"POST",headers:{"Content-Type":"application/json",
          ...authHeaders(agencia.token)},body:JSON.stringify({texto:t})});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) cargar();
    }catch(e){}
    setProc(false);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",
      height:"calc(100dvh - 223px)",minHeight:320}}>
      {/* PageHeader's fixed spacing (SPACING[4]+SPACING[16]=20) is 7px
          taller than this header's old (3+10=13), so the 230px offset was
          reduced to 223px to keep this fixed-height chat viewport exactly
          the same size below the header — see odd/tasks/page-headers.md. */}
      <PageHeader icon={<Icon name="message-circle"/>} title="Mensajes"
        description="Escribile directo a la administración."/>

      <div style={{marginBottom:12,paddingBottom:SPACING[12],
        borderBottom:`1px solid ${Q.border}`}}>
        <SelectorSonido/>
      </div>

      {/* Los canales que ven los clientes en su chat de soporte */}
      <MisCanales agencia={agencia} onSesionExpirada={onSesionExpirada}/>
      <ProductosRed agencia={agencia} onSesionExpirada={onSesionExpirada}/>
      <ProveedoresAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>

      <div style={{flex:1,overflowY:"auto",marginBottom:10}}>
        {!msgs&&<div style={{color:Q.muted,textAlign:"center",
          padding:SPACING[20]}}>Cargando…</div>}
        {msgs&&msgs.length===0&&(
          <div style={{color:Q.dim,textAlign:"center",padding:SPACING[20],
            fontSize:12.5,lineHeight:1.5}}>
            Todavía no hay mensajes.<br/>
            Escribí si necesitás algo.</div>
        )}
        {(msgs||[]).map(m=>(
          <div key={m.id} style={{display:"flex",
            justifyContent:m.mio?"flex-end":"flex-start",marginBottom:8}}>
            <div style={{maxWidth:"84%",
              background:m.mio?`${Q.violet}28`:"rgba(255,255,255,0.05)",
              border:`1px solid ${m.mio?Q.violet:Q.border}`,
              borderRadius:RADII.lg,padding:"8px 12px",color:Q.text,
              fontSize:12.5,lineHeight:1.55,whiteSpace:"pre-wrap",
              fontFamily:F_BODY}}>
              {!m.mio&&(
                <div style={{color:Q.cyan,fontSize:12,fontWeight:700,
                  marginBottom:3}}>ADMINISTRACIÓN</div>
              )}
              {m.texto}
              <div style={{color:Q.dim,fontSize:12,marginTop:4}}>{fechaHora(m.fecha)}</div>
            </div>
          </div>
        ))}
        <div ref={finRef}/>
      </div>

      <div style={{display:"flex",gap:SPACING[8]}}>
        <input value={texto} onChange={e=>setTexto(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&enviar()}
          placeholder="Escribí un mensaje…"
          style={{flex:1,minWidth:0,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"12px 16px",color:Q.text,fontSize:14,
            fontFamily:F_BODY}}/>
        <button onClick={enviar} disabled={proc||!texto.trim()}
          style={{background:texto.trim()?Q.violet:"rgba(255,255,255,0.05)",
            border:"none",borderRadius:RADII.md,padding:"0 20px",
            cursor:texto.trim()?"pointer":"default",
            color:texto.trim()?"#fff":Q.dim,fontSize:14,fontWeight:700,
            flexShrink:0}}>↑</button>
      </div>
    </div>
  );
}

function SaldoCC({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [data,setData]=useState(null);
  const [err,setErr]=useState("");

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/cc`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setData(await r.json());
      else setErr("No se pudo cargar el saldo");
    }catch(e){ setErr("Sin conexión"); }
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const tipoTxt={carga_admin:"Carga del admin",retiro_admin:"Descuento admin",
    carga_cliente:"Crédito a cliente",retiro_cliente:"Retiro de cliente",
    pago_premio:"Premio pagado",transferencia:"A sub-agencia",
    recibido:"Recibido"};
  const tipoColor={carga_admin:Q.green,recibido:Q.green,pago_premio:Q.green,
    retiro_cliente:Q.cyan,carga_cliente:Q.amber,transferencia:Q.amber,
    retiro_admin:Q.red};

  if(err) return <div style={{color:Q.red,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>{err}</div>;
  if(!data) return <div style={{color:Q.muted,padding:SPACING[20],textAlign:"center",
    fontFamily:F_BODY}}>Cargando saldo...</div>;

  return(
    <div>
      <GCard glow={data.saldo_cc>=0?Q.green:Q.red} style={{padding:SPACING[20],marginBottom:14,
        background:`linear-gradient(135deg,${data.saldo_cc>=0?Q.green:Q.red}12,${Q.violet}06)`}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:2,
          fontFamily:F_BODY,marginBottom:4}}>Tu saldo disponible</div>
        <div style={{color:data.saldo_cc>=0?Q.green:Q.red,fontWeight:900,fontSize:32,
          fontFamily:F_BODY}}>
          {money(data.saldo_cc, mon)} <span style={{fontSize:16}}>{data.moneda}</span></div>
        <div style={{color:Q.dim,fontSize:12,marginTop:6,
          fontFamily:F_BODY}}>
          Con este saldo das crédito a tus clientes. Si se agota, pedile una carga
          a tu agencia superior.
        </div>
      </GCard>

      {/* A log read in time order, not navigated into: rows on the page
          background instead of a GCard per movement. */}
      <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
        textTransform:"uppercase",marginBottom:SPACING[12],
        fontFamily:F_BODY}}>
        Movimientos</div>
      {data.movimientos.length===0&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12,
            fontFamily:F_BODY}}>Sin movimientos todavía</div>
        </GCard>
      )}
      {data.movimientos.map((m,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
          borderBottom:i<data.movimientos.length-1?`1px solid ${Q.border}55`:"none"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:tipoColor[m.tipo]||Q.text,fontWeight:600,fontSize:TEXT[15],
              fontFamily:F_BODY}}>{tipoTxt[m.tipo]||m.tipo}</div>
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
              fontFamily:F_BODY}}>
              {fechaHora(m.fecha)}{m.detalle?` · ${m.detalle}`:""}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{color:m.monto>=0?Q.green:Q.amber,fontWeight:700,fontSize:TEXT[16],
              fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>
              {m.monto>=0?"+":""}{money(m.monto, mon)}</div>
            <div style={{color:Q.dim,fontSize:12}}>saldo {money(m.saldo, mon)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PSPAgencia({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [tipo,setTipo]=useState("cargas");
  const [items,setItems]=useState(null);
  const [err,setErr]=useState("");
  const cargar=async()=>{
    setItems(null); setErr("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/psp/historial?tipo=${tipo}`,
        { headers: authHeaders(agencia.token) });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      setItems((await r.json()).items||[]);
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message); setItems([]); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[tipo]);

  const lista=items||[];
  const total=lista.filter(x=>["acreditado","completado"].includes(x.estado))
    .reduce((a,x)=>a+(x.monto||0),0);
  const colores={acreditado:Q.green,completado:Q.green,pendiente:Q.amber,
    solicitado:Q.amber,procesando:Q.cyan,vencido:Q.muted,rechazado:Q.red,fallido:Q.red};

  return(
    <div>
      <div style={{color:Q.text,fontWeight:700,fontSize:15,marginBottom:4,
        fontFamily:F_BODY}}><Icon name="landmark" size={14}/> Movimientos digitales</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        fontFamily:F_BODY}}>
        Cargas y retiros por transferencia (PSP) de tu rama.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["cargas",<><Icon name="arrow-up-right" size={12}/> Cargas</>],["retiros",<><Icon name="arrow-down-left" size={12}/> Retiros</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setTipo(k)} style={{flex:1,
            background:tipo===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${tipo===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px",cursor:"pointer",color:tipo===k?Q.violet2||Q.violet:Q.muted,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      <AlertaError mensaje={err}/>

      <GCard style={{padding:"12px 16px",marginBottom:12,textAlign:"center"}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
          fontFamily:F_BODY}}>
          Total {tipo==="cargas"?"acreditado":"completado"}</div>
        <div style={{color:Q.green,fontWeight:800,fontSize:20,
          fontFamily:F_BODY}}>{money(total, mon)}</div>
      </GCard>

      {items===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[16],
        fontFamily:F_BODY}}>Cargando...</div>}
      {items&&lista.length===0&&<GCard style={{padding:SPACING[24],textAlign:"center"}}>
        <div style={{marginBottom:6}}><Icon name="landmark" size={26}/></div>
        <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
          Sin movimientos digitales</div></GCard>}
      {/* A log read in time order, not navigated into: rows on the page
          background instead of a GCard per movement. */}
      {lista.map((x,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
          borderBottom:i<lista.length-1?`1px solid ${Q.border}55`:"none"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
              fontFamily:F_BODY}}>{x.cliente}</div>
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,fontFamily:F_BODY}}>
              {fechaHora(x.fecha)}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{color:Q.gold,fontWeight:700,fontSize:TEXT[16],
              fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{money(x.monto, mon)}</div>
            <div style={{color:colores[x.estado]||Q.muted,fontSize:12,fontWeight:700,
              fontFamily:F_BODY}}>{x.estado}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function BonosAgencia({ agencia, onSesionExpirada }){
  // Sin `mon`: esta pantalla ya no dibuja NINGÚN monto en la moneda del libro
  // de la agencia. Los dos que tenía son los del bono y salen de `b.moneda`.
  const [data,setData]=useState(null);
  const [err,setErr]=useState("");
  const cargar=async()=>{
    setErr("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/bonos`,
        { headers: authHeaders(agencia.token) });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      setData(await r.json());
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message); }
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const bonos = data?.bonos||[];
  const res = data?.resumen||{};

  return(
    <div>
      <PageHeader icon={<Gift/>} title="Bonos activos"
        description="Bonos que el admin habilitó para tu agencia. Los otorgás a tus clientes."/>
      <AlertaError mensaje={err}/>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:14}}>
        {[["Otorgados",res.otorgados||0,Q.cyan],["Activos",res.activos||0,Q.gold],
          ["Liberados",res.liberados||0,Q.green]].map(([l,v,c],i)=>(
          <GCard key={i} glow={c} style={{padding:"12px 8px",textAlign:"center"}}>
            <div style={{color:c,fontWeight:800,fontSize:16,
              fontFamily:F_BODY}}>{v}</div>
            <div style={{color:Q.muted,fontSize:12}}>{l}</div>
          </GCard>
        ))}
      </div>

      {bonos.length===0&&(
        <GCard style={{padding:SPACING[32],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Gift size={28}/></div>
          <div style={{color:Q.muted,fontSize:13,fontFamily:F_BODY}}>
            El admin todavía no habilitó bonos para tu agencia</div>
        </GCard>
      )}
      {bonos.map(b=>(
        <GCard key={b.id} glow={Q.gold} style={{padding:SPACING[16],marginBottom:8}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:4,
            fontFamily:F_BODY}}>{b.nombre}</div>
          {/* Los dos montos van con `b.moneda`, la del BONO. Mismo criterio
              que el desplegable de otorgar: la del libro de la agencia
              rotularía con el símbolo de acá un número que está definido en
              otra moneda. */}
          <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
            {b.tipo==="bienvenida"
              ? `Bono de bienvenida · ${money(b.monto_fijo||0, b.moneda)}`
              : `Bono por carga · ${b.porcentaje||0}% (máx ${money(b.tope||0, b.moneda)})`}
          </div>
          <div style={{color:Q.cyan,fontSize:12,marginTop:2,
            fontFamily:F_BODY}}>
            Rollover: apostar x{b.rollover||0} antes de retirar</div>
          <div style={{color:Q.dim,fontSize:12,marginTop:6,
            fontFamily:F_BODY}}>
            Para otorgarlo, entrá a la ficha del cliente en <Icon name="users" size={11}/> Clientes.</div>
        </GCard>
      ))}
    </div>
  );
}

function HistorialCashout({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [lista,setLista]=useState(null);
  const [err,setErr]=useState("");
  const [cargando,setCargando]=useState(true);
  const cargar=async()=>{
    setCargando(true); setErr("");
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/historial/cashout`,
        { headers: authHeaders(agencia.token) });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      const d=await r.json();
      setLista(d.cashouts||[]);
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message); setLista([]); }
    setCargando(false);
  };
  useEffect(()=>{ cargar(); /* eslint-disable-next-line */ },[]);

  const items = lista||[];
  const totalPagado = items.reduce((a,c)=>a+(c.valor||0),0);
  const rotulos={cashed_out:"Pagado ✓",cashout_pending:"Pend. caja"};
  const colores={cashed_out:Q.green,cashout_pending:Q.amber};

  return(
    <div>
      <PageHeader icon={<Icon name="wallet-cards"/>} title="Cash outs de la rama"
        description="Los cash outs pagados en tu rama y su estado."
        action={<Btn label={cargando?"...":"↻ Actualizar"} onClick={cargar} outline color={Q.muted} size="sm"/>}/>
      <AlertaError mensaje={err}/>
      {/* Auto-fit so a lone or paired stat settles at a sane card width
          instead of stretching across the whole row. */}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
        gap:SPACING[12],marginBottom:14}}>
        {[{l:"Cash outs",v:items.length,c:Q.cyan},
          {l:"Total pagado",v:money(totalPagado, mon),c:Q.gold}].map((x,i)=>(
          <GCard key={i} glow={x.c} style={{padding:"12px 12px",textAlign:"center"}}>
            <div style={{color:x.c,fontWeight:700,fontSize:13,
              fontFamily:F_BODY}}>{x.v}</div>
            <div style={{color:Q.muted,fontSize:12}}>{x.l}</div>
          </GCard>
        ))}
      </div>
      {cargando&&<GCard style={{padding:SPACING[20],textAlign:"center"}}>
        <div style={{color:Q.muted,fontSize:12}}>Cargando...</div></GCard>}
      {!cargando&&items.length===0&&!err&&(
        <GCard style={{padding:SPACING[32],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Icon name="wallet-cards" size={28}/></div>
          <div style={{color:Q.muted,fontSize:13}}>Todavía no hay cash outs en tu rama</div>
        </GCard>
      )}
      {/* A log read in time order, not navigated into: rows on the page
          background instead of a GCard per cash-out. The status pill is
          kept — it is a meaningful state badge, not card chrome. */}
      {items.map((c,i)=>(
        <div key={c.code+i} style={{display:"flex",justifyContent:"space-between",
          alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
          borderBottom:i<items.length-1?`1px solid ${Q.border}55`:"none"}}>
          <div style={{minWidth:0,flex:1}}>
            <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
              fontFamily:F_BODY}}>{c.code}</div>
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
              fontFamily:F_BODY}}>{c.cliente} · {c.agencia} · {fechaHora(c.fecha)}
              {c.pagado_por?` · pagó ${c.pagado_por}`:""}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{color:Q.gold,fontWeight:700,fontSize:TEXT[16],
              fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{money(c.valor, mon)} {c.moneda}</div>
            <div style={{background:`${colores[c.estado]||Q.muted}22`,
              border:`1px solid ${colores[c.estado]||Q.muted}`,borderRadius:RADII.md,
              padding:"4px 8px",color:colores[c.estado]||Q.muted,fontSize:12,fontWeight:700,
              fontFamily:F_BODY,display:"inline-block",marginTop:2}}>{rotulos[c.estado]||c.estado}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Historial({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [tickets,setTickets]=useState(null);
  const [err,setErr]=useState("");
  const [cargando,setCargando]=useState(true);
  // El permiso de anular lo habilita el admin, por agencia o por rama.
  const [anular,setAnular]=useState({puede_anular:false});

  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/puede-anular`,
      {headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():null)
      .then(d=>d&&setAnular(d))
      .catch(()=>{});
  },[agencia.token]);

  const cargar=async()=>{
    setCargando(true); setErr("");
    try {
      const r = await fetch(`${API_URL}/api/agencias/me/tickets`,
        { headers: authHeaders(agencia.token) });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      const d = await r.json();
      setTickets(d.tickets||[]);
    } catch(e){
      setErr(e.message==="Failed to fetch"
        ? "Sin conexión con el servidor" : e.message);
      setTickets([]);
    }
    setCargando(false);
  };

  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const lista = tickets||[];
  const total    = lista.reduce((a,t)=>a+(t.stake||0),0);
  const expuesto = lista.reduce((a,t)=>a+(t.potential_win||0),0);

  return(
    <div>
      <PageHeader title="Historial"
        description="Los tickets que emitió tu agencia, con su cobro y exposición."
        action={<Btn label={cargando?"...":"↻ Actualizar"} onClick={cargar}
          outline color={Q.muted} size="sm"/>}/>

      <AutoLiquidar agencia={agencia} onListo={cargar}
        onSesionExpirada={onSesionExpirada}/>

      <AlertaError mensaje={err}/>

      {/* Auto-fit so each stat keeps a sane width instead of squeezing
          three into one row on a narrow phone. */}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
        gap:SPACING[12],marginBottom:14}}>
        {[{l:"Tickets",v:lista.length,c:Q.cyan},
          {l:"Cobrado",v:money(total, mon),c:Q.green},
          {l:"Exposición",v:money(expuesto, mon),c:Q.amber},
        ].map((x,i)=>(
          <GCard key={i} glow={x.c} style={{padding:"12px 12px",textAlign:"center"}}>
            <div style={{color:x.c,fontWeight:700,fontSize:12,
              fontFamily:F_BODY}}>{x.v}</div>
            <div style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>{x.l}</div>
          </GCard>
        ))}
      </div>

      {cargando&&(
        <GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12,
            fontFamily:F_BODY}}>Cargando...</div>
        </GCard>
      )}

      {!cargando&&lista.length===0&&!err&&(
        <GCard style={{padding:SPACING[32],textAlign:"center"}}>
          <div style={{marginBottom:8}}><Icon name="receipt-text" size={28}/></div>
          <div style={{color:Q.muted,fontSize:13,
            fontFamily:F_BODY}}>
            Todavía no emitiste ningún ticket
          </div>
          <div style={{color:Q.dim,fontSize:12,marginTop:4}}>
            Los que cobres van a aparecer acá
          </div>
        </GCard>
      )}

      {lista.map((t,i)=>(
        <TicketHistorial key={t.code+i} t={t} agencia={agencia}
          anular={anular}
          onCambio={cargar} onSesionExpirada={onSesionExpirada}/>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CIERRES DE CAJA
// ═══════════════════════════════════════════════════════════════
function Cierres({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  // ── LA MONEDA DEL RESUMEN SALE DE LA RESPUESTA, NO DE QUIEN MIRA ──
  //
  // `/api/agencias/me/cierre` manda `moneda`, y con `cliente=<id>` es la DEL
  // CLIENTE, no la de la agencia. Un jugador en dólares abierto bajo una
  // rama en pesos —el caso de Bet Best— tiene su rentabilidad en dólares, y
  // dibujarla con `mon` le pone el `$` de los pesos a un número en dólares:
  // el símbolo equivocado no se ve equivocado, se ve correcto y miente por
  // mil. Sin filtro de cliente `res.moneda` ES la de la agencia, así que
  // esto no cambia nada en la vista de siempre.
  const monRes = (res) => res?.moneda || mon;
  const hoyISO = new Date().toISOString().slice(0,10);
  const [desde,setDesde]=useState(hoyISO);
  const [hasta,setHasta]=useState(hoyISO);
  const [res,setRes]=useState(null);
  const [err,setErr]=useState("");
  const [cargando,setCargando]=useState(false);
  const [vista,setVista]=useState("resumen");   // resumen | movs | apuestas
  const [movs,setMovs]=useState(null);
  const [apuestas,setApuestas]=useState(null);
  const [apAbierta,setApAbierta]=useState(null);
  const [clientesR,setClientesR]=useState([]);
  const [filtroCli,setFiltroCli]=useState("");
  const [impresiones,setImpresiones]=useState(null);
  // Caja: cargas, descargas y saldo vivo. Es lo que el cajero necesita
  // para cuadrar al cierre del día.
  const [caja,setCaja]=useState(null);

  const cargarCaja=async()=>{
    setCaja(null);
    try{
      const q=new URLSearchParams({desde,hasta,detalle:"true"});
      if(filtroCli) q.set("cliente",filtroCli);
      const r=await fetch(`${API_URL}/api/agencias/me/caja?${q}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){
        const e=await r.json().catch(()=>({}));
        // Sin esto la pantalla quedaba "Cargando..." para siempre y no
        // había forma de saber por qué.
        setCaja({error:e.detail||`Error ${r.status}`});
        return;
      }
      setCaja(await r.json());
    }catch(e){
      setCaja({error:e.message==="Failed to fetch"?"Sin conexión":e.message});
    }
  };
  const [combos,setCombos]=useState(null);

  const cargarCombos=async()=>{
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/historial/combos?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setCombos((await r.json()).combos||[]);
    }catch(e){}
  };

  const cargarImpresiones=async()=>{
    try{
      const r=await fetch(
        `${API_URL}/api/agencias/me/historial/impresiones?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setImpresiones((await r.json()).impresiones||[]);
    }catch(e){}
  };

  useEffect(()=>{
    fetch(`${API_URL}/api/agencias/me/clientes-rama`,{headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():{clientes:[]}).then(d=>setClientesR(d.clientes||[]))
      .catch(()=>{});
  // eslint-disable-next-line
  },[]);

  const cargarMovs=async()=>{
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(
        `${API_URL}/api/agencias/me/historial/movimientos?${q}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setMovs((await r.json()).movimientos||[]);
    }catch(e){}
  };
  const cargarApuestas=async()=>{
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(
        `${API_URL}/api/agencias/me/historial/apuestas?${q}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setApuestas((await r.json()).apuestas||[]);
    }catch(e){}
  };

  // Serie de apostado para el resumen: el cierre ya describe el período
  // con totales fijos, así que la línea de tendencia usa exactamente el
  // mismo desde/hasta elegido arriba en vez de abrir un segundo control.
  const [serie,setSerie]=useState(null);
  const [cargSerie,setCargSerie]=useState(false);
  const cargarSerie=async()=>{
    setCargSerie(true);
    try{
      const q=`metrica=apostado&desde=${desde}&hasta=${hasta}`;
      const r=await fetch(`${API_URL}/api/agencias/me/serie?${q}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setSerie((await r.json()).puntos||[]);
    }catch(e){}
    setCargSerie(false);
  };

  useEffect(()=>{
    if(vista==="movs") cargarMovs();
    if(vista==="apuestas") cargarApuestas();
    if(vista==="combos") cargarCombos();
    if(vista==="impresiones") cargarImpresiones();
    if(vista==="caja") cargarCaja();
    if(vista==="resumen") cargarSerie();
  // eslint-disable-next-line
  },[vista,desde,hasta,filtroCli]);

  const rango=(t)=>{
    const hoy=new Date(); const iso=d=>d.toISOString().slice(0,10);
    let d=new Date();
    if(t==="hoy") d=new Date();
    else if(t==="semana") d.setDate(hoy.getDate()-7);
    else if(t==="mes") d.setMonth(hoy.getMonth()-1);
    setDesde(iso(d)); setHasta(iso(hoy));
  };

  const cargar=async()=>{
    setCargando(true); setErr("");
    try{
      const q=`desde=${desde}&hasta=${hasta}`+(filtroCli?`&cliente=${filtroCli}`:"");
      const r=await fetch(
        `${API_URL}/api/agencias/me/cierre?${q}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok) throw new Error(`Error ${r.status}`);
      setRes(await r.json());
    }catch(e){ setErr(e.message==="Failed to fetch"?"Sin conexión":e.message); }
    setCargando(false);
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[desde,hasta,filtroCli]);

  return(
    <div>
      <PageHeader icon={<Icon name="chart-no-axes-combined"/>} title="Cierres"
        description="El resumen de caja, movimientos y apuestas del período que elijas."/>
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["resumen",<><Icon name="chart-no-axes-combined" size={13}/> Resumen</>],["caja",<><Icon name="receipt-text" size={13}/> Caja</>],["movs",<><Banknote size={13}/> Movs</>],["apuestas",<><Icon name="receipt-text" size={13}/> Apuestas</>],["digital",<><Icon name="landmark" size={13}/> Digital</>],["combos",<><Zap size={13}/> Combos</>],["impresiones",<><Printer size={13}/> Impres.</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px",cursor:"pointer",color:vista===k?Q.cyan:Q.muted,
            fontSize:13,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {/* Períodos rápidos */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:10}}>
        {[["hoy","Hoy"],["semana","7 días"],["mes","30 días"]].map(([k,l])=>(
          <button key={k} onClick={()=>rango(k)} style={{flex:1,
            background:"rgba(255,255,255,0.04)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px",cursor:"pointer",color:Q.cyan,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {/* Fechas a mano */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
            color:Q.text,fontSize:13,fontFamily:F_BODY}}/>
      </div>

      {clientesR.length>0&&(
        <select value={filtroCli} onChange={e=>setFiltroCli(e.target.value)}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",
            border:`1px solid ${filtroCli?Q.gold:Q.border}`,borderRadius:RADII.md,
            padding:"8px 12px",color:Q.text,fontSize:14,marginBottom:12,
            fontFamily:F_BODY}}>
          <option value="">— Toda la rama —</option>
          {clientesR.map(c=>(
            <option key={c.id} value={c.id}>{c.nombre} ({c.agencia})</option>
          ))}
        </select>
      )}
      {filtroCli&&<div style={{color:Q.gold,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>
        Viendo solo ese cliente ·{" "}
        <span onClick={()=>setFiltroCli("")}
          style={{color:Q.muted,textDecoration:"underline",cursor:"pointer"}}>ver toda la rama</span>
      </div>}

      <AlertaError mensaje={err}/>
      {cargando&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Calculando...</div>}

      {vista==="caja"&&(
        <div>
          {!caja&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
            fontFamily:F_BODY}}>Cargando...</div>}
          {caja?.error&&(
            <GCard glow={Q.red} style={{padding:SPACING[16],textAlign:"center"}}>
              <div style={{color:Q.red,fontSize:13,
                fontFamily:F_BODY}}>{caja.error}</div>
            </GCard>
          )}

          {caja&&!caja.error&&caja.cliente&&(
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:14,
                fontFamily:F_BODY}}>{caja.cliente.nombre}</div>
              <div style={{color:Q.muted,fontSize:12,marginBottom:10}}>
                Saldo actual: <span style={{color:Q.green,fontWeight:700}}>
                  {money(caja.cliente.saldo, mon)}</span>
                {caja.cliente.saldo_bono>0&&
                  <span style={{color:Q.gold}}> · bono {money(caja.cliente.saldo_bono, mon)}</span>}
              </div>
              <div style={{display:"flex",gap:SPACING[12]}}>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Cargó</div>
                  <div style={{color:Q.green,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(caja.total.cargas, mon)}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Retiró</div>
                  <div style={{color:Q.amber,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(caja.total.descargas, mon)}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Neto</div>
                  <div style={{color:caja.total.neto>=0?Q.green:Q.red,
                    fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>
                    {money(caja.total.neto, mon)}</div>
                </div>
              </div>
            </GCard>
          )}

          {caja&&!caja.error&&!caja.cliente&&(
            <>
              {/* El efectivo que pasó por tu mostrador: lo que
                  recibiste por ventas y lo que pagaste en premios.
                  Es distinto de las cargas de saldo. */}
              {caja.operativa&&(caja.operativa.entro>0||caja.operativa.salio>0)&&(
                <GCard style={{padding:SPACING[16],marginBottom:12}}>
                  <div style={{color:Q.muted,fontSize:12,marginBottom:10,
                    fontFamily:F_BODY}}>
                    Efectivo del mostrador</div>
                  <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
                    <div style={{flex:1}}>
                      <div style={{color:Q.muted,fontSize:12}}>Entró</div>
                      <div style={{color:Q.green,fontWeight:800,fontSize:17,
                        fontFamily:F_BODY}}>
                        {money(caja.operativa.entro, mon)}</div>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{color:Q.muted,fontSize:12}}>Salió</div>
                      <div style={{color:Q.red,fontWeight:800,fontSize:17,
                        fontFamily:F_BODY}}>
                        {money(caja.operativa.salio, mon)}</div>
                    </div>
                  </div>

{/* El desglose: el cajero tiene que poder cuadrar
                      el cajón contra estos números. */}
                  <div style={{marginBottom:10}}>
                    {[["Ventas de boletos",
                       caja.operativa.ventas.total,
                       `${caja.operativa.ventas.cantidad} boletos`, true],
                      ["Cargas de saldo",
                       caja.operativa.cargas?.total||0,
                       `${caja.operativa.cargas?.cantidad||0} cargas`, true],
                      ["Premios pagados",
                       -(caja.operativa.premios.total||0),
                       `${caja.operativa.premios.cantidad} pagados`, false],
                      ["Retiros de saldo",
                       -(caja.operativa.retiros?.total||0),
                       `${caja.operativa.retiros?.cantidad||0} retiros`, false],
                    ].filter(([,v])=>v!==0).map(([l,v,det])=>(
                      <div key={l} style={{display:"flex",
                        justifyContent:"space-between",alignItems:"baseline",
                        fontSize:12,padding:"4px 0",
                        borderTop:`1px solid ${Q.border}`}}>
                        <span style={{color:Q.muted,
                          fontFamily:F_BODY}}>
                          {l}
                          <span style={{color:Q.dim,fontSize:12,
                            marginLeft:6}}>{det}</span></span>
                        <span style={{color:v>=0?Q.green:Q.red,
                          fontFamily:F_BODY}}>
                          {v>=0?"+":"−"}{money(Math.abs(v), mon)}</span>
                      </div>
                    ))}
                  </div>

                  {caja.operativa.anulados.cantidad>0&&(
                    <div style={{color:Q.dim,fontSize:12,marginBottom:8,
                      lineHeight:1.45}}>
                      No se cuentan {caja.operativa.anulados.cantidad}{" "}
                      {caja.operativa.anulados.cantidad===1
                        ?"boleto anulado":"boletos anulados"} por{" "}
                      {money(caja.operativa.anulados.total, mon)}</div>
                  )}
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",paddingTop:SPACING[8],
                    borderTop:`1px solid ${Q.border}`}}>
                    <span style={{color:Q.text,fontWeight:700,fontSize:13,
                      fontFamily:F_BODY}}>
                      Debería haber en caja</span>
                    <span style={{
                      color:caja.operativa.en_caja>=0?Q.green:Q.red,
                      fontWeight:900,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(caja.operativa.en_caja, mon)}</span>
                  </div>
                </GCard>
              )}

              <GCard style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                  fontFamily:F_BODY}}>
                  Movimiento de caja · {caja.desde===caja.hasta?caja.desde
                    :`${caja.desde} a ${caja.hasta}`}</div>
                <div style={{display:"flex",gap:SPACING[12],marginBottom:12}}>
                  <div style={{flex:1}}>
                    <div style={{color:Q.muted,fontSize:12}}>Entró</div>
                    <div style={{color:Q.green,fontWeight:800,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(caja.total.cargas, mon)}</div>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{color:Q.muted,fontSize:12}}>Salió</div>
                    <div style={{color:Q.amber,fontWeight:800,fontSize:20,
                      fontFamily:F_BODY}}>
                      {money(caja.total.descargas, mon)}</div>
                  </div>
                </div>
                <div style={{borderTop:`1px solid ${Q.border}`,paddingTop:SPACING[12],
                  display:"flex",justifyContent:"space-between",
                  alignItems:"center"}}>
                  <span style={{color:Q.text,fontSize:13,fontWeight:700,
                    fontFamily:F_BODY}}>En caja</span>
                  <span style={{color:caja.total.neto_caja>=0?Q.green:Q.red,
                    fontWeight:900,fontSize:24,
                    fontFamily:F_BODY}}>
                    {money(caja.total.neto_caja, mon)}</span>
                </div>
                {caja.total.premios_pagados>0&&(
                  <div style={{color:Q.muted,fontSize:12,marginTop:6}}>
                    Premios pagados aparte: {money(caja.total.premios_pagados, mon)}</div>
                )}
              </GCard>

              <GCard style={{padding:SPACING[16],marginBottom:12}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"baseline"}}>
                  <div>
                    <div style={{color:Q.muted,fontSize:12,
                      fontFamily:F_BODY}}>
                      Saldo de los jugadores</div>
                    <div style={{color:Q.dim,fontSize:12}}>
                      {caja.total.clientes} clientes con cuenta</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{color:Q.cyan,fontWeight:900,fontSize:22,
                      fontFamily:F_BODY}}>
                      {money(caja.total.saldo_clientes, mon)}</div>
                    {caja.total.bono_clientes>0&&(
                      <div style={{color:Q.gold,fontSize:12}}>
                        + {money(caja.total.bono_clientes, mon)} en bonos</div>
                    )}
                  </div>
                </div>
                <div style={{color:Q.amber,fontSize:12,marginTop:10,
                  lineHeight:1.5,fontFamily:F_BODY}}>
                  Esta plata es de los jugadores, no ganancia de la agencia.
                  La utilidad está en el Resumen.</div>
              </GCard>

              {(caja.agencias||[]).length>1&&(
                <div style={{color:Q.muted,fontSize:12,marginBottom:6,
                  fontFamily:F_BODY}}>Por agencia</div>
              )}
              {(caja.agencias||[]).map(a=>(
                <GCard key={a.code} glow={a.es_mia?Q.cyan:null}
                  style={{padding:"12px 12px",marginBottom:7}}>
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:12.5,fontWeight:700,
                        fontFamily:F_BODY}}>
                        {"— ".repeat(Math.max(0,(a.nivel||0)-
                          ((caja.agencias[0]||{}).nivel||0)))}{a.name}
                        {a.es_mia&&<span style={{color:Q.cyan,fontSize:12}}> (vos)</span>}
                      </div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:1}}>
                        {a.code} · {a.clientes} clientes · {a.movimientos} movs</div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:a.neto_caja>=0?Q.green:Q.red,
                        fontWeight:800,fontSize:14,
                        fontFamily:F_BODY}}>
                        {money(a.neto_caja, mon)}</div>
                      <div style={{color:Q.dim,fontSize:12}}>
                        +{money(a.cargas, mon)} / −{money(a.descargas, mon)}</div>
                    </div>
                  </div>
                  {a.saldo_clientes>0&&(
                    <div style={{color:Q.cyan,fontSize:12,marginTop:5,
                      paddingTop:SPACING[4],borderTop:`1px solid ${Q.border}`}}>
                      Saldo de sus jugadores: {money(a.saldo_clientes, mon)}</div>
                  )}
                </GCard>
              ))}
            </>
          )}

          {caja&&!caja.error&&(caja.movimientos||[]).length>0&&(
            <>
              {/* A log read in time order, not navigated into. */}
              <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
                textTransform:"uppercase",marginTop:SPACING[24],marginBottom:SPACING[12],
                fontFamily:F_BODY}}>
                Detalle ({caja.movimientos.length})</div>
              {caja.movimientos.map((m,i,arr)=>{
                const esCarga=(m.tipo||"").startsWith("carga");
                return(
                  <div key={i} style={{display:"flex",
                    justifyContent:"space-between",alignItems:"center",
                    padding:`${SPACING[16]}px 0`,
                    borderBottom:i<arr.length-1?`1px solid ${Q.border}55`:"none",
                    gap:SPACING[12]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontSize:TEXT[15],
                        overflow:"hidden",textOverflow:"ellipsis",
                        whiteSpace:"nowrap"}}>{m.cliente}</div>
                      <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                        fontFamily:F_BODY}}>
                        {fechaHora(m.fecha)} · {m.agencia} · {m.tipo}</div>
                    </div>
                    <div style={{color:esCarga?Q.green:Q.amber,fontWeight:700,
                      fontSize:TEXT[16],flexShrink:0,
                      fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>
                      {esCarga?"+":"−"}{money(m.monto, mon)}</div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {vista==="resumen"&&res&&(
        <div>
          {/* El resumen ya fija los totales del período; la línea de
              tendencia usa el mismo desde/hasta elegido arriba para
              decir si ese período venía en subida o en baja. */}
          <GCard style={{padding:SPACING[16],marginBottom:12}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,marginBottom:2,fontFamily:F_BODY}}>Apostado deportivo por día</div>
            {/* El endpoint /serie suma solo deportivas — la cascada de
                casino/ruleta del total de arriba no es agrupable por día
                todavía. Sin esta nota, el número de la línea parecería
                un total equivocado frente al de arriba. */}
            <div style={{color:Q.dim,fontSize:12,marginBottom:8,
              fontFamily:F_BODY}}>Serie solo deportivas; el total de arriba incluye casino.</div>
            {cargSerie&&serie===null
              ? <div style={{color:Q.muted,fontSize:12,textAlign:"center",padding:SPACING[20],
                  fontFamily:F_BODY}}>Cargando tendencia...</div>
              : <LineaTiempo puntos={serie||[]} color={Q.cyan}
                  // Se pasa la FUNCIÓN, no el resultado: el gráfico la
                  // llama por punto. Por eso hace falta el envoltorio,
                  // para que la moneda viaje con ella.
                  formato={(v)=>money(v, mon)}
                  etiqueta="Apostado deportivo por día"/>}
          </GCard>

          {/* Resumen consolidado de la rama */}
          <GCard style={{padding:SPACING[16],marginBottom:12,
            background:`linear-gradient(135deg,${Q.green}12,${Q.violet}06)`}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
              letterSpacing:1,marginBottom:10,
              fontFamily:F_BODY}}>
              {res.es_cliente?`Rentabilidad · ${res.cliente_nombre}`:`Total de mi rama (${res.moneda})`}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:SPACING[12]}}>
              {[["Apostado",res.total.apostado,Q.cyan],
                ["Premios",res.total.premios,Q.pink],
                ["GGR",res.total.ggr,Q.green],
                ["Comisiones",res.total.comisiones,Q.amber]].map(([l,v,c])=>(
                <div key={l}>
                  <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  <div style={{color:c,fontWeight:800,fontSize:17,
                    fontFamily:F_BODY}}>{money(v, monRes(res))}</div>
                </div>
              ))}
            </div>
            {!res.es_cliente&&res.total.neto_sin_influencers!=null&&(
              <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>Neto (sin influencers)</span>
                  <span style={{color:res.total.neto_sin_influencers>=0?Q.green:Q.red,
                    fontWeight:800,fontSize:18,
                    fontFamily:F_BODY}}>{money(res.total.neto_sin_influencers, monRes(res))}</span>
                </div>
                {res.total.comision_influencers>0&&(
                  <div style={{display:"flex",justifyContent:"space-between",
                    alignItems:"center",marginTop:6}}>
                    <span style={{color:Q.muted,fontSize:12,
                      fontFamily:F_BODY}}><Star size={12}/> Comisión influencers</span>
                    <span style={{color:Q.gold,fontWeight:700,fontSize:13,
                      fontFamily:F_BODY}}>−{money(res.total.comision_influencers, monRes(res))}</span>
                  </div>
                )}
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",marginTop:8,paddingTop:SPACING[8],
                  borderTop:`1px solid ${Q.dim}`}}>
                  <span style={{color:Q.text,fontWeight:700,fontSize:13,
                    fontFamily:F_BODY}}>Neto final</span>
                  <span style={{color:res.total.neto_con_influencers>=0?Q.green:Q.red,
                    fontWeight:900,fontSize:20,
                    fontFamily:F_BODY}}>{money(res.total.neto_con_influencers, monRes(res))}</span>
                </div>
              </div>
            )}
          </GCard>

          {/* Casino y ruleta de tu red: se liquidan aparte, con su
              propia comisión sobre GGR. */}
          {res.casino&&(res.casino.apostado>0||res.casino.ggr!==0)&&(
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.gold,fontWeight:700,fontSize:12.5,
                marginBottom:8,fontFamily:F_BODY}}>
                <Icon name="spade" size={13}/> Casino y ruleta</div>
              {[["Apostado",res.casino.apostado,Q.text],
                ["Premios",res.casino.premios,Q.muted],
                ["GGR",res.casino.ggr,res.casino.ggr>=0?Q.green:Q.red],
                ["Tu comisión",res.casino.comisiones,Q.cyan]].map(([l,v,c])=>(
                <div key={l} style={{display:"flex",
                  justifyContent:"space-between",fontSize:12,padding:"4px 0"}}>
                  <span style={{color:Q.muted}}>{l}</span>
                  <span style={{color:c,fontWeight:600,
                    fontFamily:F_BODY}}>{money(v, monRes(res))}</span>
                </div>
              ))}
            </GCard>
          )}

          {/* Lo que te queda de todo: deportivas más casino */}
          {res.total_general&&(
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,letterSpacing:0.8,
                marginBottom:8}}>TODO EL PERÍODO</div>
              <div style={{display:"flex",gap:SPACING[12],marginBottom:10}}>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>Apostado</div>
                  <div style={{color:Q.text,fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {money(res.total_general.apostado, monRes(res))}</div>
                </div>
                <div style={{flex:1}}>
                  <div style={{color:Q.muted,fontSize:12}}>GGR</div>
                  <div style={{color:res.total_general.ggr>=0?Q.green:Q.red,
                    fontWeight:800,fontSize:16,
                    fontFamily:F_BODY}}>
                    {money(res.total_general.ggr, monRes(res))}</div>
                </div>
              </div>
              {/* Lo que vos pusiste en bonos: resta de tu comisión */}
              {res.total_general.bonos>0&&(
                <div style={{display:"flex",justifyContent:"space-between",
                  fontSize:12,padding:"4px 0",marginBottom:4,
                  paddingBottom:SPACING[8],borderBottom:`1px solid ${Q.border}`}}>
                  <span style={{color:Q.muted}}>Bono sports tu parte</span>
                  <span style={{color:Q.red,
                    fontFamily:F_BODY}}>
                    −{money(res.total_general.bonos, monRes(res))}</span>
                </div>
              )}

              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",paddingTop:SPACING[8],
                borderTop:`1px solid ${Q.border}`}}>
                <span style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  Tu comisión total</span>
                <span style={{color:Q.green,fontWeight:900,fontSize:22,
                  fontFamily:F_BODY}}>
                  {money(res.total_general.mi_comision, monRes(res))}</span>
              </div>
            </GCard>
          )}

          {res.es_cliente&&res.rendimiento&&(
            <div style={{display:"flex",gap:SPACING[8],marginBottom:12,fontSize:12,
              fontFamily:F_BODY}}>
              <span style={{color:Q.green}}>{res.rendimiento.ganadas} ganadas</span>
              <span style={{color:Q.red}}>{res.rendimiento.perdidas} perdidas</span>
              <span style={{color:Q.amber}}>{res.rendimiento.pendientes} pendientes</span>
            </div>
          )}

          {/* Desglose por agencia de la rama (solo si no es filtro de cliente) */}
          {!res.es_cliente&&<div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",
            letterSpacing:1,marginBottom:8,marginLeft:4,
            fontFamily:F_BODY}}>Desglose</div>}
          {res.agencias.map(a=>(
            <GCard key={a.code} glow={a.es_mia?Q.violet:undefined}
              style={{padding:"12px 12px",marginBottom:6,
                marginLeft:(a.nivel||0)*12}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",marginBottom:6}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  {a.es_mia?<><Star size={12}/> </>:""}{a.name}</div>
                <div style={{color:Q.gold,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>Com {money(a.comision, mon)}</div>
              </div>
              <div style={{display:"flex",gap:SPACING[12],fontSize:12,
                fontFamily:F_BODY}}>
                <span style={{color:Q.muted}}>Apostado <span style={{color:Q.cyan}}>{money(a.apostado, mon)}</span></span>
                <span style={{color:Q.muted}}>Premios <span style={{color:Q.pink}}>{money(a.premios, mon)}</span></span>
                <span style={{color:Q.muted}}>GGR <span style={{color:a.ggr>=0?Q.green:Q.red}}>{money(a.ggr, mon)}</span></span>
              </div>
            </GCard>
          ))}
        </div>
      )}

      {vista==="digital"&&<PSPAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}

      {/* Movs, apuestas, combos and impresiones are all logs of what
          already happened in the period, read in time order and not
          navigated elsewhere — apuestas expands inline on click, it
          does not open a new screen — so all four get the row
          standard: off the GCard, hairline separators, bigger type. */}
      {vista==="movs"&&<>
        {movs===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {movs&&movs.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
          padding:SPACING[20],fontFamily:F_BODY}}>Sin movimientos</div>}
        {(movs||[]).map((m,i,arr)=>{
          const tipoTxt={carga_admin:"Carga admin",retiro_admin:"Descuento admin",
            carga_cliente:"Crédito a cliente",retiro_cliente:"Retiro cliente",
            pago_premio:"Premio pagado",transferencia:"A sub-agencia",recibido:"Recibido",
            carga:"Carga cliente",retiro:"Retiro cliente"}[m.tipo]||m.tipo;
          const pos=m.monto>=0;
          return(
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
              borderBottom:i<arr.length-1?`1px solid ${Q.border}55`:"none"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                  fontFamily:F_BODY}}>{tipoTxt}
                  {m.origen==="cliente"&&m.cliente?<span style={{color:Q.cyan}}> · {m.cliente}</span>:null}</div>
                <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                  fontFamily:F_BODY}}>{m.agencia} · {fechaHora(m.fecha)}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{color:pos?Q.green:Q.amber,fontWeight:700,fontSize:TEXT[16],
                  fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{pos?"+":""}{money(m.monto, mon)}</div>
                {m.saldo!=null&&<div style={{color:Q.dim,fontSize:12}}>saldo {money(m.saldo, mon)}</div>}
              </div>
            </div>
          );
        })}
      </>}

      {vista==="apuestas"&&<>
        {apuestas===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {apuestas&&apuestas.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
          padding:SPACING[20],fontFamily:F_BODY}}>Sin apuestas</div>}
        {(apuestas||[]).map((a,i,arr)=>{
          const col={won:Q.green,ganada:Q.green,lost:Q.red,perdida:Q.red,
            pending:Q.amber,pendiente:Q.amber,active:Q.cyan,paid:Q.green}[(a.status||"").toLowerCase()]||Q.muted;
          const abierta=apAbierta===a.code;
          return(
            <div key={i} style={{padding:`${SPACING[16]}px 0`,cursor:"pointer",
              borderBottom:i<arr.length-1?`1px solid ${Q.border}55`:"none"}}
              onClick={()=>setApAbierta(abierta?null:a.code)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[12]}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                    fontFamily:F_BODY}}>{a.code}
                    <span style={{color:Q.cyan,fontWeight:400}}> · {a.cliente}</span></div>
                  <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                    fontFamily:F_BODY}}>{a.agencia} · {fechaHora(a.fecha)} · {a.picks.length} sel.</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:col,fontWeight:700,fontSize:TEXT[13],
                    fontFamily:F_BODY}}>{a.status}</div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>{money(a.stake, mon)} · {fmt(a.odd)}x</div>
                </div>
              </div>
              {abierta&&(
                <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8,
                    fontSize:12,fontFamily:F_BODY}}>
                    <span style={{color:Q.muted}}>Monto <span style={{color:Q.cyan}}>{money(a.stake, mon)}</span></span>
                    <span style={{color:Q.muted}}>Cuota <span style={{color:Q.violet2}}>{fmt(a.odd)}x</span></span>
                    <span style={{color:Q.muted}}>Premio <span style={{color:Q.green}}>{money(a.premio, mon)}</span></span>
                  </div>
                  {a.picks.map((p,j)=>(
                    <div key={j} style={{background:"rgba(255,255,255,0.03)",
                      borderRadius:RADII.md,padding:"8px 12px",marginBottom:5}}>
                      <div style={{color:Q.text,fontSize:12,fontWeight:600,
                        fontFamily:F_BODY}}>
                        {p.home||p.equipo_local||""}{(p.home||p.away)?" vs ":""}{p.away||p.equipo_visitante||""}</div>
                      <div style={{color:Q.muted,fontSize:12,marginTop:2}}>
                        {p.market||p.mercado||p.tipo||"—"}: <span style={{color:Q.cyan}}>
                        {p.selection||p.seleccion||p.pick||"—"}</span>
                        {p.odd||p.cuota?<span style={{color:Q.violet2}}> @ {fmt(p.odd||p.cuota)}</span>:null}</div>
                    </div>
                  ))}
                  {a.picks.length===0&&<div style={{color:Q.muted,fontSize:12,
                    fontFamily:F_BODY}}>Sin detalle</div>}
                </div>
              )}
            </div>
          );
        })}
      </>}

      {vista==="combos"&&<>
        {combos===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {combos&&combos.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
          padding:SPACING[20],fontFamily:F_BODY}}>Sin combos en el período</div>}
        {(combos||[]).map((c,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",
            alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[8],
            borderBottom:i<combos.length-1?`1px solid ${Q.border}55`:"none"}}>
            <div style={{minWidth:0,flex:1}}>
              <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                fontFamily:F_BODY}}>{c.nombre||"Combo"}
                {c.es_ia
                  ?<span style={{background:`${Q.violet}33`,border:`1px solid ${Q.violet}`,
                      borderRadius:RADII.lg,padding:"4px 8px",fontSize:12,fontWeight:700,
                      color:Q.violet2,marginLeft:6}}><Bot size={12}/> IA</span>
                  :<span style={{background:`${Q.cyan}22`,border:`1px solid ${Q.cyan}`,
                      borderRadius:RADII.lg,padding:"4px 8px",fontSize:12,fontWeight:700,
                      color:Q.cyan,marginLeft:6}}><PenLine size={12}/> Manual</span>}
                {!c.visible&&<span style={{color:Q.muted,fontSize:12,marginLeft:5}}>oculto</span>}
              </div>
              <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                fontFamily:F_BODY}}>{c.creado_por||c.origen} · {fechaHora(c.fecha)}</div>
            </div>
            <div style={{color:Q.green,fontWeight:700,fontSize:TEXT[16],flexShrink:0,
              fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{fmt(c.odd)}x</div>
          </div>
        ))}
      </>}

      {vista==="impresiones"&&<>
        {impresiones===null&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {impresiones&&impresiones.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
          padding:SPACING[20],fontFamily:F_BODY}}>Sin impresiones en el período</div>}
        {(impresiones||[]).map((im,i)=>{
          const tipoTxt={ticket:"Ticket",apuesta:"Apuesta",cobro:"Cobro",cierre:"Cierre de caja",combo:"Combo"}[im.tipo]||im.tipo;
          return(
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
              borderBottom:i<impresiones.length-1?`1px solid ${Q.border}55`:"none"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                  fontFamily:F_BODY}}>{tipoTxt}
                  {im.referencia?<span style={{color:Q.cyan,fontWeight:400}}> · {im.referencia}</span>:null}</div>
                <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                  fontFamily:F_BODY}}>{im.agencia} · por {im.quien} · {fechaHora(im.fecha)}</div>
              </div>
              <Printer size={16}/>
            </div>
          );
        })}
      </>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CONFIG
// ═══════════════════════════════════════════════════════════════
function Config({ agencia }){
  const [testDone,setTestDone]=useState(false);
  return(
    <div>
      <PageHeader icon={<Icon name="sliders-horizontal"/>} title="Config"
        description="Los datos de tu agencia, tu contraseña, Telegram y el test de impresora."/>
      {/* Auto-fit two-up on desktop, one column below the breakpoint —
          no separate desktop check needed, the min column width alone
          decides how many fit. None of these four cards is tall or
          form-heavy enough to need the full row to itself: the password
          form is only two inputs and a button, same footprint as the
          other three.
          `min(360px,100%)` instead of a bare 360px: the content column
          is only maxWidth:620 minus 24px of padding on a phone, which is
          narrower than 360px on most handsets (a 375px iPhone leaves
          351px) — a bare minmax(360px,1fr) track does not shrink below
          its floor just because the container is narrower, so it forced
          the grid wider than the viewport and produced exactly the
          horizontal scroll odd/tasks/mobile-nav.md's mobile pass was
          checking for. Capping the floor at the container's own 100%
          removes that overflow and is identical to the plain 360px on
          desktop, where the column is always wider than that. */}
      <div style={{display:"grid",
        gridTemplateColumns:"repeat(auto-fit,minmax(min(360px,100%),1fr))",
        gap:SPACING[16]}}>
        <GCard style={{padding:SPACING[20]}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:14,
            fontFamily:F_BODY}}>Datos de la agencia</div>
          {[["Nombre",agencia.name||"—"],["Código",agencia.code||"—"],
            ["Dirección",agencia.address||"Sin cargar"],
            ["Teléfono",agencia.phone||"Sin cargar"],
          ].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",
              padding:"8px 0",borderBottom:`1px solid ${Q.dim}`}}>
              <span style={{color:Q.muted,fontSize:12}}>{l}</span>
              <span style={{color:Q.text,fontSize:12}}>{v}</span>
            </div>
          ))}
        </GCard>

        <CambiarMiPassword agencia={agencia}/>

        <ConectarTelegramAgencia agencia={agencia}/>

        <GCard style={{padding:SPACING[20]}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:8,
            fontFamily:F_BODY}}>Test de impresora</div>
          <div style={{color:Q.muted,fontSize:12,marginBottom:14}}>
            Imprime un ticket de prueba para verificar la conexión
          </div>
          <Btn label={testDone?"Reimprimir test":"IMPRIMIR TICKET TEST"}
            onClick={()=>{
              const ok = printTicket({
                code:"QP-TEST",user:"Test",created_at:nowStr(),expires_at:expires24(),
                picks:[{home:"River",away:"Boca",sel:"River gana",odd:1.55,sport:"TEST"}],
                stake:10000,odd_total:1.55,potential_win:15500,agencia:agencia.code,
                moneda:agencia?.moneda,
              },"apuesta");
              if(ok) setTestDone(true);
            }} color={Q.violet} full/>
        </GCard>
      </div>
    </div>
  );
}

function ConectarTelegramAgencia({ agencia }){
  const [link,setLink]=useState(null);
  const [proc,setProc]=useState(false);
  const [copiado,setCopiado]=useState(false);

  const conectar=async()=>{
    setProc(true);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/vincular-telegram`,{
        method:"POST",headers:authHeaders(agencia.token)});
      if(r.ok){ const d=await r.json(); setLink(d); }
    }catch(e){}
    setProc(false);
  };

  return(
    <GCard style={{padding:SPACING[20],marginBottom:12}}>
      <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:6,
        fontFamily:F_BODY}}><Smartphone size={14}/> Conectar Telegram</div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,lineHeight:1.5,
        fontFamily:F_BODY}}>
        Conectá tu Telegram para recibir los avisos de la agencia
        (apuestas, premios, cargas). Abrí el link desde el celu con tu Telegram.</div>
      <Btn label={proc?"...":"Generar link de conexión"} onClick={conectar}
        color={Q.cyan} full disabled={proc}/>
      {link&&(
        <div style={{marginTop:10,display:"flex",gap:SPACING[8]}}>
          <input readOnly value={link.link} style={{flex:1,minWidth:0,
            background:"rgba(0,0,0,0.3)",border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"8px 12px",color:Q.cyan,fontSize:12,
            fontFamily:F_BODY}}/>
          <button onClick={()=>{try{navigator.clipboard.writeText(link.link);
            setCopiado(true);setTimeout(()=>setCopiado(false),1500);}catch(e){}}}
            style={{background:`${Q.cyan}22`,border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,
            padding:"8px 12px",cursor:"pointer",color:Q.cyan,fontSize:12,fontWeight:700,
            fontFamily:F_BODY}}>{copiado?"✓":"Copiar"}</button>
        </div>
      )}
    </GCard>
  );
}

function CambiarMiPassword({ agencia, obligado=false, onListo }){
  const [nueva,setNueva]=useState("");
  const [repetir,setRepetir]=useState("");
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null

  const guardar=async()=>{
    if(nueva.length<8){ setMsg({text:"La contraseña debe tener 8+ caracteres", ok:false}); return; }
    if(nueva!==repetir){ setMsg({text:"Las contraseñas no coinciden", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/password`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({nueva}),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Contraseña actualizada", ok:true});
      setNueva(""); setRepetir("");
      if(onListo) setTimeout(onListo,800);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  return(
    <GCard glow={obligado?Q.amber:null} style={{padding:SPACING[20],marginBottom:12}}>
      <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:8,
        fontFamily:F_BODY}}>
        {obligado?<><Key size={14}/> Cambiá tu contraseña para continuar</>:<><Key size={14}/> Cambiar mi contraseña</>}</div>
      {obligado&&<div style={{color:Q.amber,fontSize:12,marginBottom:12,lineHeight:1.4,
        fontFamily:F_BODY}}>
        Es tu primer ingreso. Por seguridad, elegí una contraseña nueva.</div>}
      <input type="password" value={nueva} onChange={e=>setNueva(e.target.value)}
        placeholder="Nueva contraseña (8+)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <input type="password" value={repetir} onChange={e=>setRepetir(e.target.value)}
        placeholder="Repetir contraseña"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:12,
          fontFamily:F_BODY}}/>
      <Btn label={proc?"GUARDANDO...":"Guardar contraseña"} onClick={guardar}
        color={obligado?Q.amber:Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </GCard>
  );
}

// ═══════════════════════════════════════════════════════════════
// EN VIVO
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ACÁ VIVÍA `EnVivo`, LA PANTALLA DE VENTA EN VIVO DEL MOSTRADOR.
// ═══════════════════════════════════════════════════════════════
// 478 líneas, y TODAS colgaban de un solo `fetch`: `/api/live/combined`, la
// pantalla en vivo de la cadena vieja (The Odds API + RapidAPI + Sportradar).
// Sin esa fuente no quedaba una pantalla con menos datos: quedaba un `useState`
// vacío, un `setInterval` cada 30 segundos pidiendo un 404 y el cartel "No hay
// partidos en vivo ahora" fijo. Así que se fue entera, con su carrito, su
// confirmación y su filtro de liga, en vez de quedar de cáscara.
//
// POR QUÉ SE PUEDE: medido contra staging el 2026-10-06, esa ruta devolvía
// `{"matches": [], "count": 0}` mientras la del catálogo de GR8 traía 209
// eventos, 122 con `etapa: "Live"`. O sea que el cajero ya no podía vender en
// vivo: la pantalla existía y la lista llegaba vacía.
//
// LO QUE FALTA, Y ES TRABAJO DE PRODUCTO: devolverle al mostrador la venta en
// vivo leyendo el catálogo de GR8 filtrado por `etapa`, que es exactamente la
// migración que ya se le hizo a la pantalla del jugador (`App.jsx`,
// `ScreenLive`) y a /sitio (`Web.jsx`). Lo que GR8 no manda es el MARCADOR ni
// el minuto: eso llega por la cola `scores` y todavía no tiene intérprete de
// nuestro lado. La tarjeta que se borró mostraba un marcador grande; la del
// jugador, migrada, ya no lo muestra, y por el mismo motivo.
//
// Mientras no exista, la pestaña "En Vivo" del panel tampoco está: una entrada
// de menú que abre una pantalla vacía es peor que no tenerla.


// ═══════════════════════════════════════════════════════════════
// PANEL PRINCIPAL
// ═══════════════════════════════════════════════════════════════
function InfluencersAgencia({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const hoy=new Date().toISOString().slice(0,10);
  const primero=new Date(); primero.setDate(1);
  const [desde,setDesde]=useState(primero.toISOString().slice(0,10));
  const [hasta,setHasta]=useState(hoy);
  const [data,setData]=useState(null);
  const [costos,setCostos]=useState(null);
  const [escaneos,setEscaneos]=useState(null);
  const [showForm,setShowForm]=useState(false);
  const [sel,setSel]=useState(null);
  const [vista,setVista]=useState("mios");   // mios | costos | escaneos

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/influencers/reporte?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setData(await r.json());
    }catch(e){}
  };
  const cargarCostos=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/influencers/costos?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setCostos(await r.json());
    }catch(e){}
  };
  const cargarEscaneos=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/influencers/escaneos?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setEscaneos(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar();
    if(vista==="costos") cargarCostos();
    if(vista==="escaneos") cargarEscaneos();
  // eslint-disable-next-line
  },[desde,hasta,vista]);

  return(
    <div>
      <PageHeader icon={<Star/>} title="Influencers"
        description="Los códigos de influencer que generan tus ventas, su costo y sus escaneos."
        action={<Btn label={showForm?"Cancelar":"+ Nuevo"} onClick={()=>setShowForm(s=>!s)}
          color={Q.violet} size="sm"/>}/>

      {showForm&&<CrearInfluencerAgencia agencia={agencia}
        onListo={()=>{setShowForm(false);cargar();}} onSesionExpirada={onSesionExpirada}/>}

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["mios",<><Star size={13}/> Míos</>],["costos",<><Banknote size={13}/> Cuestan</>],["escaneos",<><Icon name="scan-line" size={13}/> Escaneos</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setVista(k)} style={{flex:1,
            background:vista===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${vista===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"8px",cursor:"pointer",color:vista===k?Q.cyan:Q.muted,
            fontSize:12,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
      </div>

      {vista==="mios"&&<>
        {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {data&&(
          <div>
            <GCard style={{padding:SPACING[16],marginBottom:12,
              background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}06)`}}>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8]}}>
                {[["Ventas",data.total.ventas,Q.cyan],["GGR",data.total.ggr,Q.green],
                  ["Comisiones",data.total.comisiones,Q.gold]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{color:c,fontWeight:800,fontSize:15,
                      fontFamily:F_BODY}}>{money(v, mon)}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  </div>
                ))}
              </div>
            </GCard>
            {data.influencers.length===0&&<div style={{color:Q.muted,fontSize:12,
              textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
              No tenés influencers todavía</div>}
            {/* Navigates into DetalleInfluencerAgencia on click — an
                entity list, same criterion as TabUsuarios and Agencias
                hoy — so it gets the auto-fit card grid, not stacked rows. */}
            <div style={{display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
              gap:SPACING[12]}}>
              {data.influencers.map(inf=>(
                <GCard key={inf.code} onClick={()=>setSel(inf.code)}
                  style={{padding:SPACING[12],cursor:"pointer"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{color:Q.text,fontWeight:700,fontSize:13,
                        fontFamily:F_BODY}}>{inf.name}
                        <span style={{color:Q.muted,fontWeight:400,fontSize:12}}> · {inf.codigo_ref}</span></div>
                      <div style={{color:Q.muted,fontSize:12}}>
                        {inf.combos} combos · {inf.jugadas} jugadas</div>
                      {inf.parent_name&&<div style={{color:Q.violet2,fontSize:12,marginTop:1}}>
                        <Building2 size={11}/> {inf.parent_name}</div>}
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{color:Q.gold,fontWeight:700,fontSize:14,
                        fontFamily:F_BODY}}>{money(inf.comision, mon)}</div>
                      <div style={{color:Q.muted,fontSize:12}}>comisión ›</div>
                    </div>
                  </div>
                </GCard>
              ))}
            </div>
          </div>
        )}
      </>}

      {vista==="costos"&&<>
        {!costos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {costos&&(
          <div>
            <GCard style={{padding:SPACING[16],marginBottom:12,textAlign:"center"}}>
              <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
                marginBottom:4,fontFamily:F_BODY}}>
                Comisiones que absorbés</div>
              <div style={{color:Q.gold,fontWeight:900,fontSize:26,
                fontFamily:F_BODY}}>{money(costos.total_comision, mon)}</div>
              <div style={{color:Q.dim,fontSize:12,marginTop:4,
                fontFamily:F_BODY}}>
                Por jugadas con código de influencer en tu caja</div>
            </GCard>
            {costos.detalle.length===0&&<div style={{color:Q.muted,fontSize:12,
              textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
              Sin jugadas con influencer en el período</div>}
            {/* A log read in time order, not navigated into. */}
            {costos.detalle.map((d,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
                borderBottom:i<costos.detalle.length-1?`1px solid ${Q.border}55`:"none"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                    fontFamily:F_BODY}}>{d.influencer}</div>
                  <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                    fontFamily:F_BODY}}>
                    en {d.agencia} · {d.jugadas} jugadas · {money(d.apostado, mon)}</div>
                </div>
                <div style={{color:Q.gold,fontWeight:700,fontSize:TEXT[16],flexShrink:0,
                  fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>−{money(d.comision, mon)}</div>
              </div>
            ))}
          </div>
        )}
      </>}

      {vista==="escaneos"&&<>
        {!escaneos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {escaneos&&(
          <div>
            {/* Auto-fit so each stat keeps a sane width instead of
                squeezing three into one row on a narrow phone. */}
            <div style={{display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",
              gap:SPACING[12],marginBottom:12}}>
              {[["Escaneos",escaneos.total_escaneos,Q.cyan],["Jugaron",escaneos.total_jugadas,Q.green],
                ["Conversión",escaneos.conversion+"%",Q.gold]].map(([l,v,c])=>(
                <GCard key={l} style={{padding:"16px 8px",textAlign:"center"}}>
                  <div style={{color:c,fontWeight:800,fontSize:18,
                    fontFamily:F_BODY}}>{v}</div>
                  <div style={{color:Q.muted,fontSize:12,marginTop:2}}>{l}</div>
                </GCard>
              ))}
            </div>
            {escaneos.detalle.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
              padding:SPACING[20],fontFamily:F_BODY}}>Sin escaneos en el período</div>}
            {/* A log read in time order, not navigated into. */}
            {escaneos.detalle.map((d,i)=>(
              <div key={d.code} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
                borderBottom:i<escaneos.detalle.length-1?`1px solid ${Q.border}55`:"none"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:600,fontSize:TEXT[15],
                    fontFamily:F_BODY}}>{d.name}
                    <span style={{color:Q.muted,fontWeight:400,fontSize:12}}> · {d.codigo_ref}</span></div>
                  <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                    fontFamily:F_BODY}}>
                    {d.escaneos} escaneos · {d.jugadas} jugaron · cuota {fmt(d.cuota_prom)}x</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{color:d.conversion>=30?Q.green:d.conversion>=10?Q.gold:Q.muted,
                    fontWeight:700,fontSize:TEXT[16],fontFamily:F_MONO,
                    fontVariantNumeric:"tabular-nums"}}>{d.conversion}%</div>
                  <div style={{color:Q.muted,fontSize:12}}>conversión</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>}

      {sel&&<DetalleInfluencerAgencia code={sel} agencia={agencia} desde={desde} hasta={hasta}
        onCerrar={()=>setSel(null)} onSesionExpirada={onSesionExpirada}/>}
    </div>
  );
}

function CrearInfluencerAgencia({ agencia, onListo, onSesionExpirada }){
  const [form,setForm]=useState({name:"",username:"",password:"",pct_ggr:"",pct_ventas:"",alcance:""});
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null

  const crear=async()=>{
    if(!form.name||!form.username||!form.password){ setMsg({text:"Completá nombre, usuario y clave", ok:false}); return; }
    if(form.password.length<8){ setMsg({text:"La clave debe tener 8+ caracteres", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/influencers`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(form),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setMsg({text:`Creado: ${d.code} · código ${d.codigo_ref}`, ok:true});
      setTimeout(onListo,900);
    }catch(e){ setMsg({text:e.message, ok:false}); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[16],marginBottom:14}}>
      <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:12,
        fontFamily:F_BODY}}><Star size={14}/> Nuevo influencer</div>
      {[["name","Nombre"],["username","Usuario"],["password","Contraseña (8+)"]].map(([k,l])=>(
        <input key={k} value={form[k]} type={k==="password"?"password":"text"}
          onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={l}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
      ))}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:8}}>
        <input value={form.pct_ggr} onChange={e=>setForm(f=>({...f,pct_ggr:e.target.value}))}
          placeholder="% GGR" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
        <input value={form.pct_ventas} onChange={e=>setForm(f=>({...f,pct_ventas:e.target.value}))}
          placeholder="% Ventas" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
      </div>
      <select value={form.alcance} onChange={e=>setForm(f=>({...f,alcance:e.target.value}))}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"12px 12px",color:form.alcance?Q.text:Q.muted,fontSize:15,marginBottom:10,
          fontFamily:F_BODY}}>
        <option value="">Dónde valen sus links (por defecto: solo mi agencia)</option>
        <option value="solo_agencia">Solo mi agencia</option>
        <option value="rama">Toda mi rama</option>
        <option value="global">Cualquier lado + Telegram</option>
      </select>
      <Btn label={proc?"CREANDO...":"Crear influencer"} onClick={crear} color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </GCard>
  );
}

function DetalleInfluencerAgencia({ code, agencia, desde, hasta, onCerrar, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [d,setD]=useState(null);
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null
  const [resetOpen,setResetOpen]=useState(false);
  const [configOpen,setConfigOpen]=useState(false);

  useEffect(()=>{
    fetch(`${API_URL}/api/influencers/${code}/detalle?desde=${desde}&hasta=${hasta}`,
      {headers:authHeaders(agencia.token)})
      .then(r=>{ if(r.status===401){ onSesionExpirada(); return null; } return r.json(); })
      .then(x=>x&&setD(x)).catch(()=>{});
  // eslint-disable-next-line
  },[code]);

  const liquidar=async()=>{
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/influencers/${code}/liquidar`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({desde,hasta}),
      });
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const j=await r.json();
      setMsg({text:`Liquidación generada · ${money(j.comision, mon)}`, ok:true});
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const rep=d&&d.reporte;
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:210,
      display:"flex",alignItems:"flex-end",justifyContent:"center"}} onClick={onCerrar}>
      <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
        borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
        maxHeight:"90vh",overflowY:"auto",padding:SPACING[20],border:`1px solid ${Q.border}`,borderBottom:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Star size={15}/> {rep?rep.name:code}</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {!d&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
          fontFamily:F_BODY}}>Cargando...</div>}
        {d&&(
          <div>
            <GCard style={{padding:SPACING[16],marginBottom:12}}>
              <div style={{color:Q.muted,fontSize:12,marginBottom:4,
                fontFamily:F_BODY}}>
                Usuario: <span style={{color:Q.cyan,fontWeight:700}}>{rep.username||"—"}</span></div>
              <div style={{color:Q.muted,fontSize:12,marginBottom:8,
                fontFamily:F_BODY}}>Código: {rep.codigo_ref}</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:12}}>
                {[["Ventas",rep.apostado,Q.cyan],["GGR",rep.ggr,Q.green],
                  ["Comisión",rep.comision,Q.gold]].map(([l,v,c])=>(
                  <div key={l} style={{textAlign:"center"}}>
                    <div style={{color:c,fontWeight:800,fontSize:15,
                      fontFamily:F_BODY}}>{money(v, mon)}</div>
                    <div style={{color:Q.muted,fontSize:12}}>{l}</div>
                  </div>
                ))}
              </div>
              {/* Settling, resetting and configuring are independent one-tap actions on this account, so they share a line */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
                <div>
                  <Btn label={proc?"...":<><Icon name="wallet-cards" size={13}/> Liquidar comisión</>} onClick={liquidar}
                    color={Q.gold} full disabled={proc}/>
                </div>
                <div>
                  <Btn label={<><Key size={13}/> Resetear contraseña</>} onClick={()=>setResetOpen(true)}
                    color={Q.amber} full/>
                </div>
                <div>
                  <Btn label={<><Icon name="sliders-horizontal" size={13}/> Configurar influencer</>} onClick={()=>setConfigOpen(v=>!v)}
                    color={Q.violet} full/>
                </div>
              </div>
              {configOpen&&rep&&(
                <div style={{marginTop:12,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                  <ConfigurarCuentaAg agencia={agencia}
                    cuenta={{code, tipo:"influencer", name:rep.name, username:rep.username,
                      pct_ggr:rep.pct_ggr, pct_ventas:rep.pct_ventas, alcance:rep.alcance}}
                    onCambio={()=>setConfigOpen(false)} onSesionExpirada={onSesionExpirada}/>
                </div>
              )}
              {msg&&<div style={{fontSize:12,marginTop:8,
                color:msg.ok?Q.green:Q.red,
                fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
              {resetOpen&&<ResetPassword agencia={agencia} code={code}
                nombre={rep?rep.name:code} onCerrar={()=>setResetOpen(false)}/>}
            </GCard>
            {/* Combos and Jugadas are logs of what already happened, read
                in time order and not navigated into — the row standard,
                off the GCard, applies to both. */}
            <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
              textTransform:"uppercase",marginBottom:SPACING[12],
              fontFamily:F_BODY}}>Combos ({d.combos.length})</div>
            {d.combos.map((c,i)=>(
              <div key={c.id} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
                borderBottom:i<d.combos.length-1?`1px solid ${Q.border}55`:"none"}}>
                <div style={{color:Q.text,fontSize:TEXT[15],fontWeight:600,
                  fontFamily:F_BODY}}>{c.nombre}
                  {c.codigo&&<span style={{color:Q.cyan,fontSize:12}}> · {c.codigo}</span>}</div>
                <div style={{color:Q.green,fontWeight:700,fontSize:TEXT[16],flexShrink:0,
                  fontFamily:F_MONO,fontVariantNumeric:"tabular-nums"}}>{fmt(c.odd)}x</div>
              </div>
            ))}
            <div style={{color:Q.muted,fontSize:TEXT[12],letterSpacing:0.6,
              textTransform:"uppercase",marginTop:SPACING[24],marginBottom:SPACING[12],
              fontFamily:F_BODY}}>Jugadas ({d.jugadas.length})</div>
            {d.jugadas.length===0&&<div style={{color:Q.muted,fontSize:12,
              fontFamily:F_BODY}}>Sin jugadas en el período</div>}
            {d.jugadas.map((j,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",
                alignItems:"center",padding:`${SPACING[16]}px 0`,gap:SPACING[12],
                borderBottom:i<d.jugadas.length-1?`1px solid ${Q.border}55`:"none"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontSize:TEXT[15],fontWeight:600,
                    fontFamily:F_BODY}}>{j.code}</div>
                  <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
                    fontFamily:F_BODY}}>{j.cliente} · {money(j.stake, mon)} · {fmt(j.odd)}x</div>
                </div>
                <span style={{color:Q.muted,fontSize:TEXT[13],flexShrink:0}}>{j.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigurarCuentaAg({ agencia, cuenta, onCambio, onSesionExpirada }){
  const esInf = cuenta.tipo==="influencer" || String(cuenta.code||"").startsWith("INF");
  const [name,setName]=useState(cuenta.name||"");
  const [address,setAddress]=useState(cuenta.address||"");
  const [phone,setPhone]=useState(cuenta.phone||"");
  const [ggr,setGgr]=useState(String(cuenta.pct_ggr??""));
  const [ventas,setVentas]=useState(String(cuenta.pct_ventas??""));
  // Casino y desafíos se liquidan aparte del deportivo. Una agencia
  // puede repartir a sus subagencias solo lo que ella cobra.
  const [ggrCasino,setGgrCasino]=useState(String(cuenta?.pct_ggr_casino||0));
  const [pctDesafios,setPctDesafios]=useState(String(cuenta?.pct_desafios||0));
  const [alcance,setAlcance]=useState(cuenta.alcance||"");
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null

  const guardar=async()=>{
    setProc(true); setMsg(null);
    try{
      const body={name,address,phone,
        pct_ggr:parseFloat(ggr)||0, pct_ventas:parseFloat(ventas)||0,
        pct_ggr_casino:parseFloat(ggrCasino)||0,
        pct_desafios:parseFloat(pctDesafios)||0};
      if(esInf&&alcance) body.alcance=alcance;
      const r=await fetch(`${API_URL}/api/cuenta/${cuenta.code}/configurar`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify(body)});
      if(r.status===401){ onSesionExpirada&&onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      let extra="";
      if(d.hijos_sobre_limite&&d.hijos_sobre_limite.length)
        extra=` ⚠️ ${d.hijos_sobre_limite.length} hija(s) quedaron con % mayor.`;
      setMsg({text:"Configuración guardada."+extra, ok:true});
      onCambio&&onCambio();
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  const inp={width:"100%",background:"rgba(255,255,255,0.05)",
    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"12px 12px",
    color:Q.text,fontSize:15,marginBottom:8,fontFamily:F_BODY};

  return(
    <div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:6,
        fontFamily:F_BODY}}>
        Usuario: <span style={{color:Q.dim}}>{cuenta.username||"—"} (no editable)</span></div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:2,
        fontFamily:F_BODY}}>Nombre</div>
      <input value={name} onChange={e=>setName(e.target.value)} style={inp}/>
      {!esInf&&<>
        <div style={{color:Q.muted,fontSize:12,marginBottom:2,
          fontFamily:F_BODY}}>Dirección</div>
        <input value={address} onChange={e=>setAddress(e.target.value)} style={inp}/>
        <div style={{color:Q.muted,fontSize:12,marginBottom:2,
          fontFamily:F_BODY}}>Teléfono</div>
        <input value={phone} onChange={e=>setPhone(e.target.value)} style={inp}/>
      </>}
      <div style={{display:"flex",gap:SPACING[8]}}>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>% GGR</div>
          <input value={ggr} onChange={e=>setGgr(e.target.value)} inputMode="decimal" style={inp}/>
        </div>
        <div style={{flex:1}}>
          <div style={{color:Q.muted,fontSize:12,marginBottom:2,
            fontFamily:F_BODY}}>% Ventas</div>
          <input value={ventas} onChange={e=>setVentas(e.target.value)} inputMode="decimal" style={inp}/>
        </div>
      </div>

      {!esInf&&(
        <div style={{display:"flex",gap:SPACING[8]}}>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:2,
              fontFamily:F_BODY}}>% GGR casino</div>
            <input value={ggrCasino}
              onChange={e=>setGgrCasino(e.target.value)}
              inputMode="decimal" style={inp}/>
          </div>
          <div style={{flex:1}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:2,
              fontFamily:F_BODY}}>% Desafíos</div>
            <input value={pctDesafios}
              onChange={e=>setPctDesafios(e.target.value)}
              inputMode="decimal" style={inp}/>
          </div>
        </div>
      )}
      {!esInf&&(
        <div style={{color:Q.dim,fontSize:12,marginBottom:6,
          lineHeight:1.5,fontFamily:F_BODY}}>
          Lo que le das sale de lo tuyo: si le ponés más de lo que
          cobrás vos, esa parte la pagás de tu comisión.</div>
      )}
      {esInf&&<>
        <div style={{color:Q.muted,fontSize:12,marginBottom:2,
          fontFamily:F_BODY}}>Dónde valen sus links</div>
        <select value={alcance} onChange={e=>setAlcance(e.target.value)}
          style={{...inp,color:alcance?Q.text:Q.muted}}>
          <option value="">— Sin cambios —</option>
          <option value="solo_agencia">Solo mi agencia</option>
          <option value="rama">Toda mi rama</option>
          <option value="global">Cualquier lado + Telegram</option>
        </select>
      </>}
      {/* Acá había un selector de permisos que usaba un objeto "form"
          inexistente en este componente: la pantalla reventaba con
          "form is not defined" al abrirla. Además el endpoint de
          configuración no acepta ese campo, así que no guardaba nada.
          Los permisos se definen al crear la sub-agencia. */}
      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>El % no puede superar el tuyo.</div>
      <Btn label={proc?"GUARDANDO...":<><Save size={13}/> Guardar configuración</>} onClick={guardar}
        color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </div>
  );
}

function ResetPassword({ agencia, code, userId, nombre, esAdmin=false, onCerrar }){
  const [nueva,setNueva]=useState("");
  const [repetir,setRepetir]=useState("");
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null

  const guardar=async()=>{
    if(nueva.length<8){ setMsg({text:"La contraseña debe tener 8+ caracteres", ok:false}); return; }
    if(nueva!==repetir){ setMsg({text:"Las contraseñas no coinciden", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const url = esAdmin ? `${API_URL}/api/admin/reset-password`
                          : `${API_URL}/api/agencias/me/reset-password`;
      const headers = esAdmin
        ? {"Content-Type":"application/json","X-Admin-Key":agencia.adminKey}
        : {"Content-Type":"application/json",...authHeaders(agencia.token)};
      const body = code ? {code,nueva} : {user_id:userId,nueva};
      const r=await fetch(url,{method:"POST",headers,body:JSON.stringify(body)});
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text:"Contraseña reseteada. Deberá cambiarla en su próximo ingreso.", ok:true});
      setTimeout(onCerrar,1200);
    }catch(e){ setMsg({text:e.message, ok:false}); }
    setProc(false);
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:220,
      display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}} onClick={onCerrar}>
      <GCard glow={Q.amber} onClick={e=>e.stopPropagation()}
        style={{padding:SPACING[20],maxWidth:400,width:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:15,
            fontFamily:F_BODY}}><Key size={15}/> Resetear contraseña</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:12,lineHeight:1.5,
          fontFamily:F_BODY}}>
          Nueva contraseña para <span style={{color:Q.cyan,fontWeight:700}}>{nombre||code||"la cuenta"}</span>.
          Deberá cambiarla en su próximo ingreso.</div>
        <input type="password" value={nueva} onChange={e=>setNueva(e.target.value)}
          placeholder="Nueva contraseña (8+)"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
        <input type="password" value={repetir} onChange={e=>setRepetir(e.target.value)}
          placeholder="Repetir contraseña"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:16,marginBottom:12,
            fontFamily:F_BODY}}/>
        <Btn label={proc?"GUARDANDO...":"Resetear"} onClick={guardar} color={Q.amber} full disabled={proc}/>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>
    </div>
  );
}

function MisAgencias({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [arbol,setArbol]=useState(null);
  const [showForm,setShowForm]=useState(false);
  const [ccPara,setCcPara]=useState(null);   // code al que cargar crédito
  const [resetPara,setResetPara]=useState(null);   // code al que resetear pass
  const [configPara,setConfigPara]=useState(null);   // cuenta a configurar
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/arbol`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setArbol(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  const bloquear=async(code,bloquear_flag)=>{
    const motivo = bloquear_flag ? (prompt("Motivo del bloqueo (opcional):")||"") : "";
    try{
      const r=await fetch(`${API_URL}/api/bloquear`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({tipo:"agencia",objetivo:code,bloquear:bloquear_flag,
          cascada:true,motivo}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text: bloquear_flag?"Agencia bloqueada":"Agencia desbloqueada", ok:true});
      setTimeout(()=>setMsg(null),2000); cargar();
    }catch(e){ setMsg({text:e.message, ok:false}); setTimeout(()=>setMsg(null),3000); }
  };

  return(
    <div>
      <PageHeader icon={<Building2/>} title="Mis agencias"
        description="Tu red de subagencias: creá nuevas, revisá su rendimiento y bloqueá las que necesites."
        action={<Btn label={showForm?"Cancelar":"+ Nueva"} onClick={()=>setShowForm(s=>!s)}
          color={Q.violet} size="sm"/>}/>

      {showForm&&<CrearSubAgencia agencia={agencia}
        onListo={()=>{setShowForm(false);cargar();}} onSesionExpirada={onSesionExpirada}/>}

      {msg&&<div style={{fontSize:13,marginBottom:10,textAlign:"center",
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {!arbol&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {arbol&&arbol.agencias.length===0&&!showForm&&<div style={{color:Q.muted,fontSize:12,
        textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
        Todavía no creaste sub-agencias</div>}

      {arbol&&arbol.agencias.map(a=>{
        // El backend guarda 'suspended', no 'bloqueada' ni 'blocked'.
        // Con la comparación vieja esto era SIEMPRE falso: el botón
        // ofrecía "Bloquear" incluso con la agencia ya bloqueada, y no
        // había forma de desbloquearla. Se compara contra 'active', que
        // además aguanta cualquier estado nuevo.
        const bloqueada = !!a.status && a.status !== "active";
        return(
          <GCard key={a.code} style={{padding:"12px 16px",marginBottom:8,
            opacity:bloqueada?0.6:1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:SPACING[8]}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>
                  {"— ".repeat(Math.max(0,(a.nivel||0)-(agencia.nivel||0)-1))}{a.name}
                  {bloqueada&&<span style={{color:Q.red,fontSize:12}}> <Lock size={11}/></span>}</div>
                <div style={{color:Q.muted,fontSize:12}}>{a.code} · GGR {fmt(a.pct_ggr)}% · Ventas {fmt(a.pct_ventas)}%</div>
                {a.parent_code&&<div style={{color:Q.violet2,fontSize:12,marginTop:1}}>
                  <Building2 size={11}/> {a.parent_code===agencia.code
                    ? agencia.name
                    : ((arbol.agencias.find(x=>x.code===a.parent_code)||{}).name||a.parent_code)}</div>}
                {a.username&&(
                  <div style={{color:Q.cyan,fontSize:12,marginTop:2,
                    fontFamily:F_BODY}}>@{a.username}</div>
                )}
                <div style={{color:a.saldo_cc>=0?Q.green:Q.red,fontSize:12,marginTop:2,
                  fontFamily:F_BODY}}>CC: {money(a.saldo_cc, mon)}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:SPACING[4],flexShrink:0}}>
                <button onClick={()=>setCcPara(a.code)} style={{background:`${Q.cyan}18`,
                  border:`1px solid ${Q.cyan}`,borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                  color:Q.cyan,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}><Icon name="wallet-cards" size={11}/> Crédito</button>
                <button onClick={()=>bloquear(a.code,!bloqueada)} style={{background:"transparent",
                  border:`1px solid ${bloqueada?Q.green:Q.red}`,borderRadius:RADII.sm,padding:"4px 12px",
                  cursor:"pointer",color:bloqueada?Q.green:Q.red,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}>{bloqueada?"Desbloquear":"Bloquear"}</button>
                <button onClick={()=>setResetPara({code:a.code,nombre:a.name})} style={{background:`${Q.amber}18`,
                  border:`1px solid ${Q.amber}`,borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                  color:Q.amber,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}><Key size={11}/> Clave</button>
                <button onClick={()=>setConfigPara(a)} style={{background:`${Q.violet}18`,
                  border:`1px solid ${Q.violet}`,borderRadius:RADII.sm,padding:"4px 12px",cursor:"pointer",
                  color:Q.violet2,fontSize:12,fontWeight:700,
                  fontFamily:F_BODY}}><Icon name="sliders-horizontal" size={11}/> Config</button>
              </div>
            </div>
          </GCard>
        );
      })}

      {ccPara&&<CargarCreditoSub agencia={agencia} code={ccPara}
        onCerrar={()=>setCcPara(null)} onListo={()=>{setCcPara(null);cargar();}}
        onSesionExpirada={onSesionExpirada}/>}
      {resetPara&&<ResetPassword agencia={agencia} code={resetPara.code}
        nombre={resetPara.nombre} onCerrar={()=>setResetPara(null)}/>}
      {configPara&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:220,
          display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}}
          onClick={()=>setConfigPara(null)}>
          <GCard onClick={e=>e.stopPropagation()}
            style={{padding:SPACING[20],maxWidth:420,width:"100%",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:15,
                fontFamily:F_BODY}}><Icon name="sliders-horizontal" size={14}/> Configurar · {configPara.name}</div>
              <button onClick={()=>setConfigPara(null)} style={{background:"transparent",border:"none",
                color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <ConfigurarCuentaAg agencia={agencia} cuenta={configPara}
              onCambio={()=>{setConfigPara(null);cargar();}} onSesionExpirada={onSesionExpirada}/>
          </GCard>
        </div>
      )}
    </div>
  );
}

function CrearSubAgencia({ agencia, onListo, onSesionExpirada }){
  const [form,setForm]=useState({name:"",username:"",password:"",pct_ggr:"",pct_ventas:"",permiso:"solo_agencia"});
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null
  const miPermiso = agencia.permiso || "ambos";
  const puedeDelegarInf = miPermiso==="crea_influencers" || miPermiso==="ambos";

  const crear=async()=>{
    if(!form.name||!form.username||!form.password){ setMsg({text:"Completá nombre, usuario y clave", ok:false}); return; }
    if(form.password.length<8){ setMsg({text:"La clave debe tener 8+ caracteres", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/sub`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({name:form.name,username:form.username,password:form.password,
          pct_ggr:parseFloat(form.pct_ggr)||0,pct_ventas:parseFloat(form.pct_ventas)||0,
          permiso:form.permiso}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setMsg({text:`Creada: ${d.code||d.name||"sub-agencia"}`, ok:true});
      setTimeout(onListo,900);
    }catch(e){ setMsg({text:e.message, ok:false}); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[16],marginBottom:14}}>
      <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:12,
        fontFamily:F_BODY}}><Building2 size={14}/> Nueva sub-agencia</div>
      {[["name","Nombre"],["username","Usuario"],["password","Contraseña (8+)"]].map(([k,l])=>(
        <input key={k} value={form[k]} type={k==="password"?"password":"text"}
          onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} placeholder={l}
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,marginBottom:8,
            fontFamily:F_BODY}}/>
      ))}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:6}}>
        <input value={form.pct_ggr} onChange={e=>setForm(f=>({...f,pct_ggr:e.target.value}))}
          placeholder="% GGR" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
        <input value={form.pct_ventas} onChange={e=>setForm(f=>({...f,pct_ventas:e.target.value}))}
          placeholder="% Ventas" inputMode="decimal"
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:16,
            fontFamily:F_BODY}}/>
      </div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:4,marginTop:6,
        fontFamily:F_BODY}}>Permisos de esta sub-agencia</div>
      <select value={form.permiso} onChange={e=>setForm(f=>({...f,permiso:e.target.value}))}
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.violet}`,
          borderRadius:RADII.md,padding:"12px 12px",color:Q.text,fontSize:15,marginBottom:8,
          fontFamily:F_BODY}}>
        <option value="solo_agencia">Solo agencia (crea clientes)</option>
        <option value="crea_agencias">+ Crear sub-agencias</option>
        {puedeDelegarInf&&<option value="crea_influencers">+ Crear influencers</option>}
        {puedeDelegarInf&&<option value="ambos">Todo (sub-agencias + influencers)</option>}
      </select>
      <div style={{color:Q.dim,fontSize:12,marginBottom:10,
        fontFamily:F_BODY}}>
        No podés dar más % del que tenés vos.</div>
      <Btn label={proc?"CREANDO...":"Crear sub-agencia"} onClick={crear} color={Q.violet} full disabled={proc}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
    </GCard>
  );
}

function CargarCreditoSub({ agencia, code, onCerrar, onListo, onSesionExpirada }){
  const [monto,setMonto]=useState("");
  const [modo,setModo]=useState("cargar");   // cargar | retirar
  const [msg,setMsg]=useState(null); const [proc,setProc]=useState(false); // msg: {text, ok} | null

  const cargar=async()=>{
    const m=parseFloat(monto);
    if(!m||m<=0){ setMsg({text:"Ingresá un monto", ok:false}); return; }
    const signed = modo==="retirar" ? -Math.abs(m) : Math.abs(m);
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/agencias/me/sub/${code}/cc`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        body:JSON.stringify({monto:signed}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      setMsg({text: modo==="retirar"?"Crédito retirado":"Crédito cargado", ok:true});
      setTimeout(onListo,800);
    }catch(e){ setMsg({text:e.message, ok:false}); setProc(false); }
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.8)",zIndex:210,
      display:"flex",alignItems:"center",justifyContent:"center",padding:SPACING[16]}} onClick={onCerrar}>
      <GCard onClick={e=>e.stopPropagation()}
        style={{padding:SPACING[20],maxWidth:380,width:"100%"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:15,
            fontFamily:F_BODY}}><Icon name="wallet-cards" size={14}/> Crédito · {code}</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:24,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
          {[["cargar",<><Icon name="arrow-up-right" size={12}/> Cargar</>],["retirar",<><Icon name="arrow-down-left" size={12}/> Retirar</>]].map(([k,l])=>(
            <button key={k} onClick={()=>setModo(k)} style={{flex:1,
              background:modo===k?`${Q.cyan}33`:"rgba(255,255,255,0.04)",
              border:`1px solid ${modo===k?Q.cyan:Q.border}`,borderRadius:RADII.md,
              padding:"8px",cursor:"pointer",color:modo===k?Q.cyan:Q.muted,
              fontSize:13,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
          ))}
        </div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:10,lineHeight:1.5,
          fontFamily:F_BODY}}>
          {modo==="retirar"
            ?"Retirás crédito de la sub-agencia y vuelve a tu cuenta. No puede quedar en negativo."
            :"Transferís crédito de tu cuenta a la sub-agencia. Se descuenta de tu saldo."}</div>
        <input value={monto} onChange={e=>setMonto(e.target.value)} inputMode="decimal"
          placeholder="Ej: 50000"
          style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"12px 16px",color:Q.text,fontSize:18,marginBottom:12,
            textAlign:"center",fontFamily:F_BODY}}/>
        <Btn label={proc?"PROCESANDO...":(modo==="retirar"?"Retirar":"Cargar")} onClick={cargar} color={modo==="retirar"?Q.amber:Q.cyan} full disabled={proc}/>
        {msg&&<div style={{fontSize:12,marginTop:8,textAlign:"center",
          color:msg.ok?Q.green:Q.red,
          fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}
      </GCard>
    </div>
  );
}

// Desktop-only grouping of AgenciaPanel's TABS for the sidebar menu.
// Approved grouping: odd/tasks/sidebar-groups.md. Every key here must
// also be a key in TABS below (sidebarGroupKeysMatchTabs.test.js guards
// that pairing). Below 1024px the mobile row still renders straight
// from the flat TABS array, unaffected by this grouping.
const TAB_GROUPS = [
  { label: "Vender", keys: ["codigo", "manual", "combos", "mejorar"] },
  { label: "Clientes", keys: ["clientes", "influencers", "desafios", "mensajes"] },
  { label: "Red", keys: ["misagencias", "terminales", "asesor"] },
  { label: "Dinero", keys: ["cashout", "bonos", "cierres", "historial"] },
  { label: "Cuenta", keys: ["soporte", "config"] },
];

function AgenciaPanel({ agencia, onLogout, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [tab,setTab]=useState("codigo");
  const [saldoCC,setSaldoCC]=useState(null);
  const [verSaldo,setVerSaldo]=useState(false);
  const [menuOpen,setMenuOpen]=useState(false);
  // At 1024px and up (the prototype's own breakpoint) the tab row becomes
  // a sidebar; below it nothing changes — see odd/tasks/desktop-shell.md.
  const isDesktop=useDesktopShellWidth();
  // Mensajes del admin sin leer, para el punto en la pestaña.
  // Sin esto un mensaje quedaba invisible hasta que entraban a mirar.
  const [msgPendientes,setMsgPendientes]=useState(0);

  useEffect(()=>{
    if(!agencia?.token) return;
    let vivo=true;
    const traer=()=>fetch(`${API_URL}/api/agencias/me/mensajes/pendientes`,
        {headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!vivo||!d) return;
        setMsgPendientes(prev=>{
          const n=d.sin_leer||0;
          if(n>prev) sonarAlerta(false);
          return n;
        });
      }).catch(()=>{});
    traer();
    const t=setInterval(traer,20000);
    return()=>{ vivo=false; clearInterval(t); };
    // eslint-disable-next-line
  },[agencia?.token]);

  // Traer el saldo CC al iniciar y refrescarlo cada 30s
  useEffect(()=>{
    let vivo=true;
    const traer=async()=>{
      try{
        const r=await fetch(`${API_URL}/api/agencias/me/cc`,
          {headers:authHeaders(agencia.token)});
        if(r.ok&&vivo){ const d=await r.json(); setSaldoCC(d.saldo_cc); }
      }catch(e){}
    };
    traer();
    const t=setInterval(traer,30000);
    return ()=>{ vivo=false; clearInterval(t); };
  // eslint-disable-next-line
  },[]);

  const TABS=[
    {k:"codigo",   i:<Bot size={17}/>, l:"Código / Bot"},
    {k:"manual",   i:<Pencil size={17}/>, l:"Apuesta manual"},
    {k:"combos",   i:<Zap size={17}/>, l:"Combos IA"},
    {k:"mejorar",  i:<Icon name="scan-line" size={17}/>, l:"Mejorar"},
    {k:"clientes", i:<Icon name="users" size={17}/>, l:"Clientes"},
    {k:"misagencias", i:<Building2 size={17}/>, l:"Mis agencias"},
    {k:"influencers", i:<Star size={17}/>, l:"Influencers"},
    {k:"historial",i:<Icon name="clock-3" size={17}/>, l:"Historial"},
    {k:"cashout",  i:<Icon name="wallet-cards" size={17}/>, l:"Cash out"},
    {k:"bonos",    i:<Gift size={17}/>, l:"Bonos"},
    {k:"cierres",  i:<Icon name="chart-no-axes-combined" size={17}/>, l:"Cierres"},
    {k:"mensajes", i:<Icon name="message-circle" size={17}/>, l:"Mensajes"},
    {k:"terminales", i:<Monitor size={17}/>, l:"Terminales"},
    {k:"desafios", i:<Icon name="trophy" size={17}/>, l:"Desafíos"},
    {k:"asesor", i:<Handshake size={17}/>, l:"Asesor"},
    {k:"soporte",  i:<Headphones size={17}/>, l:"Soporte"},
    {k:"config",   i:<Icon name="sliders-horizontal" size={17}/>, l:"Config"},
  ].filter(t=>{
    const perm = agencia.permiso || "ambos";
    if(t.k==="misagencias") return perm==="crea_agencias"||perm==="ambos";
    if(t.k==="influencers") return perm==="crea_influencers"||perm==="ambos";
    return true;
  });

  return(
    <div style={isDesktop ? {
      background:Q.void,height:"100dvh",overflow:"hidden",
      fontFamily:"system-ui,-apple-system,sans-serif",
      display:"grid",gridTemplateColumns:"264px minmax(0,1fr)",
      gridTemplateRows:"auto 1fr",maxWidth:1600,margin:"0 auto",
    } : {background:Q.void,height:"100dvh",
      display:"flex",flexDirection:"column",overflow:"hidden",
      fontFamily:"system-ui,-apple-system,sans-serif"}}>
      <div style={{position:"fixed",inset:0,pointerEvents:"none",zIndex:0,
        backgroundImage:`linear-gradient(${Q.violet}04 1px,transparent 1px),linear-gradient(90deg,${Q.violet}04 1px,transparent 1px)`,
        backgroundSize:"28px 28px"}}/>
      <div style={isDesktop ? {background:Q.deep,borderBottom:`1px solid ${Q.border}`,
        padding:"12px 16px",display:"flex",alignItems:"center",flexShrink:0,
        justifyContent:"space-between",zIndex:50,overflow:"hidden",minHeight:64,
        gridColumn:"2",gridRow:"1"} : {background:Q.deep,borderBottom:`1px solid ${Q.border}`,
        padding:"12px 16px",display:"flex",alignItems:"center",flexShrink:0,
        justifyContent:"space-between",zIndex:50,overflow:"hidden"}}>
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:1,
          background:`linear-gradient(90deg,transparent,${Q.violet},${Q.cyan},${Q.violet},transparent)`}}/>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12],minWidth:0}}>
          {/* Mobile only: opens MobileTabMenu, the megamenu that replaces
              the horizontal tab strip below 1024px. */}
          {!isDesktop&&(
            <button onClick={()=>setMenuOpen(true)} aria-label="Abrir menú" style={{
              background:"transparent",border:"none",color:Q.text,
              cursor:"pointer",padding:SPACING[4],display:"flex",flexShrink:0}}>
              <Icon name="menu" size={22}/>
            </button>
          )}
          <QPLogo size={isDesktop?40:16}/>
          {isDesktop&&<span style={{color:Q.dim,fontSize:TEXT[20],
            fontWeight:300,lineHeight:1,fontFamily:F_BODY}}>|</span>}
          {isDesktop&&<span style={{color:Q.muted,fontSize:TEXT[14],fontWeight:600,
            whiteSpace:"nowrap",fontFamily:F_BODY}}>Panel de Agencia</span>}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:SPACING[12]}}>
          {/* Saldo stays visible on a phone — it's the number the agency
              checks constantly — but shrinks so it stops competing with
              the hamburger and Salir for the same crowded line. The
              agency name/code that used to sit here on mobile moved to
              the top of MobileTabMenu instead. */}
          <button onClick={()=>setVerSaldo(true)} style={{
            background:`${(saldoCC??0)>=0?Q.green:Q.red}18`,
            border:`1px solid ${(saldoCC??0)>=0?Q.green:Q.red}66`,
            borderRadius:RADII.md,padding:isDesktop?"8px 16px":"4px 12px",
            cursor:"pointer",
            display:"flex",alignItems:"baseline",gap:SPACING[8],
            whiteSpace:"nowrap"}}>
            <span style={{color:Q.muted,fontSize:TEXT[12],textTransform:"uppercase",
              letterSpacing:1,fontFamily:F_BODY}}>Saldo</span>
            <span style={{color:(saldoCC??0)>=0?Q.green:Q.red,fontWeight:800,
              fontSize:isDesktop?TEXT[20]:TEXT[14],fontFamily:F_MONO,
              fontVariantNumeric:"tabular-nums"}}>
              {saldoCC==null?"...":money(saldoCC, mon)}</span>
          </button>
          {isDesktop&&(
            <div style={{textAlign:"right"}}>
              <div style={{color:Q.text,fontSize:12,fontWeight:600,
                fontFamily:F_BODY}}>{agencia.name}</div>
              <div style={{color:Q.muted,fontSize:12}}>{agencia.code}</div>
            </div>
          )}
          <button onClick={onLogout} style={{background:"transparent",
            border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"4px 12px",
            color:Q.muted,fontSize:12,cursor:"pointer"}}>Salir</button>
        </div>
      </div>

      {/* Below 1024px this sidebar is gone entirely, replaced by the
          hamburger's MobileTabMenu overlay — see odd/tasks/mobile-nav.md.
          Desktop keeps exactly the sidebar it already had. */}
      {isDesktop&&(
      <div style={{
        // Deliberately matching Admin.jsx's literal sidebar background,
        // not the Q.deep token: parity with admin's near-black desktop
        // sidebar, keep this in sync if admin's value changes.
        background:"rgba(6,6,18,0.97)",backdropFilter:"blur(20px)",
        borderRight:`1px solid ${Q.border}`,
        padding:`${SPACING[24]}px ${SPACING[12]}px`,display:"flex",
        flexDirection:"column",alignItems:"stretch",gap:SPACING[8],
        overflowY:"auto",flexShrink:0,zIndex:40,
        gridColumn:"1",gridRow:"1 / span 2",position:"sticky",top:0,
        alignSelf:"start",width:264,height:"100dvh",
      }}>
        {TAB_GROUPS.flatMap((group,gi)=>{
          const groupTabs = group.keys.map(k=>TABS.find(t=>t.k===k)).filter(Boolean);
          if(groupTabs.length===0) return [];
          return [
            <div key={`group-${group.label}`} style={{
              marginTop:gi===0?0:SPACING[16],
              padding:"0 12px",
              color:Q.dim,fontSize:TEXT[12],fontWeight:700,
              textTransform:"uppercase",letterSpacing:1,fontFamily:F_BODY,
            }}>{group.label}</div>,
            ...groupTabs.map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} style={{
            minWidth:0,
            background:tab===t.k?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:"transparent",
            border:`1px solid ${tab===t.k?Q.violet:"transparent"}`,
            borderRadius:RADII.md,cursor:"pointer",
            color:tab===t.k?Q.cyan:Q.muted,fontSize:12,fontWeight:tab===t.k?700:400,
            fontFamily:F_BODY,
            position:"relative",
            width:"100%",minHeight:44,padding:"0 12px",flexShrink:0,
            display:"flex",flexDirection:"row",alignItems:"center",
            justifyContent:"flex-start",gap:SPACING[12],
            textAlign:"left",overflow:"visible",
          }}>
            {/* Same treatment as admin's sidebar, down to the icon's glow
                and the uppercase label: two panels of one product should
                not have two different menus. */}
            <span style={{fontSize:17,position:"relative",
              filter:tab===t.k?`drop-shadow(0 0 6px ${Q.cyan})`:"none"}}>{t.i}</span>
            <span style={{color:tab===t.k?Q.cyan:Q.muted,fontSize:TEXT[12],
              fontWeight:tab===t.k?700:400,maxWidth:"100%",
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
              fontFamily:F_BODY,letterSpacing:0.3,
              textTransform:"uppercase"}}>{t.l}</span>
            {t.k==="mensajes"&&msgPendientes>0&&(
              <span style={{position:"absolute",top:-4,right:-4,
                background:Q.red,color:"#fff",borderRadius:RADII.md,
                minWidth:16,height:16,fontSize:12,fontWeight:800,
                display:"flex",alignItems:"center",
                justifyContent:"center",padding:"0 4px",lineHeight:1,
                fontFamily:F_BODY}}>
                {msgPendientes>9?"9+":msgPendientes}</span>
            )}
          </button>
            )),
          ];
        })}
      </div>
      )}

      <MobileTabMenu open={menuOpen} onClose={()=>setMenuOpen(false)}
        groups={TAB_GROUPS} tabs={TABS} activeTab={tab} onSelect={setTab}
        badges={{mensajes:msgPendientes}}
        topContent={
          <div style={{marginBottom:SPACING[16],paddingBottom:SPACING[16],
            borderBottom:`1px solid ${Q.border}`}}>
            <div style={{color:Q.text,fontWeight:700,fontSize:TEXT[16],
              fontFamily:F_BODY}}>{agencia.name}</div>
            <div style={{color:Q.muted,fontSize:TEXT[13],marginTop:2,
              fontFamily:F_BODY}}>{agencia.code}</div>
          </div>
        }/>

      <div style={isDesktop ? {flex:1,minHeight:0,overflowY:"auto",overflowX:"hidden",
        WebkitOverflowScrolling:"touch",position:"relative",zIndex:1,
        gridColumn:"2",gridRow:"2"} : {flex:1,minHeight:0,overflowY:"auto",overflowX:"hidden",
        WebkitOverflowScrolling:"touch",position:"relative",zIndex:1}}>
      <div style={isDesktop ? {padding:"16px 12px",maxWidth:1100,margin:"0 auto",
        paddingBottom:"calc(28px + env(safe-area-inset-bottom))"} : {padding:"16px 12px",maxWidth:620,margin:"0 auto",
        paddingBottom:"calc(28px + env(safe-area-inset-bottom))"}}>
        <CazaError>
        {/* Los avisos del admin: arriba de todo y en cualquier
            pestaña, para que no dependan de que entren a mirarlos. */}
        <CarruselAvisos agencia={agencia} onSesionExpirada={onSesionExpirada}/>

        {tab==="codigo"   &&<FlujoCodigo  agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="manual"   &&<FlujoManual  agencia={agencia}/>}
        {tab==="combos"   &&<CombosIA  agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="mejorar"  &&<MejorarCombinada agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="clientes" &&<Clientes  agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="misagencias"&&<MisAgencias agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="influencers"&&<InfluencersAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="historial"&&<Historial agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="cashout"  &&<HistorialCashout agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="bonos"    &&<BonosAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="cierres"  &&<Cierres      agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="mensajes" &&<MensajesAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="terminales"&&<Terminales agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="desafios"&&<DesafiosAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="asesor"&&<AsesorAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="soporte"  &&<SoporteAgencia agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
        {tab==="config"   &&<Config       agencia={agencia}/>}
        </CazaError>
      </div>
      </div>

      {verSaldo&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",
          zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={()=>setVerSaldo(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:Q.deep,
            borderTopLeftRadius:20,borderTopRightRadius:20,width:"100%",maxWidth:620,
            maxHeight:"88vh",overflowY:"auto",padding:SPACING[20],
            border:`1px solid ${Q.border}`,borderBottom:"none"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:14}}>
              <div style={{color:Q.text,fontWeight:700,fontSize:16,
                fontFamily:F_BODY}}><Icon name="wallet-cards" size={15}/> Mi saldo</div>
              <button onClick={()=>setVerSaldo(false)} style={{background:"transparent",
                border:"none",color:Q.muted,fontSize:26,cursor:"pointer",lineHeight:1}}>×</button>
            </div>
            <SaldoCC agencia={agencia} onSesionExpirada={onSesionExpirada}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// APP ROOT
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// PANEL DEL INFLUENCER
// ═══════════════════════════════════════════════════════════════
function EditorPlaca({ combo, codigoRef, link, onCerrar }){
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const [fondo,setFondo]=useState({tipo:"template",valor:0});  // template|solido|imagen
  const [imgFondo,setImgFondo]=useState(null);   // Image() cargada
  const [filtro,setFiltro]=useState("none");
  const [capas,setCapas]=useState([]);   // textos/stickers movibles
  const [selCapa,setSelCapa]=useState(null);
  const [drag,setDrag]=useState(null);
  const [nombreCombo,setNombreCombo]=useState((combo.nombre||"COMBINADA").toUpperCase());

  const W=1080, H=1350;

  // Plantillas de fondo (degradés diseñados)
  const templates=[
    {name:"Violeta",stops:[["#0a0118",0],["#2a0a5a",0.5],["#04040f",1]]},
    {name:"Neón",stops:[["#001a2e",0],["#003554",0.5],["#00171f",1]]},
    {name:"Dorado",stops:[["#1a1500",0],["#3a2e0a",0.5],["#0f0a00",1]]},
  ];
  const solidos=["#0a0118","#1a0a3a","#001f3f","#0f0f0f","#2a0a2a","#0a2a1a"];

  // Capas iniciales: nombre del combo + código
  useEffect(()=>{
    setCapas([
      {id:"nombre",tipo:"texto",texto:nombreCombo,x:W/2,y:220,size:64,color:"#ffffff",bold:true},
      {id:"codigo",tipo:"texto",texto:`Código: ${codigoRef}`,x:W/2,y:H-260,size:44,color:"#E8C547",bold:true},
      {id:"cta",tipo:"texto",texto:"👉 JUGÁ CONMIGO",x:W/2,y:H-160,size:40,color:"#00F0FF",bold:true},
       {id:"bot",tipo:"texto",texto:`📲 @${BOT_USERNAME}`,x:W/2,y:H-90,size:36,color:"#9F5FFF",bold:true},
    ]);
  // eslint-disable-next-line
  },[]);

  // Dibujar todo en el canvas
  const dibujar=()=>{
    const cv=canvasRef.current; if(!cv) return;
    const x=cv.getContext("2d");
    x.clearRect(0,0,W,H);
    // Fondo
    if(fondo.tipo==="imagen"&&imgFondo){
      x.save();
      if(filtro==="bn") x.filter="grayscale(1)";
      else if(filtro==="calido") x.filter="sepia(0.4) saturate(1.3)";
      else if(filtro==="frio") x.filter="hue-rotate(30deg) saturate(1.2)";
      else if(filtro==="oscuro") x.filter="brightness(0.6)";
      // cover
      const rImg=imgFondo.width/imgFondo.height, rC=W/H;
      let dw=W,dh=H,dx=0,dy=0;
      if(rImg>rC){ dh=H; dw=H*rImg; dx=(W-dw)/2; }
      else { dw=W; dh=W/rImg; dy=(H-dh)/2; }
      x.drawImage(imgFondo,dx,dy,dw,dh);
      x.restore();
      // Oscurecer un poco para que se lea el texto
      x.fillStyle="rgba(0,0,0,0.35)"; x.fillRect(0,0,W,H);
    } else if(fondo.tipo==="solido"){
      x.fillStyle=solidos[fondo.valor]; x.fillRect(0,0,W,H);
    } else {
      const t=templates[fondo.valor];
      const g=x.createLinearGradient(0,0,W,H);
      t.stops.forEach(([c,p])=>g.addColorStop(p,c));
      x.fillStyle=g; x.fillRect(0,0,W,H);
    }
    // Marco
    x.strokeStyle="#7C3AED"; x.lineWidth=6; x.strokeRect(30,30,W-60,H-60);
    // Marca
    x.fillStyle="#00F0FF"; x.font="bold 56px system-ui"; x.textAlign="center";
    x.fillText("IAQP", W/2, 120);
    // Picks del combo (fijos, con cuotas)
    x.textAlign="left"; x.font="34px system-ui";
    let y=320;
    (combo.picks||[]).slice(0,7).forEach((p,i)=>{
      const local=p.home||p.h||""; const sel=p.sel||p.label||"";
      const odd=Number(p.odd||p.cuota||0).toFixed(2);
      x.fillStyle="#a78bfa"; x.fillText(`${i+1}.`, 80, y);
      x.fillStyle="#ffffff";
      let li=`${local?local+" — ":""}${sel}`;
      if(li.length>32) li=li.slice(0,32)+"…";
      x.fillText(li, 140, y);
      x.fillStyle="#00F0FF"; x.textAlign="right"; x.fillText(`@${odd}`, W-80, y);
      x.textAlign="left"; y+=64;
    });
    // Cuota total
    y+=10;
    x.fillStyle="#00FF88"; x.font="bold 58px system-ui"; x.textAlign="center";
    x.fillText(`CUOTA ${Number(combo.odd||combo.odd_total||0).toFixed(2)}x`, W/2, y);
    // Capas movibles (texto/sticker)
    capas.forEach(c=>{
      x.textAlign="center";
      if(c.tipo==="texto"){
        x.fillStyle=c.color; x.font=`${c.bold?"bold ":""}${c.size}px system-ui`;
        x.fillText(c.texto, c.x, c.y);
      } else if(c.tipo==="sticker"){
        x.font=`${c.size}px system-ui`; x.fillText(c.emoji, c.x, c.y);
      }
      // Marco de selección
      if(selCapa===c.id){
        const w=x.measureText(c.texto||c.emoji||"").width;
        x.strokeStyle="#00F0FF"; x.lineWidth=2;
        x.strokeRect(c.x-w/2-10, c.y-c.size, w+20, c.size+16);
      }
    });
  };
  useEffect(dibujar);   // redibuja en cada cambio

  // Interacción: tocar/arrastrar capas
  const coords=(e)=>{
    const cv=canvasRef.current; const r=cv.getBoundingClientRect();
    const t=e.touches?e.touches[0]:e;
    return {x:(t.clientX-r.left)*(W/r.width), y:(t.clientY-r.top)*(H/r.height)};
  };
  const onDown=(e)=>{
    const {x,y}=coords(e);
    // Buscar capa tocada (de arriba hacia abajo)
    for(let i=capas.length-1;i>=0;i--){
      const c=capas[i];
      const cv=canvasRef.current.getContext("2d");
      cv.font=`${c.bold?"bold ":""}${c.size}px system-ui`;
      const w=cv.measureText(c.texto||c.emoji||"").width;
      if(Math.abs(x-c.x)<w/2+20 && y<c.y+16 && y>c.y-c.size){
        setSelCapa(c.id); setDrag({id:c.id,dx:x-c.x,dy:y-c.y}); return;
      }
    }
    setSelCapa(null);
  };
  const onMove=(e)=>{
    if(!drag) return;
    e.preventDefault();
    const {x,y}=coords(e);
    setCapas(cs=>cs.map(c=>c.id===drag.id?{...c,x:x-drag.dx,y:y-drag.dy}:c));
  };
  const onUp=()=>setDrag(null);

  const subirImagen=(e)=>{
    const f=e.target.files?.[0]; if(!f) return;
    const r=new FileReader();
    r.onload=()=>{ const im=new Image(); im.onload=()=>{ setImgFondo(im); setFondo({tipo:"imagen",valor:0}); }; im.src=r.result; };
    r.readAsDataURL(f);
    e.target.value="";
  };

  const addSticker=(emoji)=>{
    const id="s"+Date.now();
    setCapas(cs=>[...cs,{id,tipo:"sticker",emoji,x:W/2,y:H/2,size:90}]);
    setSelCapa(id);
  };
  const editarTextoSel=(txt)=>{
    setCapas(cs=>cs.map(c=>c.id===selCapa?{...c,texto:txt}:c));
  };
  const cambiarColorSel=(col)=>{
    setCapas(cs=>cs.map(c=>c.id===selCapa?{...c,color:col}:c));
  };
  const borrarSel=()=>{
    setCapas(cs=>cs.filter(c=>c.id!==selCapa)); setSelCapa(null);
  };

  const descargar=()=>{
    setSelCapa(null);
    setTimeout(()=>{
      canvasRef.current.toBlob(b=>{
        const a=document.createElement("a");
        a.href=URL.createObjectURL(b);
        a.download=`combo_${codigoRef}_${combo.id||"qp"}.png`;
        a.click();
      },"image/png");
    },50);
  };

  const compartir=()=>{
    setSelCapa(null);
    setTimeout(()=>{
      canvasRef.current.toBlob(async(b)=>{
        const file=new File([b],`combo_${codigoRef}.png`,{type:"image/png"});
        const texto=`${nombreCombo}\nCuota ${Number(combo.odd||combo.odd_total||0).toFixed(2)}x\nMi código: ${codigoRef}\n${link}`;
        // Web Share API con archivo (móvil)
        if(navigator.canShare&&navigator.canShare({files:[file]})){
          try{ await navigator.share({files:[file],text:texto}); return; }
          catch(e){ if(e.name==="AbortError") return; }
        }
        // Fallback: descargar
        const a=document.createElement("a");
        a.href=URL.createObjectURL(b);
        a.download=`combo_${codigoRef}_${combo.id||"qp"}.png`;
        a.click();
      },"image/png");
    },50);
  };

  const capaSel = capas.find(c=>c.id===selCapa);
  const emojis=["🔥","⚽","💰","🏆","⭐","💎","🚀","✅","🎯","👑","💪","📲"];
  const colores=["#ffffff","#00F0FF","#00FF88","#E8C547","#FF0080","#9F5FFF"];

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.92)",zIndex:300,
      overflowY:"auto",padding:"12px"}}>
      <div style={{maxWidth:520,margin:"0 auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:12}}>
          <div style={{color:Q.text,fontWeight:700,fontSize:16,
            fontFamily:F_BODY}}><Palette size={16}/> Editor de placa</div>
          <button onClick={onCerrar} style={{background:"transparent",border:"none",
            color:Q.muted,fontSize:28,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        {/* Canvas */}
        <canvas ref={canvasRef} width={W} height={H}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
          style={{width:"100%",borderRadius:RADII.lg,border:`1px solid ${Q.border}`,
            marginBottom:12,touchAction:"none"}}/>

        {/* Editar capa seleccionada */}
        {capaSel&&(
          <GCard style={{padding:SPACING[12],marginBottom:10}}>
            <div style={{color:Q.cyan,fontSize:12,marginBottom:8,
              fontFamily:F_BODY}}>Editando: {capaSel.tipo}</div>
            {capaSel.tipo==="texto"&&(
              <input value={capaSel.texto} onChange={e=>editarTextoSel(e.target.value)}
                style={{width:"100%",background:"rgba(255,255,255,0.05)",
                  border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
                  color:Q.text,fontSize:14,marginBottom:8,
                  fontFamily:F_BODY}}/>
            )}
            <div style={{display:"flex",gap:SPACING[8],alignItems:"center",marginBottom:8}}>
              {colores.map(col=>(
                <button key={col} onClick={()=>cambiarColorSel(col)}
                  style={{width:28,height:28,borderRadius:"50%",background:col,
                    border:capaSel.color===col?`2px solid ${Q.cyan}`:"1px solid #333",
                    cursor:"pointer"}}/>
              ))}
            </div>
            <div style={{display:"flex",gap:SPACING[8]}}>
              <button onClick={()=>setCapas(cs=>cs.map(c=>c.id===selCapa?{...c,size:c.size+8}:c))}
                style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                  borderRadius:RADII.md,padding:"8px",color:Q.text,cursor:"pointer",fontSize:13}}>A+</button>
              <button onClick={()=>setCapas(cs=>cs.map(c=>c.id===selCapa?{...c,size:Math.max(20,c.size-8)}:c))}
                style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                  borderRadius:RADII.md,padding:"8px",color:Q.text,cursor:"pointer",fontSize:13}}>A−</button>
              <button onClick={borrarSel}
                style={{flex:1,background:`${Q.red}22`,border:`1px solid ${Q.red}`,
                  borderRadius:RADII.md,padding:"8px",color:Q.red,cursor:"pointer",fontSize:13}}><Trash2 size={13} role="img" aria-label="Borrar"/></button>
            </div>
          </GCard>
        )}

        {/* Fondos */}
        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>Fondo</div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:8,flexWrap:"wrap"}}>
          {templates.map((t,i)=>(
            <button key={i} onClick={()=>setFondo({tipo:"template",valor:i})}
              style={{padding:"8px 12px",borderRadius:RADII.md,fontSize:12,cursor:"pointer",
                background:fondo.tipo==="template"&&fondo.valor===i?`${Q.violet}44`:"rgba(255,255,255,0.05)",
                border:`1px solid ${fondo.tipo==="template"&&fondo.valor===i?Q.violet:Q.border}`,
                color:Q.text,fontFamily:F_BODY}}>{t.name}</button>
          ))}
          <button onClick={()=>fileRef.current?.click()}
            style={{padding:"8px 12px",borderRadius:RADII.md,fontSize:12,cursor:"pointer",
              background:fondo.tipo==="imagen"?`${Q.cyan}33`:"rgba(255,255,255,0.05)",
              border:`1px solid ${fondo.tipo==="imagen"?Q.cyan:Q.border}`,
              color:Q.text,fontFamily:F_BODY}}><Icon name="camera" size={13}/> Mi foto</button>
          <input ref={fileRef} type="file" accept="image/*" onChange={subirImagen} style={{display:"none"}}/>
        </div>
        <div style={{display:"flex",gap:SPACING[8],marginBottom:10,flexWrap:"wrap"}}>
          {solidos.map((col,i)=>(
            <button key={i} onClick={()=>setFondo({tipo:"solido",valor:i})}
              style={{width:30,height:30,borderRadius:RADII.md,background:col,cursor:"pointer",
                border:fondo.tipo==="solido"&&fondo.valor===i?`2px solid ${Q.cyan}`:"1px solid #333"}}/>
          ))}
        </div>

        {/* Filtros (solo si hay imagen) */}
        {fondo.tipo==="imagen"&&(
          <div style={{marginBottom:10}}>
            <div style={{color:Q.muted,fontSize:12,marginBottom:6,
              fontFamily:F_BODY}}>Filtro</div>
            <div style={{display:"flex",gap:SPACING[8],flexWrap:"wrap"}}>
              {[["none","Normal"],["bn","B/N"],["calido","Cálido"],["frio","Frío"],["oscuro","Oscuro"]].map(([k,l])=>(
                <button key={k} onClick={()=>setFiltro(k)}
                  style={{padding:"8px 12px",borderRadius:RADII.md,fontSize:12,cursor:"pointer",
                    background:filtro===k?`${Q.violet}44`:"rgba(255,255,255,0.05)",
                    border:`1px solid ${filtro===k?Q.violet:Q.border}`,
                    color:Q.text,fontFamily:F_BODY}}>{l}</button>
              ))}
            </div>
          </div>
        )}

        {/* Stickers */}
        <div style={{color:Q.muted,fontSize:12,marginBottom:6,
          fontFamily:F_BODY}}>Stickers (tocá para agregar)</div>
        <div style={{display:"flex",gap:SPACING[4],marginBottom:12,flexWrap:"wrap"}}>
          {emojis.map(em=>(
            <button key={em} onClick={()=>addSticker(em)}
              style={{fontSize:24,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
                borderRadius:RADII.md,padding:"4px 8px",cursor:"pointer"}}>{em}</button>
          ))}
        </div>

        <div style={{display:"flex",gap:SPACING[8]}}>
          <Btn label={<><Smartphone size={13}/> Compartir</>} onClick={compartir} color={Q.violet} full/>
          <Btn label={<><Icon name="arrow-down-left" size={13}/> Descargar</>} onClick={descargar} color={Q.gold} full/>
        </div>
        <div style={{color:Q.dim,fontSize:12,textAlign:"center",marginTop:8,
          fontFamily:F_BODY}}>
          Tocá un texto para moverlo o editarlo · Las cuotas del combo son fijas</div>
      </div>
    </div>
  );
}


function InfluencerEscaner({ agencia, onSesionExpirada }){
  const hoy=new Date().toISOString().slice(0,10);
  const primero=new Date(); primero.setDate(1);
  const [desde,setDesde]=useState(primero.toISOString().slice(0,10));
  const [hasta,setHasta]=useState(hoy);
  const [data,setData]=useState(null);
  const [copiado,setCopiado]=useState("");
  const ref = agencia.codigo_ref || "";
  const linkScanTg=`https://t.me/${BOT_USERNAME}?start=scan_${ref}`;
  const linkScanWeb=`${APP_ORIGIN}/?scan=${ref}`;

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/influencer/me/escaneos?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setData(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[desde,hasta]);

  const copiar=(txt,cual)=>{ try{ navigator.clipboard.writeText(txt);
    setCopiado(cual); setTimeout(()=>setCopiado(""),1500); }catch(e){} };

  return(
    <div>
      <GCard style={{padding:SPACING[16],marginBottom:12,
        background:`linear-gradient(135deg,${Q.cyan}10,${Q.violet}06)`}}>
        <div style={{color:Q.text,fontWeight:700,fontSize:14,marginBottom:6,
          fontFamily:F_BODY}}><Icon name="scan-line" size={14}/> Tu escáner de apuestas</div>
        <div style={{color:Q.muted,fontSize:12,marginBottom:12,lineHeight:1.5,
          fontFamily:F_BODY}}>
          Compartí este link. Tus seguidores escanean su apuesta, se la mejoramos,
          y cada jugada suma a tu comisión.</div>
        <div style={{display:"flex",flexDirection:"column",gap:SPACING[8]}}>
          <button onClick={()=>copiar(linkScanTg,"tg")} style={{background:`${Q.cyan}18`,
            border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"12px",cursor:"pointer",
            color:Q.cyan,fontSize:12,fontWeight:700,textAlign:"left",
            fontFamily:F_BODY}}>
            {copiado==="tg"?<><Icon name="circle-check" size={12}/> Copiado</>:<><Smartphone size={12}/> Copiar link de escáner (Telegram)</>}</button>
          <button onClick={()=>copiar(linkScanWeb,"web")} style={{background:`${Q.violet}18`,
            border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"12px",cursor:"pointer",
            color:Q.violet2,fontSize:12,fontWeight:700,textAlign:"left",
            fontFamily:F_BODY}}>
            {copiado==="web"?<><Icon name="circle-check" size={12}/> Copiado</>:<><Globe size={12}/> Copiar link de escáner (web)</>}</button>
        </div>
      </GCard>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
      </div>

      {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {data&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8],marginBottom:12}}>
            {[["Escaneos",data.escaneos,Q.cyan],["Jugaron",data.jugadas,Q.green],
              ["Conversión",data.conversion+"%",Q.gold]].map(([l,v,c])=>(
              <GCard key={l} style={{padding:"16px 8px",textAlign:"center"}}>
                <div style={{color:c,fontWeight:800,fontSize:18,
                  fontFamily:F_BODY}}>{v}</div>
                <div style={{color:Q.muted,fontSize:12,marginTop:2}}>{l}</div>
              </GCard>
            ))}
          </div>

          <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
            marginBottom:8,fontFamily:F_BODY}}>Últimos escaneos</div>
          {data.ultimos.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
            padding:SPACING[20],fontFamily:F_BODY}}>Sin escaneos en el período</div>}
          {data.ultimos.map((u,i)=>(
            <GCard key={i} style={{padding:"12px 12px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{minWidth:0,flex:1}}>
                  <div style={{color:Q.text,fontWeight:600,fontSize:12,
                    fontFamily:F_BODY}}>
                    {u.picks_ok}/{u.picks_leidos} picks · {fmt(u.cuota)}x</div>
                  <div style={{color:Q.muted,fontSize:12}}>{fechaHora(u.fecha)}
                    {u.betslip?` · ${u.betslip}`:""}</div>
                </div>
                <span style={{color:u.jugo?Q.green:Q.muted,fontWeight:700,fontSize:12,
                  fontFamily:F_BODY}}>{u.jugo?<><Icon name="circle-check" size={11}/> Jugó</>:"Escaneó"}</span>
              </div>
            </GCard>
          ))}
        </div>
      )}
    </div>
  );
}

function InfluencerEscanerFin(){ return null; }

function InfluencerPanel({ agencia, onLogout, onSesionExpirada }){
  const [tab,setTab]=useState("panel");   // panel | combos
  return(
    <div style={{maxWidth:520,margin:"0 auto",padding:"0 16px 40px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        padding:"16px 0 12px"}}>
        <div style={{color:Q.text,fontWeight:800,fontSize:18,
          fontFamily:F_BODY}}><Star size={18}/> {agencia.name}</div>
        <button onClick={onLogout} style={{background:"transparent",
          border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px 12px",
          color:Q.muted,fontSize:12,cursor:"pointer",
          fontFamily:F_BODY}}>Salir</button>
      </div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:14}}>
        {[["panel",<><Icon name="chart-no-axes-combined" size={13}/> Mi panel</>],["combos",<><Zap size={13}/> Mis combos</>],["ia",<><Bot size={13}/> Combos IA</>],["escaner",<><Icon name="scan-line" size={13}/> Escáner</>]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{flex:1,
            background:tab===k?`${Q.violet}33`:"rgba(255,255,255,0.04)",
            border:`1px solid ${tab===k?Q.violet:Q.border}`,borderRadius:RADII.md,
            padding:"12px",cursor:"pointer",color:tab===k?Q.cyan:Q.muted,
            fontSize:13,fontWeight:700,fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {tab==="panel"&&<InfluencerHome agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
      {tab==="combos"&&<InfluencerCombos agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
      {tab==="ia"&&<InfluencerCombosIA agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
      {tab==="escaner"&&<InfluencerEscaner agencia={agencia} onSesionExpirada={onSesionExpirada}/>}
    </div>
  );
}

function InfluencerHome({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const hoy=new Date().toISOString().slice(0,10);
  const primero=new Date(); primero.setDate(1);
  const [desde,setDesde]=useState(primero.toISOString().slice(0,10));
  const [hasta,setHasta]=useState(hoy);
  const [data,setData]=useState(null);
  const [copiado,setCopiado]=useState("");

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/influencer/me/panel?desde=${desde}&hasta=${hasta}`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setData(await r.json());
    }catch(e){}
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[desde,hasta]);

  const ref = (data&&data.codigo_ref) || agencia.codigo_ref || "";
  const linkTelegram = `https://t.me/${BOT_USERNAME}?start=${ref}`;
  const linkWeb = `${APP_ORIGIN}/?ref=${ref}`;

  const copiar=(txt,cual)=>{
    try{ navigator.clipboard.writeText(txt); setCopiado(cual);
      setTimeout(()=>setCopiado(""),1500);
    }catch(e){}
  };

  return(
    <div>
      {/* Código y links para compartir */}
      <GCard style={{padding:SPACING[16],marginBottom:12,
        background:`linear-gradient(135deg,${Q.violet}12,${Q.cyan}06)`}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
          marginBottom:6,fontFamily:F_BODY}}>Tu código</div>
        <div style={{color:Q.cyan,fontWeight:800,fontSize:22,marginBottom:12,
          fontFamily:F_BODY}}>{ref||"—"}</div>
        <div style={{display:"flex",flexDirection:"column",gap:SPACING[8]}}>
          <button onClick={()=>copiar(linkTelegram,"tg")} style={{background:`${Q.cyan}18`,
            border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"12px",cursor:"pointer",
            color:Q.cyan,fontSize:12,fontWeight:700,textAlign:"left",
            fontFamily:F_BODY}}>
            {copiado==="tg"?<><Icon name="circle-check" size={12}/> Copiado</>:<><Smartphone size={12}/> Copiar link de Telegram</>}</button>
          <button onClick={()=>copiar(linkWeb,"web")} style={{background:`${Q.violet}18`,
            border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"12px",cursor:"pointer",
            color:Q.violet2,fontSize:12,fontWeight:700,textAlign:"left",
            fontFamily:F_BODY}}>
            {copiado==="web"?<><Icon name="circle-check" size={12}/> Copiado</>:<><Globe size={12}/> Copiar link web</>}</button>
        </div>
        <div style={{color:Q.dim,fontSize:12,marginTop:8,
          fontFamily:F_BODY}}>
          Quien juegue por tus links o tus combos suma a tu comisión.</div>
      </GCard>

      {/* Fechas */}
      <div style={{display:"flex",gap:SPACING[8],marginBottom:12,alignItems:"center"}}>
        <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
        <span style={{color:Q.muted}}>→</span>
        <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
          style={{flex:1,background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
            borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:13,
            fontFamily:F_BODY}}/>
      </div>

      {!data&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {data&&(
        <div>
          <GCard style={{padding:SPACING[20],marginBottom:12}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              marginBottom:4,fontFamily:F_BODY}}>Tu comisión</div>
            <div style={{color:Q.gold,fontWeight:900,fontSize:30,
              fontFamily:F_BODY}}>{money(data.comision, mon)}</div>
            <div style={{color:Q.muted,fontSize:12,marginTop:4}}>
              {fmt(data.pct_ggr)}% GGR + {fmt(data.pct_ventas)}% ventas</div>
          </GCard>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:SPACING[8]}}>
            {[["Ventas",data.apostado,Q.cyan],["GGR",data.ggr,Q.green],
              ["Jugadas",data.jugadas,Q.violet2,true]].map(([l,v,c,esNum])=>(
              <GCard key={l} style={{padding:"12px 8px",textAlign:"center"}}>
                <div style={{color:c,fontWeight:800,fontSize:16,
                  fontFamily:F_BODY}}>{esNum?v:money(v, mon)}</div>
                <div style={{color:Q.muted,fontSize:12,marginTop:2}}>{l}</div>
              </GCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InfluencerCombosIA({ agencia, onSesionExpirada }){
  const [combos,setCombos]=useState(null);
  const [abierto,setAbierto]=useState(null);   // id del combo con links desplegados
  const [copiado,setCopiado]=useState("");
  const [editando,setEditando]=useState(null);   // combo en edición de imagen
  const [histo,setHisto]=useState(false);   // ver historial (incluye expirados vencidos)

  useEffect(()=>{
    fetch(`${API_URL}/api/influencer/me/combos-ia?historial=${histo}`,{headers:authHeaders(agencia.token)})
      .then(r=>{ if(r.status===401){ onSesionExpirada(); return {combos:[]}; } return r.json(); })
      .then(d=>setCombos(d.combos||[])).catch(()=>setCombos([]));
  // eslint-disable-next-line
  },[histo]);

  const ref = agencia.codigo_ref || "";
  const copiar=(txt,cual)=>{
    try{ navigator.clipboard.writeText(txt); setCopiado(cual);
      setTimeout(()=>setCopiado(""),1500);
    }catch(e){}
  };

  return(
    <div>
      <div style={{color:Q.muted,fontSize:12,marginBottom:12,
        fontFamily:F_BODY}}>
        Compartí estos combos con tu firma. Quien juegue por tu link suma a tu comisión.</div>

      <div style={{display:"flex",gap:SPACING[8],marginBottom:12}}>
        {[["activos",<><Zap size={12}/> Activos</>,false],["histo",<><Icon name="clock-3" size={12}/> Historial</>,true]].map(([k,l,v])=>(
          <button key={k} onClick={()=>setHisto(v)} style={{flex:1,
            background:histo===v?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`:"rgba(255,255,255,0.04)",
            border:`1px solid ${histo===v?Q.cyan:Q.border}`,borderRadius:RADII.md,padding:"8px",
            cursor:"pointer",color:histo===v?Q.cyan:Q.muted,fontSize:12,fontWeight:700,
            fontFamily:F_BODY}}>{l}</button>
        ))}
      </div>

      {!combos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {combos&&combos.length===0&&<div style={{color:Q.muted,fontSize:12,textAlign:"center",
        padding:SPACING[20],fontFamily:F_BODY}}>No hay combos de la IA por ahora</div>}

      {(combos||[]).map(c=>{
        const abierta=abierto===c.id;
        const linkTg=`https://t.me/${BOT_USERNAME}?start=${ref}_c${c.id}`;
        const linkWeb=`${APP_ORIGIN}/?ref=${ref}&combo=${c.id}`;
        return(
          <GCard key={c.id} style={{padding:"12px 16px",marginBottom:8}}>
            <div onClick={()=>setAbierto(abierta?null:c.id)}
              style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                gap:SPACING[8],cursor:"pointer"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{c.nombre}
                  {c.es_ia&&<span style={{background:`${Q.violet}33`,border:`1px solid ${Q.violet}`,
                    borderRadius:RADII.md,padding:"4px 8px",fontSize:12,color:Q.violet2,marginLeft:6}}><Bot size={12}/> IA</span>}
                  {c.expirado&&<span style={{background:`${Q.amber}22`,border:`1px solid ${Q.amber}`,
                    borderRadius:RADII.md,padding:"4px 8px",fontSize:12,color:Q.amber,marginLeft:6}}>expirado</span>}
                  {!c.expirado&&c.estado==="activo"&&<span style={{background:`${Q.green}22`,border:`1px solid ${Q.green}`,
                    borderRadius:RADII.md,padding:"4px 8px",fontSize:12,color:Q.green,marginLeft:6}}>activo</span>}</div>
                <div style={{color:Q.muted,fontSize:12}}>{c.picks.length} picks · {fmt(c.odd)}x</div>
              </div>
              <span style={{color:Q.muted,fontSize:16}}>{abierta?"▾":"›"}</span>
            </div>

            {abierta&&(
              <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                {c.picks.map((p,j)=>(
                  <div key={j} style={{color:Q.muted,fontSize:12,marginBottom:4,
                    fontFamily:F_BODY}}>
                    {(p.home||p.h||"")}{(p.home||p.h)?" · ":""}
                    <span style={{color:Q.cyan}}>{p.sel||p.label||""}</span>
                    <span style={{color:Q.violet2}}> @{fmt(p.odd)}</span></div>
                ))}
                <div style={{display:"flex",flexDirection:"column",gap:SPACING[8],marginTop:10}}>
                  <button onClick={()=>copiar(linkTg,`tg${c.id}`)} style={{background:`${Q.cyan}18`,
                    border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`tg${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Smartphone size={12}/> Link Telegram con tu firma</>}</button>
                  <button onClick={()=>copiar(linkWeb,`web${c.id}`)} style={{background:`${Q.violet}18`,
                    border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.violet2,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`web${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Globe size={12}/> Link web con tu firma</>}</button>
                  <button onClick={()=>copiar(ref,`ref${c.id}`)} style={{background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.muted,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`ref${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Icon name="ticket" size={12}/> Copiar tu código ({ref}) para agencia</>}</button>
                  <div style={{height:1,background:Q.dim,margin:"4px 0"}}/>
                  <button onClick={()=>copiar(textoCombo(c,ref,linkTg),`txt${c.id}`)}
                    style={{background:`${Q.green}18`,border:`1px solid ${Q.green}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.green,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    {copiado===`txt${c.id}`?<><Icon name="circle-check" size={12}/> Texto copiado</>:<><FileText size={12}/> Copiar texto para redes</>}</button>
                  <button onClick={()=>descargarPlacaCombo(c,ref,linkTg)}
                    style={{background:`${Q.gold}18`,border:`1px solid ${Q.gold}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.gold,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    <ImageIcon size={12}/> Descargar placa (imagen)</button>
                  <button onClick={()=>setEditando(c)}
                    style={{background:`${Q.violet}18`,border:`1px solid ${Q.violet}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.violet2,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    <Palette size={12}/> Editar imagen (crear placa)</button>
                </div>
              </div>
            )}
          </GCard>
        );
      })}
      {editando&&<EditorPlaca combo={editando} codigoRef={agencia.codigo_ref||""}
        link={`https://t.me/${BOT_USERNAME}?start=${agencia.codigo_ref||""}_c${editando.id}`}
        onCerrar={()=>setEditando(null)}/>}
    </div>
  );
}

function InfluencerCombos({ agencia, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [combos,setCombos]=useState(null);
  const [crear,setCrear]=useState(false);
  const [copiado,setCopiado]=useState("");
  const [abierto,setAbierto]=useState(null);
  const [editando,setEditando]=useState(null);
  const ref = agencia.codigo_ref || "";
  const copiar=(txt,cual)=>{ try{ navigator.clipboard.writeText(txt);
    setCopiado(cual); setTimeout(()=>setCopiado(""),1500); }catch(e){} };

  const cargar=async()=>{
    try{
      const r=await fetch(`${API_URL}/api/influencer/me/combos`,
        {headers:authHeaders(agencia.token)});
      if(r.status===401){ onSesionExpirada(); return; }
      if(r.ok) setCombos((await r.json()).combos||[]);
    }catch(e){}
  };
  useEffect(()=>{ cargar(); // eslint-disable-next-line
  },[]);

  return(
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
          fontFamily:F_BODY}}>Mis combos</div>
        <Btn label={crear?"Cancelar":"+ Nuevo"} onClick={()=>setCrear(c=>!c)}
          color={Q.violet} size="sm"/>
      </div>

      {crear&&<CrearComboInfluencer agencia={agencia}
        onListo={()=>{setCrear(false);cargar();}} onSesionExpirada={onSesionExpirada}/>}

      {!combos&&<div style={{color:Q.muted,textAlign:"center",padding:SPACING[20],
        fontFamily:F_BODY}}>Cargando...</div>}
      {combos&&combos.length===0&&!crear&&<div style={{color:Q.muted,fontSize:12,
        textAlign:"center",padding:SPACING[20],fontFamily:F_BODY}}>
        Todavía no creaste combos</div>}

      {(combos||[]).map(c=>{
        const abierta=abierto===c.id;
        const codOut=c.codigo||ref;
        const linkTg=`https://t.me/${BOT_USERNAME}?start=${ref}_c${c.id}`;
        const linkWeb=`${APP_ORIGIN}/?ref=${ref}&combo=${c.id}`;
        return(
          <GCard key={c.id} style={{padding:"12px 16px",marginBottom:8}}>
            <div onClick={()=>setAbierto(abierta?null:c.id)}
              style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                gap:SPACING[8],cursor:"pointer"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontWeight:700,fontSize:13,
                  fontFamily:F_BODY}}>{c.nombre}
                  {c.codigo&&<span style={{background:`${Q.cyan}22`,border:`1px solid ${Q.cyan}`,
                    borderRadius:RADII.md,padding:"4px 8px",fontSize:12,color:Q.cyan,marginLeft:6}}>{c.codigo}</span>}</div>
                <div style={{color:Q.muted,fontSize:12}}>{c.picks.length} picks · {fmt(c.odd)}x{c.fecha?` · ${fechaHora(c.fecha)}`:""}</div>
              </div>
              <span style={{color:Q.muted,fontSize:16}}>{abierta?"▾":"›"}</span>
            </div>

            {abierta&&(
              <div style={{marginTop:10,paddingTop:SPACING[12],borderTop:`1px solid ${Q.dim}`}}>
                {c.picks.map((p,j)=>(
                  <div key={j} style={{color:Q.muted,fontSize:12,marginBottom:4,
                    fontFamily:F_BODY}}>
                    {(p.home||p.h||"")}{(p.home||p.h)?" · ":""}
                    <span style={{color:Q.cyan}}>{p.sel||p.label||""}</span>
                    <span style={{color:Q.violet2}}> @{fmt(p.odd)}</span></div>
                ))}
                {/* Ganancias */}
                <div style={{marginTop:8,marginBottom:6}}>
                  {montosDe(agencia?.moneda).slice(1).map(m=>(
                    <div key={m} style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                      <span style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>Apostás {money(m, mon)}</span>
                      <span style={{color:Q.green,fontWeight:700,fontSize:12,
                        fontFamily:F_BODY}}>→ {money(Math.round(m*(c.odd||1)), mon)}</span>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:SPACING[8],marginTop:6}}>
                  <button onClick={()=>copiar(linkTg,`tg${c.id}`)} style={{background:`${Q.cyan}18`,
                    border:`1px solid ${Q.cyan}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.cyan,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`tg${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Smartphone size={12}/> Link Telegram con tu firma</>}</button>
                  <button onClick={()=>copiar(linkWeb,`web${c.id}`)} style={{background:`${Q.violet}18`,
                    border:`1px solid ${Q.violet}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.violet2,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`web${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Globe size={12}/> Link web con tu firma</>}</button>
                  <button onClick={()=>copiar(codOut,`ref${c.id}`)} style={{background:"rgba(255,255,255,0.05)",
                    border:`1px solid ${Q.border}`,borderRadius:RADII.md,padding:"8px",cursor:"pointer",
                    color:Q.muted,fontSize:12,fontWeight:700,textAlign:"left",
                    fontFamily:F_BODY}}>
                    {copiado===`ref${c.id}`?<><Icon name="circle-check" size={12}/> Copiado</>:<><Icon name="ticket" size={12}/> Copiar código ({codOut}) para agencia</>}</button>
                  <div style={{height:1,background:Q.dim,margin:"4px 0"}}/>
                  <button onClick={()=>copiar(textoCombo(c,ref,linkTg),`txt${c.id}`)}
                    style={{background:`${Q.green}18`,border:`1px solid ${Q.green}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.green,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    {copiado===`txt${c.id}`?<><Icon name="circle-check" size={12}/> Texto copiado</>:<><FileText size={12}/> Copiar texto para redes</>}</button>
                  <button onClick={()=>descargarPlacaCombo(c,ref,linkTg)}
                    style={{background:`${Q.gold}18`,border:`1px solid ${Q.gold}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.gold,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    <ImageIcon size={12}/> Descargar placa (imagen)</button>
                  <button onClick={()=>setEditando(c)}
                    style={{background:`${Q.violet}18`,border:`1px solid ${Q.violet}`,borderRadius:RADII.md,
                    padding:"8px",cursor:"pointer",color:Q.violet2,fontSize:12,fontWeight:700,
                    textAlign:"left",fontFamily:F_BODY}}>
                    <Palette size={12}/> Editar imagen (Instagram)</button>
                </div>
              </div>
            )}
          </GCard>
        );
      })}

      {editando&&<EditorPlaca combo={editando} codigoRef={ref}
        link={`https://t.me/${BOT_USERNAME}?start=${ref}_c${editando.id}`}
        onCerrar={()=>setEditando(null)}/>}
    </div>
  );
}

function CrearComboInfluencer({ agencia, onListo, onSesionExpirada }){
  const mon = agencia?.moneda || "ARS";
  const [nombre,setNombre]=useState("");
  const [codigo,setCodigo]=useState("");
  const [picks,setPicks]=useState([]);
  const [prematch,setPrematch]=useState(null);
  const [liga,setLiga]=useState(null);
  const [busqueda,setBusqueda]=useState("");
  const [msg,setMsg]=useState(null); // {text, ok} | null — status lives here, not in the text
  const [proc,setProc]=useState(false);
  const [editando,setEditando]=useState(null);

  useEffect(()=>{
    fetch(`${API_BOT}/api/live/prematch`)
      .then(r=>r.ok?r.json():null)
      .then(data=>setPrematch(data?.sports || []))
      .catch(()=>setPrematch([]));
  },[]);

  const hasPick=(home,sel)=>picks.some(p=>p.home===home&&p.sel===sel);
  // `market` y `nombre` son el mercado y el nombre del resultado TAL COMO LOS
  // EMITE GR8, y los trae `nombresDe1x2` leyendo `ev.markets.h2h`. Este
  // `togglePick` declaraba CUATRO argumentos y no guardaba ninguno de los dos,
  // así que el combo del influencer se publicaba con la etiqueta de pantalla
  // donde va la clave: se vende y lo liquida una persona a mano. Es la cuarta
  // puerta de la misma clase, y la única que quedaba sin arreglar después de
  // la agencia y las dos del admin.
  //
  // Van ADEMÁS de `sel`, que sigue siendo la etiqueta —lo que el seguidor lee
  // en la placa del combo— y la identidad del carrito (`hasPick`).
  const togglePick=(ev,sport,label,odd,market,nombre)=>{
    setPicks(ps=>{
      // un pick por partido: si ya hay uno de ese partido, lo reemplaza
      const sinEse=ps.filter(p=>!(p.home===ev.home&&p.away===ev.away));
      if(ps.some(p=>p.home===ev.home&&p.sel===label)) return sinEse;
      return [...sinEse,{home:ev.home,away:ev.away,sel:label,odd,
        market:market||"",nombre:nombre||"",
        sport,event_id:ev.event_id,sport_key:ev.sport_key}];
    });
  };
  const quitarPick=(i)=>setPicks(ps=>ps.filter((_,j)=>j!==i));

  const oddTotal=picks.reduce((a,p)=>a*(parseFloat(p.odd)||1),1);

  const guardar=async()=>{
    if(picks.length===0){ setMsg({text:"Elegí al menos un partido", ok:false}); return; }
    setProc(true); setMsg(null);
    try{
      const r=await fetch(`${API_URL}/api/influencer/me/combos`,{
        method:"POST",headers:{"Content-Type":"application/json",...authHeaders(agencia.token)},
        // `sel` se queda como está —es la etiqueta que el seguidor lee— y el
        // mercado y el nombre del emisor viajan AL LADO. La contraparte de
        // servidor es `_procesar_picks_combo`, la lista blanca que los
        // descartaba igual que las del admin y de la agencia.
        body:JSON.stringify({nombre,codigo,picks:picks.map(p=>({
          home:p.home,away:p.away,sel:p.sel,odd:parseFloat(p.odd),
          sport:p.sport,event_id:p.event_id,sport_key:p.sport_key,
          market:p.market||"",nombre:p.nombre||""}))}),
      });
      if(r.status===401){ onSesionExpirada(); return; }
      if(!r.ok){ const e=await r.json().catch(()=>({})); throw new Error(e.detail||`Error ${r.status}`); }
      const d=await r.json();
      setMsg({text:"Combo creado", ok:true});
      // Ofrecer editar la placa del combo recién creado
      setEditando({id:d.id, nombre, codigo, picks, odd_total:d.odd_total||oddTotal});
      setProc(false);
    }catch(e){ setMsg({text:e.message, ok:false}); setProc(false); }
  };

  return(
    <GCard style={{padding:SPACING[16],marginBottom:14}}>
      <div style={{color:Q.violet2,fontWeight:700,fontSize:14,marginBottom:12,
        fontFamily:F_BODY}}><Zap size={13}/> Nuevo combo · elegí de la oferta real</div>
      <input value={nombre} onChange={e=>setNombre(e.target.value)} placeholder="Nombre del combo"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:16,marginBottom:8,
          fontFamily:F_BODY}}/>
      <input value={codigo} onChange={e=>setCodigo(e.target.value.toUpperCase())}
        placeholder="Código de salida (opcional, ej: JUANTIPS)"
        style={{width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${Q.border}`,
          borderRadius:RADII.md,padding:"8px 12px",color:Q.text,fontSize:16,marginBottom:12,
          fontFamily:F_BODY}}/>

      {/* Picks elegidos + ganancias */}
      {picks.length>0&&(
        <GCard style={{padding:SPACING[12],marginBottom:12,
          background:`linear-gradient(135deg,${Q.violet}10,${Q.gold}08)`}}>
          <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
            marginBottom:8,fontFamily:F_BODY}}>Tu combo ({picks.length})</div>
          {picks.map((p,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              padding:"4px 0",borderBottom:i<picks.length-1?`1px solid ${Q.dim}`:"none"}}>
              <div style={{minWidth:0,flex:1}}>
                <div style={{color:Q.text,fontSize:12,fontWeight:600,
                  fontFamily:F_BODY}}>{p.sel}</div>
                <div style={{color:Q.muted,fontSize:12}}>{p.home} vs {p.away}</div>
              </div>
              <span style={{color:Q.cyan,fontWeight:700,fontSize:13,marginRight:8,
                fontFamily:F_BODY}}>{fmt(p.odd)}</span>
              <span onClick={()=>quitarPick(i)} style={{color:Q.red,fontSize:12,cursor:"pointer"}}>✕</span>
            </div>
          ))}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
            marginTop:10,paddingTop:SPACING[8],borderTop:`1px solid ${Q.violet}44`}}>
            <span style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              fontFamily:F_BODY}}>Cuota total</span>
            <span style={{color:Q.gold,fontWeight:900,fontSize:22,
              fontFamily:F_BODY}}>{fmt(oddTotal)}x</span>
          </div>
          <div style={{marginTop:8}}>
            <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
              marginBottom:4,fontFamily:F_BODY}}>Ganancias</div>
            {montosDe(agencia?.moneda).slice(1).map(m=>(
              <div key={m} style={{display:"flex",justifyContent:"space-between",padding:"4px 0"}}>
                <span style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
                  Apostás {money(m, mon)}</span>
                <span style={{color:Q.green,fontWeight:700,fontSize:12,
                  fontFamily:F_BODY}}>→ {money(Math.round(m*oddTotal), mon)}</span>
              </div>
            ))}
          </div>
        </GCard>
      )}

      <Btn label={proc?"GUARDANDO...":<><Save size={13}/> Crear combo</>} onClick={guardar}
        color={Q.violet} full disabled={proc||picks.length===0}/>
      {msg&&<div style={{fontSize:12,marginTop:8,
        color:msg.ok?Q.green:Q.red,
        fontFamily:F_BODY}}><Icon name={msg.ok?"circle-check":"triangle-alert"} size={13}/> {msg.text}</div>}

      {editando&&(
        <div style={{marginTop:12}}>
          {/* Editing the image and finishing are two independent choices at this step, so they share a line */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:SPACING[8]}}>
            <div>
              <Btn label={<><Palette size={13}/> Editar imagen (crear placa)</>} onClick={()=>setEditando({...editando,_abrir:true})}
                color={Q.violet} outline full/>
            </div>
            <div>
              <Btn label="✓ Listo" onClick={onListo} color={Q.green} full/>
            </div>
          </div>
        </div>
      )}
      {editando&&editando._abrir&&<EditorPlaca combo={editando}
        codigoRef={agencia.codigo_ref||""}
        link={`https://t.me/${BOT_USERNAME}?start=${agencia.codigo_ref||""}_c${editando.id}`}
        onCerrar={()=>setEditando({...editando,_abrir:false})}/>}

      {/* Oferta de prematch para elegir */}
      <div style={{marginTop:16}}>
        <div style={{color:Q.muted,fontSize:12,textTransform:"uppercase",letterSpacing:1,
          marginBottom:8,fontFamily:F_BODY}}><Icon name="clipboard-list" size={12}/> Elegí partidos</div>
        {!prematch&&<GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
            Cargando cuotas...</div></GCard>}
        {prematch&&prematch.length>0&&(
          <FiltroEventos ligas={prematch} liga={liga} setLiga={setLiga}
            busqueda={busqueda} setBusqueda={setBusqueda}
            total={prematch.reduce((a,d)=>a+(d.events||[]).length,0)}/>
        )}
        {prematch&&prematch.length===0&&<GCard style={{padding:SPACING[20],textAlign:"center"}}>
          <div style={{color:Q.muted,fontSize:12,fontFamily:F_BODY}}>
            No hay eventos con cuotas ahora</div></GCard>}
        {filtrarDeportes(prematch||[], liga, busqueda).map(d=>(
          <GCard key={d.name} style={{padding:SPACING[16],marginBottom:10}}>
            <div style={{color:Q.violet2,fontWeight:700,fontSize:13,marginBottom:10,
              fontFamily:F_BODY}}>{d.icon} {d.name}</div>
            {(d.events||[]).map(ev=>(
              <div key={ev.id||(ev.h+ev.a)} style={{marginBottom:12,
                borderBottom:`1px solid ${Q.dim}`,paddingBottom:SPACING[12]}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6,gap:SPACING[8]}}>
                  <div style={{display:"flex",alignItems:"center",gap:SPACING[8],minWidth:0}}>
                    <TeamLogo name={ev.h} size={20}/>
                    <span style={{color:Q.text,fontWeight:600,fontSize:12,
                      fontFamily:F_BODY,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.h} vs {ev.a}</span>
                    <TeamLogo name={ev.a} size={20}/>
                  </div>
                  <span style={{color:Q.muted,fontSize:12,flexShrink:0}}>{horaLocal(ev)}</span>
                </div>
                <div style={{display:"flex",gap:SPACING[4]}}>
                  {/* La etiqueta NO se toca: el seguidor sigue leyendo
                      "Boca gana" y "Empate". Lo que se agrega es el nombre
                      del emisor y su mercado, que vienen de `nombresDe1x2`
                      leyendo el diccionario del catálogo y van vacíos cuando
                      ese diccionario no tiene el resultado. */}
                  {[{label:ev.h+" gana",odd:ev.odds?.L,...nombresDe1x2(ev).L},
                    ev.odds?.E?{label:"Empate",odd:ev.odds.E,
                                ...nombresDe1x2(ev).E}:null,
                    {label:ev.a+" gana",odd:ev.odds?.V,...nombresDe1x2(ev).V}]
                    .filter(Boolean).filter(o=>o.odd).map(opt=>(
                    <button key={opt.label}
                      onClick={()=>togglePick({home:ev.h,away:ev.a,event_id:ev.id,sport_key:ev.sport_key,commence_time:ev.commence_time||ev.time||""},d.name,opt.label,opt.odd,opt.market,opt.nombre)}
                      style={{flex:"1 1 0",minWidth:0,overflow:"hidden",
                        background:hasPick(ev.h,opt.label)
                          ?`linear-gradient(135deg,${Q.violet}44,${Q.cyan}22)`
                          :"rgba(255,255,255,0.04)",
                        border:`1.5px solid ${hasPick(ev.h,opt.label)?Q.cyan:Q.border}`,
                        borderRadius:RADII.md,padding:"8px 4px",cursor:"pointer",textAlign:"center"}}>
                      <div style={{color:Q.muted,fontSize:12,overflow:"hidden",
                        textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{opt.label}</div>
                      <div style={{color:hasPick(ev.h,opt.label)?Q.cyan:Q.text,
                        fontWeight:700,fontSize:15,
                        fontFamily:F_BODY}}>{opt.odd}</div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </GCard>
        ))}
      </div>
    </GCard>
  );
}

// Salir del panel: limpia lo local Y avisa al servidor para que borre la fila.
//
// Antes salir solo borraba `qp_agencia` del navegador: el token seguía
// abriendo en el servidor hasta que vencía solo, así que quien se lo hubiera
// copiado, o un teléfono perdido, seguía operando con la plata de los
// jugadores. El aviso es lo que de verdad cierra la sesión.
//
// LO LOCAL SE LIMPIA PRIMERO Y NO DEPENDE DEL SERVIDOR. Si el pedido falla
// (sin señal, servidor caído, 401 porque ya había vencido) o ni siquiera
// termina, la persona sale igual. Dejarla "adentro" de su panel porque no se
// pudo avisar es peor que una sesión huérfana en el servidor, que vence sola
// en `SESSION_TTL_H`: quien toca Salir en un equipo ajeno tiene que ver la
// pantalla de login sí o sí. Por eso el token se captura antes y el pedido
// va después, sin `await` en el camino de la limpieza.
export function salirDeLaSesion(token, limpiarLocal, fetchFn){
  limpiarLocal();
  if(!token) return Promise.resolve(false);
  const llamar = fetchFn || (typeof fetch==="function" ? fetch : null);
  if(!llamar) return Promise.resolve(false);
  // `keepalive` para que el aviso no se corte si la pestaña se cierra o
  // recarga justo después de salir.
  try{
    return Promise.resolve(llamar(`${API_URL}/api/agencias/logout`,
      {method:"POST", headers:authHeaders(token), keepalive:true}))
      .then(r=>!!(r&&r.ok), ()=>false);
  }catch(e){ return Promise.resolve(false); }
}

export default function QuartzAgencia(){
  // La sesión se guarda en localStorage para sobrevivir a un F5.
  // Antes vivía solo en memoria: al actualizar volvía a null y pedía login.
  const [agencia,setAgenciaRaw]=useState(()=>{
    try{
      const g = localStorage.getItem("qp_agencia");
      return g ? JSON.parse(g) : null;
    }catch(e){ return null; }
  });
  const setAgencia=(a)=>{
    setAgenciaRaw(a);
    try{
      if(a) localStorage.setItem("qp_agencia", JSON.stringify(a));
      else  localStorage.removeItem("qp_agencia");
    }catch(e){}
  };
  const [avisoSesion,setAvisoSesion]=useState(false);

  // Las sesiones guardadas antes de que el login devolviera la moneda
  // no la tienen: se completa al arrancar. Sin esto, una agencia en USD
  // seguiría viendo montos en pesos hasta volver a entrar.
  useEffect(()=>{
    if(!agencia?.token || agencia?.moneda) return;
    fetch(`${API_URL}/api/agencias/me/cc`,
      {headers:authHeaders(agencia.token)})
      .then(r=>r.ok?r.json():null)
      .then(d=>{ if(d?.moneda) setAgencia({...agencia, moneda:d.moneda}); })
      .catch(()=>{});
    // eslint-disable-next-line
  },[agencia?.token]);

  const sesionExpirada=()=>{
    setAgencia(null);
    setAvisoSesion(true);
  };

  // Las tres salidas (cambio de clave obligado, panel de influencer y de
  // agencia) pasan por acá: ninguna puede quedarse con el token vivo.
  const salir=()=>salirDeLaSesion(agencia?.token, ()=>setAgencia(null));

  return(
    <div style={{background:Q.void,minHeight:"100vh"}}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        html,body,#root{height:100%;overscroll-behavior:none;
                        max-width:100%;overflow-x:hidden}
        /* 16px evita que iOS haga zoom al tocar un campo */
        input,select,textarea{font-size:16px}
        button{font-family:inherit;-webkit-tap-highlight-color:transparent;
               touch-action:manipulation}
        @keyframes qPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.3;transform:scale(1.4)}}
        input:focus{outline:none} button:active{opacity:.85}
        ::-webkit-scrollbar{width:3px}
        ::-webkit-scrollbar-thumb{background:rgba(124,58,237,0.3)}
        ${CSS_CUOTAS_CARRUSEL}
      `}</style>
      {!agencia ? (
        <div style={{position:"relative"}}>
          {avisoSesion&&(
            <div style={{position:"fixed",top:12,left:"50%",transform:"translateX(-50%)",
              zIndex:100,width:"calc(100% - 32px)",maxWidth:380}}>
              <AlertaError mensaje="La sesión expiró por inactividad. Volvé a ingresar."/>
            </div>
          )}
          <LoginScreen onLogin={a=>{setAvisoSesion(false);setAgencia(a);}}/>
        </div>
      ) : agencia.debe_cambiar_pass ? (
        <div style={{maxWidth:440,margin:"0 auto",padding:"40px 16px"}}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{marginBottom:8}}><Lock size={44}/></div>
            <div style={{color:Q.text,fontWeight:800,fontSize:18,
              fontFamily:F_BODY}}>Bienvenido/a {agencia.name}</div>
          </div>
          <CambiarMiPassword agencia={agencia} obligado
            onListo={()=>setAgencia(a=>({...a,debe_cambiar_pass:false}))}/>
          <button onClick={salir} style={{width:"100%",
            background:"transparent",border:`1px solid ${Q.border}`,borderRadius:RADII.md,
            padding:"12px",color:Q.muted,fontSize:12,cursor:"pointer",marginTop:8,
            fontFamily:F_BODY}}>Salir</button>
        </div>
      ) : agencia.tipo==="influencer" ? (
        <InfluencerPanel agencia={agencia}
          onLogout={salir}
          onSesionExpirada={sesionExpirada}/>
      ) : (
        <AgenciaPanel agencia={agencia}
          onLogout={salir}
          onSesionExpirada={sesionExpirada}/>
      )}
    </div>
  );
}
